import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, ArrowRight, User, Calendar as CalendarIcon, Stethoscope, Armchair,
  Plus, Trash2, Check, X, Search, AlertTriangle, Save,
  Banknote, Smartphone, CreditCard, Building2, Receipt, PrinterIcon, CheckCircle2,
  QrCode, Send, PenLine,
  CalendarPlus, Inbox, Ban, Info, ClipboardList, Lock,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { isBareToothBooking } from "@/lib/procedureFlags";
import { surfaceBreakdown, surfaceBreakdownReconciles, surfaceCount as countSurfaces } from "@/lib/surfacePricing";
import { reconcileChartToReminders } from "@/lib/scheduleReconcile";
import { useBranch } from "@/context/BranchContext";
import { useOngoingTreatmentRollout } from "@/context/OngoingTreatmentRolloutContext";
import QRPaymentModal from "@/components/checkout/QRPaymentModal";
import CheckoutMaterials from "@/components/checkout/CheckoutMaterials";
import ReceiptPrint from "@/components/checkout/ReceiptPrint";
import BookFutureNeedDialog from "@/components/checkout/BookFutureNeedDialog";
import DeclineFutureNeedDialog from "@/components/checkout/DeclineFutureNeedDialog";
import PaymentAllocationSummary from "@/components/checkout/PaymentAllocationSummary";
import PaymentReview from "@/components/checkout/PaymentReview";
import CheckoutSettlementReview from "@/components/checkout/CheckoutSettlementReview";
import OrthoCaseGlance, { selectAppointmentOrthoCase } from "@/components/checkout/OrthoCaseGlance";
import PlanVisitFollowUp from "@/components/checkout/PlanVisitFollowUp";
import OtherActivePlansFollowUp from "@/components/checkout/OtherActivePlansFollowUp";
import SignatureDialog from "@/components/documents/SignatureDialog";
import AddRowControls from "@/components/checkout/AddRowControls";
import NotTodayReasonDialog from "@/components/doctor/NotTodayReasonDialog";
import FoldIntoPlanToggle from "@/components/checkout/FoldIntoPlanToggle";
import PaymentPlanCard from "@/components/checkout/PaymentPlanCard";
import SetBillingModelDialog from "@/components/treatment/SetBillingModelDialog";
import { SetupBillingDialog } from "@/components/ortho/OrthoPanel";
import { CHECKOUT_FOCUS_STATE, CHECKOUT_NAV_REQUEST } from "@/layout/Sidebar";
import { describeFollowUpDays } from "@/lib/followUpWindow";
import {
  followUpBookingHeading,
  followUpItemNounPlural,
  followUpRequestedDays,
  isPostOpFollowUpBundle,
} from "@/lib/followUpLabels";
import { resolvePhaseCadenceForVisits } from "@/lib/txPlanPhaseCadence";
import { buildAtomicBookingDispositions } from "@/lib/checkoutFollowUpPayload";
import {
  buildBundleableOrthoNeed,
  checkoutNeedIdsForCommit,
  resolvedOrthoNextVisitDecision,
  withOrthoBundle,
} from "@/lib/orthoFutureNeedBundle";
import { buildPostOpSuggestions, checkoutWorkKey, sameCheckoutWork } from "@/lib/postOpSuggestions";
import { resolvePostOpPlanBookingPriority } from "@/lib/postOpPlanPriority";
import {
  buildCheckoutFollowupWaivers,
  legacyFollowupWaiverRpcInput,
} from "@/lib/postOpWaivers";
import { buildReceiptItems } from "@/lib/checkoutReceipt";
import { sendCheckoutReceiptEmailNotification } from "@/lib/checkoutReceiptEmail";
import { completedReceiptChargeEvents } from "@/lib/completedCheckoutReceipt";
import {
  hasUnresolvedPendingClinicalSave,
  isPendingClinicalSaveOrthoRow,
  requiresSavedOrthoClinicalRecord,
  seedCheckoutScheduledItem,
} from "@/lib/checkoutOrthoClinicalSave";
import {
  DOCUMENT_TYPES,
  createDocumentSnapshot,
  getSignedDocumentStatus,
  signDocumentSnapshot,
  signatureForPdf,
} from "@/lib/signedDocuments";
import {
  calculateCurrentVisitAllocationCreditCentavos,
  canSubmitCheckoutPayment,
} from "@/lib/checkoutPaymentGate";
import { buildCheckoutItemPayloadRows } from "@/lib/checkoutPayload";
import {
  buildCheckoutPriorArCollections,
  normalizeCheckoutPriorArRows,
  priorArCollectionAmountCentavos,
  selectedPriorArTotalCentavos,
} from "@/lib/checkoutPriorAr";
import { planCheckoutSubmission } from "@/lib/checkoutSettlementRoute";
import { buildCheckoutPlanProgressSummary } from "@/lib/checkoutPlanPaymentSummary";
import {
  checkoutDraftItemIdentity,
  dropDeadPlanDraftLines,
  mergeCheckoutDraftItems,
  repairDraftItemsWithScheduled,
} from "@/lib/checkoutDraftItems";
import {
  discardCheckoutDraftWithRevision,
  isCheckoutDraftRevisionConflict,
  normalizeCheckoutDraftRevision,
  saveCheckoutDraftWithRevision,
} from "@/lib/checkoutDraftRevision";
import { checkoutPlanIds } from "@/lib/checkoutPlanIds";
import {
  buildBundleFinishFields,
  buildDenturePaymentAllocation,
  currentDentureItemIds,
  defaultDentureManualAmounts,
  dentureBillingItems,
  dentureItemLabel,
  isDentureItemPaidInFull,
} from "@/lib/dentureBilling";
import { buildOrthoVisitIdempotencyKey } from "@/components/ortho/record/orthoVisitPayload";
import {
  calculatePercentDiscountCentavos,
  centavosToPesoInput,
  formatPercentLabel,
  parsePesoToCentavos,
} from "@/lib/money";
import {
  addClinicDays,
  clinicDateAt,
  clinicTodayDateString,
  CLINIC_TIME_ZONE,
} from "@/lib/clinicTime";
import { qrCodeSvgDataUrl } from "@/lib/qrCodeSvg";
import { checkoutTutorialStageSeed } from "@/tutorials/checkoutTutorialStage";
import {
  deriveNoTreatmentReasonCode,
  hasMissingNotTodayReason,
  isNoPaymentVisitEligible,
  notTodayReasonLabel,
} from "@/lib/noPaymentVisit";

// =====================================================================
// Checkout — Step 1 (Today)
//
// Hydrates from get_checkout_hydration() and renders:
//   * Header: patient + appointment + senior/PWD banner
//   * Items list: scheduled treatment-needs pre-fill rows;
//     receptionist edits qty/teeth/price/discount/status; can add
//     catalog procedures or custom one-offs.
//   * Auto-save to checkout_drafts (debounced) so a page reload picks
//     up where the receptionist left off.
//
// Steps 2 (Payment) + 3 (Follow-up) ship in follow-up PRs. The
// "Continue to payment" button is disabled with a "Coming soon"
// tooltip until then.
// =====================================================================

const STATUS_OPTIONS = [
  { key: "done",         label: "Done",        color: "emerald" },
  { key: "not_today",    label: "Not today",   color: "slate" },
  { key: "future_need",  label: "Future need", color: "amber" },
  { key: "declined",     label: "Declined",    color: "rose" },
];

const DEFAULT_SENIOR_PWD_DISCOUNT_PCT = 20;
const CHECKOUT_TODAY_TUTORIAL_APPOINTMENT_ID = "tutorial-checkout-today";
const CHECKOUT_TODAY_TUTORIAL_SEARCH = "tutorial=checkout-today&demo=1";
const CHECKOUT_TODAY_TUTORIAL_VISIT_ROUTE = `/checkout?appointment_id=${CHECKOUT_TODAY_TUTORIAL_APPOINTMENT_ID}&${CHECKOUT_TODAY_TUTORIAL_SEARCH}`;
const CHECKOUT_TODAY_TUTORIAL_NET_CENTAVOS = 2170000;
const CHECKOUT_TODAY_TUTORIAL_MOCK_QR = Object.freeze({
  id: "tutorial-gcash-demo-qr",
  image_url: qrCodeSvgDataUrl(
    "DENTAL-OS-TUTORIAL-GCASH-DEMO-NOT-FOR-PAYMENT",
    "Demo GCash QR, not for payment",
  ),
  label: "GCash demo QR",
  method: "gcash",
  source: "tutorial_mock",
});
const HMO_TUTORIAL_DEMO_PAYER = {
  id: "tutorial-maxicare-hmo",
  name: "Maxicare",
  short_name: "MAXICARE",
};
const HMO_TUTORIAL_DEMO_LOA_NUMBER = "LOA-MAXICARE-0529";
const HMO_TUTORIAL_DEMO_MEMBER_ID = "MAX-77881";
const HMO_TUTORIAL_DEMO_COVERED_CENTAVOS = 180000;
const CHECKOUT_ADD_PROCEDURES_TUTORIAL_APPOINTMENT_ID = "tutorial-checkout-add-procedures";
const CHECKOUT_ADD_PROCEDURES_TUTORIAL_SEARCH = "tutorial=checkout-add-procedures&demo=1";
const CHECKOUT_ADD_PROCEDURES_TUTORIAL_VISIT_ROUTE = `/checkout?appointment_id=${CHECKOUT_ADD_PROCEDURES_TUTORIAL_APPOINTMENT_ID}&${CHECKOUT_ADD_PROCEDURES_TUTORIAL_SEARCH}`;
const CHECKOUT_TX_PLAN_TUTORIAL_APPOINTMENT_ID = "tutorial-checkout-tx-plan";
const CHECKOUT_TX_PLAN_TUTORIAL_PLAN_ID = "tutorial-tx-plan-1";
const CHECKOUT_TX_PLAN_TUTORIAL_TOTAL_CENTAVOS = 2650000;
const CHECKOUT_TX_PLAN_TUTORIAL_BILLING_STATE = {
  plan_id: CHECKOUT_TX_PLAN_TUTORIAL_PLAN_ID,
  plan_code: "TP-TUTORIAL",
  plan_status: "Accepted",
  pipeline_stage: "Scheduled",
  total_centavos: CHECKOUT_TX_PLAN_TUTORIAL_TOTAL_CENTAVOS,
  plan_total_items_centavos: CHECKOUT_TX_PLAN_TUTORIAL_TOTAL_CENTAVOS,
  original_estimate_centavos: CHECKOUT_TX_PLAN_TUTORIAL_TOTAL_CENTAVOS,
  amendments_centavos: 0,
  final_total_centavos: CHECKOUT_TX_PLAN_TUTORIAL_TOTAL_CENTAVOS,
  paid_centavos: 0,
  remaining_centavos: CHECKOUT_TX_PLAN_TUTORIAL_TOTAL_CENTAVOS,
  total_visits: 4,
  completed_visits: 0,
  remaining_visits: 4,
  is_first_visit_pending: true,
  is_final_visit_pending: false,
  requires_clearing: false,
  charge_locked: false,
  preset: null,
  suggested_next_centavos: null,
  custom_schedule: null,
};
const CHECKOUT_ONGOING_TUTORIAL_APPOINTMENT_ID = "tutorial-checkout-ongoing-treatment";
const CHECKOUT_ONGOING_TUTORIAL_PLAN_ID = "tutorial-ongoing-treatment";
const CHECKOUT_ONGOING_TUTORIAL_TOTAL_CENTAVOS = 850000;
const CHECKOUT_ONGOING_TUTORIAL_HALF_CENTAVOS = 425000;
const CHECKOUT_ONGOING_TUTORIAL_BILLING_STATE = {
  plan_id: CHECKOUT_ONGOING_TUTORIAL_PLAN_ID,
  plan_code: "MV-TUTORIAL",
  plan_status: "Accepted",
  pipeline_stage: "Scheduled",
  total_centavos: CHECKOUT_ONGOING_TUTORIAL_TOTAL_CENTAVOS,
  plan_total_items_centavos: CHECKOUT_ONGOING_TUTORIAL_TOTAL_CENTAVOS,
  original_estimate_centavos: CHECKOUT_ONGOING_TUTORIAL_TOTAL_CENTAVOS,
  amendments_centavos: 0,
  final_total_centavos: CHECKOUT_ONGOING_TUTORIAL_TOTAL_CENTAVOS,
  paid_centavos: 0,
  remaining_centavos: CHECKOUT_ONGOING_TUTORIAL_TOTAL_CENTAVOS,
  // Ongoing treatment is open-ended. The catalogue count is an estimate,
  // never a fixed final-visit countdown.
  total_visits: 0,
  completed_visits: 0,
  remaining_visits: 0,
  is_first_visit_pending: false,
  is_final_visit_pending: false,
  requires_clearing: false,
  charge_locked: true,
  preset: "50_50",
  suggested_next_centavos: 425000,
  custom_schedule: null,
};

function normalizeCheckoutOngoingTutorialVisit(value) {
  const visit = Number(value || 1);
  return Number.isFinite(visit) ? Math.min(3, Math.max(1, Math.trunc(visit))) : 1;
}

export function buildCheckoutOngoingTutorialBillingState(visitValue = 1) {
  const visit = normalizeCheckoutOngoingTutorialVisit(visitValue);
  const firstHalfAlreadyPaid = visit > 1;
  const paidCentavos = firstHalfAlreadyPaid ? CHECKOUT_ONGOING_TUTORIAL_HALF_CENTAVOS : 0;
  const remainingCentavos = CHECKOUT_ONGOING_TUTORIAL_TOTAL_CENTAVOS - paidCentavos;
  return {
    ...CHECKOUT_ONGOING_TUTORIAL_BILLING_STATE,
    paid_centavos: paidCentavos,
    remaining_centavos: remainingCentavos,
    completed_visits: visit - 1,
    is_final_visit_pending: visit === 3,
    suggested_next_centavos: visit === 2 ? 0 : CHECKOUT_ONGOING_TUTORIAL_HALF_CENTAVOS,
  };
}

export function requiresFlexibleCollectionDecision(billingState) {
  const remaining = Math.max(0, Number(billingState?.remaining_centavos || 0));
  const suggested = Math.max(0, Number(billingState?.suggested_next_centavos || 0));
  if (billingState?.denture_item_billing) {
    return remaining > 0 && suggested === 0;
  }
  if (billingState?.preset !== "custom") return false;
  const schedule = billingState?.custom_schedule || {};
  const dp = Math.max(0, Number(schedule.dp_centavos || 0));
  const paid = Math.max(0, Number(billingState?.paid_centavos || 0));

  // A real first-visit down payment remains due. After that anchor has been
  // collected, Flexible means the clinic chooses today's collection amount;
  // legacy min-per-visit / installment suggestions must not become an
  // automatic checkout amount.
  const downPaymentStillDue = dp > 0 && paid < dp;
  return remaining > 0 && !downPaymentStillDue;
}

export function isDenturePaymentOnlyCollection({
  billingState,
  doneItemCount = 0,
  collectionTargetCentavos = 0,
} = {}) {
  return !!billingState?.denture_item_billing
    && Number(doneItemCount || 0) === 0
    && Number(collectionTargetCentavos || 0) > 0;
}

export function resolveFlexibleCollectionTarget({ billingState, planId, choice }) {
  if (!requiresFlexibleCollectionDecision(billingState)) {
    return Math.max(0, Number(billingState?.suggested_next_centavos || 0));
  }
  if (!choice || choice.plan_id !== planId) return 0;
  if (choice.decision === "none") return 0;
  if (choice.decision !== "amount") return 0;
  return Math.min(
    Math.max(0, Number(choice.amount_centavos || 0)),
    Math.max(0, Number(billingState?.remaining_centavos || 0)),
  );
}

// Finishing a treatment changes what is billable; it does not prove that any
// money was received. Keep the receptionist's recorded tender exactly as-is
// and only clear stale per-denture allocation overrides so the universal
// settlement preview can re-sort the unchanged payment against the newly due
// balance. This is intentionally a pure helper so the clinical/financial
// boundary has a permanent regression test.
export function paymentStateAfterBundleFinish(paymentState = {}) {
  return {
    ...paymentState,
    denture_item_allocation_overrides_enabled: false,
    denture_item_allocation_overrides: {},
  };
}

export function resolvePrimaryPlanAllocationTarget({
  collectionTargetCentavos,
  remainingCentavos,
  tenderedCentavos,
  nonPlanObligationCentavos = 0,
  advancePaymentEligible = false,
}) {
  const chosen = Math.max(0, Number(collectionTargetCentavos || 0));
  if (!advancePaymentEligible) return chosen;
  const availableForPlan = Math.max(
    0,
    Math.max(0, Number(tenderedCentavos || 0))
      - Math.max(0, Number(nonPlanObligationCentavos || 0)),
  );
  return Math.min(
    Math.max(0, Number(remainingCentavos || 0)),
    Math.max(chosen, availableForPlan),
  );
}

const CHECKOUT_TODAY_TUTORIAL_SCHEDULED_ITEMS = [
  tutorialScheduledItem("tutorial-composite-15-o", "Composite Restoration", "Composite Restoration", "15", "O", 250000),
  tutorialScheduledItem("tutorial-composite-16-o", "Composite Restoration", "Composite Restoration", "16", "O", 250000),
  tutorialScheduledItem("tutorial-composite-24-d", "Composite Restoration", "Composite Restoration", "24", "D", 250000),
  tutorialScheduledItem("tutorial-composite-27-o", "Composite Restoration", "Composite Restoration", "27", "O", 250000),
  tutorialScheduledItem("tutorial-composite-36-o", "Composite Restoration", "Composite Restoration", "36", "O", 250000),
  tutorialScheduledItem("tutorial-composite-37-d", "Composite Restoration", "Composite Restoration", "37", "D", 250000),
  tutorialScheduledItem("tutorial-composite-44-o", "Composite Restoration", "Composite Restoration", "44", "O", 250000),
  tutorialScheduledItem("tutorial-extraction-48", "Tooth Extraction", "Tooth Extraction", "48", null, 300000),
  tutorialScheduledItem("tutorial-prophy-general", "Oral Prophylaxis", "Oral Prophylaxis", "", null, 120000),
];

const CHECKOUT_TODAY_TUTORIAL_FUTURE_NEEDS = [
  {
    id: "tutorial-future-need-rct-46",
    procedure_id: "tutorial-rct-procedure",
    procedure_name: "Root Canal Treatment",
    color_hex: "#0f766e",
    tooth_number: "46",
    surface: null,
    default_fee_centavos: 850000,
    follow_up_after_days: 14,
    created_at: "2026-05-18T10:10:00+08:00",
    tutorial_id: "checkout-future-needs-step3",
    book_tutorial_id: "checkout-future-book-rct-46",
    handled_tutorial_id: "checkout-future-booked-rct-46",
  },
];

const CHECKOUT_TODAY_TUTORIAL_PROC_MAP = {
  "tutorial-rct-procedure": {
    duration: 60,
    category: "Endodontic",
    name: "Root Canal Treatment",
  },
};

const CHECKOUT_TODAY_TUTORIAL_BOOKING_DATA = {
  doctors: [{ id: "tutorial-doctor", display_name: "Dr. Sample (Demo)" }],
  chairs: [{ id: "tutorial-chair", name: "Chair 1" }],
  settings: {
    hours: {
      sunday: { closed: true },
      monday: { open: "09:00", close: "18:00", closed: false },
      tuesday: { open: "09:00", close: "18:00", closed: false },
      wednesday: { open: "09:00", close: "18:00", closed: false },
      thursday: { open: "09:00", close: "18:00", closed: false },
      friday: { open: "09:00", close: "18:00", closed: false },
      saturday: { open: "09:00", close: "15:00", closed: false },
    },
  },
};

const CHECKOUT_TODAY_TUTORIAL_QUEUE_ROWS = [
  {
    id: CHECKOUT_TODAY_TUTORIAL_APPOINTMENT_ID,
    patient_name: "Juan Dela Cruz",
    patient_number: "DEMO-0001",
    doctor_name: "Dr. Sample (Demo)",
    chair_name: "Chair 1",
    scheduled_start: "2026-05-18T09:00:00+08:00",
    checked_in_at: "2026-05-18T08:55:00+08:00",
    walk_in: false,
    has_draft: true,
  },
];

const CHECKOUT_ADD_PROCEDURES_TUTORIAL_QUEUE_ROWS = [
  {
    ...CHECKOUT_TODAY_TUTORIAL_QUEUE_ROWS[0],
    id: CHECKOUT_ADD_PROCEDURES_TUTORIAL_APPOINTMENT_ID,
    has_draft: false,
  },
];

const CHECKOUT_ADD_PROCEDURES_TUTORIAL_CATALOG = [
  {
    id: "tutorial-add-prophy",
    code: "OP",
    name: "Oral Prophylaxis",
    default_fee_centavos: 120000,
    _tutorialAddedProcedure: true,
  },
  {
    id: "tutorial-add-composite",
    code: "COMP",
    name: "Composite Restoration",
    default_fee_centavos: 250000,
  },
];

function tutorialScheduledItem(tnId, procedureId, procedureName, toothNumber, surface, feeCentavos) {
  return {
    tn_id: tnId,
    work_key: `tn:${tnId}`,
    procedure_id: procedureId,
    procedure_code: null,
    procedure_name: procedureName,
    display_label: procedureName,
    default_fee_centavos: feeCentavos,
    display_price_centavos: feeCentavos,
    performed_price_centavos: feeCentavos,
    tooth_number: toothNumber,
    surface,
    source: "doctor_today",
    treatment_plan_id: null,
    treatment_plan_item_id: null,
  };
}

function normalizeEventMetadata(event) {
  const metadata = event?.metadata;
  return metadata && typeof metadata === "object" && !Array.isArray(metadata)
    ? metadata
    : {};
}

function formatReceiptNumberFromEvent(event) {
  const year = Number(event?.document_year || 0);
  const number = Number(event?.document_number || 0);
  if (!year || !number) return null;
  const prefixes = {
    receipt: "R",
    invoice: "I",
    official_receipt: "OR",
    credit_note: "CN",
  };
  const prefix = prefixes[event?.document_type] || String(event?.document_type || "DOC").toUpperCase();
  return `${prefix}-${year}-${String(number).padStart(6, "0")}`;
}

function completedReceiptProcedureName(event, procedureNameById, orthoCaseNameById) {
  const metadata = normalizeEventMetadata(event);
  const orthoSourceLabels = {
    ortho_down_payment: "Ortho down payment",
    ortho_full_payment: "Ortho full payment",
    ortho_debonding_remaining: "Ortho remaining balance",
  };
  const orthoSourceLabel = orthoSourceLabels[metadata.source] || null;
  const orthoCaseName = event?.ortho_case_id
    ? orthoCaseNameById.get(event.ortho_case_id)
    : null;
  return (
    metadata.procedure_name
    || metadata.custom_name
    || metadata.description
    || (orthoSourceLabel && orthoCaseName ? `${orthoSourceLabel} — ${orthoCaseName}` : orthoSourceLabel)
    || (event?.procedure_id ? procedureNameById.get(event.procedure_id) : null)
    || "Procedure"
  );
}

function isConfirmedPaymentEvent(event) {
  return event?.event_type === "payment" && event?.payment_status === "Confirmed";
}

export function resolveCompletedCheckoutSummary({
  checkoutMetadata,
  chargeEvents,
  discountEvents,
  paymentEvents,
}) {
  const metadata = checkoutMetadata && typeof checkoutMetadata === "object"
    ? checkoutMetadata
    : {};
  const ledgerSubtotalCents = (chargeEvents || [])
    .reduce((sum, event) => sum + Number(event?.amount_centavos || 0), 0);
  const ledgerDiscountCents = (discountEvents || [])
    .reduce((sum, event) => sum + Number(event?.amount_centavos || 0), 0);
  const ledgerPaidCents = (paymentEvents || [])
    .reduce((sum, event) => sum + Number(event?.amount_centavos || 0), 0);

  // complete_checkout records this immutable summary in activity_log in the
  // same transaction as the visit, payment, and allocations. On refresh it is
  // the canonical receipt snapshot. This matters for Tx Plan payments because
  // their charge may have been created at plan acceptance and therefore has no
  // visit_id for today's finalized visit.
  const subtotalCents = metadata.subtotal_centavos == null
    ? ledgerSubtotalCents
    : Number(metadata.subtotal_centavos || 0);
  const discountCents = metadata.total_discount_centavos == null
    ? ledgerDiscountCents
    : Number(metadata.total_discount_centavos || 0);
  const netCents = metadata.net_charged_centavos == null
    ? Math.max(0, subtotalCents - discountCents)
    : Number(metadata.net_charged_centavos || 0);
  const paidCents = metadata.total_paid_centavos == null
    ? ledgerPaidCents
    : Number(metadata.total_paid_centavos || 0);
  const creditAppliedCents = metadata.credit_applied_centavos == null
    ? Math.max(0, paidCents - netCents)
    : Number(metadata.credit_applied_centavos || 0);
  const refundIssuedCents = Number(metadata.refund_issued_centavos || 0);

  return {
    subtotalCents,
    discountCents,
    netCents,
    paidCents,
    creditAppliedCents,
    refundIssuedCents,
  };
}

export function isNoPaymentCheckoutResult(result) {
  if (!result) return false;
  if (result.no_payment_visit) return true;
  if (!result.summary || typeof result.summary !== "object") return false;
  const summary = result.summary;
  return !result.receipt_number
    && Number(summary.subtotal_centavos || 0) === 0
    && Number(summary.net_charged_centavos || 0) === 0
    && Number(summary.total_paid_centavos || 0) === 0
    && Number(summary.credit_applied_centavos || 0) === 0
    && Number(summary.refund_issued_centavos || 0) === 0;
}

async function buildCompletedCheckoutReceiptView({ appointmentId, hydration }) {
  if (!appointmentId) return null;

  const { data: visits, error: visitError } = await supabase
    .from("visits")
    .select("id, clinic_id, patient_id, appointment_id, doctor_id, status, finalized_at, no_treatment_reason_code, created_at")
    .eq("appointment_id", appointmentId)
    .order("finalized_at", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(5);

  if (visitError) throw visitError;

  const visitRows = Array.isArray(visits) ? visits : [];
  const visit =
    visitRows.find((row) => ["Finalized", "Completed"].includes(row?.status))
    || visitRows[0]
    || null;

  if (!visit?.id) return null;

  const { data: events, error: eventError } = await supabase
    .from("money_events")
    .select(`
      id,
      event_type,
      adjustment_type,
      amount_centavos,
      currency,
      payment_method,
      payment_status,
      document_type,
      document_year,
      document_number,
      document_voided,
      reverses_event_id,
      procedure_id,
      procedure_instance_id,
      ortho_case_id,
      metadata,
      occurred_at,
      created_at
    `)
    .eq("visit_id", visit.id)
    .eq("document_voided", false)
    .order("occurred_at", { ascending: true })
    .order("created_at", { ascending: true });

  if (eventError) throw eventError;

  const ledgerEvents = (Array.isArray(events) ? events : [])
    .filter((event) => !event.reverses_event_id && event.event_type !== "reversal");
  const discountEvents = ledgerEvents.filter((event) =>
    event.event_type === "adjustment"
    && ["discount", "write_off", "goodwill_credit"].includes(event.adjustment_type)
  );
  const paymentEvents = ledgerEvents.filter(isConfirmedPaymentEvent);
  const paymentEventIds = paymentEvents.map((event) => event.id).filter(Boolean);
  let receiptAllocations = [];
  let allocatedTargetEvents = [];

  if (paymentEventIds.length > 0) {
    const { data: allocations, error: allocationError } = await supabase
      .from("money_allocations")
      .select("payment_event_id, allocated_to_event_id, amount_centavos")
      .in("payment_event_id", paymentEventIds)
      .not("allocated_to_event_id", "is", null);
    if (allocationError) throw allocationError;
    receiptAllocations = Array.isArray(allocations) ? allocations : [];

    const allocatedTargetIds = Array.from(new Set(
      receiptAllocations.map((allocation) => allocation.allocated_to_event_id).filter(Boolean),
    ));
    if (allocatedTargetIds.length > 0) {
      const { data: targetEvents, error: targetEventError } = await supabase
        .from("money_events")
        .select(`
          id,
          event_type,
          adjustment_type,
          amount_centavos,
          currency,
          payment_method,
          payment_status,
          document_type,
          document_year,
          document_number,
          document_voided,
          reverses_event_id,
          procedure_id,
          procedure_instance_id,
          ortho_case_id,
          metadata,
          occurred_at,
          created_at
        `)
        .in("id", allocatedTargetIds)
        .eq("document_voided", false);
      if (targetEventError) throw targetEventError;
      allocatedTargetEvents = Array.isArray(targetEvents) ? targetEvents : [];
    }
  }

  const chargeEvents = completedReceiptChargeEvents({
    directEvents: ledgerEvents,
    allocations: receiptAllocations,
    allocatedTargetEvents,
  });

  const { data: checkoutActivity, error: checkoutActivityError } = await supabase
    .from("activity_log")
    .select("metadata")
    .eq("action_name", "visit.checkout_completed")
    .eq("target_table", "visits")
    .eq("target_id", visit.id)
    .order("occurred_at", { ascending: false })
    .limit(1);

  if (checkoutActivityError) throw checkoutActivityError;

  // completedReceiptChargeEvents already includes allocated external charges
  // (for example Ortho Installation Day and prior AR) without duplicating
  // direct visit charges, and caps partial collections to the amount paid now.
  const receiptChargeEvents = chargeEvents;
  const checkoutMetadata = normalizeEventMetadata(checkoutActivity?.[0]);
  const {
    subtotalCents,
    discountCents,
    netCents,
    paidCents,
    creditAppliedCents,
    refundIssuedCents,
  } = resolveCompletedCheckoutSummary({
    checkoutMetadata,
    chargeEvents: receiptChargeEvents,
    discountEvents,
    paymentEvents,
  });

  const procedureIds = Array.from(new Set(
    receiptChargeEvents.map((event) => event.procedure_id).filter(Boolean),
  ));
  const procedureNameById = new Map();
  const orthoCaseNameById = new Map();

  if (procedureIds.length > 0) {
    const { data: procedures, error: procedureError } = await supabase
      .from("procedures")
      .select("id, name")
      .in("id", procedureIds);
    if (!procedureError) {
      for (const procedure of (procedures || [])) {
        if (procedure?.id) procedureNameById.set(procedure.id, procedure.name || "Procedure");
      }
    }
  }

  const orthoCaseIds = Array.from(new Set(
    receiptChargeEvents.map((event) => event.ortho_case_id).filter(Boolean),
  ));
  if (orthoCaseIds.length > 0) {
    const { data: orthoCases, error: orthoCaseError } = await supabase
      .from("ortho_cases")
      .select("id, system")
      .in("id", orthoCaseIds);
    if (!orthoCaseError) {
      for (const orthoCase of (orthoCases || [])) {
        if (orthoCase?.id) orthoCaseNameById.set(orthoCase.id, orthoCase.system || "Ortho case");
      }
    }
  }
  const receiptEvent =
    chargeEvents.find((event) => event.document_type === "receipt" && event.document_number)
    || ledgerEvents.find((event) => event.document_type === "receipt" && event.document_number)
    || null;
  const receiptNumber =
    formatReceiptNumberFromEvent(receiptEvent)
    || hydration?.checkout_completion?.receipt_number
    || null;

  let remainingDiscountCents = discountCents;
  const receiptItems = receiptChargeEvents.map((event) => {
    const grossCents = Number(event.amount_centavos || 0);
    const lineDiscountCents = Math.min(grossCents, remainingDiscountCents);
    remainingDiscountCents -= lineDiscountCents;
    const metadata = normalizeEventMetadata(event);
    return {
      id: event.id,
      outcome: "done",
      procedure_id: event.procedure_id || null,
      procedure_name: completedReceiptProcedureName(event, procedureNameById, orthoCaseNameById),
      custom_name: metadata.custom_name || null,
      qty: Number(metadata.qty || 1),
      tooth_number: metadata.tooth_number || "",
      surface: metadata.surface || null,
      performed_price_centavos: grossCents,
      item_discount_centavos: lineDiscountCents,
      receipt_total_discount_centavos: lineDiscountCents,
      receipt_net_centavos: grossCents - lineDiscountCents,
    };
  });

  const primaryPayment = paymentEvents[0] || null;
  const primaryPaymentMetadata = normalizeEventMetadata(primaryPayment);
  const paymentMethod =
    paymentEvents.length > 1
      ? "multiple"
      : primaryPayment?.payment_method || "";

  return {
    result: {
      visit_id: visit.id,
      receipt_number: receiptNumber,
      no_treatment_reason_code: visit.no_treatment_reason_code || null,
      no_payment_visit: !!visit.no_treatment_reason_code && paidCents === 0,
      summary: {
        subtotal_centavos: subtotalCents,
        total_discount_centavos: discountCents,
        net_charged_centavos: netCents,
        total_paid_centavos: paidCents,
        balance_centavos: Math.max(0, netCents - paidCents),
        credit_applied_centavos: creditAppliedCents,
        refund_issued_centavos: refundIssuedCents,
      },
    },
    items: receiptItems,
    paymentState: {
      method: paymentMethod,
      amount_tendered_cents: paidCents,
      reference_number: primaryPaymentMetadata.reference_number || "",
      proof_url: primaryPaymentMetadata.proof_url || "",
      paid_by_patient_id: primaryPaymentMetadata.paid_by_patient_id || "",
      paid_by_full_name: primaryPaymentMetadata.paid_by_full_name || "",
    },
    breakdown: {
      seniorPwdEligible: false,
      seniorPwdDiscountCents: 0,
      hmoCoveredCents: 0,
    },
  };
}

function tutorialTxPlanScheduledItem(visitId, itemId, procedureId, procedureName, toothNumber, surface, feeCentavos, options = {}) {
  return {
    tn_id: null,
    procedure_visit_id: visitId,
    work_key: `pv:${visitId}`,
    procedure_id: procedureId,
    procedure_code: null,
    procedure_name: procedureName,
    display_label: options.displayLabel || procedureName,
    default_fee_centavos: feeCentavos,
    display_price_centavos: feeCentavos,
    performed_price_centavos: feeCentavos,
    tooth_number: toothNumber,
    surface,
    source: "tx_plan",
    treatment_plan_id: options.planId || CHECKOUT_TX_PLAN_TUTORIAL_PLAN_ID,
    treatment_plan_item_id: itemId,
  };
}

const CHECKOUT_TX_PLAN_TUTORIAL_PLAN_ITEMS = [
  {
    id: "demo-tx-rct-46",
    procedure_id: "demo-tx-rct-46",
    procedure_name: "Root Canal Treatment",
    procedure_category: "Endodontic",
    tooth_number: "46",
    surface: null,
    status: "Accepted",
    fee_centavos: 850000,
  },
  {
    id: "demo-tx-zirconia-46",
    procedure_id: "demo-tx-zirconia-46",
    procedure_name: "Zirconia Crown",
    procedure_category: "Restorative",
    tooth_number: "46",
    surface: null,
    status: "Accepted",
    fee_centavos: 1800000,
  },
];

const CHECKOUT_TX_PLAN_TUTORIAL_COMPLETED_AT = "2026-05-25T10:00:00+08:00";

function tutorialTxPlanVisit({
  id,
  itemId,
  sequence,
  phaseNumber,
  templateLabel,
  chairMin,
  labGapDays = 0,
  appointmentId = null,
  completedAt = null,
  outcome = null,
}) {
  const item = CHECKOUT_TX_PLAN_TUTORIAL_PLAN_ITEMS.find((row) => row.id === itemId) || {};
  return {
    id,
    item_id: itemId,
    sequence,
    phase_number: phaseNumber,
    template_label: templateLabel,
    procedure_id: item.procedure_id,
    procedure_name: item.procedure_name,
    tooth_number: item.tooth_number,
    chair_min: chairMin,
    lab_gap_days: labGapDays,
    appointment_id: appointmentId,
    completed_at: completedAt,
    outcome,
  };
}

const CHECKOUT_TX_PLAN_TUTORIAL_VISITS = [
  tutorialTxPlanVisit({
    id: "demo-tx-rct-46-visit-1",
    itemId: "demo-tx-rct-46",
    sequence: 1,
    phaseNumber: 1,
    templateLabel: "Urgent RCT start: pulp removal, clean and shape, temp filling",
    chairMin: 90,
    labGapDays: 7,
    appointmentId: CHECKOUT_TX_PLAN_TUTORIAL_APPOINTMENT_ID,
    completedAt: CHECKOUT_TX_PLAN_TUTORIAL_COMPLETED_AT,
    outcome: "done",
  }),
  tutorialTxPlanVisit({
    id: "demo-tx-rct-46-visit-2",
    itemId: "demo-tx-rct-46",
    sequence: 2,
    phaseNumber: 2,
    templateLabel: "Confirm clear, root fill, seal",
    chairMin: 75,
    labGapDays: 7,
  }),
  tutorialTxPlanVisit({
    id: "demo-tx-zirconia-46-visit-1",
    itemId: "demo-tx-zirconia-46",
    sequence: 1,
    phaseNumber: 3,
    templateLabel: "Zirconia prep, scan, shade, temp crown",
    chairMin: 90,
    labGapDays: 7,
  }),
  tutorialTxPlanVisit({
    id: "demo-tx-zirconia-46-visit-2",
    itemId: "demo-tx-zirconia-46",
    sequence: 2,
    phaseNumber: 4,
    templateLabel: "Zirconia try-in, fit, contacts, occlusion, shade",
    chairMin: 30,
    labGapDays: 3,
  }),
  tutorialTxPlanVisit({
    id: "demo-tx-zirconia-46-visit-3",
    itemId: "demo-tx-zirconia-46",
    sequence: 3,
    phaseNumber: 5,
    templateLabel: "Zirconia crown cementation",
    chairMin: 45,
  }),
];

const CHECKOUT_TX_PLAN_TUTORIAL_PLAN_DATA = {
  [CHECKOUT_TX_PLAN_TUTORIAL_PLAN_ID]: {
    plan: {
      id: CHECKOUT_TX_PLAN_TUTORIAL_PLAN_ID,
      plan_code: "TP-TUTORIAL",
      items: CHECKOUT_TX_PLAN_TUTORIAL_PLAN_ITEMS,
    },
    visits: CHECKOUT_TX_PLAN_TUTORIAL_VISITS,
    billing: CHECKOUT_TX_PLAN_TUTORIAL_BILLING_STATE,
  },
};

const CHECKOUT_ONGOING_TUTORIAL_ITEM = {
  id: "demo-ongoing-rct-item",
  procedure_id: "demo-ongoing-rct",
  procedure_name: "Root Canal Treatment",
  procedure_category: "Endodontic",
  tooth_number: "46",
  surface: null,
  // Procedure visits remain schedulable while the parent item is Accepted;
  // the user-facing Ongoing label comes from the informal-plan context.
  status: "Accepted",
  fee_centavos: CHECKOUT_ONGOING_TUTORIAL_TOTAL_CENTAVOS,
};

function buildCheckoutOngoingTutorialVisits(visitValue = 1) {
  const visit = normalizeCheckoutOngoingTutorialVisit(visitValue);
  const visits = Array.from({ length: visit }, (_, index) => {
    const sequence = index + 1;
    return {
      id: `demo-ongoing-rct-visit-${sequence}`,
      item_id: CHECKOUT_ONGOING_TUTORIAL_ITEM.id,
      sequence,
      phase_number: sequence,
      template_label: `RCT visit ${sequence}`,
      procedure_id: CHECKOUT_ONGOING_TUTORIAL_ITEM.procedure_id,
      procedure_name: CHECKOUT_ONGOING_TUTORIAL_ITEM.procedure_name,
      tooth_number: "46",
      chair_min: 75,
      lab_gap_days: 7,
      appointment_id: sequence === visit
        ? CHECKOUT_ONGOING_TUTORIAL_APPOINTMENT_ID
        : `${CHECKOUT_ONGOING_TUTORIAL_APPOINTMENT_ID}-visit-${sequence}`,
      completed_at: `2026-06-${String(4 + (sequence * 7)).padStart(2, "0")}T10:00:00+08:00`,
      outcome: "done",
    };
  });
  if (visit < 3) {
    visits.push({
      id: `demo-ongoing-rct-next-visit-${visit + 1}`,
      item_id: CHECKOUT_ONGOING_TUTORIAL_ITEM.id,
      sequence: visit + 1,
      phase_number: visit + 1,
      template_label: "Continue treatment",
      procedure_id: CHECKOUT_ONGOING_TUTORIAL_ITEM.procedure_id,
      procedure_name: CHECKOUT_ONGOING_TUTORIAL_ITEM.procedure_name,
      tooth_number: "46",
      chair_min: 75,
      lab_gap_days: 7,
      appointment_id: null,
      completed_at: null,
      outcome: null,
    });
  }
  return visits;
}

function buildCheckoutOngoingTutorialPlanData(visitValue = 1) {
  const visit = normalizeCheckoutOngoingTutorialVisit(visitValue);
  return {
    [CHECKOUT_ONGOING_TUTORIAL_PLAN_ID]: {
      plan: {
        id: CHECKOUT_ONGOING_TUTORIAL_PLAN_ID,
        plan_code: "MV-TUTORIAL",
        status: visit === 3 ? "Completed" : "Accepted",
        is_informal: true,
        items: [{
          ...CHECKOUT_ONGOING_TUTORIAL_ITEM,
          status: visit === 3 ? "Completed" : CHECKOUT_ONGOING_TUTORIAL_ITEM.status,
        }],
      },
      visits: buildCheckoutOngoingTutorialVisits(visit),
      billing: buildCheckoutOngoingTutorialBillingState(visit),
    },
  };
}

export function buildCheckoutTodayTutorialHydration() {
  // These lines came from Dynamic Charting Today, so they are performed
  // chairside work, not calendar-booked work. Keeping scheduled_items empty
  // also prevents ItemsTable from hiding the same rows as duplicates.
  const doneItems = CHECKOUT_TODAY_TUTORIAL_SCHEDULED_ITEMS
    .map((item, index) => ({
      ...seedItemFromSchedule({ ...item }),
      _tutorialTodayHandoff: index === 0,
    }));

  return {
    currency: "PHP",
    patient: {
      id: "tutorial-checkout-patient",
      full_name: "Juan Dela Cruz",
      patient_number: "DEMO-0001",
      email: "juan.delacruz@example.com",
      is_senior_citizen: false,
      is_pwd: false,
      senior_citizen_id: null,
      pwd_id: null,
    },
    appointment: {
      id: CHECKOUT_TODAY_TUTORIAL_APPOINTMENT_ID,
      status: "Checked In",
      scheduled_start: "2026-05-18T09:00:00+08:00",
      doctor_name: "Dr. Sample (Demo)",
      doctor_id: "tutorial-doctor",
      chair_name: "Chair 1",
      chair_id: "tutorial-chair",
    },
    checkout_completion: { completed: false },
    tutorial_expected_net_centavos: CHECKOUT_TODAY_TUTORIAL_NET_CENTAVOS,
    scheduled_items: [],
    draft: {
      items: doneItems,
      dispositions: {},
      followup_dispositions: [],
      plan_visit_bookings: [],
      plan_visit_unsure: [],
      post_op_decisions: {},
      ortho_next_visit_decision: null,
    },
    pending_ortho_billing: [],
    ortho_next_visit: null,
  };
}

export function checkoutTodayTutorialQrs(clinicQrs) {
  return Array.isArray(clinicQrs) && clinicQrs.length > 0
    ? clinicQrs
    : [CHECKOUT_TODAY_TUTORIAL_MOCK_QR];
}

function buildCheckoutAddProceduresTutorialHydration() {
  const scheduledItems = CHECKOUT_TODAY_TUTORIAL_SCHEDULED_ITEMS.slice(0, 3).map((item) => ({ ...item }));

  return {
    currency: "PHP",
    patient: {
      id: "tutorial-checkout-patient",
      full_name: "Juan Dela Cruz",
      patient_number: "DEMO-0001",
      email: "juan.delacruz@example.com",
      is_senior_citizen: false,
      is_pwd: false,
      senior_citizen_id: null,
      pwd_id: null,
    },
    appointment: {
      id: CHECKOUT_ADD_PROCEDURES_TUTORIAL_APPOINTMENT_ID,
      status: "Checked In",
      scheduled_start: "2026-05-18T09:00:00+08:00",
      doctor_name: "Dr. Sample (Demo)",
      doctor_id: "tutorial-doctor",
      chair_name: "Chair 1",
      chair_id: "tutorial-chair",
    },
    checkout_completion: { completed: false },
    tutorial_expected_net_centavos: 120000,
    scheduled_items: scheduledItems,
    draft: {
      items: [],
      dispositions: {},
      followup_dispositions: [],
      plan_visit_bookings: [],
      plan_visit_unsure: [],
      post_op_decisions: {},
      ortho_next_visit_decision: null,
    },
    pending_ortho_billing: [],
    ortho_next_visit: null,
  };
}

function buildCheckoutTxPlanTutorialHydration() {
  const scheduledItems = [
    tutorialTxPlanScheduledItem(
      "demo-tx-rct-46-visit-1",
      "demo-tx-rct-46",
      "demo-tx-rct-46",
      "Root Canal Treatment",
      "46",
      null,
      0,
      { displayLabel: "Root Canal Treatment · Pulp removal, clean and shape, temp filling" },
    ),
  ];
  const doneItems = scheduledItems.map((item) => seedItemFromSchedule(item));

  return {
    currency: "PHP",
    patient: {
      id: "tutorial-patient",
      full_name: "Juan Dela Cruz",
      patient_number: "PT-C57352",
      email: "juan.delacruz@example.com",
      is_senior_citizen: false,
      is_pwd: false,
      senior_citizen_id: null,
      pwd_id: null,
    },
    appointment: {
      id: CHECKOUT_TX_PLAN_TUTORIAL_APPOINTMENT_ID,
      status: "Checked In",
      scheduled_start: "2026-05-25T10:00:00+08:00",
      doctor_name: "Dr. Levi Jordan",
      doctor_id: "tutorial-doctor",
      chair_name: "Chair 1",
      chair_id: "tutorial-chair",
    },
    checkout_completion: { completed: false },
    scheduled_items: scheduledItems,
    draft: {
      items: doneItems,
      dispositions: {},
      followup_dispositions: [],
      plan_visit_bookings: [],
      plan_visit_unsure: [],
      post_op_decisions: {},
      ortho_next_visit_decision: null,
    },
    pending_ortho_billing: [],
    ortho_next_visit: null,
  };
}

export function buildCheckoutOngoingTreatmentTutorialHydration(visitValue = 1) {
  const visit = normalizeCheckoutOngoingTutorialVisit(visitValue);
  const scheduledItems = [
    tutorialTxPlanScheduledItem(
      `demo-ongoing-rct-visit-${visit}`,
      CHECKOUT_ONGOING_TUTORIAL_ITEM.id,
      CHECKOUT_ONGOING_TUTORIAL_ITEM.procedure_id,
      CHECKOUT_ONGOING_TUTORIAL_ITEM.procedure_name,
      "46",
      null,
      0,
      {
        planId: CHECKOUT_ONGOING_TUTORIAL_PLAN_ID,
        displayLabel: `Root Canal Treatment · Visit ${visit} recorded today`,
      },
    ),
  ];
  const doneItems = scheduledItems.map((item) => seedItemFromSchedule(item));

  return {
    currency: "PHP",
    patient: {
      id: "tutorial-patient",
      full_name: "Juan Dela Cruz",
      patient_number: "PT-C57352",
      email: "juan.delacruz@example.com",
      is_senior_citizen: false,
      is_pwd: false,
      senior_citizen_id: null,
      pwd_id: null,
    },
    appointment: {
      id: CHECKOUT_ONGOING_TUTORIAL_APPOINTMENT_ID,
      status: "Checked In",
      scheduled_start: `2026-06-${String(4 + (visit * 7)).padStart(2, "0")}T10:00:00+08:00`,
      doctor_name: "Dr. Levi Jordan",
      doctor_id: "tutorial-doctor",
      chair_name: "Chair 1",
      chair_id: "tutorial-chair",
    },
    checkout_completion: { completed: false },
    scheduled_items: scheduledItems,
    draft: {
      items: doneItems,
      dispositions: {},
      followup_dispositions: [],
      plan_visit_bookings: [],
      plan_visit_unsure: [],
      post_op_decisions: {},
      ortho_next_visit_decision: null,
    },
    pending_ortho_billing: [],
    ortho_next_visit: null,
  };
}

export function BundleFinishControl({
  finishSelected = false,
  doctorLocked = false,
  onChange,
}) {
  const selected = doctorLocked || finishSelected;
  return (
    <label
      data-testid="checkout-bundle-finish"
      aria-disabled={doctorLocked ? "true" : undefined}
      className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 transition-colors ${
        selected
          ? "border-emerald-300 bg-emerald-50"
          : "border-slate-200 bg-white hover:bg-slate-50"
      } ${doctorLocked ? "cursor-not-allowed" : "cursor-pointer"}`}
    >
      <input
        type="checkbox"
        data-testid="checkout-bundle-finish-toggle"
        checked={selected}
        disabled={doctorLocked}
        onChange={(event) => {
          if (!doctorLocked) onChange?.(event.target.checked);
        }}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-70"
      />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
          Finish this treatment
          {doctorLocked ? <Lock className="h-3.5 w-3.5 text-emerald-700" aria-hidden="true" /> : null}
        </span>
        <span className={`block text-[12px] ${doctorLocked ? "font-medium text-emerald-800" : "text-slate-500"}`}>
          {doctorLocked
            ? "Doctor marked this treatment finished and completed. Collect the full remaining balance."
            : "Use this only after every procedure is marked done. Checkout will verify that before closing the treatment."}
        </span>
      </span>
    </label>
  );
}

function checkoutRpc(...args) {
  // SupabaseClient.rpc reads client state through `this`. Keep the method call
  // attached to the client instead of passing supabase.rpc around unbound.
  return supabase.rpc(...args);
}

export async function readCheckoutBundleState({
  planId,
  ongoingTreatmentEnabled,
  rpc = checkoutRpc,
} = {}) {
  if (!planId || !ongoingTreatmentEnabled) {
    return { isBundle: false, finishRequested: false, mvCode: null };
  }

  const { data, error } = await rpc("bundle_finish_state", { p_plan_id: planId });
  if (error) throw error;

  return {
    isBundle: Boolean(data?.is_bundle),
    finishRequested: Boolean(data?.finish_requested),
    mvCode: data?.mv_code || null,
  };
}

export async function readCheckoutPlanState({
  planId,
  ongoingTreatmentEnabled,
  rpc = checkoutRpc,
} = {}) {
  const [billingResult, bundleResult] = await Promise.allSettled([
    (async () => {
      const { data, error } = await rpc("get_plan_billing_state", { p_plan_id: planId });
      if (error) throw error;
      return data;
    })(),
    readCheckoutBundleState({ planId, ongoingTreatmentEnabled, rpc }),
  ]);

  return {
    billingState: billingResult.status === "fulfilled" ? billingResult.value : null,
    billingError: billingResult.status === "rejected" ? billingResult.reason : null,
    bundleState: bundleResult.status === "fulfilled"
      ? bundleResult.value
      : { isBundle: false, finishRequested: false, mvCode: null },
    bundleError: bundleResult.status === "rejected" ? bundleResult.reason : null,
  };
}

export default function Checkout() {
  const [params] = useSearchParams();
  const apptId = params.get("appointment_id");
  const isCheckoutTodayTutorialDemo = params.get("tutorial") === "checkout-today" && params.get("demo") === "1";
  const isCheckoutAddProceduresTutorialDemo = params.get("tutorial") === "checkout-add-procedures" && params.get("demo") === "1";
  const isCheckoutTxPlanTutorialDemo = params.get("tutorial") === "checkout-tx-planning" && params.get("demo") === "1";
  const isCheckoutOngoingTutorialDemo = params.get("tutorial") === "ongoing-treatment" && params.get("demo") === "1";
  const checkoutOngoingTutorialVisit = normalizeCheckoutOngoingTutorialVisit(params.get("visit"));
  const isCheckoutPlanTutorialDemo = isCheckoutTxPlanTutorialDemo || isCheckoutOngoingTutorialDemo;
  const isCheckoutTutorialDemo = isCheckoutTodayTutorialDemo || isCheckoutAddProceduresTutorialDemo || isCheckoutPlanTutorialDemo;
  const isHmoTutorialDemo = isCheckoutTodayTutorialDemo && params.get("hmo_demo") === "1";
  const isAcceptedNotPaidTutorialDemo = isCheckoutTxPlanTutorialDemo && params.get("accepted_not_paid_demo") === "1";
  const checkoutTutorialStage = params.get("tutorial_stage");
  const checkoutStageSeed = useMemo(() => checkoutTutorialStageSeed({
    stage: isCheckoutTutorialDemo ? checkoutTutorialStage : null,
    acceptedNotPaid: isAcceptedNotPaidTutorialDemo,
    hmo: isHmoTutorialDemo,
  }), [checkoutTutorialStage, isAcceptedNotPaidTutorialDemo, isCheckoutTutorialDemo, isHmoTutorialDemo]);
  const initialCheckoutStep = checkoutStageSeed.step;
  const navigate = useNavigate();
  const { enabled: ongoingTreatmentEnabled } = useOngoingTreatmentRollout();
  // Keep the demo plan reference stable. Rebuilding this object on every
  // Checkout render makes PlanVisitFollowUp reload its demo state, report a
  // fresh resolution object, and render Checkout again forever. The URL can
  // then change to Visit 2 while React never gets to unmount Checkout.
  const checkoutOngoingTutorialPlanData = useMemo(
    () => (isCheckoutOngoingTutorialDemo
      ? buildCheckoutOngoingTutorialPlanData(checkoutOngoingTutorialVisit)
      : null),
    [checkoutOngoingTutorialVisit, isCheckoutOngoingTutorialDemo],
  );

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [hydration, setHydration] = useState(null);
  const [items, setItems]     = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [draftConflict, setDraftConflict] = useState(false);
  const debounceRef = useRef(null);
  const draftRevisionRef = useRef(0);
  const draftSaveChainRef = useRef(Promise.resolve());
  // Chairside-staged dispositions on each scheduled treatment_need
  // (kind: 'not_today' | 'declined', plus reason for declined).
  // Owned by the chairside Today's Plan card via useVisitDraft, but
  // persisted in checkout_drafts.draft_data so this front-desk
  // surface sees the same staged state. Applied at commit by
  // appending to follow_up_dispositions, where the new
  // commit_checkout_with_followup `unschedule` kind fires
  // unschedule_treatment_need atomically alongside payment + visit
  // close. Shape: { [tn_id]: { kind, reason? } }
  const [chairsideDispositions, setChairsideDispositions] = useState({});
  // "Add today's walk-in work to the payment plan?" — off by default, so
  // checkout behaves exactly as before unless staff opt in. When on, the
  // eligible walk-in lines are billed on the plan's schedule instead of
  // collected today (routed via commit_checkout_with_fold).
  const [foldIntoPlan, setFoldIntoPlan] = useState(false);
  // Opens the billing-model picker from the compact Payment plan card's
  // "Change" link (the picker is the same dialog the old BillingStateCard used).
  const [billingPickerOpen, setBillingPickerOpen] = useState(false);
  // After the down payment, Flexible plans have no system-authored amount due
  // today. Reception must record either a positive collection target or an
  // explicit no-collection decision. This is draft state until checkout
  // commits.
  const [flexiblePlanCollection, setFlexiblePlanCollection] = useState({
    plan_id: null,
    decision: null,
    amount_centavos: null,
  });

  // Wizard state machine: today → payment → done
  const [step, setStep]       = useState(initialCheckoutStep);
  useEffect(() => {
    if (isCheckoutTutorialDemo && ["payment", "follow_up"].includes(checkoutTutorialStage)) {
      setStep(checkoutTutorialStage);
    }
  }, [checkoutTutorialStage, isCheckoutTutorialDemo]);
  const [paymentState, setPaymentState] = useState({
    method:                "cash",
    amount_tendered_cents: 0,
    reference_number:      "",
    proof_url:             "",
    visit_discount_cents:  0,
    visit_discount_reason: "",
    price_override_reason: "",
    // null => discount lands on add-on procedures (legacy). A plan id => the
    // discount is applied to that treatment plan (engine stamps the plan so
    // its balance drops; the charge reduction is scoped to that plan).
    visit_discount_target_plan_id: null,
    keep_overpay_as_credit: false,
    decline_senior_pwd:    false,
    senior_pwd_discount_removed_by: "",
    senior_pwd_discount_removed_reason: "",
    senior_pwd_discount_removed_at: "",
    // HMO coverage (set by HMO panel before submit)
    hmo_payer_id:          "",
    hmo_loa_number:        "",
    hmo_member_id:         "",
    hmo_covered_cents:     0,
    patient_hmo_profile_id: "",
    // Family payer (paid by another patient in the same clinic)
    paid_by_patient_id:    "",
    paid_by_full_name:     "",
    // Procedure-level payment allocation override.
    allocation_overrides_enabled: false,
    procedure_allocation_overrides: [],
    denture_item_allocation_overrides_enabled: false,
    denture_item_allocation_overrides: {},
    manager_approval_by:   "",
    manager_approval_reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [settlementReview, setSettlementReview] = useState(null);
  const [doneResult, setDoneResult] = useState(null); // result from complete_checkout
  const [completedReceiptView, setCompletedReceiptView] = useState(null);

  useEffect(() => {
    if (step !== "follow_up") setSettlementReview(null);
  }, [step]);
  // (Removed: the staged "Accept on checkout" pattern was superseded
  // by main's design where booking an Other-Active-Plan IS the
  // acceptance — see OtherActivePlansFollowUp's inline Book flow.)

  // procedure_id -> { enabled, after_days, followup_procedure_id,
  //                   followup_procedure_name }
  // Populated once after hydration. Drives the "Post-op check in Nd ✓"
  // chip on Done item rows. Empty until loaded; the chip simply does
  // not render while empty.
  const [postOpByProcedure, setPostOpByProcedure] = useState({});

  // itemKey -> { waived: true, reason: string }
  // The chief-dentist clinical rule: post-op follow-up defaults ON for
  // procedures with post_op_followup_enabled. The doctor can wave it
  // off at the chair in one tap with a reason. This local state holds
  // those one-tap waivers; they apply at Complete checkout via the
  // set_procedure_visit_followup_waiver RPC.
  const [followupWaivers, setFollowupWaivers] = useState({});

  // suggestion_key (`pv:<procedure_visit_id>`) -> { kind, ... }
  //   kind === "book"  → { appointment_id, appointment_label, due_date }
  //   kind === "unsure" → { reason }
  // Step 3's post-op cards stage a Book or Unsure decision per suggestion,
  // matching the Next Plan Visit card pattern. Complete checkout is
  // gated on every card having one of these decisions. At commit:
  //   - "book" entries get the auto-created treatment_need linked to
  //     the Pencil via schedule_treatment_need, and the Pencil promotes.
  //   - "unsure" entries leave the need Open for Recall Center pickup.
  const [postOpDecisions, setPostOpDecisions] = useState({});
  const [postOpResolution, setPostOpResolution] = useState({ ready: true, total: 0, unresolved: 0 });

  // Plan-aware checkout state. When the appointment has at least one
  // scheduled item bound to a Tx Plan, we surface that plan's billing
  // (BillingStateCard) above the items list and pre-fill the payment
  // amount with the model-driven suggestion. The same fields drive the
  // final-visit AR gate at submit-time.
  //
  // Hybrid visits (plan items + chairside walk-ins) are supported — the
  // backend's complete_checkout_plan_aware already routes plan items
  // through _ensure_plan_visit_charge() and walk-ins through per-item
  // charges in one transaction. Frontend just surfaces the plan
  // suggestion; receptionist adds chairside on top freely.
  const [planBillingState, setPlanBillingState] = useState(null);
  const [planBillingError, setPlanBillingError] = useState(null);
  const [bundleStateError, setBundleStateError] = useState(null);
  const [planBillingNonce, setPlanBillingNonce] = useState(0);
  // True when the plan being billed is an on-the-go bundle — drives the
  // "Ongoing treatment" label and the bundle billing-model list at checkout.
  const [planIsBundle, setPlanIsBundle] = useState(false);
  // Front desk toggled "Finish treatment (final visit)" — after this checkout
  // posts the charge/payment, finish_bundle completes + closes the bundle.
  const [finishBundleOnComplete, setFinishBundleOnComplete] = useState(false);
  const [bundleFinishRequested, setBundleFinishRequested] = useState(false);
  // A doctor-authored finish request is authoritative. The receptionist may
  // choose to finish an unflagged MV, but cannot reverse a clinical finish
  // decision made chairside. Use this derived value for every preview, gate,
  // and commit payload so the database flag cannot be bypassed by stale UI.
  const bundleFinishSelected = bundleFinishRequested || finishBundleOnComplete;
  // The MV's own MV-#### code, shown lightly beside the Ongoing panel header.
  const [bundleMvCode, setBundleMvCode] = useState(null);
  // Billing state for any ADDITIONAL plans on this visit (rare — one visit
  // carrying work from more than one accepted plan). Empty in the common
  // single-plan case, so every downstream money term below adds 0 there.
  const [extraPlanBillings, setExtraPlanBillings] = useState([]);
  // Database preview of the one universal settlement. This is read-only;
  // the final settlement is recalculated and enforced inside the commit RPC.
  const [settlementPreview, setSettlementPreview] = useState(null);
  const [settlementPreviewLoading, setSettlementPreviewLoading] = useState(false);
  const [settlementPreviewError, setSettlementPreviewError] = useState(null);
  const settlementPreviewRequestRef = useRef(0);
  useEffect(() => {
    if (!ongoingTreatmentEnabled) {
      settlementPreviewRequestRef.current += 1;
      setSettlementPreview(null);
      setSettlementPreviewLoading(false);
      setSettlementPreviewError(null);
    }
  }, [ongoingTreatmentEnabled]);
  const [priorArRows, setPriorArRows] = useState([]);
  const [priorArSelections, setPriorArSelections] = useState({});
  const [priorArLoading, setPriorArLoading] = useState(false);
  const [priorArError, setPriorArError] = useState(null);
  // Auto-fill the payment amount with suggested_next_centavos exactly
  // once per hydrated plan. If the receptionist edits the field after
  // auto-fill, we never overwrite their choice.
  const autoFillAppliedRef = useRef(false);
  // Exit-gate dialog: shown when the clerk tries to leave a dirty checkout.
  // pendingNav remembers the destination the clerk requested (sidebar nav or
  // Back-to-Calendar) so Save / Discard route them there instead of always
  // dumping them on /calendar.
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [pendingNav, setPendingNav] = useState(null); // string | null

  // Step 3 (Follow-up) staging area. Each entry maps a bundle.key to
  // the staged disposition (book / recall / decline + payload). Nothing
  // is persisted to the DB until the final "Complete checkout" button
  // fires commit_checkout_with_followup as one atomic transaction.
  // Map<bundleKey, { kind, ...payload, needIds, bundle }>
  const [dispositions, setDispositions] = useState(new Map());
  // Followup panel reports back its bundle count + total via this so
  // the parent can gate "Complete checkout" without owning the bundle
  // computation itself.
  const [followupTotals, setFollowupTotals] = useState({ totalBundles: 0, totalNeeds: 0, ready: false });

  // Other-active-plans (quotes) soft reminder. The quotes panel reports how
  // many quotes are still unresolved; if staff hits Complete checkout with any
  // untouched, we show a one-time, dismissible "finish anyway?" reminder. It
  // never blocks — leaving quotes open is a legitimate outcome (Guided Freedom).
  const [openQuotesCount, setOpenQuotesCount] = useState(0);
  const [quotesReminderOpen, setQuotesReminderOpen] = useState(false);
  const quotesReminderAckRef = useRef(false);

  // Plan-visit bookings staged in Step 3. Map<plan_id,
  // { appointment_id, appointment_label, visit_ids, visit_labels }>. Each entry
  // represents a Pencil appointment created via PlanVisitFollowUp's
  // "Book next visit" action. The Pencil reserves chair time now; the
  // Tx Plan procedure_visit links are written only by
  // commit_checkout_with_followup after checkout succeeds.
  const [planVisitBookings, setPlanVisitBookings] = useState(new Map());
  // Explicit "Unsure" resolutions for Step 3 plan-visit cards. This is
  // checkout draft state only: it lets the clerk complete checkout after
  // making a deliberate follow-up decision without creating a fake booking.
  const [planVisitUnsure, setPlanVisitUnsure] = useState(new Set());
  const [planVisitResolution, setPlanVisitResolution] = useState({ ready: true, totalPlans: 0, unresolvedPlans: 0 });
  const [planVisitFutureNeedBundles, setPlanVisitFutureNeedBundles] = useState([]);
  const [pendingOrthoBillingRows, setPendingOrthoBillingRows] = useState([]);
  const [appointmentOrthoCase, setAppointmentOrthoCase] = useState(null);
  const [appointmentOrthoCaseLoading, setAppointmentOrthoCaseLoading] = useState(false);
  const [appointmentOrthoCaseError, setAppointmentOrthoCaseError] = useState(null);
  const [orthoInstallationResolvedCaseIds, setOrthoInstallationResolvedCaseIds] = useState(new Set());
  const [orthoNextVisitDecision, setOrthoNextVisitDecision] = useState(null);
  const [orthoNextVisitBookOpen, setOrthoNextVisitBookOpen] = useState(false);
  const [orthoCollectionChoice, setOrthoCollectionChoice] = useState(null);
  const [orthoUnderpaymentDialogOpen, setOrthoUnderpaymentDialogOpen] = useState(false);
  const autoOpenedOrthoNextVisitKeyRef = useRef(null);
  const pendingOrthoBillingLoading = false;

  useEffect(() => {
    const patientId = hydration?.patient?.id;
    const caseId = hydration?.appointment?.ortho_case_id;
    let cancelled = false;

    if (!patientId || !caseId || isCheckoutTutorialDemo) {
      setAppointmentOrthoCase(null);
      setAppointmentOrthoCaseLoading(false);
      setAppointmentOrthoCaseError(null);
      return undefined;
    }

    setAppointmentOrthoCase(null);
    setAppointmentOrthoCaseLoading(true);
    setAppointmentOrthoCaseError(null);

    supabase.rpc("list_patient_ortho_cases", { p_patient_id: patientId })
      .then(({ data, error: caseError }) => {
        if (cancelled) return;
        const matchedCase = selectAppointmentOrthoCase(data, caseId);
        setAppointmentOrthoCaseLoading(false);
        setAppointmentOrthoCase(matchedCase);
        setAppointmentOrthoCaseError(
          caseError?.message || (!matchedCase ? "Appointment Ortho case was not found." : null),
        );
      })
      .catch((caseError) => {
        if (cancelled) return;
        setAppointmentOrthoCaseLoading(false);
        setAppointmentOrthoCase(null);
        setAppointmentOrthoCaseError(caseError?.message || "Could not load Ortho case.");
      });

    return () => {
      cancelled = true;
    };
  }, [
    hydration?.appointment?.ortho_case_id,
    hydration?.patient?.id,
    isCheckoutTutorialDemo,
  ]);

  const planVisitSameSlotCounts = useMemo(() => {
    const counts = {};
    for (const d of dispositions.values()) {
      if (!d?.plan_visit_booking || !d.plan_id) continue;
      counts[d.plan_id] = (counts[d.plan_id] || 0) + 1;
    }
    return counts;
  }, [dispositions]);

  const stageFutureNeedsWithPlanBooking = useCallback(({ planId, appointment_id, appointment_label, bundles }) => {
    if (!planId || !appointment_id || !Array.isArray(bundles) || bundles.length === 0) return;
    setDispositions((prev) => {
      const next = new Map(prev);
      const planAppointmentIds = new Set([appointment_id]);
      for (const booking of planVisitBookings.values()) {
        if (booking?.appointment_id) planAppointmentIds.add(booking.appointment_id);
      }
      for (const bundle of bundles) {
        if (!bundle?.key || !Array.isArray(bundle.needs)) continue;
        const old = prev.get(bundle.key);
        if (
          old?.kind === "book"
          && old.appointment_id
          && old.appointment_id !== appointment_id
          && !old.plan_visit_booking
          && !old.ortho_installation_booking
          && !planAppointmentIds.has(old.appointment_id)
        ) {
          supabase
            .rpc("cancel_pencil_appointment", { p_appointment_id: old.appointment_id })
            .catch(() => { /* best-effort; the staged disposition is being replaced */ });
        }
        next.set(bundle.key, {
          kind: "book",
          appointment_id,
          appointment_label: appointment_label || "plan visit appointment",
          plan_visit_booking: true,
          plan_id: planId,
          needIds: bundle.needs.map((n) => n.id).filter(Boolean),
        });
      }
      return next;
    });
  }, [planVisitBookings]);

  const stageFutureNeedsWithOrthoInstallation = useCallback(({ caseId, appointment_id, appointment_label, bundles }) => {
    if (!appointment_id) return;
    setDispositions((prev) => {
      const next = new Map(prev);
      const planAppointmentIds = new Set();
      for (const booking of planVisitBookings.values()) {
        if (booking?.appointment_id) planAppointmentIds.add(booking.appointment_id);
      }
      const orthoKey = `ortho_installation:${caseId || appointment_id}`;
      const oldOrtho = prev.get(orthoKey);
      if (
        oldOrtho?.kind === "book"
        && oldOrtho.appointment_id
        && oldOrtho.appointment_id !== appointment_id
        && !planAppointmentIds.has(oldOrtho.appointment_id)
      ) {
        supabase
          .rpc("cancel_pencil_appointment", { p_appointment_id: oldOrtho.appointment_id })
          .catch(() => { /* best-effort; the staged ortho hold is being replaced */ });
      }
      next.set(orthoKey, {
        bundleKey: orthoKey,
        kind: "book",
        appointment_id,
        appointment_label: appointment_label || "Ortho installation appointment",
        ortho_installation_booking: true,
        ortho_case_id: caseId || null,
        needIds: [],
      });
      for (const bundle of (bundles || [])) {
        if (!bundle?.key || !Array.isArray(bundle.needs)) continue;
        const old = prev.get(bundle.key);
        if (
          old?.kind === "book"
          && old.appointment_id
          && old.appointment_id !== appointment_id
          && !old.plan_visit_booking
          && !old.ortho_installation_booking
          && !planAppointmentIds.has(old.appointment_id)
        ) {
          supabase
            .rpc("cancel_pencil_appointment", { p_appointment_id: old.appointment_id })
            .catch(() => { /* best-effort; the staged disposition is being replaced */ });
        }
        next.set(bundle.key, {
          kind: "book",
          appointment_id,
          appointment_label: appointment_label || "Ortho installation appointment",
          ortho_installation_booking: true,
          needIds: bundle.needs.map((n) => n.id).filter(Boolean),
        });
      }
      return next;
    });
  }, [planVisitBookings]);

  // Clinic QR codes for digital-payment display + payment-link modal.
  const [qrs, setQrs] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);

  const mergeServerDraftIntoLocal = useCallback((serverDraft) => {
    const serverItems = Array.isArray(serverDraft?.items) ? serverDraft.items : [];
    const serverDispositions =
      serverDraft?.dispositions && typeof serverDraft.dispositions === "object"
        ? serverDraft.dispositions
        : {};
    const serverDoneKeys = new Set(serverItems.map((it) => scheduledWorkKey(it)).filter(Boolean));

    if (serverItems.length > 0) {
      setItems((prev) => {
        const merged = mergeCheckoutDraftItems(prev, serverItems);
        return merged.length === prev.length
          && merged.every((item, index) => item === prev[index])
          ? prev
          : merged;
      });
    }

    setChairsideDispositions((prev) => {
      let changed = false;
      const next = { ...(prev || {}) };

      for (const key of serverDoneKeys) {
        if (next[key]) {
          delete next[key];
          changed = true;
        }
      }

      for (const [key, value] of Object.entries(serverDispositions)) {
        if (serverDoneKeys.has(key)) continue;
        if (!next[key]) {
          next[key] = value;
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, []);

  // Catalogue-driven Senior/PWD discount %. Hydrated once via
  // get_clinic_settings (added to clinic_settings in 20260606000001).
  // Falls back to the statutory default only when the setting is missing.
  const [seniorPwdPct, setSeniorPwdPct] = useState(DEFAULT_SENIOR_PWD_DISCOUNT_PCT / 100);
  // Clinic's Settings-configured billing plans (single source of truth for
  // which billing models the checkout picker offers + the default).
  const [billingPlansEnabled, setBillingPlansEnabled] = useState(null);
  const [billingPlanDefault, setBillingPlanDefault]   = useState(null);

  const load = useCallback(async () => {
    if (isCheckoutTutorialDemo) {
      const data = isCheckoutOngoingTutorialDemo
        ? buildCheckoutOngoingTreatmentTutorialHydration(checkoutOngoingTutorialVisit)
        : isCheckoutTxPlanTutorialDemo
        ? buildCheckoutTxPlanTutorialHydration()
        : isCheckoutAddProceduresTutorialDemo
        ? buildCheckoutAddProceduresTutorialHydration()
        : buildCheckoutTodayTutorialHydration();
      setLoading(false);
      setError(null);
      setHydration(data);
      draftRevisionRef.current = 0;
      setDraftConflict(false);
      setSaveError(null);
      setCompletedReceiptView(null);
      setPendingOrthoBillingRows([]);
      setItems(repairDraftItemsWithScheduled(data.draft.items, data.scheduled_items));
      setChairsideDispositions({});
      setDispositions(new Map());
      setPlanVisitBookings(new Map());
      setPlanVisitUnsure(new Set());
      setPostOpDecisions({});
      setOrthoNextVisitDecision(null);
      setOrthoNextVisitBookOpen(false);
      if (checkoutStageSeed.paymentStatePatch) {
        setPaymentState((current) => ({ ...current, ...checkoutStageSeed.paymentStatePatch }));
      }
      return;
    }
    if (!apptId) { setError("Missing appointment_id."); setLoading(false); return; }
    setLoading(true); setError(null);
    setCompletedReceiptView(null);
    const { data, error: e } = await supabase.rpc("get_checkout_hydration", { p_appointment_id: apptId });
    if (e) { setLoading(false); setError(e.message ?? "Could not load checkout."); return; }
    setHydration(data);
    draftRevisionRef.current = normalizeCheckoutDraftRevision(data?.draft_revision);
    setDraftConflict(false);
    setSaveError(null);
    setPendingOrthoBillingRows(coerceJsonArray(data?.pending_ortho_billing));
    // Strict: items reflect what the doctor explicitly performed
    // (or what the receptionist explicitly adds here in Step 1).
    // We used to auto-seed from scheduled_items with outcome='done',
    // but that quietly billed booked procedures that weren't actually
    // performed AND mirrored its way back into the chairside Today's
    // Plan card so the doctor saw a fake-completed checklist on
    // first open. Booked-but-not-yet-done procedures live in
    // scheduled_items (the same source the Today's Plan card reads);
    // the doctor's explicit "Done" gesture is what writes them into
    // this items list.
    // Strip any line whose treatment plan was abandoned/rejected/superseded
    // after it was added to the cart — that dropped work must never be billed
    // (the database backstop refuses it too). Quiet notice so the clerk isn't
    // left wondering why a line vanished; the debounced auto-save then
    // persists the cleaned draft so it never reappears.
    const scheduledForRepair = Array.isArray(data?.scheduled_items) ? data.scheduled_items : [];
    const { items: liveDraftItems, dropped: droppedDeadPlanLines } = dropDeadPlanDraftLines(
      Array.isArray(data?.draft?.items) ? data.draft.items : [],
      scheduledForRepair,
    );
    if (droppedDeadPlanLines > 0) {
      toast.info(
        droppedDeadPlanLines === 1
          ? "Removed 1 line from a closed treatment plan — it won't be billed."
          : `Removed ${droppedDeadPlanLines} lines from closed treatment plans — they won't be billed.`
      );
    }
    setItems(repairDraftItemsWithScheduled(liveDraftItems, scheduledForRepair));
    setChairsideDispositions(
      data?.draft?.dispositions && typeof data.draft.dispositions === "object"
        ? data.draft.dispositions
        : {}
    );
    // Hydrate Step 3 staged dispositions from the saved draft. Without
    // this, a clerk who clicks out of checkout and comes back loses
    // their Book/Recall/Decline staging — which used to leave Pencil
    // appointments orphaned. Now state survives navigation: the same
    // bundles, with the same staged dispositions, re-render exactly
    // as the clerk left them.
    const savedFollowups = Array.isArray(data?.draft?.followup_dispositions)
      ? data.draft.followup_dispositions
      : [];
    const restored = new Map();
    for (const entry of savedFollowups) {
      if (!entry || !entry.bundleKey) continue;
      const { bundleKey, ...rest } = entry;
      restored.set(bundleKey, rest);
    }
    setDispositions(restored);
    const savedPlanBookings = Array.isArray(data?.draft?.plan_visit_bookings)
      ? data.draft.plan_visit_bookings
      : [];
    const restoredPlanBookings = new Map();
    for (const entry of savedPlanBookings) {
      if (!entry?.plan_id || !entry?.appointment_id) continue;
      restoredPlanBookings.set(entry.plan_id, {
        appointment_id: entry.appointment_id,
        appointment_label: entry.appointment_label || null,
        visit_ids: Array.isArray(entry.visit_ids) ? entry.visit_ids : [],
        visit_labels: Array.isArray(entry.visit_labels) ? entry.visit_labels : [],
      });
    }
    setPlanVisitBookings(restoredPlanBookings);
    const savedPlanUnsure = Array.isArray(data?.draft?.plan_visit_unsure)
      ? data.draft.plan_visit_unsure
      : [];
    setPlanVisitUnsure(new Set(savedPlanUnsure.filter(Boolean)));
    setPostOpDecisions(
      data?.draft?.post_op_decisions && typeof data.draft.post_op_decisions === "object"
        ? data.draft.post_op_decisions
        : {}
    );
    const savedFlexibleCollection = data?.draft?.flexible_plan_collection;
    setFlexiblePlanCollection(
      savedFlexibleCollection && typeof savedFlexibleCollection === "object"
        ? {
            plan_id: savedFlexibleCollection.plan_id || null,
            decision: ["amount", "none"].includes(savedFlexibleCollection.decision)
              ? savedFlexibleCollection.decision
              : null,
            amount_centavos: savedFlexibleCollection.decision === "amount"
              ? Math.max(0, Number(savedFlexibleCollection.amount_centavos || 0))
              : null,
          }
        : { plan_id: null, decision: null, amount_centavos: null },
    );
    const savedOrthoNextVisitDecision = data?.draft?.ortho_next_visit_decision;
    setOrthoNextVisitDecision(
      resolvedOrthoNextVisitDecision(
        data?.ortho_next_visit,
        savedOrthoNextVisitDecision,
      )
    );
    setOrthoNextVisitBookOpen(false);
    if (data?.checkout_completion?.completed) {
      try {
        const receiptView = await buildCompletedCheckoutReceiptView({
          appointmentId: apptId,
          hydration: data,
        });
        setCompletedReceiptView(receiptView);
        if (receiptView?.result?.visit_id) {
          setStep("done");
        }
      } catch (receiptError) {
        console.warn("[checkout] completed receipt reconstruction failed", receiptError);
      }
    }
    setLoading(false);
  }, [apptId, checkoutOngoingTutorialVisit, checkoutStageSeed.paymentStatePatch, isCheckoutAddProceduresTutorialDemo, isCheckoutOngoingTutorialDemo, isCheckoutTutorialDemo, isCheckoutTxPlanTutorialDemo]);

  const refreshSharedDraft = useCallback(async () => {
    if (isCheckoutTutorialDemo || !apptId) return;
    const { data, error: e } = await supabase.rpc("get_checkout_hydration", { p_appointment_id: apptId });
    if (e || !data) return;
    const serverRevision = normalizeCheckoutDraftRevision(data?.draft_revision);
    if (serverRevision < draftRevisionRef.current) return;
    if (serverRevision > draftRevisionRef.current) {
      setDraftConflict(true);
      setSaveError("This visit changed in another screen. Reload the latest saved procedures before continuing.");
      toast.error("This visit changed in another screen", {
        id: "checkout-draft-conflict",
        description: "Your screen was not allowed to overwrite it. Reload and review the saved procedures.",
      });
      return;
    }
    setHydration(data);
    setPendingOrthoBillingRows(coerceJsonArray(data?.pending_ortho_billing));
    mergeServerDraftIntoLocal({
      ...(data?.draft ?? {}),
      items: repairDraftItemsWithScheduled(
        Array.isArray(data?.draft?.items) ? data.draft.items : [],
        Array.isArray(data?.scheduled_items) ? data.scheduled_items : [],
      ),
    });
  }, [apptId, isCheckoutTutorialDemo, mergeServerDraftIntoLocal]);

  useEffect(() => {
    if (isCheckoutTutorialDemo || !apptId || !hydration || step !== "today") return undefined;

    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") refreshSharedDraft();
    };

    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    const interval = window.setInterval(refreshIfVisible, 4000);

    return () => {
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
      window.clearInterval(interval);
    };
  }, [apptId, hydration, isCheckoutTutorialDemo, refreshSharedDraft, step]);

  useEffect(() => {
    const request = activeOrthoNextVisit(hydration?.ortho_next_visit);
    const key = orthoNextVisitDecisionKey(request);
    if (
      step !== "follow_up"
      || !request
      || request.status !== "needs_booking"
      || request.next_visit_type !== "debonding"
      || request.debonding_timing !== "schedule_sooner"
      || orthoNextVisitDecision?.key === key
      || autoOpenedOrthoNextVisitKeyRef.current === key
    ) {
      return;
    }
    autoOpenedOrthoNextVisitKeyRef.current = key;
    setOrthoNextVisitBookOpen(true);
  }, [hydration?.ortho_next_visit, orthoNextVisitDecision?.key, step]);

  useEffect(() => { load(); }, [load]);

  // Load clinic-level Senior/PWD discount %. Best-effort: missing
  // RPC or column leaves the statutory fallback in place.
  useEffect(() => {
    if (isCheckoutTutorialDemo) return undefined;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("get_clinic_settings");
      if (cancelled) return;
      const pct = Number(data?.senior_pwd_discount_pct);
      if (Number.isFinite(pct) && pct > 0 && pct <= 100) {
        setSeniorPwdPct(pct / 100);
      }
      if (Array.isArray(data?.payment_schedules_enabled) && data.payment_schedules_enabled.length > 0) {
        setBillingPlansEnabled(data.payment_schedules_enabled);
      }
      if (data?.payment_schedule_type) {
        setBillingPlanDefault(data.payment_schedule_type);
      }
    })();
    return () => { cancelled = true; };
  }, [isCheckoutTutorialDemo]);

  // Pull QR codes once after hydration. Used to render QR + payment
  // link in Step 2 when a digital method is picked.
  useEffect(() => {
    if (!hydration) return;
    let cancelled = false;
    (async () => {
      if (isCheckoutPlanTutorialDemo) {
        setQrs([]);
        return;
      }
      const { data } = await supabase.rpc("list_payment_qrs", { p_include_archived: false });
      const clinicQrs = Array.isArray(data) ? data : [];

      if (isCheckoutTodayTutorialDemo) {
        if (!cancelled) setQrs(checkoutTodayTutorialQrs(clinicQrs));
        return;
      }

      if (!cancelled) setQrs(clinicQrs);
    })();
    return () => { cancelled = true; };
  }, [hydration, isCheckoutPlanTutorialDemo, isCheckoutTodayTutorialDemo]);

  // Load post-op follow-up settings for every procedure that appears in
  // today's scheduled items or impromptu checkout rows. The chip on a
  // Done row reads from this map to know whether to render and what to
  // say. Tolerant of permissions issues — if the read returns nothing
  // the chip silently stays hidden.
  useEffect(() => {
    if (isCheckoutTutorialDemo) {
      setPostOpByProcedure({});
      return undefined;
    }
    if (!hydration) return;
    const ids = new Set();
    for (const it of (hydration?.scheduled_items ?? [])) {
      if (it.procedure_id) ids.add(it.procedure_id);
    }
    for (const it of (items ?? [])) {
      if (it.procedure_id) ids.add(it.procedure_id);
    }
    if (ids.size === 0) { setPostOpByProcedure({}); return; }
    let cancelled = false;
    (async () => {
      const { data, error: e } = await supabase
        .from("procedures")
        .select("id, name, post_op_followup_enabled, post_op_followup_after_days, post_op_followup_procedure_id")
        .in("id", Array.from(ids));
      if (cancelled) return;
      if (e || !Array.isArray(data)) { setPostOpByProcedure({}); return; }
      const followupIds = data
        .map((p) => p.post_op_followup_procedure_id)
        .filter(Boolean);
      let followupById = {};
      if (followupIds.length > 0) {
        // Pull the follow-up procedure's own row so we know its
        // duration + category for the Book dialog's bundle. Without
        // this the slot grid renders the wrong block size.
        const { data: followupRows } = await supabase
          .from("procedures")
          .select("id, name, suggested_duration_minutes, category")
          .in("id", followupIds);
        if (cancelled) return;
        followupById = Object.fromEntries((followupRows ?? []).map((r) => [r.id, r]));
      }
      const map = {};
      for (const p of data) {
        if (!p.post_op_followup_enabled) continue;
        if (!p.post_op_followup_after_days || !p.post_op_followup_procedure_id) continue;
        const target = followupById[p.post_op_followup_procedure_id] || {};
        map[p.id] = {
          enabled:                  true,
          after_days:               Number(p.post_op_followup_after_days) || 7,
          followup_procedure_id:    p.post_op_followup_procedure_id,
          followup_procedure_name:  target.name || "Follow-up",
          followup_duration_min:    15,
          followup_category:        target.category || null,
        };
      }
      setPostOpByProcedure(map);
    })();
    return () => { cancelled = true; };
  }, [hydration, isCheckoutTutorialDemo, items]);

  // Pick the QR matching the selected payment method.
  const activeQr = useMemo(() => {
    if (!qrs.length) return null;
    const m = paymentState.method;
    if (!["gcash","maya","bank_transfer"].includes(m)) return null;
    return qrs.find((q) => q.method === m) || qrs.find((q) => !q.method) || qrs[0] || null;
  }, [qrs, paymentState.method]);

  const buildCheckoutDraft = useCallback(() => ({
    items,
    dispositions: chairsideDispositions,
    followup_dispositions: Array.from(dispositions.entries()).map(
      ([bundleKey, value]) => ({ bundleKey, ...value })
    ),
    plan_visit_bookings: Array.from(planVisitBookings.entries()).map(
      ([plan_id, value]) => ({ plan_id, ...value })
    ),
    plan_visit_unsure: Array.from(planVisitUnsure),
    post_op_decisions: postOpDecisions || {},
    ortho_next_visit_decision: orthoNextVisitDecision || null,
    flexible_plan_collection: flexiblePlanCollection,
    last_step: "today",
  }), [
    chairsideDispositions,
    dispositions,
    flexiblePlanCollection,
    items,
    orthoNextVisitDecision,
    planVisitBookings,
    planVisitUnsure,
    postOpDecisions,
  ]);

  // Serialize writes from this tab. Each queued save reads the revision only
  // when it reaches the database, so two fast local edits cannot race each
  // other with the same expected revision.
  const persistCheckoutDraft = useCallback((draft) => {
    const save = async () => {
      const saved = await saveCheckoutDraftWithRevision({
        rpc: supabase.rpc.bind(supabase),
        appointmentId: apptId,
        draft,
        expectedRevision: draftRevisionRef.current,
      });
      draftRevisionRef.current = saved.revision;
      return saved;
    };
    const queued = draftSaveChainRef.current.catch(() => undefined).then(save);
    draftSaveChainRef.current = queued;
    return queued;
  }, [apptId]);

  const reportDraftSaveFailure = useCallback((draftError) => {
    const conflict = isCheckoutDraftRevisionConflict(draftError);
    const message = conflict
      ? "This visit changed in another screen. Reload the latest saved procedures before continuing."
      : (draftError?.message || "Couldn't save draft.");
    setSaveError(message);
    if (conflict) setDraftConflict(true);
    toast.error(conflict ? "This visit changed in another screen" : "Auto-save failed", {
      id: conflict ? "checkout-draft-conflict" : "checkout-autosave-fail",
      description: conflict
        ? "Your screen was not allowed to overwrite it. Reload and review the saved procedures."
        : "Your latest changes might be lost if you leave. Keep this tab open.",
    });
  }, []);

  // Auto-save draft (debounced). Only runs if appointment is Checked In.
  // saveError surfaces persistent failures so the clerk knows their
  // recent edits aren't being persisted. Without this, a network drop
  // would silently lose work and the clerk would only find out by
  // reopening the patient and seeing missing items.
  useEffect(() => {
    if (isCheckoutTutorialDemo) return undefined;
    if (!hydration || hydration.appointment.status !== "Checked In") return;
    if (draftConflict) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSavingDraft(true);
      try {
        const saved = await persistCheckoutDraft(buildCheckoutDraft());
        mergeServerDraftIntoLocal(saved.draftData);
        setDraftConflict(false);
        setSaveError(null);
        setSavedAt(new Date(saved.updatedAt || Date.now()));
      } catch (draftError) {
        // eslint-disable-next-line no-console
        console.warn("[checkout] auto-save failed", draftError);
        reportDraftSaveFailure(draftError);
      } finally {
        setSavingDraft(false);
      }
    }, 700);
    return () => clearTimeout(debounceRef.current);
  }, [
    buildCheckoutDraft,
    draftConflict,
    hydration,
    isCheckoutTutorialDemo,
    mergeServerDraftIntoLocal,
    persistCheckoutDraft,
    reportDraftSaveFailure,
  ]);

  // ---------- Exit gate: detect staged work + handle leave attempts -------
  // Triggered when the receptionist tries to navigate away from a checkout
  // that has unfinished work staged. Auto-save protects the draft itself,
  // but Pencil-status appointments created via Step 3's BookFutureNeedDialog
  // sit on the calendar until either commit_checkout_with_followup promotes
  // them or discard_checkout_draft_v2 releases them. Without this gate, a
  // distracted clerk who clicks "Back to Calendar" leaves orphaned Pencils.
  // Computed and the beforeunload effect mounted ABOVE the early returns
  // so the hook order is stable across renders (rules-of-hooks).
  const isDirty = !isCheckoutTutorialDemo && step !== "done" && (
    items.length > 0 ||
    Object.keys(chairsideDispositions || {}).length > 0 ||
    dispositions.size > 0 ||
    planVisitBookings.size > 0 ||
    planVisitUnsure.size > 0 ||
    Object.keys(postOpDecisions || {}).length > 0 ||
    !!orthoNextVisitDecision ||
    !!flexiblePlanCollection?.decision ||
    paymentState.amount_tendered_cents > 0 ||
    paymentState.visit_discount_cents > 0 ||
    paymentState.hmo_covered_cents > 0 ||
    !!paymentState.paid_by_patient_id ||
    !!paymentState.reference_number ||
    !!paymentState.proof_url
  );
  const pencilBookingCount = (() => {
    const ids = new Set();
    for (const d of dispositions.values()) {
      if (d.kind === "book" && d.appointment_id && !d.ortho_installation_booking) ids.add(d.appointment_id);
    }
    for (const b of planVisitBookings.values()) {
      if (b?.appointment_id) ids.add(b.appointment_id);
    }
    for (const d of Object.values(postOpDecisions || {})) {
      if (d?.kind === "book" && d.appointment_id) ids.add(d.appointment_id);
    }
    return ids.size;
  })();

  // Browser close / refresh / hard URL change — native confirm prompt.
  // Only armed while there's staged work. Modern browsers ignore returnValue
  // text and show their own generic message; setting a non-empty string
  // (or calling preventDefault) is what triggers the prompt.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // In-app sidebar nav requests. Sidebar dispatches CHECKOUT_NAV_REQUEST when
  // the clerk clicks any nav item during an active checkout. If there's
  // staged work we open the exit dialog with the requested destination
  // remembered (Save/Discard then routes there). If nothing's staged we just
  // navigate immediately so the lock-banner experience doesn't nag for
  // nothing.
  useEffect(() => {
    function handleNavRequest(e) {
      const to = e?.detail?.to;
      if (!to) return;
      if (isDirty) {
        setPendingNav(to);
        setExitDialogOpen(true);
      } else {
        navigate(to);
      }
    }
    window.addEventListener(CHECKOUT_NAV_REQUEST, handleNavRequest);
    return () => window.removeEventListener(CHECKOUT_NAV_REQUEST, handleNavRequest);
  }, [isDirty, navigate]);

  // A completed visit stays on the checkout URL as a read-only record. Tell
  // the shell to restore normal navigation once checkout reaches Done or the
  // database says this appointment was already finalized.
  useEffect(() => {
    if (!apptId) return;
    window.dispatchEvent(new CustomEvent(CHECKOUT_FOCUS_STATE, {
      detail: {
        active: step !== "done" && !hydration?.checkout_completion?.completed,
      },
    }));
  }, [apptId, hydration?.checkout_completion?.completed, step]);

  // Completed work is the first plan source because recording a visit removes
  // it from unfinished scheduled rows. The persisted draft item still carries
  // treatment_plan_id and must keep MV billing and follow-up attached.
  const planIds = useMemo(() => checkoutPlanIds({
    items,
    scheduledItems: hydration?.scheduled_items ?? [],
    finishableBundle: hydration?.finishable_bundle ?? null,
  }), [hydration, items]);
  const planId = planIds[0] || null;

  // Plan+billing tray flags. Billing only renders once the plan's billing
  // state has loaded; until then (or with no plan at all) the plan list
  // takes the full width so it never leaves an empty column beside it.
  const showPlanBilling   = Boolean(planId && planBillingState);
  // Today's booked work splits into two buckets so checkout can list it in
  // separate cards: standalone work (Scheduled bucket) vs plan-bound work
  // (Plan / Ongoing bucket, which also owns billing). hasNonPlanScheduled also
  // drives the billing card's caption so it's clear the plan total covers only
  // the "On plan" rows.
  const showOrthoChargePrompt = (hydration?.scheduled_items || []).some((s) => (
    s?.source === "ortho_checkout"
      && (s?.clinical_only === true || s?.payable_status === "clinical_only")
  ));
  const hasNonPlanScheduled = (hydration?.scheduled_items || []).some((s) => scheduledWorkKey(s) && !s.treatment_plan_id);
  const hasPlanScheduled    = (hydration?.scheduled_items || []).some((s) => scheduledWorkKey(s) && s.treatment_plan_id);

  // planIds also drives Step 3 follow-up, including plans represented only by
  // a completed draft item.

  // Fetch billing state whenever the planId resolves (or a sibling
  // mutation bumps the nonce — e.g. set_plan_billing_model from the
  // BillingStateCard's picker).
  useEffect(() => {
    if (!planId) {
      setPlanBillingState(null);
      setPlanBillingError(null);
      setBundleStateError(null);
      setPlanIsBundle(false);
      setBundleFinishRequested(false);
      setBundleMvCode(null);
      setFinishBundleOnComplete(false);
      return;
    }
    if (isCheckoutPlanTutorialDemo) {
      setPlanBillingError(null);
      setBundleStateError(null);
      const demoBillingState = isCheckoutOngoingTutorialDemo
        ? buildCheckoutOngoingTutorialBillingState(checkoutOngoingTutorialVisit)
        : checkoutStageSeed.billingStatePatch
          ? { ...CHECKOUT_TX_PLAN_TUTORIAL_BILLING_STATE, ...checkoutStageSeed.billingStatePatch }
          : CHECKOUT_TX_PLAN_TUTORIAL_BILLING_STATE;
      setPlanBillingState(demoBillingState);
      setPlanIsBundle(isCheckoutOngoingTutorialDemo);
      setBundleFinishRequested(isCheckoutOngoingTutorialDemo && checkoutOngoingTutorialVisit === 3);
      setBundleMvCode(isCheckoutOngoingTutorialDemo ? "MV-TUTORIAL" : null);
      setFinishBundleOnComplete(false);
      return;
    }
    let cancelled = false;
    setPlanBillingState(null);
    setPlanBillingError(null);
    setBundleStateError(null);
    setPlanIsBundle(false);
    setBundleFinishRequested(false);
    setBundleMvCode(null);
    setFinishBundleOnComplete(false);

    readCheckoutPlanState({ planId, ongoingTreatmentEnabled }).then((result) => {
      if (cancelled) return;
      setPlanBillingState(result.billingState);
      setPlanBillingError(result.billingError?.message ?? null);
      setBundleStateError(result.bundleError?.message ?? null);
      setPlanIsBundle(result.bundleState.isBundle);
      setBundleFinishRequested(result.bundleState.finishRequested);
      setBundleMvCode(result.bundleState.mvCode);
    });

    return () => { cancelled = true; };
  }, [planId, planBillingNonce, checkoutOngoingTutorialVisit, checkoutStageSeed.billingStatePatch, isCheckoutOngoingTutorialDemo, isCheckoutPlanTutorialDemo, ongoingTreatmentEnabled]);

  // Fetch billing state for any ADDITIONAL plans on this visit. Skipped
  // entirely (state cleared) when there are 0 or 1 plans, so the common
  // single-plan checkout never runs this and never carries extra math.
  const extraPlanIdsKey = planIds.filter((id) => id && id !== planId).join(",");
  useEffect(() => {
    const ids = extraPlanIdsKey ? extraPlanIdsKey.split(",") : [];
    if (ids.length === 0 || isCheckoutPlanTutorialDemo) { setExtraPlanBillings([]); return undefined; }
    let cancelled = false;
    Promise.all(ids.map((id) =>
      supabase.rpc("get_plan_billing_state", { p_plan_id: id })
        .then(({ data, error: e }) => (e ? null : data)),
    )).then((rows) => {
      if (!cancelled) setExtraPlanBillings(rows.filter(Boolean));
    });
    return () => { cancelled = true; };
  }, [extraPlanIdsKey, planBillingNonce, isCheckoutPlanTutorialDemo]);

  useEffect(() => {
    const patientId = hydration?.patient?.id;
    if (isCheckoutTutorialDemo || !patientId) {
      setPriorArRows([]);
      setPriorArSelections({});
      setPriorArError(null);
      setPriorArLoading(false);
      return undefined;
    }

    let cancelled = false;
    setPriorArLoading(true);
    setPriorArError(null);
    supabase.rpc("list_patient_open_ar", { p_patient_id: patientId }).then(({ data, error: e }) => {
      if (cancelled) return;
      setPriorArLoading(false);
      if (e) {
        setPriorArRows([]);
        setPriorArSelections({});
        setPriorArError(e.message ?? "Could not load prior AR.");
        return;
      }
      const rows = normalizeCheckoutPriorArRows(data);
      setPriorArRows(rows);
      setPriorArSelections((prev) => {
        const next = {};
        rows.forEach((row) => {
          if (prev[row.ar_receivable_id]) next[row.ar_receivable_id] = true;
        });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [hydration?.patient?.id, isCheckoutTutorialDemo]);

  // Auto-fill amount tendered with Total Due (= plan_suggested +
  // walk_in_net). We track:
  //   - userEditedAmountRef: a real boolean flag, set true when the
  //     receptionist edits the amount field directly (PesoInput
  //     onChange) OR clicks a manual quick-fill (50%, No payment).
  //     Auto-fill never overrides once this is true.
  //   - lastAutoFilledRef: the value we most recently auto-filled.
  //     Used only to detect "current value is exactly what we filled
  //     last" so we don't churn on no-op renders.
  // The earlier implementation used the ref-value as a proxy for
  // "user hasn't typed yet" — that broke when the user typed a value
  // that coincidentally equaled an old auto-fill, then a walk-in was
  // added: their typed value got silently overwritten.
  const userEditedAmountRef = useRef(false);
  const lastAutoFilledRef   = useRef(null);

  // Bill math — computed BEFORE the early returns so the auto-fill
  // useEffect below can depend on totalDueCents without violating
  // rules-of-hooks. Safe with hydration=null (defaults to empty
  // arrays / zeros), so this runs harmlessly on first render and
  // before checkout has loaded.
  const seniorPwdPercent = seniorPwdPct * 100;
  const seniorPwdPctLabel = formatPercentLabel(seniorPwdPercent);

  const workKeyToPlanIdEarly = (() => {
    const m = new Map();
    for (const s of hydration?.scheduled_items ?? []) {
      const key = scheduledWorkKey(s);
      if (key) m.set(key, s.treatment_plan_id || null);
      if (s.tn_id) m.set(`tn:${s.tn_id}`, s.treatment_plan_id || null);
    }
    return m;
  })();
  const isPlanBoundEarly = (it) => {
    const key = scheduledWorkKey(it);
    return !!(it.treatment_plan_id || (key && workKeyToPlanIdEarly.get(key)));
  };
  const doneItemsEarly       = items.filter((it) => (it.outcome || "done") === "done");
  const walkInDoneItemsEarly = doneItemsEarly.filter((it) => !isPlanBoundEarly(it));
  const seniorPwdEligibleEarly = (
    !!(hydration?.patient?.is_senior_citizen || hydration?.patient?.is_pwd)
    && !paymentState.decline_senior_pwd
  );
  const walkInSubtotalEarly = walkInDoneItemsEarly.reduce(
    (s, it) => s + Number(it.qty || 1) * Number(it.performed_price_centavos || 0), 0);
  const walkInItemDiscEarly = walkInDoneItemsEarly.reduce(
    (s, it) => s + Number(it.item_discount_centavos || 0), 0);
  const walkInSeniorPwdEarly = seniorPwdEligibleEarly
    ? walkInDoneItemsEarly.reduce(
        (s, it) => s + calculatePercentDiscountCentavos(
          Number(it.qty || 1) * Number(it.performed_price_centavos || 0),
          seniorPwdPercent,
        ), 0)
    : 0;
  const walkInNetEarly = Math.max(
    0,
    walkInSubtotalEarly
      - walkInItemDiscEarly
      - walkInSeniorPwdEarly
      - Number(paymentState.visit_discount_cents || 0)
      - Number(paymentState.hmo_covered_cents || 0),
  );
  const orthoCollectionTargetEarly = findOrthoCollectionTarget(doneItemsEarly);
  const orthoCollectionKeyEarly = orthoCollectionTargetEarly ? scheduledWorkKey(orthoCollectionTargetEarly) : null;
  const orthoCollectionStateEarly = normalizeOrthoCollectionChoice(orthoCollectionTargetEarly, orthoCollectionChoice);
  const orthoCollectionExtraEarly = getOrthoCollectionExtraCents(orthoCollectionTargetEarly, orthoCollectionStateEarly);
  const flexibleCollectionRequired = !!planId && requiresFlexibleCollectionDecision(planBillingState);
  const activeFlexibleCollection = flexiblePlanCollection?.plan_id === planId
    ? flexiblePlanCollection
    : { plan_id: planId, decision: null, amount_centavos: null };
  const flexibleCollectionDecided = !flexibleCollectionRequired
    || activeFlexibleCollection.decision === "none"
    || (
      activeFlexibleCollection.decision === "amount"
      && Number(activeFlexibleCollection.amount_centavos || 0) > 0
    );
  const planSuggestedEarly = resolveFlexibleCollectionTarget({
    billingState: planBillingState,
    planId,
    choice: activeFlexibleCollection,
  });
  // Additional plans on this visit (0 in the common single-plan case).
  // suggested = each extra plan's due-this-visit; remaining = its balance
  // (used to lift the overpayment cap so both plans can be paid in full).
  const extraPlanSuggestedCents = extraPlanBillings.reduce(
    (s, b) => s + Math.max(0, Number(b?.suggested_next_centavos ?? 0)), 0);
  const extraPlanRemainingCents = extraPlanBillings.reduce(
    (s, b) => s + Math.max(0, Number(b?.remaining_centavos ?? 0)), 0);
  const selectedPriorArEarly = selectedPriorArTotalCentavos(priorArRows, priorArSelections);
  // The model's first-visit share of a folded bundle. Mirrors
  // dos_v2.plan_addon_first_visit_share (kept in lockstep). One frontend
  // definition, reused for the prefill AND the live collect math below.
  const foldShareOf = (bundleCents) => {
    const b = Math.max(0, Number(bundleCents) || 0);
    if (b <= 0) return 0;
    const preset = planBillingState?.preset;
    const total  = Number(planBillingState?.plan_total_items_centavos ?? planBillingState?.total_centavos ?? 0);
    const dp     = Number(planBillingState?.custom_schedule?.dp_centavos ?? 0);
    const visits = Math.max(1, Number(planBillingState?.total_visits ?? 1));
    if (preset === "full")  return b;
    if (preset === "50_50") return Math.floor(b / 2);
    if (preset === "custom" && total > 0) return Math.round(b * (dp / total));
    return Math.floor(b / visits);   // per_phase / default: even split
  };
  // Fold-aware prefill: when today's non-plan work is folded in, prefill
  // the plan due + the folded share (not the full walk-in bill).
  const foldCandidatesEarly = planId && !planBillingState?.denture_item_billing
    ? walkInDoneItemsEarly.filter((it) => !!it.procedure_id && it.source !== "ortho_checkout")
    : [];
  const foldActiveEarly = foldIntoPlan && foldCandidatesEarly.length > 0;
  const foldBundleEarly = foldActiveEarly
    ? foldCandidatesEarly.reduce((s, it) => s + Math.max(0,
        Number(it.qty || 1) * Number(it.performed_price_centavos || 0)
        - Number(it.item_discount_centavos || 0)), 0)
    : 0;
  const foldShareEarly = foldActiveEarly ? foldShareOf(foldBundleEarly) : 0;
  const walkInNetEarlyForDue = foldActiveEarly ? Math.max(0, walkInNetEarly - foldBundleEarly) : walkInNetEarly;
  const totalDueEarly      = planSuggestedEarly + extraPlanSuggestedCents + foldShareEarly + walkInNetEarlyForDue + orthoCollectionExtraEarly + selectedPriorArEarly;

  // Auto-fill effect. Driven by Total Due. Skips when the receptionist
  // has typed/quick-filled (userEditedAmountRef.current === true). Re-
  // fires when totalDue changes (walk-in added, billing model picker
  // saves) — those changes also bump planBillingNonce, which we use
  // below to RESET the user-edited flag (a model change is itself an
  // explicit signal to start over).
  useEffect(() => {
    if (totalDueEarly <= 0) return;
    if (userEditedAmountRef.current) return; // sticky — user owns the field
    const current = Number(paymentState.amount_tendered_cents ?? 0);
    if (current === totalDueEarly) {
      lastAutoFilledRef.current = totalDueEarly;
      return; // no churn
    }
    setPaymentState((s) => ({ ...s, amount_tendered_cents: totalDueEarly }));
    lastAutoFilledRef.current = totalDueEarly;
  }, [totalDueEarly, paymentState.amount_tendered_cents]);

  // Picking a new billing model (or DP) is an explicit reset of intent
  // — clear the user-edited lock so the next totalDue can auto-fill.
  // planBillingNonce bumps from BillingStateCard's onChanged callback
  // when set_plan_billing_model succeeds.
  useEffect(() => {
    userEditedAmountRef.current = false;
  }, [planBillingNonce]);

  // Toggling "include today's work in plan billing" changes what's
  // collected today (plan due + folded share vs the full walk-in bill),
  // so it's an explicit reset of intent — let the amount re-fill.
  useEffect(() => {
    userEditedAmountRef.current = false;
  }, [foldIntoPlan]);

  if (!apptId) {
    return params.get("view") === "payment-review"
      ? <PaymentReview navigate={navigate} />
      : <CheckoutQueue
          navigate={navigate}
          tutorialDemo={isCheckoutTodayTutorialDemo || isCheckoutAddProceduresTutorialDemo}
          tutorialRows={isCheckoutAddProceduresTutorialDemo ? CHECKOUT_ADD_PROCEDURES_TUTORIAL_QUEUE_ROWS : undefined}
          tutorialVisitRoute={isCheckoutAddProceduresTutorialDemo ? CHECKOUT_ADD_PROCEDURES_TUTORIAL_VISIT_ROUTE : undefined}
        />;
  }
  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-12 text-center text-sm text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading checkout…
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-xl mx-auto">
        <AlertTriangle className="w-8 h-8 mx-auto text-rose-400" />
        <p className="text-sm text-slate-600 mt-3">{error}</p>
        <Link to="/calendar" className="mt-4 inline-block text-sm text-teal-700 hover:underline">← Back to Calendar</Link>
      </div>
    );
  }

  const status = hydration.appointment.status;
  const checkoutCompletion = hydration?.checkout_completion || {};
  const isCheckoutCompleted = !!checkoutCompletion.completed;
  const isCheckedIn = status === "Checked In" && !isCheckoutCompleted;

  // Plan-bound classification. The hydration's scheduled_items array
  // carries treatment_plan_id per row (NULL for walk-ins). Build a
  // tn_id → plan_id map so we can label items in the bill as
  // "plan-bound" (route to parent plan charge, NOT individually billed
  // today) vs "walk-in" (charged per line as the legacy flow always
  // has). complete_checkout_plan_aware does this routing on the server;
  // here we just use it for the bill display + Total due math.
  const workKeyToPlanId = (() => {
    const m = new Map();
    for (const s of hydration?.scheduled_items ?? []) {
      const key = scheduledWorkKey(s);
      if (key) m.set(key, s.treatment_plan_id || null);
      if (s.tn_id) m.set(`tn:${s.tn_id}`, s.treatment_plan_id || null);
    }
    return m;
  })();
  const isPlanBound = (it) => {
    const key = scheduledWorkKey(it);
    return !!(it.treatment_plan_id || (key && workKeyToPlanId.get(key)));
  };

  const doneItems        = items.filter((it) => (it.outcome || "done") === "done");
  // Done-work keys for this visit (used by reconciliation + guardrail 3).
  // Plain consts (not hooks) — this runs after the loading/error early returns.
  const doneWorkKeys = new Set();
  for (const it of doneItems) {
    const k = scheduledWorkKey(it);
    if (k) doneWorkKeys.add(k);
    if (it?.tn_id) doneWorkKeys.add(`tn:${it.tn_id}`);
  }
  // Chart reconciliation (mirrors the chairside): a booked reminder auto-checks
  // off when matching work is recorded this visit. Same shared logic so the two
  // screens never disagree.
  const chartReconciled = reconcileChartToReminders(hydration?.scheduled_items, doneItems, {
    keyOf: scheduledWorkKey,
    doneKeys: doneWorkKeys,
    dispositions: chairsideDispositions,
  });
  // Guardrail 3: a booked tooth/surface procedure can't silently vanish. If it
  // wasn't recorded (no matching chart), tapped (Done/Logged), or set aside
  // (Not today / Decline), block payment until front desk makes a call — so
  // work the doctor may have done off-app isn't lost.
  const unresolvedBookedReminders = (hydration?.scheduled_items || []).filter((s) => {
    const k = scheduledWorkKey(s);
    if (!k) return false;
    if (!(s?.tooth_specific || s?.surface_specific) || s?.treatment_plan_id || s?.ortho_case_id) return false;
    if (doneWorkKeys.has(k)) return false;
    if (chartReconciled?.has?.(k)) return false;
    const d = chairsideDispositions?.[k] || (s?.tn_id ? chairsideDispositions?.[`tn:${s.tn_id}`] : null);
    return !d;
  });
  const unresolvedBookedBlocked = isCheckedIn && unresolvedBookedReminders.length > 0;
  const planDoneItems    = doneItems.filter(isPlanBound);
  const walkInDoneItemsAll = doneItems.filter((it) => !isPlanBound(it));
  // Fold candidates: walk-in lines done today that can ride the plan — a real
  // catalog procedure (custom one-offs have no procedure_id and can't fold)
  // and not an ortho contract line. Only meaningful when a plan is present.
  const foldEligible = (it) => !!planId
    && !planBillingState?.denture_item_billing
    && !!it.procedure_id
    && it.source !== "ortho_checkout";
  const foldCandidates = walkInDoneItemsAll.filter(foldEligible);
  const foldActive = foldIntoPlan && foldCandidates.length > 0;
  const foldedDoneItems = foldActive ? foldCandidates : [];
  const foldedItemKeySet = new Set(foldedDoneItems.map((it) => scheduledWorkKey(it) || it._localId || it.procedure_id));
  // When folding, the folded lines leave the walk-in bucket entirely, so every
  // downstream walk-in total (subtotal, discounts, net, today's due) excludes
  // them automatically — they'll be billed on the plan instead.
  const walkInDoneItems  = foldActive
    ? walkInDoneItemsAll.filter((it) => !foldedItemKeySet.has(scheduledWorkKey(it) || it._localId || it.procedure_id))
    : walkInDoneItemsAll;

  // Fold: how much of today's folded (scheduled and/or add-on) work is
  // collected NOW, per the plan's billing model. Formula kept in lockstep
  // with dos_v2.plan_addon_first_visit_share. The share is collected as
  // part of the plan payment; the remainder rides the plan's schedule.
  const foldBundleCents = foldedDoneItems.reduce(
    (s, it) => s + Math.max(0,
      Number(it.qty || 1) * Number(it.performed_price_centavos || 0)
      - Number(it.item_discount_centavos || 0)), 0);
  const foldShareCents = foldActive ? foldShareOf(foldBundleCents) : 0;

  // Legacy aggregates over ALL done items — kept for places that need
  // an "everything performed today" total (receipt, audit, exit-gate
  // dirty check). NOT what the patient owes today when a plan is bound.
  const subtotalCents          = doneItems.reduce((s, it) => s + Number(it.qty || 1) * Number(it.performed_price_centavos || 0), 0);
  const itemDiscountTotalCents = doneItems.reduce((s, it) => s + Number(it.item_discount_centavos || 0), 0);

  const seniorPwdEligible = (hydration.patient.is_senior_citizen || hydration.patient.is_pwd) && !paymentState.decline_senior_pwd;
  // PH compliance: senior/PWD is a settings-driven PER-LINE discount,
  // not a global one. Receipts must show the discount on each line. Compute the
  // per-row equivalent (qty × price × configured %) for every "done" item, sum
  // for display, and let the submit logic push the same number into
  // each line's item_discount_centavos.
  const seniorPwdDiscountCents = seniorPwdEligible
    ? doneItems.reduce((s, it) => s + calculatePercentDiscountCentavos(
      Number(it.qty || 1) * Number(it.performed_price_centavos || 0),
      seniorPwdPercent,
    ), 0)
    : 0;
  // HMO coverage reduces the patient's share of the bill. Captured in
  // paymentState (set by HMO panel) and applied here.
  const hmoCoveredCents = Number(paymentState.hmo_covered_cents || 0);
  const visitDiscountCents = Number(paymentState.visit_discount_cents || 0);
  // Global discount = visit-level only (senior/PWD lives per-line now).
  const totalGlobalDiscountCents = visitDiscountCents;

  // Plan-aware bill math. Two buckets:
  //   * Plan items don't add their per-line fee to "Total due today" —
  //     they roll into the plan's parent charge (created/updated by
  //     _ensure_plan_visit_charge() on the server). The patient's
  //     obligation today for the plan portion is the billing model's
  //     suggested amount.
  //   * Walk-in items add their per-line net (after item discount,
  //     senior/PWD, and HMO coverage) to Total due. They become
  //     individual charge events on the server, exactly as today.
  //
  // For walk-in-only visits (planSuggestedCents=0, planDoneItems empty),
  // this falls through to the legacy walk-in math.
  const walkInSubtotalCents     = walkInDoneItems.reduce((s, it) => s + Number(it.qty || 1) * Number(it.performed_price_centavos || 0), 0);
  const walkInItemDiscountCents = walkInDoneItems.reduce((s, it) => s + Number(it.item_discount_centavos || 0), 0);
  const walkInSeniorPwdCents = seniorPwdEligible
    ? walkInDoneItems.reduce((s, it) => s + calculatePercentDiscountCentavos(
      Number(it.qty || 1) * Number(it.performed_price_centavos || 0),
      seniorPwdPercent,
    ), 0)
    : 0;
  // HMO coverage and visit-level discounts are applied to the walk-in
  // bucket. For plan items, those concessions belong at plan acceptance
  // time (out of scope for this PR). If the visit is walk-in-only, the
  // bucket gets everything and the math matches the legacy net.
  const walkInHmoCoveredCents     = hmoCoveredCents;
  const walkInVisitDiscountCents  = visitDiscountCents;
  const walkInNetCents = Math.max(
    0,
    walkInSubtotalCents
      - walkInItemDiscountCents
      - walkInSeniorPwdCents
      - walkInVisitDiscountCents
      - walkInHmoCoveredCents,
  );

  // Plan portion due today comes straight from the billing-model
  // suggestion. NULL → 0 (e.g. preset not picked yet).
  const planSuggestedCents = planSuggestedEarly;
  const orthoCollectionTarget = findOrthoCollectionTarget(doneItems);
  // A "blank" visit: the doctor charted nothing, so all three buckets are
  // empty. This is the paper-handoff / verbal workflow — front desk builds the
  // whole visit from the catalog + custom charges. When true, Step 1 leads with
  // a prominent "encode what the doctor did" panel instead of the muted
  // "nothing added" card. Only while checked in (else the visit is read-only).
  const nothingCharted =
    isCheckedIn &&
    (items?.length ?? 0) === 0 &&
    !hasNonPlanScheduled &&
    !hasPlanScheduled &&
    !showPlanBilling &&
    !planIsBundle &&
    !orthoCollectionTarget;
  const orthoCollectionKey = orthoCollectionTarget ? scheduledWorkKey(orthoCollectionTarget) : null;
  const orthoCollectionState = normalizeOrthoCollectionChoice(orthoCollectionTarget, orthoCollectionChoice);
  const orthoCollectionDecision = orthoCollectionState.decision;
  const orthoCollectionDueCents = getOrthoCollectionDueCents(orthoCollectionTarget, orthoCollectionState);
  const orthoCollectionShortfallCents = getOrthoCollectionShortfallCents(orthoCollectionTarget, orthoCollectionState);
  const orthoCollectionExtraCents = getOrthoCollectionExtraCents(orthoCollectionTarget, orthoCollectionState);

  // Current visit due = plan portion + walk-in portion. Prior patient
  // AR can be added below as an explicit collection target, but it
  // stays separate from today's procedure allocation and production.
  const currentVisitDueCents = planSuggestedCents + extraPlanSuggestedCents + foldShareCents + walkInNetCents + orthoCollectionExtraCents;
  const selectedPriorArCents = selectedPriorArTotalCentavos(priorArRows, priorArSelections);

  // Total collected at checkout = current visit due + selected prior AR.
  // This replaces
  // the legacy netCents for walk-in-only visits (planSuggestedCents=0
  // makes it equivalent) AND fixes the per-line-sum bug for plan-bound
  // visits (TP-5EBA83 was reading ₱19,500 from line-item subtotal when
  // the right number for 50/50 Visit 1 is ₱43,500).
  const totalDueCents = currentVisitDueCents + selectedPriorArCents;
  const noTreatmentReasonCode = deriveNoTreatmentReasonCode(chairsideDispositions);
  const missingNotTodayReason = hasMissingNotTodayReason(chairsideDispositions);
  const denturePaymentOnlyCollection = isDenturePaymentOnlyCollection({
    billingState: planBillingState,
    doneItemCount: doneItems.length,
    collectionTargetCentavos: planSuggestedCents,
  });
  const noPaymentVisit = isNoPaymentVisitEligible({
    isCheckedIn,
    itemCount: items.length,
    doneItemCount: doneItems.length,
    totalDueCents,
    finishBundleOnComplete: bundleFinishSelected,
    dispositions: chairsideDispositions,
  });
  const noTreatmentDeferredItems = (noPaymentVisit || denturePaymentOnlyCollection)
    ? (hydration?.scheduled_items || []).filter((scheduledItem) => {
        const key = scheduledWorkKey(scheduledItem);
        const disposition = chairsideDispositions?.[key]
          || (scheduledItem?.tn_id ? chairsideDispositions?.[`tn:${scheduledItem.tn_id}`] : null);
        return disposition?.kind === "not_today";
      })
    : [];

  // Staff can collect less or more than the suggested plan portion.
  // The suggested amount is only the visit guide; the hard cap before
  // true overpayment is the remaining Tx Plan balance plus any walk-in
  // work billed today. Without this, a patient paying ahead on a plan
  // would be clipped to the suggested installment.
  const planRemainingCents = Math.max(0, Number(planBillingState?.remaining_centavos ?? 0));
  // A Flexible plan's suggested amount is a schedule, not an allocation cap.
  // When there is exactly one plan, any tender left after prior AR and
  // standalone work belongs to its open ledger balance. Multiple plans stay
  // explicit because checkout must not guess which one receives an advance.
  const flexibleAdvancePaymentEligible = planBillingState?.preset === "custom"
    && planRemainingCents > 0
    && extraPlanBillings.length === 0
    && !foldActive;
  const simpleFlexiblePlanPayment = flexibleAdvancePaymentEligible
    && extraPlanBillings.length === 0
    && walkInNetCents === 0
    && orthoCollectionExtraCents === 0
    && selectedPriorArCents === 0
    && !foldActive;
  const primaryPlanAllocationTargetCents = planBillingState?.denture_item_billing
    ? Math.min(
        planRemainingCents,
        Math.max(
          0,
          Number(paymentState.amount_tendered_cents || 0)
            - selectedPriorArCents
            - walkInNetCents
            - orthoCollectionExtraCents,
        ),
      )
    : resolvePrimaryPlanAllocationTarget({
        collectionTargetCentavos: planSuggestedCents,
        remainingCentavos: planRemainingCents,
        tenderedCentavos: paymentState.amount_tendered_cents,
        nonPlanObligationCentavos:
          selectedPriorArCents + walkInNetCents + orthoCollectionExtraCents,
        advancePaymentEligible: flexibleAdvancePaymentEligible,
      });
  const primaryDentureItemIds = currentDentureItemIds(planDoneItems);
  const primaryDentureAllocation = buildDenturePaymentAllocation({
    billingState: planBillingState,
    amountCentavos: primaryPlanAllocationTargetCents,
    currentItemIds: primaryDentureItemIds,
    manualAmounts: paymentState.denture_item_allocation_overrides_enabled
      ? paymentState.denture_item_allocation_overrides
      : null,
  });
  const currentVisitPaymentCapCents = planId
    ? Math.max(currentVisitDueCents, planRemainingCents + extraPlanRemainingCents + walkInNetCents + orthoCollectionExtraCents)
    : currentVisitDueCents;
  const paymentCapCents = currentVisitPaymentCapCents + selectedPriorArCents;

  // Backwards-compat alias for the old per-line net. Some downstream
  // code (subtotal-card text, receipt totals) still references it.
  // For walk-in-only visits this is identical to totalDueCents.
  const netCents = totalDueCents;
  const receiptItems = buildReceiptItems({
    items,
    isPlanBound,
    seniorPwdEligible,
    seniorPwdPct: seniorPwdPercent,
  });
  const doneScreenResult = doneResult || completedReceiptView?.result || null;
  const noPaymentStepFlow = noPaymentVisit || isNoPaymentCheckoutResult(doneScreenResult);
  const doneScreenItems = doneResult ? items : completedReceiptView?.items || items;
  const doneScreenPaymentState = doneResult ? paymentState : completedReceiptView?.paymentState || paymentState;
  const doneScreenBreakdown = doneResult ? {
    seniorPwdEligible,
    seniorPwdDiscountCents: walkInSeniorPwdCents,
    hmoCoveredCents,
  } : completedReceiptView?.breakdown || {
    seniorPwdEligible,
    seniorPwdDiscountCents: walkInSeniorPwdCents,
    hmoCoveredCents,
  };

  const billingModelBlocked = !!planId && (
    !!planBillingError ||
    !planBillingState ||
    !planBillingState.preset
  );
  const bundleStateBlocked = !!planId && !!bundleStateError;
  const orthoBillingBlocked = pendingOrthoBillingRows.some((row) => !row?.billing);
  const pendingClinicalSaveRows = [
    ...(Array.isArray(hydration?.scheduled_items) ? hydration.scheduled_items : []),
    ...items,
  ];
  const unresolvedPendingClinicalSaveBlocked = hasUnresolvedPendingClinicalSave({
    rows: pendingClinicalSaveRows,
    items,
    chairsideDispositions,
  });
  const pendingOrthoInstallationRow = pendingOrthoBillingRows.find((row) => {
    const caseId = row?.case?.id;
    if (!caseId || orthoInstallationResolvedCaseIds.has(caseId)) return false;
    if (!row?.billing || row?.billing?.contract_status === "voided") return false;
    if (row?.case?.status !== "active_pending_bonding") return false;
    if (row?.bonding_appointment?.id) return false;
    return true;
  }) || null;
  const billingModelBlockCopy = planBillingError
    ? "Couldn't verify the Tx Plan billing model. Refresh or set the model before continuing."
    : !planBillingState
      ? "Loading Tx Plan billing model..."
      : !planBillingState.preset
        ? "Set a billing model before continuing to payment."
        : "";
  const bundleStateBlockCopy = bundleStateBlocked
    ? "We couldn't check this treatment's finish status. Refresh this page before continuing."
    : "";

  function setFlexibleCollectionAmount(amountCentavos) {
    const amount = amountCentavos == null
      ? null
      : Math.min(planRemainingCents, Math.max(0, Number(amountCentavos || 0)));
    const next = {
      plan_id: planId,
      decision: amount != null && amount > 0 ? "amount" : null,
      amount_centavos: amount != null && amount > 0 ? amount : null,
    };
    setFlexiblePlanCollection(next);
    userEditedAmountRef.current = false;
    setPaymentState((current) => ({
      ...current,
      amount_tendered_cents: next.decision === "amount" ? next.amount_centavos : 0,
    }));
  }

  function setFlexibleNoCollection() {
    setFlexiblePlanCollection({
      plan_id: planId,
      decision: "none",
      amount_centavos: null,
    });
    userEditedAmountRef.current = false;
    setPaymentState((current) => ({ ...current, amount_tendered_cents: 0 }));
  }

  async function refreshCheckoutSettlementPreview(
    tenderedOverride = paymentState.amount_tendered_cents,
    priorSelectionsOverride = priorArSelections,
  ) {
    // Fail closed. A loading/failed rollout read resolves to false in the
    // provider, so Suyom and every disabled clinic remain on the exact legacy
    // path and never call a Package 1C RPC.
    if (!ongoingTreatmentEnabled || isCheckoutTutorialDemo) {
      settlementPreviewRequestRef.current += 1;
      setSettlementPreview(null);
      setSettlementPreviewLoading(false);
      setSettlementPreviewError(null);
      return;
    }

    const requestNumber = settlementPreviewRequestRef.current + 1;
    settlementPreviewRequestRef.current = requestNumber;
    setSettlementPreviewLoading(true);
    setSettlementPreviewError(null);

    const tendered = Math.max(0, Number(tenderedOverride || 0));
    const priorArBuild = buildCheckoutPriorArCollections({
      rows: priorArRows,
      selected: priorSelectionsOverride,
      amountTenderedCents: tendered,
      method: paymentState.method,
      referenceNumber: paymentState.reference_number,
      proofUrl: paymentState.proof_url,
      appointmentId: apptId,
    });
    if (priorArBuild.error) {
      if (settlementPreviewRequestRef.current === requestNumber) {
        setSettlementPreview(null);
        setSettlementPreviewError(priorArBuild.error);
        setSettlementPreviewLoading(false);
      }
      return;
    }
    const priorArTotal = Number(priorArBuild.total_centavos || 0);
    let recordedTender = tendered;
    if (
      tendered > paymentCapCents
      && paymentState.method === "cash"
      && !paymentState.keep_overpay_as_credit
    ) {
      recordedTender = paymentCapCents;
    }
    const visitTender = Math.max(0, recordedTender - priorArTotal);
    const previewPayments = visitTender > 0 ? [{
      method: paymentState.method,
      amount_centavos: visitTender,
      reference_number: paymentState.method === "cash"
        ? undefined
        : (paymentState.reference_number || "PREVIEW"),
    }] : [];

    const previewPrimaryPlanTarget = resolvePrimaryPlanAllocationTarget({
      collectionTargetCentavos: planSuggestedCents,
      remainingCentavos: planRemainingCents,
      tenderedCentavos: tendered,
      nonPlanObligationCentavos:
        priorArTotal + walkInNetCents + orthoCollectionExtraCents,
      advancePaymentEligible: flexibleAdvancePaymentEligible,
    });
    const previewPlanTargets = [];
    if (planId && (previewPrimaryPlanTarget > 0 || foldShareCents > 0)) {
      previewPlanTargets.push({
        plan_id: planId,
        amount_centavos: previewPrimaryPlanTarget + foldShareCents,
      });
    }
    for (const billing of extraPlanBillings) {
      const amount = Math.max(0, Number(billing?.suggested_next_centavos || 0));
      if (billing?.plan_id && amount > 0) {
        previewPlanTargets.push({ plan_id: billing.plan_id, amount_centavos: amount });
      }
    }

    const previewItems = receiptItems.map((item) => ({
      procedure_visit_id: item.procedure_visit_id || null,
      treatment_plan_id: item.treatment_plan_id || null,
      treatment_plan_item_id: item.treatment_plan_item_id || null,
      procedure_id: item.procedure_id || null,
      source: item.source || null,
      outcome: item.outcome || "done",
      performed_price_centavos: Math.max(
        0,
        Number(item.qty || 1) * Number(item.performed_price_centavos || 0),
      ),
      item_discount_centavos: Math.max(0, Number(item.receipt_total_discount_centavos || 0)),
      fold_into_plan: foldActive
        && foldedItemKeySet.has(scheduledWorkKey(item) || item._localId || item.procedure_id)
        ? true
        : undefined,
    }));

    const previewInput = {
      appointment_id: apptId,
      draft_revision: draftRevisionRef.current,
      items: previewItems,
      payments: previewPayments,
      global_discount_centavos: totalGlobalDiscountCents > 0
        ? totalGlobalDiscountCents
        : undefined,
      global_discount_target_plan_id: (
        totalGlobalDiscountCents > 0 && paymentState.visit_discount_target_plan_id
      ) ? paymentState.visit_discount_target_plan_id : undefined,
      plan_payment_targets: previewPlanTargets.length ? previewPlanTargets : undefined,
      prior_ar_collections: priorArBuild.collections.length
        ? priorArBuild.collections
        : undefined,
      hmo_coverage: (hmoCoveredCents > 0 && paymentState.hmo_payer_id) ? {
        payer_id: paymentState.hmo_payer_id,
        covered_centavos: hmoCoveredCents,
      } : undefined,
      ortho_collection: orthoCollectionTarget ? {
        ortho_case_id: orthoCollectionTarget.ortho_case_id,
        decision: orthoCollectionDecision,
        amount_centavos: orthoCollectionDueCents,
      } : undefined,
      ...buildBundleFinishFields({
        planId,
        planIsBundle: ongoingTreatmentEnabled && planIsBundle,
        finishSelected: bundleFinishSelected,
        hasOpenDentureItems: !!planBillingState?.has_open_denture_items,
        arReason: "checkout_shortfall",
      }),
    };

    const { data, error: previewError } = await supabase.rpc("preview_checkout_settlement", {
      p_input: previewInput,
    });
    if (settlementPreviewRequestRef.current !== requestNumber) return;
    setSettlementPreviewLoading(false);
    if (previewError) {
      setSettlementPreview(null);
      setSettlementPreviewError(previewError.message || "Could not balance this checkout.");
      return;
    }
    setSettlementPreview(data || null);
    setSettlementPreviewError(data?.valid === false
      ? (data?.error || "This checkout does not balance yet.")
      : null);
  }

  function continueToPayment() {
    if (orthoBillingBlocked) {
      toast.error("Complete Ortho billing setup before continuing to payment.");
      return;
    }
    if (unresolvedPendingClinicalSaveBlocked) {
      toast.error("Mark the Ortho visit Done, Not today, or Decline before payment.");
      return;
    }
    if (billingModelBlocked && !noPaymentVisit) {
      toast.error(billingModelBlockCopy || "Set a billing model before continuing to payment.");
      return;
    }
    if (bundleStateBlocked && !noPaymentVisit) {
      toast.error(bundleStateBlockCopy);
      return;
    }
    if (!flexibleCollectionDecided && !noPaymentVisit) {
      toast.error("Choose how much to collect today, or select No payment today.");
      return;
    }
    if (orthoCollectionTarget && orthoCollectionShortfallCents > 0 && !orthoCollectionState.underpayment_confirmed) {
      setOrthoUnderpaymentDialogOpen(true);
      return;
    }
    if (missingNotTodayReason) {
      toast.error("Choose why each treatment was marked Not today.");
      return;
    }
    if (noPaymentVisit) {
      setStep("follow_up");
      return;
    }
    setStep("payment");
    if (ongoingTreatmentEnabled) {
      void refreshCheckoutSettlementPreview(paymentState.amount_tendered_cents);
    }
  }

  function handleFinishAndBillBundle() {
    if (!planId || !planIsBundle) return;

    // This is staged intent only. The database will reject closure unless all
    // procedures are already completed, then posts payment and closes the MV
    // together. Until commit, no clinical or financial truth changes.
    setFinishBundleOnComplete(true);
    setPaymentState((current) => paymentStateAfterBundleFinish(current));
    toast.success("Treatment will close at checkout. Record only what the patient actually paid today.");
  }

  function handleKeepBundleOpen() {
    setFinishBundleOnComplete(false);
  }

  function resolveOrthoInstallation(row) {
    const caseId = row?.case?.id;
    if (caseId) {
      setOrthoInstallationResolvedCaseIds((prev) => {
        const next = new Set(prev);
        next.add(caseId);
        return next;
      });
    }
  }

  async function handleSubmitCheckout() {
    if (isCheckoutTutorialDemo) {
      toast.info("Tutorial mode stops before writing checkout to the database.");
      return;
    }
    if (isCheckoutCompleted) {
      const message = "Checkout is already completed for this appointment.";
      setSubmitError(message);
      toast.error(message);
      return;
    }
    if (draftConflict) {
      const message = "Reload the latest saved procedures before completing checkout.";
      setSubmitError(message);
      toast.error(message);
      return;
    }
    // Soft reminder: the patient still has untouched quotes. Guide, don't
    // block — surface once, and proceed if staff chooses to finish anyway.
    if (openQuotesCount > 0 && !quotesReminderAckRef.current) {
      setQuotesReminderOpen(true);
      return;
    }
    const priorArBuild = buildCheckoutPriorArCollections({
      rows: priorArRows,
      selected: priorArSelections,
      amountTenderedCents: paymentState.amount_tendered_cents,
      method: paymentState.method,
      referenceNumber: paymentState.reference_number,
      proofUrl: paymentState.proof_url,
      appointmentId: apptId,
    });
    if (priorArBuild.error) {
      const message = priorArBuild.error;
      setSubmitError(message);
      toast.error(message);
      return;
    }
    if (ongoingTreatmentEnabled && settlementPreviewError) {
      const message = `Review the payment summary before checkout: ${settlementPreviewError}`;
      setSubmitError(message);
      toast.error(message);
      return;
    }
    const priorArCollectionTotalCents = Number(priorArBuild.total_centavos || 0);
    const hmoValidationError = validateHmoCoverageForCheckout({
      paymentState,
      billBaseCents: currentVisitDueCents + hmoCoveredCents,
      patientCopayCents: Math.max(0, currentVisitDueCents),
    });
    if (hmoValidationError) {
      setSubmitError(hmoValidationError);
      toast.error(hmoValidationError);
      return;
    }
    if (planBillingState?.denture_item_billing
        && primaryPlanAllocationTargetCents > 0
        && !primaryDentureAllocation.valid) {
      const message = primaryDentureAllocation.error || "Fix the denture payment split before checkout.";
      setSubmitError(message);
      toast.error(message);
      return;
    }
    setSubmitting(true); setSubmitError(null);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    let submissionDraftRevision = draftRevisionRef.current;
    let submissionItems = items;
    try {
      const draftForSubmission = buildCheckoutDraft();
      const saved = await persistCheckoutDraft(draftForSubmission);
      submissionDraftRevision = saved.revision;
      mergeServerDraftIntoLocal(saved.draftData);
      setSaveError(null);
      setSavedAt(new Date(saved.updatedAt || Date.now()));

      const canonicalItems = Array.isArray(saved.draftData?.items)
        ? saved.draftData.items
        : draftForSubmission.items;
      const submittedIdentities = new Set(
        draftForSubmission.items.map(checkoutDraftItemIdentity).filter(Boolean),
      );
      const canonicalIdentities = new Set(
        canonicalItems.map(checkoutDraftItemIdentity).filter(Boolean),
      );
      if (
        canonicalItems.length !== draftForSubmission.items.length
        || canonicalIdentities.size !== submittedIdentities.size
        || Array.from(canonicalIdentities).some((identity) => !submittedIdentities.has(identity))
      ) {
        setSubmitting(false);
        const message = "The saved procedure list changed. Review the updated bill before checkout.";
        setSubmitError(message);
        toast.error(message);
        return;
      }
      submissionItems = canonicalItems;
    } catch (draftError) {
      setSubmitting(false);
      reportDraftSaveFailure(draftError);
      setSubmitError(
        isCheckoutDraftRevisionConflict(draftError)
          ? "Another screen changed this visit. Reload and review the saved procedures before checkout."
          : (draftError?.message || "Could not save the procedure list before checkout."),
      );
      return;
    }
    // Expand qty>1 items into N rows so each becomes its own charge.
    const expanded = [];
    submissionItems.forEach((it) => {
      const foldThis = foldActive
        && foldedItemKeySet.has(scheduledWorkKey(it) || it._localId || it.procedure_id);
      expanded.push(...buildCheckoutItemPayloadRows({
        item: it,
        workKey: scheduledWorkKey(it),
        seniorPwdEligible,
        seniorPwdPercent,
        patientDiscountType: hydration?.patient?.is_senior_citizen ? "senior" : "pwd",
        // Each procedure owns its optional note. The saved payment-level value
        // remains a fallback so older checkout drafts do not lose their note.
        priceOverrideReason: it.price_override_reason || paymentState.price_override_reason,
        planBound: isPlanBound(it),
        foldIntoPlan: foldThis,
      }));
    });

    // Senior/PWD is now per-line above, so the global reason is just
    // the receptionist's typed visit-level discount (if any).
    const globalReason = visitDiscountCents > 0
      ? (paymentState.visit_discount_reason.trim() || null)
      : null;

    const payments = [];
    if (paymentState.amount_tendered_cents > 0) {
      // Cash overpay: record only the kept portion as the payment.
      // For Tx Plans, "kept" means up to the remaining plan balance
      // plus walk-in work, not merely the suggested installment.
      const tendered = paymentState.amount_tendered_cents;
      let recordAmount = tendered;
      let overpayMode = null;
      if (tendered > paymentCapCents) {
        if (paymentState.method === "cash" && !paymentState.keep_overpay_as_credit) {
          // Give back change — record only the amount the clinic kept.
          recordAmount = paymentCapCents;
        } else {
          overpayMode = paymentState.method === "cash" && !paymentState.keep_overpay_as_credit
            ? "refund"
            : "credit";
        }
      }
      const visitRecordAmount = Math.max(0, recordAmount - priorArCollectionTotalCents);
      if (visitRecordAmount > 0) {
        payments.push({
          method:           paymentState.method,
          amount_centavos:  visitRecordAmount,
          reference_number: paymentState.method !== "cash" ? paymentState.reference_number.trim() : undefined,
          proof_url:        paymentState.method !== "cash" ? (paymentState.proof_url || undefined) : undefined,
        });
      }
      // Stash overpay mode at the top-level payload below if applicable.
      if (overpayMode) {
        payments.push({ _overpayMode: overpayMode });
      }
    }
    const overpayHandling = payments.find((p) => p._overpayMode)?._overpayMode ?? undefined;
    const cleanedPayments = payments.filter((p) => !p._overpayMode);

    // HMO covered amount rides as legacy hmo-method tender for old
    // payload compatibility. The backend strips it out of patient cash
    // and posts the receivable through hmo_coverage below.
    if (hmoCoveredCents > 0 && paymentState.hmo_payer_id) {
      cleanedPayments.push({
        method:           "hmo",
        amount_centavos:  hmoCoveredCents,
        reference_number: paymentState.hmo_loa_number?.trim() || "HMO-LOA",
      });
    }

    // Pack staged Step-3 dispositions into the atomic-commit payload.
    // Recall is captured as activity-log only at v1 (the recall queue
    // surface ships later). Book + Decline call the existing transition
    // RPCs inside the same transaction as payment + visit close.
    let followUpDispositions = [];

    // Chairside-staged dispositions (Today's Plan card → Not today /
    // Decline) ride first so a Step-3 book/decline on the SAME need
    // gets the right starting state — unschedule flips Scheduled→Open,
    // then a follow-up book can call schedule_treatment_need cleanly.
    // Skip any tn_id that ended up Done (in items) — done supersedes.
    const doneTnIds = new Set();
    const doneWorkKeys = new Set();
    for (const it of expanded) {
      if (it.tn_id) doneTnIds.add(it.tn_id);
      const key = scheduledWorkKey(it);
      if (key) doneWorkKeys.add(key);
    }
    const scheduledByKey = new Map();
    for (const s of hydration?.scheduled_items ?? []) {
      const key = scheduledWorkKey(s);
      if (key) scheduledByKey.set(key, s);
      if (s.tn_id) scheduledByKey.set(`tn:${s.tn_id}`, s);
    }
    const stagedNotToday = [];
    for (const [workKey, disp] of Object.entries(chairsideDispositions || {})) {
      if (!workKey || !disp || doneWorkKeys.has(workKey)) continue;
      const scheduledRow = scheduledByKey.get(workKey) || scheduledByKey.get(`tn:${workKey}`);
      const tnId = scheduledRow?.tn_id || (workKey.startsWith("tn:") ? workKey.slice(3) : workKey);
      if (tnId && doneTnIds.has(tnId)) continue;
      if (scheduledRow?.procedure_visit_id) {
        expanded.push({
          // procedure_visit_id is the authoritative outcome target
          // for plan sub-visits. Keep tn_id off declined/not-today
          // pseudo-lines so legacy treatment_need transition RPCs
          // don't mutate an old mirror row from a prior visit.
          tn_id: null,
          procedure_visit_id: scheduledRow.procedure_visit_id,
          treatment_plan_id: scheduledRow.treatment_plan_id ?? null,
          treatment_plan_item_id: scheduledRow.treatment_plan_item_id ?? null,
          work_key: scheduledWorkKey(scheduledRow),
          procedure_id: scheduledRow.procedure_id ?? null,
          custom_name: null,
          outcome: disp.kind === "declined" ? "declined" : "not_today",
          not_today_reason_code: disp.kind === "not_today" ? (disp.reason_code || null) : undefined,
          performed_price_centavos: 0,
          item_discount_centavos: 0,
          decline_reason: disp.kind === "declined"
            ? ((disp.reason || "").trim() || "Declined chairside")
            : undefined,
        });
        continue;
      }
      if (disp.kind === "not_today") {
        stagedNotToday.push({ need_id: tnId, reason_code: disp.reason_code || null });
      } else if (disp.kind === "declined") {
        const reason = (disp.reason || "").trim() || "Declined chairside";
        followUpDispositions.push({ kind: "decline", reason, need_ids: [tnId] });
      }
    }
    for (const staged of stagedNotToday) {
      if (!staged.need_id) continue;
      followUpDispositions.push({
        kind: "unschedule",
        need_ids: [staged.need_id],
        reason_code: staged.reason_code,
      });
    }

    for (const d of dispositions.values()) {
      const need_ids = checkoutNeedIdsForCommit(d.needIds);
      if (need_ids.length === 0 && !d.ortho_installation_booking) continue;
      if (d.kind === "book") {
        followUpDispositions.push({ kind: "book", appointment_id: d.appointment_id, need_ids });
      } else if (d.kind === "decline") {
        followUpDispositions.push({ kind: "decline", reason: d.reason, need_ids });
      } else if (d.kind === "recall") {
        followUpDispositions.push({ kind: "recall", due_at: d.due_at || null, need_ids });
      }
    }

    // Plan-visit and post-op Pencil bookings are committed atomically
    // by the DB. Until this payload reaches commit_checkout_with_followup,
    // the appointment is only a draft-owned calendar hold.
    followUpDispositions = buildAtomicBookingDispositions({
      baseDispositions: followUpDispositions,
      planVisitBookings,
      postOpDecisions,
    });

    const checkoutFollowupWaivers = buildCheckoutFollowupWaivers(
      followupWaivers,
      items,
      scheduledWorkKey,
    );

    const payload = {
      appointment_id:            apptId,
      draft_revision:            submissionDraftRevision,
      items:                     expanded,
      payments:                  cleanedPayments,
      no_treatment_reason_code:  (noPaymentVisit || denturePaymentOnlyCollection)
        ? noTreatmentReasonCode
        : undefined,
      global_discount_centavos:  totalGlobalDiscountCents > 0 ? totalGlobalDiscountCents : undefined,
      global_discount_reason:    globalReason || undefined,
      // When the "Other discount" is aimed at a plan, tell the engine so it
      // stamps the discount with that plan (balance drops) instead of blindly
      // reducing add-on charges. null/undefined => legacy add-on behaviour.
      global_discount_target_plan_id: (totalGlobalDiscountCents > 0 && paymentState.visit_discount_target_plan_id)
        ? paymentState.visit_discount_target_plan_id
        : undefined,
      global_discount_type:      totalGlobalDiscountCents > 0 ? "manual" : undefined,
      overpayment_handling:      overpayHandling,
      plan_payment_targets:      (() => {
        const targets = [];
        // Primary plan: the explicit Flexible collection target (or the
        // model's normal suggestion) PLUS folded work's scheduled share.
        // In a simple Flexible-only checkout, tender up to the remaining
        // balance also belongs to the plan rather than patient credit.
        if (planId && (primaryPlanAllocationTargetCents > 0 || foldShareCents > 0)) {
          targets.push({
            plan_id:         planId,
            amount_centavos: Math.max(0, Number(primaryPlanAllocationTargetCents || 0) + Number(foldShareCents || 0)),
          });
        }
        // Additional plans on this visit each get their own explicit target
        // so payment is split per plan (backend allocates each separately).
        for (const b of extraPlanBillings) {
          const amt = Math.max(0, Number(b?.suggested_next_centavos ?? 0));
          if (b?.plan_id && amt > 0) targets.push({ plan_id: b.plan_id, amount_centavos: amt });
        }
        return targets.length ? targets : undefined;
      })(),
      denture_item_payment_targets: (() => {
        const targets = [];
        if (planBillingState?.denture_item_billing && primaryPlanAllocationTargetCents > 0) {
          targets.push(...primaryDentureAllocation.targets);
        }
        for (const billing of extraPlanBillings) {
          const amount = Math.max(0, Number(billing?.suggested_next_centavos ?? 0));
          if (!billing?.denture_item_billing || amount <= 0) continue;
          const allocation = buildDenturePaymentAllocation({
            billingState: billing,
            amountCentavos: amount,
          });
          if (allocation.valid) targets.push(...allocation.targets);
        }
        return targets.length ? targets : undefined;
      })(),
      // Family-payer + HMO extras (handled by complete_checkout_extended)
      paid_by_patient_id:        paymentState.paid_by_patient_id || undefined,
      hmo_coverage:              (hmoCoveredCents > 0 && paymentState.hmo_payer_id) ? {
        payer_id:         paymentState.hmo_payer_id,
        loa_number:       paymentState.hmo_loa_number?.trim() || null,
        member_id:        paymentState.hmo_member_id?.trim() || null,
        covered_centavos: hmoCoveredCents,
        patient_hmo_profile_id: paymentState.patient_hmo_profile_id || null,
      } : undefined,
      senior_pwd_discount_override: (
        (hydration.patient.is_senior_citizen || hydration.patient.is_pwd)
        && paymentState.decline_senior_pwd
      ) ? {
        removed:     true,
        approved_by: paymentState.senior_pwd_discount_removed_by?.trim() || undefined,
        reason:      paymentState.senior_pwd_discount_removed_reason?.trim() || undefined,
        approved_at: paymentState.senior_pwd_discount_removed_at || new Date().toISOString(),
      } : undefined,
      procedure_allocation_overrides: paymentState.allocation_overrides_enabled
        ? (paymentState.procedure_allocation_overrides || []).map((ov) => ({
            item_index: Number(ov.item_index),
            amount_centavos: Number(ov.amount_centavos || 0),
          }))
        : undefined,
      prior_ar_collections: priorArBuild.collections.length
        ? priorArBuild.collections
        : undefined,
      manager_approval: paymentState.allocation_overrides_enabled
        ? {
            approved_by: paymentState.manager_approval_by?.trim() || undefined,
            reason: paymentState.manager_approval_reason?.trim() || undefined,
          }
        : undefined,
      ortho_collection: orthoCollectionTarget ? {
        ortho_case_id: orthoCollectionTarget.ortho_case_id,
        decision: orthoCollectionDecision,
        amount_centavos: orthoCollectionDueCents,
        underpayment_confirmed: !!orthoCollectionState.underpayment_confirmed,
        ar_reason: orthoCollectionShortfallCents > 0 ? "other" : undefined,
        notes: orthoCollectionDecision === "collect_remaining"
          ? "Front desk chose to collect the remaining Ortho contract balance during checkout."
          : "Front desk confirmed final-visit Ortho underpayment; remaining balance routes to AR follow-up.",
      } : undefined,
      ortho_next_visit_decision: orthoNextVisitDecision || undefined,
      ...buildBundleFinishFields({
        planId,
        planIsBundle: ongoingTreatmentEnabled && planIsBundle,
        finishSelected: bundleFinishSelected,
        hasOpenDentureItems: !!planBillingState?.has_open_denture_items,
        arReason: "checkout_shortfall",
      }),
      follow_up_dispositions:    followUpDispositions.length ? followUpDispositions : undefined,
      followup_waivers:          checkoutFollowupWaivers.length ? checkoutFollowupWaivers : undefined,
    };

    // Keep the exact payload reviewed by Package 1C identical to the payload
    // that is eventually committed. Folded walk-in work is part of that
    // settlement contract, not a post-review frontend decision.
    const useFold = foldActive && foldedDoneItems.length > 0 && !!planId;
    const settlementPayload = useFold ? { ...payload, fold_plan_id: planId } : payload;

    let commitPlan;
    try {
      commitPlan = await planCheckoutSubmission({
        rpc: supabase.rpc.bind(supabase),
        payload: settlementPayload,
        settlementReview,
      });
    } catch (routeError) {
      setSettlementReview(null);
      setSubmitting(false);
      setSubmitError(routeError?.message || "Could not verify this checkout. Nothing was posted.");
      return;
    }

    if (commitPlan.action === "review") {
      setSettlementReview(commitPlan.review);
      setSubmitting(false);
      setSubmitError(null);
      return;
    }

    // Package 1C applies the reviewed waiver payload inside the same database
    // transaction as checkout. Rollout-OFF clinics keep the established
    // pre-commit RPC path, and the legacy checkout payload remains unchanged.
    if (commitPlan.rpcName !== "commit_checkout_settlement" && checkoutFollowupWaivers.length > 0) {
      try {
        for (const waiver of checkoutFollowupWaivers) {
          const { error: wErr } = await supabase.rpc("set_procedure_visit_followup_waiver", {
            ...legacyFollowupWaiverRpcInput(waiver),
          });
          if (wErr) throw wErr;
        }
      } catch (preErr) {
        setSubmitting(false);
        setSubmitError(
          `Could not record post-op waiver(s) before checkout: ${preErr?.message || "unknown error"}`,
        );
        return;
      }
    }

    // Package 1C owns follow-up waivers atomically. Rollout-OFF clinics use
    // the established pre-commit waiver RPC, so keep their legacy checkout
    // payload byte-compatible and do not send the new waiver field twice.
    const legacySettlementPayload = { ...settlementPayload };
    delete legacySettlementPayload.followup_waivers;
    const commitPayload = commitPlan.rpcName === "commit_checkout_settlement"
      ? settlementPayload
      : legacySettlementPayload;
    const { data, error: e } = await supabase.rpc(commitPlan.rpcName, {
      p_input: commitPayload,
    });
    if (e) {
      setSubmitting(false);
      setSubmitError(e.message ?? "Could not complete checkout.");
      return;
    }

    // The commit response proves the write succeeded, but it does not include
    // the full plan read model. Re-read that model before rendering Done so
    // the success screen never displays the pre-payment paid/balance totals.
    if (planId) {
      const { data: refreshedPlanBilling, error: refreshedPlanBillingError } = await supabase.rpc(
        "get_plan_billing_state",
        { p_plan_id: planId },
      );
      setPlanBillingState(refreshedPlanBillingError ? null : refreshedPlanBilling);
    }

    const postOpBookEntries = Object.values(postOpDecisions || {}).filter((d) => d?.kind === "book");
    if (postOpBookEntries.length > 0) {
      toast.success(postOpBookEntries.length === 1
        ? "Post-op booking committed with checkout."
        : `${postOpBookEntries.length} post-op bookings committed with checkout.`);
    }

    setSubmitting(false);
    setSettlementReview(null);
    setDoneResult(data);
    setStep("done");
    void sendCheckoutReceiptEmailNotification({
      result: data,
      hydration,
      toast,
    });

    // The database already closed any clinically complete plan and created
    // its AR route inside the same settlement transaction. The UI reports
    // that result; it never performs a second financial write.
    if (planId) {
      const closure = (Array.isArray(data?.plan_closures) ? data.plan_closures : [])
        .find((entry) => entry?.plan_id === planId);
      if (planIsBundle && bundleFinishSelected) {
        toast.success(
          closure?.status === "AR" || data?.bundle_finish?.status === "AR"
            ? "Treatment finished. Remaining balance added to AR."
            : "Treatment finished and closed.",
        );
      } else if (closure?.status === "AR") {
        toast.success("Treatment completed. Remaining balance added to AR.");
      } else if (closure?.status === "Completed" || closure?.status === "Closed") {
        toast.success("Treatment completed and closed.");
      }
      setPlanBillingNonce((n) => n + 1);
    }
  }

  // ----- Exit gate handlers ----------------------------------------------
  // Destination defaults to /calendar (the Back-to-Calendar button) but a
  // sidebar nav request may have set pendingNav to something else (e.g.
  // /patients). Save/Discard route there.
  function handleBackToCalendar() {
    setPendingNav(null); // explicit /calendar — clear any prior pending nav
    if (isDirty) {
      setExitDialogOpen(true);
    } else {
      navigate("/calendar");
    }
  }
  function handleStay() {
    setExitDialogOpen(false);
    setPendingNav(null);
  }
  function handleSaveAndLeave() {
    const dest = pendingNav || "/calendar";
    setExitDialogOpen(false);
    setPendingNav(null);
    navigate(dest);
  }
  async function handleDiscardAndLeave() {
    const dest = pendingNav || "/calendar";
    setDiscarding(true);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    try {
      // The database first verifies this screen's revision, persists the
      // current staged hold IDs, and then releases checkout-only work in one
      // transaction. A stale screen cannot cancel another screen's work.
      const discarded = await discardCheckoutDraftWithRevision({
        rpc: supabase.rpc.bind(supabase),
        appointmentId: apptId,
        draft: buildCheckoutDraft(),
        expectedRevision: draftRevisionRef.current,
      });
      draftRevisionRef.current = discarded.revision;
      setDraftConflict(false);
      setSaveError(null);
    } catch (discardError) {
      setDiscarding(false);
      // eslint-disable-next-line no-console
      console.warn("[checkout] discard_checkout_draft_v2 failed:", discardError);
      reportDraftSaveFailure(discardError);
      return;
    }
    setDiscarding(false);
    setExitDialogOpen(false);
    setPendingNav(null);
    navigate(dest);
  }

  // ----- Render -----------------------------------------------------------

  return (
    <div className="space-y-4 max-w-5xl mx-auto" data-testid="checkout">
      {/* Top bar */}
      <div className="flex items-center gap-3">
        {step !== "done" ? (
          <button type="button" onClick={handleBackToCalendar} data-testid="checkout-back"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" />Back to Calendar
          </button>
        ) : null}
        <span className="ml-auto text-xs">
          {draftConflict ? (
            <button
              type="button"
              onClick={() => load()}
              className="inline-flex items-center gap-1 font-semibold text-rose-700 underline underline-offset-2"
              data-testid="checkout-draft-conflict-reload"
            >
              <AlertTriangle className="w-3 h-3" />Reload latest saved procedures
            </button>
          ) : saveError ? (
            <span className="inline-flex items-center gap-1 text-rose-700 font-semibold" data-testid="checkout-save-error">
              <AlertTriangle className="w-3 h-3" />Save failed — keep this tab open
            </span>
          ) : savingDraft ? (
            <span className="inline-flex items-center gap-1 text-slate-400"><Loader2 className="w-3 h-3 animate-spin" />Saving…</span>
          ) : savedAt && step === "today" ? (
            <span className="text-slate-400">Draft saved</span>
          ) : null}
        </span>
      </div>

      {/* Step indicator */}
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3 flex items-center gap-2">
        <Step label="Today"     active={step === "today"}     muted={step !== "today"}     />
        {!noPaymentStepFlow ? (
          <>
            <StepDivider />
            <Step label="Payment" active={step === "payment"} muted={step !== "payment"} />
          </>
        ) : null}
        <StepDivider />
        <Step label="Follow-up" active={step === "follow_up"} muted={step !== "follow_up"} />
        <StepDivider />
        <Step label="Done"      active={step === "done"}      muted={step !== "done"}      />
      </div>

      {/* Patient + appointment header — always visible */}
      <PatientHeader hydration={hydration}>
        {hydration.appointment?.ortho_case_id ? (
          <OrthoCaseGlance
            row={appointmentOrthoCase}
            patientId={hydration.patient?.id}
            loading={appointmentOrthoCaseLoading}
            error={appointmentOrthoCaseError}
          />
        ) : null}
      </PatientHeader>

      {isCheckoutCompleted ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-900" data-testid="checkout-already-completed-banner">
          <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
          Checkout already completed
          {checkoutCompletion.finalized_at ? (
            <> on {new Date(checkoutCompletion.finalized_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</>
          ) : null}
          . This visit is read-only now.
        </div>
      ) : null}

      {/* Status guard banner — only on Today */}
      {step === "today" && !isCheckedIn && !isCheckoutCompleted ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 inline mr-1.5" />
          This appointment is <strong>{status}</strong>. Check the patient in from the Calendar before starting checkout. Items below are read-only.
        </div>
      ) : null}

      {/* Senior/PWD banner — Today only. Payment shows this inside the bill card. */}
      {step === "today" && (hydration.patient.is_senior_citizen || hydration.patient.is_pwd) ? (
        <div className="bg-teal-50 border border-teal-100 rounded-xl px-4 py-3 text-sm text-teal-900">
          <strong>{hydration.patient.is_senior_citizen ? "Senior Citizen" : "PWD"}</strong> —
          a {seniorPwdPctLabel} mandatory discount will be auto-applied at checkout.
          {hydration.patient.is_senior_citizen
            ? <> SC ID: <span className="font-mono">{hydration.patient.senior_citizen_id || "(not on file)"}</span>.</>
            : <> PWD ID: <span className="font-mono">{hydration.patient.pwd_id || "(not on file)"}</span>.</>}
        </div>
      ) : null}

      {/* === STEP: TODAY === */}
      {step === "today" ? (
        <>
          {/* What the visit was booked for — the front desk / doctor note, so
              the receptionist has the heads-up right where they record the
              work. Read-only; the bill still comes from what's recorded below. */}
          {(hydration.appointment?.notes || "").trim() ? (
            <div
              className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2"
              data-testid="checkout-booking-note"
            >
              <ClipboardList className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
              <p className="min-w-0 whitespace-pre-wrap text-[13px] text-amber-900">
                <span className="font-semibold">Booked for: </span>{hydration.appointment.notes}
              </p>
            </div>
          ) : null}
          {!isCheckoutTodayTutorialDemo ? (
            <PendingOrthoBillingGate
              rows={pendingOrthoBillingRows}
              patient={hydration.patient}
              clinic={hydration.clinic}
              loading={pendingOrthoBillingLoading}
              onSaved={() => {
                load();
              }}
            />
          ) : null}
          {/* Plan + billing render as ONE connected card: a shared white
              surface + teal ring, split by a divider so they read as two
              regions of the same block, not two floating cards. Desktop /
              iPad-landscape (lg+): plan list (left) and billing (right) side
              by side. Narrower: they stack, the divider turns horizontal.
              With no billing to show, the plan list fills the whole card. */}
          {/* ════════════════════════════════════════════════════════════
              Today's checkout reads as three plain-language buckets, in the
              order the receptionist works them, so the reason for every line
              is obvious:
                1 · Scheduled       — booked on the calendar, not on a plan
                2 · Added chairside — walk-in work the doctor did today
                3 · Plan / Ongoing  — TP / MV / Ortho + their billing
              Presentation only — every handler, price lock, and billing
              target is unchanged from the previous single-card layout.
              ════════════════════════════════════════════════════════════ */}

          {/* ── Bucket 1 · Scheduled ──────────────────────────────────────
              Booked-on-the-calendar work not tied to any plan. Self-hides
              (renders null) when there's nothing booked outside a plan. */}
          <ScheduledNeedsPanel
            only="nonplan"
            title="Scheduled"
            subtitle="Booked on the calendar"
            scheduled={hydration?.scheduled_items || []}
            items={items}
            setItems={setItems}
            planIsBundle={planIsBundle}
            chairsideDispositions={chairsideDispositions}
            setChairsideDispositions={setChairsideDispositions}
            chartReconciled={chartReconciled}
            currency={hydration.currency}
            readOnly={!isCheckedIn}
            postOpByProcedure={postOpByProcedure}
            followupWaivers={followupWaivers}
            setFollowupWaivers={setFollowupWaivers}
            appointmentId={apptId}
            onNoAdjustmentSaved={() => load()}
          />

          {/* ── Bucket 2 · Added chairside ────────────────────────────────
              Walk-in procedures / one-off charges added at the chair today.
              ItemsTable filters out scheduled + plan rows so it lists only
              these; AddRowControls adds more. */}
          {nothingCharted ? (
            /* Blank visit — the doctor charted nothing. Front desk encodes the
               whole visit from paper/verbal here. Reuses the exact same add
               mechanism; production credit goes to the booked dentist. */
            <AddRowControls
              emphasis
              heading="Nothing was charted for this visit"
              subheading="Add what the doctor did — search your price list, or use Custom procedure for anything not on it."
              customLabel="Custom procedure"
              patientCurrency={hydration.currency}
              demoProcedures={isCheckoutAddProceduresTutorialDemo ? CHECKOUT_ADD_PROCEDURES_TUTORIAL_CATALOG : null}
              showOrthoChargePrompt={showOrthoChargePrompt}
              onAdd={(it) => setItems((prev) => [...prev, it])}
            />
          ) : (
            <ItemsTable
              items={items}
              setItems={setItems}
              readOnly={!isCheckedIn}
              currency={hydration.currency}
              postOpByProcedure={postOpByProcedure}
              // Add buttons lead the card (larger, at the top) so front desk
              // sees the action first, then the table of what they've added.
              topActions={isCheckedIn ? (
                <AddRowControls
                  bare
                  large
                  split
                  colored
                  patientCurrency={hydration.currency}
                  demoProcedures={isCheckoutAddProceduresTutorialDemo ? CHECKOUT_ADD_PROCEDURES_TUTORIAL_CATALOG : null}
                  showOrthoChargePrompt={showOrthoChargePrompt}
                  onAdd={(it) => setItems((prev) => [...prev, it])}
                />
              ) : null}
              // Plan-bound items are price-locked at chairside per Cameron's
              // Tx Plan Amendments spec — all bill changes route through
              // dos_v2.add_plan_amendment (the "Add amendment" button on
              // the Billing card in the Plan / Ongoing bucket). This passes a
              // predicate the table uses to disable price/qty/discount on plan rows.
              isPlanBound={(it) => isPlanBoundEarly(it)}
              // Hide rows already shown in the Scheduled / Plan buckets so no
              // procedure appears twice; the table lists walk-in add-ons only.
              isScheduledItem={(it) => (hydration?.scheduled_items || []).some((s) => sameScheduledWork(it, s))}
              hasPlan={planId != null}
              // The "add these to the plan?" toggle renders as the table's
              // footer so it clearly acts on the added-at-the-chair rows above
              // and routes them into the treatment-plan billing.
              footer={isCheckedIn && foldCandidates.length > 0 ? (
                <FoldIntoPlanToggle
                  embedded
                  value={foldIntoPlan}
                  onChange={setFoldIntoPlan}
                  collectTodayNote={foldActive && foldShareCents > 0
                    ? `${fmtPeso(foldShareCents)} (${Math.round((foldShareCents / Math.max(1, foldBundleCents)) * 100)}%) of added chairside procedures will be collected today.`
                    : null}
                />
              ) : null}
            />
          )}

          {/* ── Bucket 3 · Plan / Ongoing ─────────────────────────────────
              Everything tied to a plan: plan-bound scheduled work up top,
              then its billing (TP / MV) and any extra plans, as one
              connected card so the work and its money read as one region. */}
          {(hasPlanScheduled || showPlanBilling || planIsBundle) ? (
            <div className="bg-white rounded-2xl shadow-sm ring-1 ring-teal-100 overflow-hidden">
              <ScheduledNeedsPanel
                only="plan"
                embedded
                title={planIsBundle ? "Ongoing treatment" : "Plan work today"}
                subtitle="Part of the patient's plan"
                scheduled={hydration?.scheduled_items || []}
                items={items}
                setItems={setItems}
                planIsBundle={planIsBundle}
                chairsideDispositions={chairsideDispositions}
                setChairsideDispositions={setChairsideDispositions}
                chartReconciled={chartReconciled}
                currency={hydration.currency}
                readOnly={!isCheckedIn}
                postOpByProcedure={postOpByProcedure}
                followupWaivers={followupWaivers}
                setFollowupWaivers={setFollowupWaivers}
              />
              {(showPlanBilling || planIsBundle || extraPlanBillings.length > 0) ? (
                <div className={"p-4 space-y-3 min-w-0" + (hasPlanScheduled ? " border-t border-teal-100" : "")}>
                  {/* Compact plain-language plan card (replaces the dense billing
                      card on the Today tab; BillingStateCard still runs on the
                      Treatment tab). "Change" opens the same billing-model picker. */}
                  {planIsBundle && bundleMvCode ? (
                    <p
                      data-testid="checkout-bundle-mv-code"
                      className="text-[11px] text-slate-400 font-normal"
                    >
                      {bundleMvCode}
                    </p>
                  ) : null}
                  {showPlanBilling ? (
                    <PaymentPlanCard
                      embedded
                      noPad
                      title={planIsBundle ? "Ongoing treatment" : "Treatment plan billing"}
                      coverageNote={hasNonPlanScheduled ? 'Covers the items marked "On plan" only' : null}
                      state={planBillingState ? {
                        payment_preset:     planBillingState.preset,
                        // Full plan value (total_centavos only tracks the charged
                        // portion, which is 0 until Visit 1 posts).
                        total_centavos:     Number(planBillingState.final_total_centavos
                                               ?? planBillingState.plan_total_items_centavos
                                               ?? planBillingState.total_centavos ?? 0),
                        paid_centavos:      Number(planBillingState.paid_centavos ?? 0),
                        remaining_centavos: Math.max(0,
                                               Number(planBillingState.final_total_centavos
                                                 ?? planBillingState.plan_total_items_centavos
                                                 ?? planBillingState.total_centavos ?? 0)
                                               - Number(planBillingState.paid_centavos ?? 0)),
                        locked:             planBillingState.charge_locked,
                      } : null}
                      onChangePlan={() => setBillingPickerOpen(true)}
                      cardTutorialId="checkout-tx-plan-billing-card"
                      changeTutorialId="checkout-tx-plan-set-billing-model"
                    />
                  ) : null}
                  {planIsBundle && planBillingState?.denture_item_billing ? (
                    <DentureCheckoutCompletionCard
                      items={dentureBillingItems(planBillingState)}
                      currency={hydration.currency}
                      remainingCentavos={planRemainingCents}
                      readOnly={!isCheckedIn}
                      finishSelected={bundleFinishSelected}
                      onFinish={handleFinishAndBillBundle}
                      onKeepOpen={handleKeepBundleOpen}
                    />
                  ) : null}
                  {flexibleCollectionRequired ? (
                    <FlexibleCollectionDecisionCard
                      remainingCentavos={planRemainingCents}
                      decision={activeFlexibleCollection.decision}
                      amountCentavos={activeFlexibleCollection.amount_centavos}
                      onAmountChange={setFlexibleCollectionAmount}
                      onNoCollection={setFlexibleNoCollection}
                      currency={hydration.currency}
                      dentureItemBilling={!!planBillingState?.denture_item_billing}
                    />
                  ) : null}
                  {denturePaymentOnlyCollection ? (
                    <div
                      className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5"
                      data-testid="checkout-denture-payment-only"
                    >
                      <span className="block text-sm font-semibold text-sky-950">
                        Payment only today
                      </span>
                      <span className="mt-0.5 block text-[12px] text-sky-800">
                        This only records a payment. It does not finish a denture.
                      </span>
                    </div>
                  ) : null}
                  {/* On-the-go bundle: "Finish treatment" control. Ticking it means
                      this is the final visit — after payment posts, the whole bundle
                      completes and closes. Pre-ticked when the doctor already flagged
                      it chairside ("tapos na, bill it"). */}
                  {planIsBundle && planBillingState?.has_open_denture_items ? null : planIsBundle ? (
                    <BundleFinishControl
                      finishSelected={bundleFinishSelected}
                      doctorLocked={bundleFinishRequested}
                      onChange={setFinishBundleOnComplete}
                    />
                  ) : null}
                  {/* Additional plans on this visit — shown as their own lines so
                      nothing is hidden. Each is billed via its own payment target. */}
                  {extraPlanBillings.length > 0 ? (
                    <div className="space-y-1" data-testid="checkout-extra-plans">
                      {extraPlanBillings.map((b) => {
                        const due  = Math.max(0, Number(b?.suggested_next_centavos ?? 0));
                        const left = Math.max(0, Number(b?.remaining_centavos ?? 0));
                        return (
                          <div
                            key={b.plan_id}
                            className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs"
                          >
                            <span className="font-medium text-slate-700">
                              Also on this visit{b?.plan_code ? ` · ${b.plan_code}` : ""}
                            </span>
                            <span className="text-slate-600">
                              ₱{Math.round(due / 100).toLocaleString()} due
                              {" · "}₱{Math.round(left / 100).toLocaleString()} left
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Ortho collection sits with the Plan / Ongoing bucket (an ortho
              case is its own plan line). Self-hides when there's no ortho
              target for this visit. */}
          <OrthoCollectionOptionsCard
            item={orthoCollectionTarget}
            state={orthoCollectionState}
            currency={hydration.currency}
            onChange={(patch) => {
              if (!orthoCollectionKey) return;
              setOrthoCollectionChoice((prev) => ({
                key: orthoCollectionKey,
                decision: "collect_remaining",
                ...(prev?.key === orthoCollectionKey ? prev : {}),
                ...patch,
                underpayment_confirmed: false,
              }));
              userEditedAmountRef.current = false;
            }}
          />
          {planId ? (
            <SetBillingModelDialog
              open={billingPickerOpen}
              onOpenChange={setBillingPickerOpen}
              planId={planId}
              planTotalCentavos={planBillingState?.charge_locked
                ? planBillingState?.total_centavos
                : (planBillingState?.plan_total_items_centavos ?? planBillingState?.total_centavos)}
              planVisitCount={planBillingState?.total_visits}
              currentPreset={planBillingState?.preset}
              currentDpCentavos={planBillingState?.custom_schedule?.dp_centavos}
              currentMinPerVisitCentavos={planBillingState?.custom_schedule?.min_per_visit_centavos}
              currentNumPayments={planBillingState?.custom_schedule?.num_payments}
              planIsBundle={planIsBundle}
              // A bundle offers Pay in full, 50/50, and Flexible. "Even split"
              // stays out — it divides by a fixed visit count an open-ended
              // bundle doesn't have. 50/50 works because "Finish this treatment"
              // now defines the end: half up front, the rest at Finish.
              enabledPresets={planIsBundle ? ["full", "50_50", "custom"] : billingPlansEnabled}
              defaultPreset={billingPlanDefault}
              demoMode={isCheckoutPlanTutorialDemo}
              onSaved={(result) => {
                if (isCheckoutPlanTutorialDemo) {
                  setPlanBillingState((prev) => prev ? {
                    ...prev,
                    preset: result?.preset ?? prev.preset,
                    custom_schedule: result?.custom_schedule ?? prev.custom_schedule,
                  } : prev);
                  return;
                }
                setPlanBillingNonce((n) => n + 1);
              }}
            />
          ) : null}
          {noPaymentVisit ? (
            <div
              className="flex items-start gap-3 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3"
              data-testid="checkout-no-payment-visit-summary"
            >
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
              <div>
                <p className="text-sm font-semibold text-sky-950">No treatment completed today</p>
                <p className="mt-0.5 text-xs text-sky-800">
                  No payment is due. Continue to follow-up, then close the visit without issuing a receipt.
                </p>
              </div>
            </div>
          ) : null}
          <CheckoutMaterials
            appointmentId={apptId}
            items={items}
            readOnly={!isCheckedIn || isCheckoutCompleted}
            tutorialDemo={isCheckoutTutorialDemo}
          />
          <div className="flex items-center gap-3 sticky bottom-0 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm px-5 py-3 border border-slate-100">
            <SubtotalSummary
              items={items}
              currency={hydration.currency}
              isPlanBound={(it) => isPlanBoundEarly(it)}
              hasPlan={planId != null}
            />
            {orthoBillingBlocked ? (
              <div
                className="ml-auto max-w-sm rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900"
                data-testid="checkout-ortho-billing-required"
              >
                Complete Ortho billing setup before payment.
              </div>
            ) : unresolvedPendingClinicalSaveBlocked ? (
              <div
                className="ml-auto max-w-sm rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900"
                data-testid="checkout-ortho-clinical-save-required"
              >
                Mark the Ortho visit Done, Not today, or Decline before payment.
              </div>
            ) : billingModelBlocked && !noPaymentVisit ? (
              <div
                className="ml-auto max-w-sm rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900"
                data-testid="checkout-billing-model-required"
              >
                {billingModelBlockCopy}
              </div>
            ) : bundleStateBlocked && !noPaymentVisit ? (
              <div
                className="ml-auto max-w-sm rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900"
                data-testid="checkout-bundle-state-required"
              >
                {bundleStateBlockCopy}
              </div>
            ) : !flexibleCollectionDecided && !noPaymentVisit ? (
              <div
                className="ml-auto max-w-sm rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900"
                data-testid="checkout-flexible-collection-required"
              >
                Choose how much to collect today, or select No payment today.
              </div>
            ) : unresolvedBookedBlocked ? (
              <div
                className="ml-auto max-w-sm rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900"
                data-testid="checkout-unresolved-booked-required"
              >
                {unresolvedBookedReminders.length} booked {unresolvedBookedReminders.length === 1 ? "procedure isn't" : "procedures aren't"} recorded yet — mark Done, Not today, or Decline (or add the actual work) before payment.
              </div>
            ) : null}
            <button type="button"
              // Normally you need at least one "done" line to bill. Exception: a
              // finished on-the-go bundle (Finish treatment tapped) collects its
              // remaining balance with no new work today, so finishBundleOnComplete
              // alone is enough to proceed.
              disabled={!isCheckedIn || (doneItems.length === 0 && !bundleFinishSelected && !noPaymentVisit && !denturePaymentOnlyCollection) || missingNotTodayReason || (billingModelBlocked && !noPaymentVisit) || (bundleStateBlocked && !noPaymentVisit) || (!flexibleCollectionDecided && !noPaymentVisit) || orthoBillingBlocked || unresolvedPendingClinicalSaveBlocked || unresolvedBookedBlocked}
              onClick={continueToPayment}
              data-testid="checkout-next"
              data-tutorial-id="checkout-next"
              className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
              {noPaymentVisit ? "Continue to follow-up" : "Continue to payment"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </>
      ) : null}

      {/* === STEP: PAYMENT === */}
      {step === "payment" ? (
        <PaymentStep
          hydration={hydration}
          items={receiptItems}
          subtotalCents={subtotalCents}
          itemDiscountTotalCents={itemDiscountTotalCents}
          seniorPwdDiscountCents={seniorPwdDiscountCents}
          seniorPwdPct={seniorPwdPct}
          seniorPwdPctLabel={seniorPwdPctLabel}
          hmoCoveredCents={hmoCoveredCents}
          paymentState={paymentState}
          setPaymentState={setPaymentState}
          netCents={netCents}
          submitting={submitting}
          submitError={submitError}
          onClearSubmitError={() => setSubmitError(null)}
          activeQr={activeQr}
          onOpenQrModal={() => setQrModalOpen(true)}
          onBack={() => setStep("today")}
          // Plan-aware: pass planId + nonce so PaymentStep renders the
          // BillingStateCard at the top (same component as Step 1) and
          // re-fetches when set_plan_billing_model fires from the picker.
          planId={planId}
          planBillingState={planBillingState}
          planIsBundle={planIsBundle}
          planCode={bundleMvCode}
          extraPlanBillings={extraPlanBillings}
          // Called by PaymentStep when the receptionist directly edits
          // the amount-tendered input or clicks a manual quick-fill
          // (50%, No payment). Marks the ref so auto-fill never
          // clobbers their intent — even if they happen to type a
          // value that matches an old auto-fill snapshot.
          onUserEditedAmount={() => { userEditedAmountRef.current = true; }}
          // Plan vs walk-in split for the Bill section. PaymentStep's
          // Bill renders TWO buckets when planId is set: plan portion
          // (= billing model's suggested amount; line items are
          // informational, NOT summed into Total due) + walk-in portion
          // (per-line bill: subtotal, item disc, senior/PWD, HMO, net).
          // Total due = plan + walk-in. For walk-in-only visits the
          // plan bucket is zero and the math collapses to the legacy
          // per-line net.
          planDoneItems={planDoneItems}
          walkInDoneItems={walkInDoneItems}
          planSuggestedCents={planSuggestedCents}
          planAllocationTargetCents={primaryPlanAllocationTargetCents}
          simpleFlexiblePlanPayment={simpleFlexiblePlanPayment}
          foldShareCents={foldActive ? foldShareCents : 0}
          walkInSubtotalCents={walkInSubtotalCents}
          walkInItemDiscountCents={walkInItemDiscountCents}
          walkInSeniorPwdCents={walkInSeniorPwdCents}
          walkInNetCents={walkInNetCents}
          orthoCollectionItem={orthoCollectionTarget}
          orthoCollectionDecision={orthoCollectionDecision}
          orthoCollectionExtraCents={orthoCollectionExtraCents}
          orthoCollectionDueCents={orthoCollectionDueCents}
          currentVisitDueCents={currentVisitDueCents}
          totalDueCents={totalDueCents}
          selectedPriorArCents={selectedPriorArCents}
          priorArRows={priorArRows}
          priorArSelections={priorArSelections}
          priorArLoading={priorArLoading}
          priorArError={priorArError}
          onPriorArSelectionChange={(next) => {
            setPriorArSelections(next);
            void refreshCheckoutSettlementPreview(
              paymentState.amount_tendered_cents,
              next,
            );
          }}
          planRemainingCents={planRemainingCents}
          paymentCapCents={paymentCapCents}
          settlementPreview={settlementPreview}
          settlementPreviewLoading={settlementPreviewLoading}
          settlementPreviewError={settlementPreviewError}
          onSettlementPreviewRequest={refreshCheckoutSettlementPreview}
          // Step 2 stages payment in client state only — no DB write yet.
          // The actual commit fires from the Follow-up step's "Complete
          // checkout" button so nothing is saved until everything (payment
          // intent + future-need dispositions) is reviewed together.
          onSubmit={() => setStep("follow_up")}
          onPatientUpdated={(patch) => {
            setHydration((prev) => prev ? ({ ...prev, patient: { ...prev.patient, ...patch } }) : prev);
          }}
          hmoTutorialDemo={isHmoTutorialDemo}
        />
      ) : null}

      {/* Exit-gate dialog: blocks accidental navigation away from a dirty checkout. */}
      {exitDialogOpen ? (
        <ExitConfirmDialog
          items={items}
          chairsideDispositions={chairsideDispositions}
          dispositions={dispositions}
          paymentState={paymentState}
          pencilBookingCount={pencilBookingCount}
          discarding={discarding}
          destination={pendingNav}
          onStay={handleStay}
          onSaveAndLeave={handleSaveAndLeave}
          onDiscardAndLeave={handleDiscardAndLeave}
        />
      ) : null}

      {orthoUnderpaymentDialogOpen ? (
        <OrthoUnderpaymentConfirmDialog
          item={orthoCollectionTarget}
          amountDueCentavos={orthoCollectionDueCents}
          shortfallCentavos={orthoCollectionShortfallCents}
          currency={hydration.currency}
          onCancel={() => {
            if (orthoCollectionKey && orthoCollectionTarget) {
              setOrthoCollectionChoice({
                key: orthoCollectionKey,
                decision: "collect_remaining",
                amount_centavos: Number(orthoCollectionTarget.contract_remaining_centavos || 0),
                underpayment_confirmed: false,
              });
              userEditedAmountRef.current = false;
            }
            setOrthoUnderpaymentDialogOpen(false);
          }}
          onConfirm={() => {
            if (!orthoCollectionKey) return;
            setOrthoCollectionChoice((prev) => ({
              key: orthoCollectionKey,
              decision: "collect_visit_only",
              ...(prev?.key === orthoCollectionKey ? prev : {}),
              amount_centavos: orthoCollectionDueCents,
              underpayment_confirmed: true,
            }));
            setOrthoUnderpaymentDialogOpen(false);
            setStep("payment");
          }}
        />
      ) : null}

      {quotesReminderOpen ? (
        <OpenQuotesReminderDialog
          count={openQuotesCount}
          onGoBack={() => setQuotesReminderOpen(false)}
          onFinish={() => {
            quotesReminderAckRef.current = true;
            setQuotesReminderOpen(false);
            handleSubmitCheckout();
          }}
        />
      ) : null}

      {/* QR / payment-link modal (mounted on the Payment step) */}
      <QRPaymentModal
        open={qrModalOpen && step === "payment"}
        onClose={() => setQrModalOpen(false)}
        demoMode={isCheckoutTodayTutorialDemo}
        clinicName={hydration?.clinic?.name || ""}
        clinicSlug={hydration?.clinic?.slug || ""}
        amountCentavos={netCents}
        qrEntry={activeQr}
        appointmentId={apptId}
        recipientEmail={hydration?.patient?.email || ""}
        onProofVerified={({ proof_url, reference_number }) => {
          setPaymentState((prev) => ({
            ...prev,
            reference_number: reference_number || prev.reference_number,
            proof_url:        proof_url        || prev.proof_url,
            // Auto-fill amount tendered to the full net so staff can
            // submit without retyping. They can still edit if needed.
            amount_tendered_cents: prev.amount_tendered_cents || netCents,
          }));
          toast.success("Proof verified — Step 2 pre-filled.");
        }}
      />

      {/* === STEP: FOLLOW-UP === */}
      {step === "follow_up" ? (() => {
        // Gating: every future-need bundle must have a staged disposition,
        // and every next-plan-visit card must be resolved as either booked
        // or explicitly Not sure before "Complete checkout" enables. Empty
        // lists are ready immediately (nothing to handle).
        const orthoNextVisit = activeOrthoNextVisit(hydration?.ortho_next_visit);
        const orthoNextVisitKey = orthoNextVisitDecisionKey(orthoNextVisit);
        const orthoNextVisitResolution = resolvedOrthoNextVisitDecision(
          orthoNextVisit,
          orthoNextVisitDecision,
        );
        const handled = Array.from(dispositions.values()).filter((d) => (d?.needIds || []).length > 0).length;
        const remaining = Math.max(0, followupTotals.totalBundles - handled);
        const futureNeedsHandled = followupTotals.ready && remaining === 0;
        const unresolvedPlanVisits = Math.max(0, Number(planVisitResolution.unresolvedPlans) || 0);
        const planVisitsHandled = planVisitResolution.ready && unresolvedPlanVisits === 0;
        const orthoInstallationHandled = !pendingOrthoInstallationRow;
        const orthoNextVisitHandled = !orthoNextVisit
          || orthoNextVisit.status === "already_booked"
          || !!orthoNextVisitResolution;
        const unresolvedPostOps = Math.max(0, Number(postOpResolution.unresolved) || 0);
        const postOpHandled = postOpResolution.ready && unresolvedPostOps === 0;
        const allHandled = !isCheckoutCompleted && orthoInstallationHandled && orthoNextVisitHandled && futureNeedsHandled && planVisitsHandled && postOpHandled;
        const gateMessage = isCheckoutCompleted
          ? "Checkout is already completed for this appointment."
          : !orthoInstallationHandled
          ? "Book or defer the Ortho installation visit."
          : !orthoNextVisitHandled
          ? `Book the next Ortho ${orthoNextVisit?.next_visit_type === "debonding" ? "debonding visit" : "adjustment"} or choose Not sure.`
          : !planVisitsHandled
          ? `Choose Book next visit or Unsure for ${unresolvedPlanVisits} next plan visit${unresolvedPlanVisits === 1 ? "" : "s"}.`
          : !postOpHandled
          ? `Choose Book or Unsure for ${unresolvedPostOps} post-op follow-up${unresolvedPostOps === 1 ? "" : "s"}.`
          : (!futureNeedsHandled
              ? `Handle ${remaining} more future need${remaining === 1 ? "" : "s"}.`
              : null);
        return (
        <>
          {pendingOrthoInstallationRow ? (
            <OrthoInstallationStep
              row={pendingOrthoInstallationRow}
              appointment={hydration.appointment}
              patient={hydration.patient}
              currency={hydration.currency}
              sameSlotFutureNeedBundles={planVisitFutureNeedBundles}
              onSameSlotFutureNeedsBooked={stageFutureNeedsWithOrthoInstallation}
              onBack={() => setStep(noPaymentVisit ? "today" : "payment")}
              onDefer={() => resolveOrthoInstallation(pendingOrthoInstallationRow)}
              onBooked={() => {
                resolveOrthoInstallation(pendingOrthoInstallationRow);
              }}
            />
          ) : null}

          {orthoNextVisit ? (
            <>
              <OrthoNextVisitFollowUpStep
                request={orthoNextVisit}
                currency={hydration.currency}
                decision={orthoNextVisitResolution}
                onBook={() => setOrthoNextVisitBookOpen(true)}
                onNotSure={() => setOrthoNextVisitDecision({
                  key: orthoNextVisitKey,
                  kind: "unsure",
                  decided_at: new Date().toISOString(),
                })}
                onUndo={() => setOrthoNextVisitDecision(null)}
              />
              {orthoNextVisit.status === "needs_booking" ? <BookFutureNeedDialog
                open={orthoNextVisitBookOpen}
                onOpenChange={setOrthoNextVisitBookOpen}
                bundle={{
                  procedure_name: orthoNextVisit.procedure_name || "Ortho Adjustment",
                  estimatedMinutes: Number(orthoNextVisit.duration_minutes || 15),
                  needs: [],
                  isAll: false,
                }}
                patientId={hydration.patient.id}
                patientName={hydration.patient.full_name}
                defaultDoctorId={hydration.appointment.doctor_id}
                defaultChairId={hydration.appointment.chair_id}
                priorVisitDate={orthoNextVisit.prior_visit_date || hydration.appointment.scheduled_start || null}
                mustStartAfter={orthoNextVisit.prior_visit_date || hydration.appointment.scheduled_start || null}
                doctorRequestedDays={Number(orthoNextVisit.interval_days || 0)}
                appointmentStatus="Scheduled"
                headingOverride={orthoNextVisit.next_visit_type === "debonding" ? "Book Ortho debonding" : "Book next Ortho adjustment"}
                subtitleOverride={orthoNextVisitSubtitle(orthoNextVisit)}
                appointmentSummary={<OrthoNextVisitBookingSummary request={orthoNextVisit} />}
                appointmentInputExtra={{
                  procedure_id: orthoNextVisit.procedure_id || undefined,
                  ortho_case_id: orthoNextVisit.case_id,
                  ortho_visit_type: orthoNextVisit.next_visit_type || "adjustment",
                  notes: orthoNextVisitBookingNote(orthoNextVisit),
                }}
                initialNotes={orthoNextVisitBookingNote(orthoNextVisit)}
                onBooked={(appointmentId, label) => {
                  setOrthoNextVisitDecision({
                    key: orthoNextVisitKey,
                    kind: "booked",
                    appointment_id: appointmentId,
                    appointment_label: label,
                    decided_at: new Date().toISOString(),
                  });
                  setOrthoNextVisitBookOpen(false);
                  load();
                }}
              /> : null}
            </>
          ) : null}

          {/* Post-op follow-ups: surfaces the auto-suggestions that
              this checkout's Done items will trigger. The doctor's
              chairside chip already lets them wave off; this section
              shows the receptionist what's about to be created so
              they can plan the follow-up call / booking. The actual
              treatment_need rows are inserted by
              tg_suggest_post_op_followup at commit time and land in
              Recall Center / Future Needs from there. */}
          <PostOpSuggestionsSection
            items={items}
            scheduled={hydration?.scheduled_items || []}
            postOpByProcedure={postOpByProcedure}
            followupWaivers={followupWaivers}
            patientId={hydration?.patient?.id}
            patientName={hydration?.patient?.full_name}
            currency={hydration?.currency}
            defaultDoctorId={hydration?.appointment?.doctor_id}
            defaultChairId={hydration?.appointment?.chair_id}
            priorVisitDate={hydration?.appointment?.scheduled_start || null}
            decisions={postOpDecisions}
            setDecisions={setPostOpDecisions}
            onResolutionChange={setPostOpResolution}
            planVisitBookings={planVisitBookings}
            setPlanVisitBookings={setPlanVisitBookings}
            demoMode={isCheckoutTutorialDemo}
          />

          {/* Plan-bound visits get a card per plan with unbooked Accepted
              items. Checkout completion now requires an explicit front-desk
              choice: book the next plan visit or mark timing as unsure. */}
          <PlanVisitFollowUp
            planIds={planIds}
            finishingPlanId={bundleFinishSelected && planId ? planId : null}
            patientId={hydration.patient.id}
            patientName={hydration.patient.full_name}
            currency={hydration.currency}
            defaultDoctorId={hydration.appointment.doctor_id}
            defaultChairId={hydration.appointment.chair_id}
            priorVisitDate={hydration.appointment.scheduled_start || null}
            bookings={planVisitBookings}
            setBookings={setPlanVisitBookings}
            unsurePlanIds={planVisitUnsure}
            setUnsurePlanIds={setPlanVisitUnsure}
            onResolutionChange={setPlanVisitResolution}
            sameSlotFutureNeedBundles={planVisitFutureNeedBundles}
            sameSlotCounts={planVisitSameSlotCounts}
            onSameSlotFutureNeedsBooked={stageFutureNeedsWithPlanBooking}
            demoMode={isCheckoutPlanTutorialDemo}
            demoPlanData={isCheckoutOngoingTutorialDemo
              ? checkoutOngoingTutorialPlanData
              : isCheckoutTxPlanTutorialDemo
                ? CHECKOUT_TX_PLAN_TUTORIAL_PLAN_DATA
                : null}
          />

          {/* Other active plans on the patient's record that are NOT
              tied to today's appointment. Soft prompt only -- never
              blocks "Complete checkout." Highest-conversion moment:
              patient is at the desk paying. */}
          <OtherActivePlansFollowUp
            appointmentId={hydration.appointment.id}
            patient={hydration.patient}
            currency={hydration.currency}
            defaultDoctorId={hydration.appointment.doctor_id}
            defaultChairId={hydration.appointment.chair_id}
            priorVisitDate={hydration.appointment.scheduled_start || null}
            onOpenQuotesChange={setOpenQuotesCount}
            demoMode={isCheckoutTutorialDemo}
            demoPlans={isCheckoutTutorialDemo ? [] : null}
          />

          {(noPaymentVisit || denturePaymentOnlyCollection) && noTreatmentDeferredItems.length > 0 ? (
            <section
              className="rounded-2xl border border-sky-200 bg-sky-50 p-4"
              data-testid="checkout-no-payment-recall-summary"
            >
              <div className="flex items-start gap-3">
                <Inbox className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
                <div>
                  <h2 className="text-sm font-semibold text-sky-950">Human follow-up</h2>
                  <p className="mt-1 text-xs leading-5 text-sky-800">
                    {noTreatmentDeferredItems.length === 1 ? "This treatment" : "These treatments"} will return to the Recall call list when you close the visit.
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-sky-900">
                    {noTreatmentDeferredItems.map((item) => {
                      const key = scheduledWorkKey(item);
                      const disposition = chairsideDispositions?.[key]
                        || (item?.tn_id ? chairsideDispositions?.[`tn:${item.tn_id}`] : null);
                      return (
                        <li key={key} className="font-medium">
                          {item.display_label || item.procedure_name || "Treatment"}
                          {item.tooth_number ? ` · #${item.tooth_number}` : ""}
                          {` · ${notTodayReasonLabel(disposition?.reason_code)}`}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </section>
          ) : null}

          <FutureNeedsFollowUp
            patientId={hydration.patient.id}
            patientName={hydration.patient.full_name}
            currency={hydration.currency}
            navigate={navigate}
            demoMode={isCheckoutTutorialDemo}
            demoNeeds={isCheckoutTodayTutorialDemo ? CHECKOUT_TODAY_TUTORIAL_FUTURE_NEEDS : null}
            demoProcMap={isCheckoutTodayTutorialDemo ? CHECKOUT_TODAY_TUTORIAL_PROC_MAP : null}
            dispositions={dispositions}
            setDispositions={setDispositions}
            onTotalsChange={setFollowupTotals}
            defaultDoctorId={hydration.appointment.doctor_id}
            defaultChairId={hydration.appointment.chair_id}
            priorVisitDate={hydration.appointment.scheduled_start || null}
            planVisitBookings={planVisitBookings}
            onPlanVisitFutureNeedsChange={setPlanVisitFutureNeedBundles}
            orthoNextVisit={orthoNextVisit}
            orthoNextVisitDecision={orthoNextVisitResolution}
            onOrthoBundled={(appointment_id, appointment_label) => {
              setOrthoNextVisitDecision({
                key: orthoNextVisitKey,
                kind: "booked",
                appointment_id,
                appointment_label,
                bundled: true,
                decided_at: new Date().toISOString(),
              });
            }}
          />

          {submitError ? (
            <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{submitError}</div>
          ) : null}

          {settlementReview ? (
            <CheckoutSettlementReview
              preview={settlementReview.preview}
              currency={hydration.currency}
              formatAmount={fmtPeso}
            />
          ) : null}

          <div className="flex items-center gap-3 sticky bottom-0 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm px-5 py-3 border border-slate-100">
            <button type="button" onClick={() => setStep(noPaymentVisit ? "today" : "payment")} disabled={submitting}
              data-testid="followup-back"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-40">
              <ArrowLeft className="w-4 h-4" />{noPaymentVisit ? "Back to Today" : "Back to Payment"}
            </button>
            <span className="ml-auto text-sm text-slate-700">
              {!allHandled
                ? <span className="text-amber-700"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />{gateMessage}</span>
                : noPaymentVisit
                  ? <><strong>No payment due</strong> · Visit will close with no receipt</>
                  : <>Paying <strong>{fmtPeso(paymentState.amount_tendered_cents || 0, hydration.currency)}</strong> · Net <strong>{fmtPeso(netCents, hydration.currency)}</strong></>}
            </span>
            <button type="button" onClick={handleSubmitCheckout} disabled={submitting || !allHandled}
              data-testid="followup-complete-checkout"
              data-tutorial-id="checkout-complete-checkout"
              title={!allHandled ? `${gateMessage} Complete checkout is locked until this is resolved.` : undefined}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                allHandled && !submitting
                  ? "bg-emerald-600 hover:bg-emerald-700 ring-2 ring-emerald-400 ring-offset-1 animate-pulse"
                  : "bg-teal-600 hover:bg-teal-700"
              }`}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {noPaymentVisit ? "Close visit — no payment" : "Complete checkout"}
            </button>
          </div>
        </>
        );
      })() : null}

      {/* === STEP: DONE === */}
      {step === "done" && doneScreenResult ? (
        <DoneScreen
          result={doneScreenResult}
          hydration={hydration}
          navigate={navigate}
          items={doneScreenItems}
          paymentState={doneScreenPaymentState}
          breakdown={doneScreenBreakdown}
          planBillingState={planBillingState}
        />
      ) : null}
    </div>
  );
}

// ----- Sub-components --------------------------------------------------

export function CheckoutQueue({
  navigate,
  tutorialDemo = false,
  tutorialRows = CHECKOUT_TODAY_TUTORIAL_QUEUE_ROWS,
  tutorialVisitRoute = CHECKOUT_TODAY_TUTORIAL_VISIT_ROUTE,
}) {
  const { activeBranchId } = useBranch();
  const [loading, setLoading] = useState(!tutorialDemo);
  const [error, setError]     = useState(null);
  const [rows, setRows]       = useState(() => tutorialDemo ? tutorialRows : []);
  const [now, setNow]         = useState(Date.now());
  const [reviewCount, setReviewCount] = useState(tutorialDemo ? 2 : 0);

  const load = useCallback(async () => {
    if (tutorialDemo) {
      setRows(tutorialRows);
      setReviewCount(2);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true); setError(null);
    const { data, error: e } = await supabase.rpc("list_checked_in_appointments", {
      p_branch_id: activeBranchId || null,
    });
    setLoading(false);
    if (e) { setError(e.message ?? "Could not load."); return; }
    setRows(data ?? []);
  }, [activeBranchId, tutorialDemo, tutorialRows]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tutorialDemo) return;
    let cancelled = false;
    supabase
      .from("payment_links")
      .select("id", { count: "exact", head: true })
      .eq("status", "proof_submitted")
      .then(({ count }) => {
        if (!cancelled) setReviewCount(Number(count || 0));
      });
    return () => { cancelled = true; };
  }, [tutorialDemo]);

  // tick the relative-time display every 30s
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const checkedInLabel = `${rows.length} patient${rows.length === 1 ? "" : "s"}`;

  return (
    <div
      className="mx-auto max-w-5xl space-y-4"
      data-testid="checkout-queue"
      data-tutorial-id="checkout-queue"
    >
      <div className="bg-white rounded-2xl shadow-sm px-5 pt-4">
        <div className="flex flex-wrap items-end gap-6 border-b border-slate-100">
          <button
            type="button"
            className="whitespace-nowrap border-b-2 border-teal-600 px-1 pb-3 text-lg font-semibold leading-none tracking-normal text-slate-900"
            aria-current="page"
          >
            Checkout queue
          </button>
          <button
            type="button"
            onClick={() => navigate("/checkout?view=payment-review")}
            data-testid="checkout-payment-review-tab"
            data-tutorial-id="checkout-payment-review-tab"
            className="group inline-flex items-center gap-3 whitespace-nowrap px-1 pb-3 text-lg font-semibold leading-none tracking-normal text-slate-500 transition hover:text-slate-900"
          >
            <span className="tracking-normal">Payment Review</span>
            <span className="inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 px-2 text-xs font-semibold leading-none tracking-normal text-slate-600 group-hover:bg-slate-200">
              {reviewCount}
            </span>
          </button>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3 py-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">
              Checked-in patients
            </p>
            <p className="mt-1 max-w-2xl text-xs text-slate-500">
              {checkedInLabel} in queue. Patients appear by arrival time.
            </p>
          </div>
          <button
            onClick={() => navigate("/calendar")}
            data-testid="checkout-queue-open-calendar"
            data-tutorial-id="checkout-queue-open-calendar"
            className="inline-flex items-center gap-1.5 rounded bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
          >
            <CalendarIcon className="h-3.5 w-3.5" />Open Calendar
          </button>
        </div>
      </div>

      <div className="space-y-4">

        {error ? (
          <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>
        ) : null}

        {loading ? (
          <div className="rounded-2xl bg-white p-8 text-center text-sm text-slate-400 shadow-sm">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading queue...
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl bg-white p-12 text-center shadow-sm" data-testid="checkout-queue-empty">
            <User className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm font-medium text-slate-600">No one is checked in right now.</p>
            <p className="mt-1 text-xs text-slate-500">Check in the patient from Calendar and they will appear here.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="checkout-queue-list">
            {rows.map((r, index) => {
              const start = new Date(r.scheduled_start);
              const checkedIn = r.checked_in_at ? new Date(r.checked_in_at) : null;
              const waitMin  = checkedIn ? Math.max(0, Math.round((now - checkedIn.getTime()) / 60_000)) : null;
              const accent = checkoutQueueAccent(index);
              const status = r.has_draft ? "Draft saved" : "Checked in";
              return (
                <li key={r.id}>
                  <button
                    onClick={() => navigate(tutorialDemo ? tutorialVisitRoute : `/checkout?appointment_id=${r.id}`)}
                    data-testid={`queue-row-${r.id}`}
                    data-tutorial-id={index === 0 ? "checkout-queue-first-patient" : undefined}
                    className="group grid w-full grid-cols-[auto_minmax(0,1fr)] gap-4 rounded-2xl bg-white px-5 py-4 text-left shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 sm:grid-cols-[auto_minmax(0,1fr)_auto_auto] sm:items-center"
                  >
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${accent}`}>
                      {checkoutQueueInitials(r.patient_name)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <p className="truncate text-sm font-semibold leading-tight text-slate-900">{r.patient_name || "(no name)"}</p>
                        {r.patient_number ? <span className="text-[11px] text-slate-400">{r.patient_number}</span> : null}
                        {r.walk_in ? <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 ring-1 ring-amber-100">Walk-in</span> : null}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                        <span>{r.doctor_name || "No doctor"}</span>
                        <span className="text-slate-300">·</span>
                        <span>{r.chair_name || "No chair"}</span>
                        <span className="text-slate-300">·</span>
                        <span>Scheduled {start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                      </div>
                    </div>
                    <div className="col-start-2 flex flex-wrap items-center justify-start gap-2 sm:col-start-auto sm:flex-col sm:items-end">
                      <span className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${r.has_draft ? "bg-violet-50 text-violet-700 ring-1 ring-violet-100" : "bg-slate-100 text-slate-600"}`}>
                        <span className={`h-2.5 w-2.5 border ${r.has_draft ? "border-violet-500" : "border-slate-400"}`} />
                        {status}
                      </span>
                      <div className="text-left sm:text-right">
                        <p className="text-sm font-semibold leading-tight text-emerald-700">
                          {checkedIn ? checkedIn.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "-"}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {waitMin == null ? "checked in" : waitMin === 0 ? "just now" : `${waitMin} min ago`}
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="col-start-2 h-4 w-4 justify-self-start text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-slate-500 sm:col-start-auto sm:justify-self-end" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function checkoutQueueInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function checkoutQueueAccent(index) {
  const accents = [
    "bg-blue-50 text-blue-700",
    "bg-green-50 text-green-700",
    "bg-amber-50 text-amber-700",
    "bg-teal-50 text-teal-700",
  ];
  return accents[index % accents.length];
}

function PatientHeader({ hydration, children }) {
  const p = hydration.patient;
  const a = hydration.appointment;
  const start = new Date(a.scheduled_start);
  return (
    <div className="bg-white rounded-2xl shadow-sm px-5 py-4">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-slate-400" />
          <Link to={`/patients/${p.id}`} data-testid="checkout-patient-link"
            className="text-base font-semibold text-slate-900 hover:underline">
            {p.full_name || "(no name)"}
          </Link>
          {p.patient_number ? <span className="text-xs text-slate-400">{p.patient_number}</span> : null}
        </div>
        <div className="flex items-center gap-1.5 text-sm text-slate-600">
          <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
          {start.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
        </div>
        <div className="flex items-center gap-1.5 text-sm text-slate-600">
          <Stethoscope className="w-3.5 h-3.5 text-slate-400" />
          {a.doctor_name}
        </div>
        <div className="flex items-center gap-1.5 text-sm text-slate-600">
          <Armchair className="w-3.5 h-3.5 text-slate-400" />
          {a.chair_name}
        </div>
      </div>
      {children}
    </div>
  );
}

function PendingOrthoBillingGate({ rows, patient, loading, onSaved, clinic }) {
  const [openCaseId, setOpenCaseId] = useState(null);
  const [orthoSettings, setOrthoSettings] = useState(null);
  // The attending-doctor identity (full name + PRC) is resolved by the database
  // via list_ortho_doctor_records — the same source of truth the Doctor page
  // uses. Without it the embedded SetupBillingDialog cannot resolve the primary
  // doctor and the contract guard falsely blocks with "dentist full name is
  // missing", and the contract PDF would be missing the dentist/clinic.
  const [orthoDoctors, setOrthoDoctors] = useState([]);
  useEffect(() => {
    let cancelled = false;
    supabase.rpc("get_ortho_settings").then(({ data }) => {
      if (!cancelled) setOrthoSettings(data || null);
    });
    supabase.rpc("list_ortho_doctor_records").then(({ data }) => {
      if (!cancelled) setOrthoDoctors(Array.isArray(data) ? data : []);
    });
    return () => { cancelled = true; };
  }, []);
  if (loading) {
    return (
      <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 text-sm text-slate-500">
        Checking Ortho billing handoffs...
      </section>
    );
  }
  if (!rows?.length) return null;

  const openRow = rows.find((row) => row?.case?.id === openCaseId) || rows[0];
  const needsSetup = !openRow?.billing;
  const caseLabel = openRow?.case?.system_name || openRow?.case?.system || "Ortho case";

  return (
    <section className="bg-amber-50 rounded-2xl border border-amber-200 p-5 space-y-4" data-testid="checkout-ortho-billing-gate">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700">
            {needsSetup ? "Ortho billing required" : "Ortho billing"}
          </div>
          <h3 className="mt-1 text-lg font-semibold text-amber-950">
            {needsSetup ? "Set up Ortho billing before payment" : `${caseLabel} is waiting for bonding`}
          </h3>
          <p className="mt-1 text-sm text-amber-800">
            {needsSetup
              ? "This patient has an Ortho case waiting for billing setup. Payment is locked until the contract math is saved."
              : "Billing is already saved. Down payment stays pending until the bonding visit is booked or recorded."}
          </p>
        </div>
        {rows.length > 1 ? (
          <select
            value={openRow?.case?.id || ""}
            onChange={(e) => setOpenCaseId(e.target.value)}
            className="h-9 rounded-lg border border-amber-200 bg-white px-2 text-sm"
          >
            {rows.map((row) => (
              <option key={row.case.id} value={row.case.id}>
                {row.case.system_name || row.case.system || "Ortho case"}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <div className="rounded-xl border border-amber-100 bg-white p-4">
        {needsSetup ? (
          <SetupBillingDialog
            row={openRow}
            patient={patient}
            doctors={orthoDoctors}
            clinic={clinic}
            settings={orthoSettings}
            embedded
            onSaved={onSaved}
            onClose={() => {}}
          />
        ) : (
          <OrthoBillingReadyNotice row={openRow} />
        )}
      </div>
    </section>
  );
}

function OrthoBillingReadyNotice({ row }) {
  const c = row?.case || {};
  const billing = row?.billing || {};
  const currency = "PHP";
  return (
    <div className="space-y-3" data-testid="checkout-ortho-billing-ready">
      <div className="grid gap-3 md:grid-cols-4">
        <MiniMetric label="Package price" value={fmtPeso(Number(billing.package_price_centavos || 0), currency)} />
        <MiniMetric label="Down payment" value={`${fmtPeso(Number(billing.down_payment_centavos || 0), currency)} · Pending`} />
        <MiniMetric label="Per payment" value={fmtPeso(Number(billing.per_visit_charge_centavos || 0), currency)} />
        <MiniMetric label="Status" value={labelize(c.status || "active_pending_bonding")} />
      </div>
      <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        Book or record Installation Day 0 to start the case. Only the down payment is due that day; Adjustment 1 starts the regular payment schedule.
      </div>
    </div>
  );
}

function OrthoInstallationStep({
  row,
  appointment,
  patient,
  currency,
  sameSlotFutureNeedBundles = [],
  onSameSlotFutureNeedsBooked,
  onBack,
  onDefer,
  onBooked,
}) {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selectedFutureNeedKeys, setSelectedFutureNeedKeys] = useState([]);
  const [localFutureNeedBundles, setLocalFutureNeedBundles] = useState([]);
  const futureNeedSelectionInitializedRef = useRef(false);
  const c = row?.case || {};
  const patientId = patient?.id || c.patient_id;
  const patientName = patient?.full_name || c.patient_name || "Patient";

  useEffect(() => {
    const caseId = row?.case?.id;
    if (!caseId) return;
    let cancelled = false;
    setLoadingOptions(true);
    supabase.rpc("list_ortho_installation_options", { p_case_id: caseId }).then(({ data }) => {
      if (!cancelled) {
        setOptions(Array.isArray(data) ? data : []);
        setLoadingOptions(false);
      }
    });
    return () => { cancelled = true; };
  }, [row?.case?.id]);

  useEffect(() => {
    if (!patientId) {
      setLocalFutureNeedBundles([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const [needsRes, procRes] = await Promise.all([
        supabase.rpc("list_open_treatment_needs", { p_patient_id: patientId }),
        supabase.rpc("list_clinic_procedures"),
      ]);
      if (cancelled || needsRes.error) return;
      const procMap = {};
      for (const p of (procRes.data ?? [])) {
        procMap[p.id] = {
          duration: Number(p.suggested_duration_minutes || 0),
          category: p.category || null,
          name: p.name || null,
        };
      }
      setLocalFutureNeedBundles(buildFutureNeedBundlesFromNeeds(Array.isArray(needsRes.data) ? needsRes.data : [], procMap));
    })();
    return () => { cancelled = true; };
  }, [patientId]);

  const sameSlotBookableFutureNeedBundles = sameSlotFutureNeedBundles.length > 0
    ? sameSlotFutureNeedBundles
    : localFutureNeedBundles;

  const futureNeedOptionKeys = useMemo(
    () => new Set((sameSlotBookableFutureNeedBundles || []).map((b) => b.key).filter(Boolean)),
    [sameSlotBookableFutureNeedBundles],
  );

  useEffect(() => {
    const availableKeys = (sameSlotBookableFutureNeedBundles || []).map((b) => b.key).filter(Boolean);
    if (availableKeys.length === 0) {
      futureNeedSelectionInitializedRef.current = false;
      setSelectedFutureNeedKeys([]);
      return;
    }
    setSelectedFutureNeedKeys((prev) => {
      if (!futureNeedSelectionInitializedRef.current) {
        futureNeedSelectionInitializedRef.current = true;
        return availableKeys;
      }
      const kept = prev.filter((key) => futureNeedOptionKeys.has(key));
      return kept.length === prev.length ? prev : kept;
    });
  }, [futureNeedOptionKeys, sameSlotBookableFutureNeedBundles]);

  if (!row) {
    return (
      <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <p className="text-sm text-slate-500">No installation visit is waiting to be booked.</p>
        <button type="button" onClick={onDefer} className="mt-4 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white">
          Continue to payment
        </button>
      </section>
    );
  }

  const bestOption = options[0] || {};
  const duration = Number(bestOption.duration_minutes || 75);
  const systemName = c.system_name || c.system || "Ortho installation";
  const procedureName = bestOption.name || `Bracket Installation — ${systemName}`;
  const selectedFutureBundles = (sameSlotBookableFutureNeedBundles || []).filter((bundle) => selectedFutureNeedKeys.includes(bundle.key));
  const selectedFutureCount = selectedFutureBundles.length;
  const selectedFutureNeeds = selectedFutureBundles.flatMap((bundle) => bundle.needs || []);
  const selectedFutureLabel = selectedFutureCount > 0
    ? followUpItemNounPlural({ needs: selectedFutureNeeds }, selectedFutureCount)
    : "future needs";
  const futureMinutes = selectedFutureBundles.reduce((sum, bundle) => sum + (Number(bundle.estimatedMinutes) || 0), 0);
  const combinedDuration = duration + futureMinutes;
  const selectedFutureRequestedDays = pickDoctorRequestedDays(selectedFutureNeeds);
  const futureNeedLine = (bundle) => {
    if (!bundle) return "Future need";
    const tooth = bundle.tooth_number ? ` · #${bundle.tooth_number}` : "";
    const surfaces = (bundle.surfaces || []).filter(Boolean);
    const surfaceText = surfaces.length > 0 ? ` · ${surfaces.join(", ")}` : "";
    return `${bundle.procedure_name || "Future need"}${tooth}${surfaceText}`;
  };
  const toggleSameSlotFutureNeed = (bundleKey) => {
    setSelectedFutureNeedKeys((prev) => {
      const selected = new Set(prev);
      if (selected.has(bundleKey)) selected.delete(bundleKey);
      else selected.add(bundleKey);
      return Array.from(selected).filter((key) => futureNeedOptionKeys.has(key));
    });
  };
  const sameSlotFutureNeedsSummary = sameSlotBookableFutureNeedBundles.length > 0 ? (
    <div className="rounded-xl border border-teal-100 bg-teal-50/50 p-3" data-testid="ortho-installation-same-slot-options">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold text-slate-800">Add future needs to this same appointment?</p>
        {selectedFutureCount > 0 ? (
          <p className="text-xs tabular-nums text-teal-800">+{fmtMinutes(futureMinutes)}</p>
        ) : null}
      </div>
      <div className="mt-2 grid gap-1.5 md:grid-cols-2">
        {sameSlotBookableFutureNeedBundles.map((bundle) => {
          const checked = selectedFutureNeedKeys.includes(bundle.key);
          return (
            <label key={bundle.key}
              className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-xs cursor-pointer transition ${
                checked
                  ? "border-teal-300 bg-white text-teal-900"
                  : "border-slate-200 bg-white/70 text-slate-600 hover:border-teal-200"
              }`}
              data-testid={`ortho-installation-same-slot-option-${bundle.key}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleSameSlotFutureNeed(bundle.key)}
                className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600"
              />
              <span className="font-medium truncate">{futureNeedLine(bundle)}</span>
              {bundle.estimatedMinutes > 0 ? (
                <span className="ml-auto tabular-nums text-slate-500">+{fmtMinutes(bundle.estimatedMinutes)}</span>
              ) : null}
              {bundle.subtotalCents > 0 ? (
                <span className="tabular-nums text-slate-500">{fmtPeso(bundle.subtotalCents, currency)}</span>
              ) : null}
            </label>
          );
        })}
      </div>
    </div>
  ) : null;
  const appointmentSummary = (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-3 text-sm">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">This appointment will include</p>
      <div className="mt-2 space-y-1.5">
        <div>
          <p className="text-xs font-semibold text-slate-800">Installation visit</p>
          <p className="mt-1 text-xs text-slate-600">{procedureName} · {duration} min</p>
        </div>
        {sameSlotBookableFutureNeedBundles.length > 0 ? (
          <div className="pt-2 border-t border-slate-200">
            <p className="text-xs font-semibold text-slate-800">Optional same-slot follow-up</p>
            <div className="mt-1.5 space-y-1.5">
              {sameSlotBookableFutureNeedBundles.map((futureBundle) => {
                const checked = selectedFutureNeedKeys.includes(futureBundle.key);
                return (
                  <label key={futureBundle.key}
                    className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-xs cursor-pointer transition ${
                      checked
                        ? "border-teal-300 bg-teal-50 text-teal-900"
                        : "border-slate-200 bg-white text-slate-600 hover:border-teal-200"
                    }`}
                    data-testid={`book-ortho-same-slot-option-${futureBundle.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSameSlotFutureNeed(futureBundle.key)}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600"
                    />
                    <span className="font-medium truncate">{futureNeedLine(futureBundle)}</span>
                    {futureBundle.estimatedMinutes > 0 ? (
                      <span className="ml-auto tabular-nums text-slate-500">+{fmtMinutes(futureBundle.estimatedMinutes)}</span>
                    ) : null}
                    {futureBundle.subtotalCents > 0 ? (
                      <span className="tabular-nums text-slate-500">{fmtPeso(futureBundle.subtotalCents, currency)}</span>
                    ) : null}
                  </label>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
      <p className="mt-2 text-xs font-semibold text-slate-700 tabular-nums">
        Estimated chair time: ~{fmtMinutes(combinedDuration)}
      </p>
    </div>
  );

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-5" data-testid="checkout-ortho-installation-step">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-700">Follow-up task</div>
          <h2 className="mt-1 text-xl font-semibold text-slate-950">Book Installation Visit</h2>
          <p className="mt-2 text-sm text-slate-500">
            {systemName} · {loadingOptions ? "checking duration..." : `${duration} min`}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Booking is recommended, but not required. If deferred, the case stays Active Pending Bonding and appears in Ortho Center follow-up.
          </p>
        </div>
        <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Contract is active. Down payment is collected at bonding unless it was already handled.
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="grid gap-3 md:grid-cols-3">
          <MiniMetric label="Package" value={fmtPeso(Number(row.billing?.package_price_centavos || 0), currency)} />
          <MiniMetric label="Down payment" value={fmtPeso(Number(row.billing?.down_payment_centavos || 0), currency)} />
          <MiniMetric label="Per payment" value={fmtPeso(Number(row.billing?.per_visit_charge_centavos || 0), currency)} />
        </div>
      </div>

      {sameSlotFutureNeedsSummary}

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
          <ArrowLeft className="h-4 w-4" /> Back to Payment
        </button>
        <button type="button" onClick={() => setBookingOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700">
          <CalendarPlus className="h-4 w-4" />
          {selectedFutureCount > 0
            ? `Book installation + ${selectedFutureCount} ${selectedFutureLabel}`
            : "Book Now"}
        </button>
        <button type="button" onClick={onDefer} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          Defer
        </button>
      </div>

      {bookingOpen ? (
        <BookFutureNeedDialog
          open={bookingOpen}
          onOpenChange={setBookingOpen}
          bundle={{
            procedure_name: procedureName,
            estimatedMinutes: combinedDuration,
            needs: [],
            isAll: false,
          }}
          patientId={patientId}
          patientName={patientName}
          defaultDoctorId={appointment?.doctor_id || c.primary_doctor_id}
          defaultChairId={appointment?.chair_id}
          priorVisitDate={appointment?.scheduled_start || null}
          doctorRequestedDays={selectedFutureRequestedDays}
          appointmentStatus="Pencil"
          headingOverride="Book Ortho installation"
          subtitleOverride={selectedFutureCount > 0
            ? `${procedureName} · installation + ${selectedFutureCount} ${selectedFutureLabel} · ${combinedDuration} min`
            : `${procedureName} · ${duration} min`}
          appointmentSummary={appointmentSummary}
          appointmentInputExtra={{
            procedure_id: bestOption.id || undefined,
            ortho_case_id: row.case.id,
            ortho_visit_type: "bonding",
            notes: "Ortho installation visit",
          }}
          submitLabel="Book and continue"
          onBooked={(appointmentId, label) => {
            onSameSlotFutureNeedsBooked?.({
              caseId: row.case.id,
              appointment_id: appointmentId,
              appointment_label: label,
              bundles: selectedFutureBundles,
            });
            setBookingOpen(false);
            onBooked?.(appointmentId, label);
          }}
        />
      ) : null}
    </section>
  );
}

function OrthoNextVisitFollowUpStep({ request, currency, decision, onBook, onNotSure, onUndo }) {
  const intervalWeeks = Number(request?.interval_weeks || 0);
  const visitNumber = Number(request?.next_visit_number || 0);
  const procedureName = request?.procedure_name || "Ortho Adjustment";
  const windowText = orthoDateWindowText(request);
  const targetText = formatShortDate(request?.target_date);
  const isDebonding = request?.next_visit_type === "debonding";
  const alreadyBooked = request?.status === "already_booked";

  if (alreadyBooked) {
    return (
      <section className="bg-emerald-50 rounded-2xl shadow-sm border border-emerald-200 p-6 space-y-4" data-testid="checkout-ortho-next-visit-step">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700">Ortho follow-up</div>
        <div>
          <h2 className="text-xl font-semibold text-emerald-950">Next Ortho appointment booked</h2>
          <p className="mt-2 text-sm text-emerald-900">No additional booking is needed for this checkout.</p>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <MiniMetric label="Visit" value={isDebonding ? "Debonding" : `Adjustment ${visitNumber || ""}`.trim()} />
          <MiniMetric label="Date & time" value={formatDateTime(request.existing_appointment_start) || "Booked"} />
          <MiniMetric label="Doctor" value={request.existing_doctor_name || "Assigned doctor"} />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-amber-100 p-6 space-y-5" data-testid="checkout-ortho-next-visit-step">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700">Ortho follow-up</div>
          <h2 className="mt-1 text-xl font-semibold text-slate-950">{isDebonding ? "Book Debonding Visit" : "Book Next Adjustment"}</h2>
          <p className="mt-2 text-sm text-slate-500">
            {procedureName}{visitNumber ? ` · Adjustment ${visitNumber}` : ""}{intervalWeeks ? ` · ${intervalWeeks}w interval` : ""}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {isDebonding
              ? "Doctor marked this case ready for removal. Book inside the target range, or choose Not sure if the patient cannot decide today."
              : "Doctor set the next visit interval during the Ortho visit. Book now, or choose Not sure if the patient cannot decide today."}
          </p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Target {targetText || "date"}{windowText ? ` · ${windowText}` : ""}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="grid gap-3 md:grid-cols-3">
          <MiniMetric label="Interval" value={intervalWeeks ? `${intervalWeeks} weeks` : "Not set"} />
          <MiniMetric label="Target date" value={targetText || "Not set"} />
          <MiniMetric label="Charge" value={fmtPeso(Number(request?.expected_charge_centavos || 0), currency)} />
        </div>
      </div>

      {decision ? (
        <div className={`rounded-lg px-3 py-2 text-sm ${
          decision.kind === "booked"
            ? "border border-emerald-200 bg-emerald-50 text-emerald-900"
            : "border border-amber-200 bg-amber-50 text-amber-900"
        }`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-semibold">
              {decision.kind === "booked"
                ? `Booked: ${decision.appointment_label || "next adjustment appointment"}`
                : "Marked Not sure. Checkout can continue; no appointment was created."}
            </span>
            <button type="button" onClick={onUndo} className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold hover:bg-white/60">
              <X className="h-3.5 w-3.5" /> Undo
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={onBook} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700">
            <CalendarPlus className="h-4 w-4" /> {isDebonding ? "Book debonding" : "Book next adjustment"}
          </button>
          <button
            type="button"
            onClick={onNotSure}
            className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50"
            data-testid="checkout-ortho-next-visit-not-sure"
          >
            Not sure
          </button>
        </div>
      )}
    </section>
  );
}

function OrthoNextVisitBookingSummary({ request }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <p className="font-semibold">Doctor-requested Ortho interval: {Number(request?.interval_weeks || 0) || "not set"} weeks.</p>
      <p className="mt-0.5">
        Target date: <strong>{formatShortDate(request?.target_date) || "not set"}</strong>
        {orthoDateWindowText(request) ? <> · Recommended range: <strong>{orthoDateWindowText(request)}</strong></> : null}
      </p>
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function OrthoCollectionOptionsCard({ item, state, currency, onChange }) {
  if (!item) return null;

  const currentVisit = Number(item.current_visit_charge_centavos ?? item.performed_price_centavos ?? 0);
  const contractRemaining = Number(item.contract_remaining_centavos || 0);
  const amountDue = getOrthoCollectionDueCents(item, state);
  const shortfall = getOrthoCollectionShortfallCents(item, state);
  const label = item.procedure_name || item.display_label || "Ortho visit";
  const collectRemainingSelected = state?.decision !== "collect_visit_only";
  const collectVisitOnlySelected = state?.decision === "collect_visit_only";

  function optionClass(selected, tone = "teal") {
    if (selected && tone === "amber") {
      return "border-amber-400 bg-amber-50 shadow-sm";
    }
    if (selected) {
      return "border-teal-500 bg-teal-50 shadow-sm";
    }
    return "border-slate-200 bg-white hover:border-slate-300";
  }

  return (
    <div
      className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 space-y-3"
      data-testid="checkout-ortho-final-visit"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-bold text-amber-800">
            ⚠ Final visit — Ortho program closing
          </p>
          <h3 className="text-base font-semibold text-slate-900 mt-1">{label}</h3>
          <p className="text-sm text-amber-900/90 mt-1">
            Collect the full remaining balance to close the program. Retainers stay separate.
          </p>
        </div>
        <div className="rounded-xl bg-white/80 border border-amber-100 px-3 py-2 text-right shrink-0">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">Remaining balance</p>
          <p className="text-lg font-bold tabular-nums text-amber-950">{fmtPeso(contractRemaining, currency)}</p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <button
          type="button"
          onClick={() => onChange({ decision: "collect_remaining", amount_centavos: contractRemaining })}
          className={`rounded-xl border px-4 py-3 text-left transition ${optionClass(collectRemainingSelected)}`}
          data-testid="checkout-ortho-final-option-full"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-950">Collect remaining balance today</p>
              <p className="mt-1 text-xs leading-5 text-slate-600">
                Patient pays today's visit plus the remaining contract balance. Closes the ortho program.
              </p>
            </div>
            <span className="text-base font-bold tabular-nums text-slate-950">
              {fmtPeso(contractRemaining, currency)}
            </span>
          </div>
        </button>

        <button
          type="button"
          onClick={() => onChange({ decision: "collect_visit_only", amount_centavos: currentVisit })}
          className={`rounded-xl border px-4 py-3 text-left transition ${optionClass(collectVisitOnlySelected, "amber")}`}
          data-testid="checkout-ortho-final-option-visit-only"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-950">Collect this visit only</p>
              <p className="mt-1 text-xs leading-5 text-slate-600">
                Collect today's visit charge. Remaining balance becomes Final Visit AR — tracked in Ortho Center and the Patient Ortho tab.
              </p>
            </div>
            <span className="text-base font-bold tabular-nums text-slate-950">
              {fmtPeso(currentVisit, currency)}
            </span>
          </div>
        </button>
      </div>

      <div className="rounded-xl border border-amber-300 bg-amber-100/70 px-4 py-3 text-sm text-amber-950">
        <p className="font-semibold">
          ⚠ Leaving a balance creates Final Visit AR. Patient has no future appointments scheduled — this is the last opportunity to collect in person.
        </p>
      </div>

      <div className="rounded-xl border border-amber-200 bg-white px-4 py-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Amount due today</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">
            {fmtPeso(amountDue, currency)}
          </p>
          <p className={`mt-1 text-xs ${shortfall > 0 ? "text-amber-800" : "text-slate-600"}`}>
            {shortfall > 0
              ? `Leaves ${fmtPeso(shortfall, currency)} unpaid as Final Visit AR.`
              : "Collect full balance to close program."}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white">
          <Check className="h-4 w-4" />
          {shortfall > 0 ? "Visit-only selected" : `Collect ${fmtPeso(contractRemaining, currency)}`}
        </div>
      </div>
    </div>
  );
}

function OrthoUnderpaymentConfirmDialog({ item, amountDueCentavos, shortfallCentavos, currency, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-slate-950/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-lg w-full">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-950">
            Leaving {fmtPeso(shortfallCentavos, currency)} unpaid
          </h3>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-700">
            This is the patient's final scheduled visit. There are no future appointments to collect against.
            Leaving a balance creates Final Visit AR — the hardest AR to recover.
          </p>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Confirm you want to proceed with partial collection of {fmtPeso(amountDueCentavos, currency)}?
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">
            Cancel — Go Back
          </button>
          <button type="button" onClick={onConfirm} className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700">
            Confirm Underpayment
          </button>
        </div>
      </div>
    </div>
  );
}

function OpenQuotesReminderDialog({ count, onFinish, onGoBack }) {
  const n = Number(count) || 0;
  return (
    <div className="fixed inset-0 bg-slate-950/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-lg w-full">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-950">
            {n} quote{n === 1 ? "" : "s"} still open
          </h3>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-700">
            This patient has {n} treatment {n === 1 ? "quote" : "quotes"} you haven't accepted, booked, or
            skipped yet. Now is the best moment — they're still at the desk.
          </p>
          <p className="text-xs text-slate-500">
            You can finish anyway; open quotes stay on file and the Recall Center will follow up.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button type="button" onClick={onGoBack}
            data-testid="open-quotes-go-back"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Go back
          </button>
          <button type="button" onClick={onFinish}
            data-testid="open-quotes-finish-anyway"
            className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700">
            Finish anyway
          </button>
        </div>
      </div>
    </div>
  );
}

export function DentureCheckoutCompletionCard({
  items = [],
  currency = "PHP",
  remainingCentavos = 0,
  readOnly = false,
  finishSelected = false,
  onFinish,
  onKeepOpen,
}) {
  const [doctorConfirmed, setDoctorConfirmed] = useState(false);
  const openItems = items.filter((item) => item?.status !== "Completed");
  if (openItems.length === 0) return null;
  const allItemsHaveArch = openItems.every((item) => (
    ["upper", "lower"].includes(String(item?.arch || "").toLowerCase())
  ));
  const remaining = Math.max(0, Number(remainingCentavos || 0));

  function confirmFinish() {
    if (!doctorConfirmed || readOnly || !allItemsHaveArch) return;
    onFinish?.();
    setDoctorConfirmed(false);
  }

  function setFinishConfirmation(checked) {
    if (finishSelected && !checked) {
      setDoctorConfirmed(false);
      onKeepOpen?.();
      return;
    }
    setDoctorConfirmed(checked);
  }

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white p-3"
      data-testid="checkout-denture-completion"
    >
      <div>
        <p className="text-sm font-semibold text-slate-800">Is this treatment finished?</p>
        <p className="mt-0.5 text-[12px] leading-5 text-slate-600">
          Leave this alone unless the doctor says the patient is done. It&apos;s fine to check out with the treatment still open.
        </p>
      </div>
      <div className="mt-2 divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white">
        {openItems.map((item) => {
          const itemId = item.treatment_plan_item_id || item.item_id;
          const scoped = ["upper", "lower"].includes(String(item.arch || "").toLowerCase());
          return (
            <div
              key={itemId}
              className="flex items-center justify-between gap-3 px-3 py-2.5"
              data-testid={`checkout-denture-item-${itemId}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">{dentureItemLabel(item)}</p>
                <p className="text-[11px] text-slate-500">
                  Fee {fmtPeso(Number(item.fee_centavos || 0), currency)} · Paid {fmtPeso(Number(item.paid_centavos || 0), currency)} · Balance {fmtPeso(Number(item.balance_centavos || 0), currency)}
                </p>
                {!scoped ? (
                  <p className="mt-0.5 text-[11px] font-medium text-amber-700">Choose upper or lower for this item first.</p>
                ) : null}
              </div>
              <span className={`shrink-0 text-[11px] font-semibold ${finishSelected ? "text-emerald-700" : "text-slate-500"}`}>
                {finishSelected ? "Will be finished" : "Still open"}
              </span>
            </div>
          );
        })}
      </div>
      <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
        <input
          type="checkbox"
          checked={finishSelected || doctorConfirmed}
          onChange={(event) => setFinishConfirmation(event.target.checked)}
          disabled={readOnly || !allItemsHaveArch}
          data-testid="checkout-doctor-confirmed-treatment-finished"
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 accent-teal-600 focus:ring-2 focus:ring-teal-500 focus:ring-offset-1"
        />
        <span className="text-sm font-medium text-slate-800">
          The doctor confirmed every denture is delivered and this treatment is finished today.
        </span>
      </label>
      <button
        type="button"
        onClick={confirmFinish}
        disabled={!doctorConfirmed || readOnly || !allItemsHaveArch || finishSelected}
        data-testid="checkout-confirm-finish-and-bill-treatment"
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-teal-600 bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
      >
        {finishSelected ? (
          <><CheckCircle2 className="h-4 w-4" /> Treatment will close at checkout</>
        ) : doctorConfirmed ? (
          <><CheckCircle2 className="h-4 w-4" /> Finish treatment and bill {fmtPeso(remaining, currency)}</>
        ) : (
          <><Lock className="h-4 w-4" /> Confirm above to finish</>
        )}
      </button>
      <p className="mt-1.5 text-center text-[11px] text-slate-500">
        Finishing bills the {fmtPeso(remaining, currency)} balance and closes this treatment for good.
      </p>
    </section>
  );
}

function ItemsTable({ items, setItems, readOnly, currency, isPlanBound, hasPlan, postOpByProcedure = {}, isScheduledItem, footer = null, topActions = null }) {
  function update(idx, patch) {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  }
  function remove(idx) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }
  // Plan-bound items get price/qty/discount locked at chairside per
  // Cameron's Tx Plan Amendments spec. Receptionist routes any
  // adjustment through "Add amendment" on the Billing card above.
  const isLocked = typeof isPlanBound === "function" ? isPlanBound : () => false;
  // Scheduled work already shows (with its own status + follow-up controls)
  // in the "Today's plan" panel above, so this table doesn't repeat it —
  // it lists walk-in add-ons only. No procedure appears twice.
  const isScheduled = typeof isScheduledItem === "function" ? isScheduledItem : () => false;
  const walkInItems = items.filter((it) => !isScheduled(it));

  if (walkInItems.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="checkout-no-items" data-tutorial-id="checkout-items">
        {/* Add buttons lead the card so front desk sees the action first. */}
        {topActions ? (
          <div className="px-4 py-3 border-b border-slate-100">{topActions}</div>
        ) : null}
        <div className="p-6 text-center">
          <p className="text-sm font-semibold text-slate-700">Added chairside</p>
          <p className="text-xs text-slate-500 mt-1">Nothing added at the chair yet. Use the buttons above to add a procedure or one-off charge the doctor did during this visit. Scheduled and plan work show in their own cards.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="checkout-items">
      {/* Add buttons lead the card (front desk sees the action first), then
          the plan-aware hint, then the table. */}
      {topActions ? (
        <div className="px-4 py-3 border-b border-slate-100">{topActions}</div>
      ) : null}
      {/* Plan-aware lockdown hint — shown only when at least one plan
          item is in this visit. Tells the receptionist where to go to
          adjust pricing. Walk-in rows below stay editable. */}
      {hasPlan ? (
        <div className="bg-slate-50 border-b border-slate-100 px-4 py-2 flex items-start gap-2 text-[11px] text-slate-600"
             data-testid="checkout-items-plan-lockdown-hint">
          <Info className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" strokeWidth={2.25} />
          <span>
            <strong>Added chairside</strong> — procedures the doctor added during this visit, not part of the treatment plan.
          </span>
        </div>
      ) : null}
      <table className="w-full text-sm" data-tutorial-id="checkout-items">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            <th className="text-left px-4 py-2">Added chairside</th>
            <th className="text-center px-2 py-2 w-16">Qty</th>
            <th className="text-center px-2 py-2 w-24">Tooth</th>
            <th className="text-right px-2 py-2 w-32">Price</th>
            <th className="text-right px-2 py-2 w-28">Discount</th>
            <th className="text-right px-2 py-2 w-32">Net</th>
            <th className="text-center px-2 py-2 w-32">Status</th>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => {
            if (isScheduled(it)) return null;   // shown in "Today's plan" above
            const qty   = Number(it.qty || 1);
            const price = Number(it.performed_price_centavos || 0);
            const disc  = Number(it.item_discount_centavos || 0);
            const net   = Math.max(0, qty * price - disc);
            // Surface fillings: QTY = surface count (driven by charting), and
            // the line itemizes each surface — first at full base, extras at the
            // Settings bundle %. The tiers sum to the same total the app already
            // charges; this just makes the ₱ auditable. Only shown when it
            // honestly reconciles (not a manually-overridden price).
            const surfBreakdown = surfaceBreakdownReconciles(
              it.surface, it.procedure_base_centavos, it.additional_surface_pct, price,
            ) ? surfaceBreakdown(it.surface, it.procedure_base_centavos, it.additional_surface_pct) : null;
            const surfaceQty = surfBreakdown ? surfBreakdown.length : 0;
            const planLocked = isLocked(it);
            const orthoLine = it.source === "ortho_checkout";
            const orthoPending = orthoLine && it.payable_status === "pending_clinical_save";
            const priceLocked = readOnly || planLocked || orthoLine;
            const postOp = it.procedure_id ? postOpByProcedure[it.procedure_id] : null;
            const showPostOpChip = !!postOp && (it.outcome || "done") === "done";
            const manualPriceChanged = !!it.procedure_id
              && it.default_fee_centavos != null
              && Number(it.default_fee_centavos) !== price
              && !surfBreakdown;
            const showAdjustmentReason = (manualPriceChanged || disc > 0)
              && (it.outcome || "done") === "done";
            return (
              <tr
                key={it.tn_id || it._localId || idx}
                data-tutorial-id={
                  it._tutorialTodayHandoff
                    ? "checkout-chairside-first-row"
                    : it._tutorialAddedProcedure
                    ? "checkout-added-procedure-row"
                    : undefined
                }
                className={[
                  "border-t border-slate-100",
                  planLocked ? "bg-teal-50/30" : "",
                  orthoPending ? "bg-amber-50/40" : "",
                ].join(" ")}
              >
                <td className="px-4 py-2">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <p className="text-sm text-slate-800 font-medium">{it.procedure_name || it.custom_name || "(unnamed)"}</p>
                    {planLocked ? (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-teal-100 text-teal-800"
                        title="Plan procedure — price locked. Use Add amendment on the Billing card above."
                      >
                        Plan
                      </span>
                    ) : null}
                    {orthoLine ? (
                      <span
                        className={[
                          "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded",
                          orthoPending ? "bg-amber-100 text-amber-800" : "bg-teal-100 text-teal-800",
                        ].join(" ")}
                        title={orthoPending ? "Waiting for the doctor to save the ortho visit before payment." : "Ortho contract charge from the saved clinical visit."}
                      >
                        {orthoPending ? "Waiting for doctor" : "Ortho"}
                      </span>
                    ) : null}
                  </div>
                  {it.procedure_code ? <p className="text-[11px] text-slate-400">{it.procedure_code}</p> : null}
                  {showAdjustmentReason ? (
                    <div className="mt-1.5">
                      <input
                        type="text"
                        value={it.price_override_reason || ""}
                        onChange={(e) => update(idx, { price_override_reason: e.target.value })}
                        disabled={readOnly}
                        placeholder="Reason for price or discount (optional)"
                        aria-label={`Optional note for ${it.procedure_name || it.custom_name || "procedure"} price or discount change`}
                        data-testid={`item-adjustment-reason-${idx}`}
                        className="w-full min-w-52 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-900 outline-none placeholder:text-slate-400 focus:border-teal-500 disabled:bg-slate-50 disabled:text-slate-400"
                      />
                    </div>
                  ) : null}
                  {surfBreakdown ? (
                    <p className="text-[11px] text-slate-400" data-testid={`item-surface-breakdown-${idx}`}>
                      {surfBreakdown.map((r) => `${r.surface} ${fmtPeso(r.priceCentavos, currency)}`).join(" · ")}
                      {" = "}{fmtPeso(price, currency)}
                    </p>
                  ) : null}
                  {orthoPending ? (
                    <p className="text-[11px] text-amber-700 mt-0.5">Waiting for doctor to save this Ortho visit before payment.</p>
                  ) : null}
                  {showPostOpChip ? (
                    <p className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-[11px] font-semibold text-emerald-800">
                      ↪ {postOp.followup_procedure_name} in {postOp.after_days}d · 15 min
                    </p>
                  ) : null}
                </td>
                <td className="text-center px-2 py-2">
                  {/* Surface fillings: QTY is locked to 1 (the surfaces, not the
                      quantity, drive the price — shown itemized under the name).
                      Editable QTY here would let a 3-surface line be tripled. */}
                  <input type="number" min={1} max={99} value={qty}
                    disabled={priceLocked || !!surfBreakdown}
                    title={surfBreakdown ? "One filling — surfaces set the price (see breakdown). Change surfaces on the tooth chart." : undefined}
                    onChange={(e) => update(idx, { qty: Math.max(1, Number(e.target.value) || 1) })}
                    data-testid={`item-qty-${idx}`}
                    className="w-12 px-1 py-1 text-center text-sm rounded border border-slate-200 focus:border-teal-500 outline-none disabled:bg-slate-50 disabled:text-slate-400" />
                </td>
                <td className="text-center px-2 py-2">
                  <input type="text" value={it.tooth_number || ""}
                    disabled={priceLocked}
                    onChange={(e) => update(idx, { tooth_number: e.target.value })}
                    placeholder="—"
                    data-testid={`item-tooth-${idx}`}
                    className="w-20 px-1 py-1 text-center text-sm rounded border border-slate-200 focus:border-teal-500 outline-none disabled:bg-slate-50 disabled:text-slate-400" />
                </td>
                <td className="text-right px-2 py-2">
                  <PesoInput centavos={price} disabled={priceLocked}
                    onChange={(v) => update(idx, { performed_price_centavos: v })}
                    testid={`item-price-${idx}`} />
                </td>
                <td className="text-right px-2 py-2">
                  <PesoInput centavos={disc} disabled={priceLocked}
                    onChange={(v) => update(idx, { item_discount_centavos: v })}
                    testid={`item-discount-${idx}`} />
                </td>
                <td className="text-right px-2 py-2 text-sm font-semibold text-slate-800">
                  {fmtPeso(net, currency)}
                </td>
                <td className="text-center px-2 py-2">
                  {/* Done reads green here so the "done" language matches the
                      Today's plan list above — one done-look across both. */}
                  <select value={it.outcome || "done"}
                    disabled={readOnly}
                    onChange={(e) => update(idx, { outcome: e.target.value })}
                    data-testid={`item-status-${idx}`}
                    className={`px-2 py-1 text-xs rounded border outline-none disabled:bg-slate-50 disabled:text-slate-400 ${
                      (it.outcome || "done") === "done"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800 font-semibold focus:border-emerald-500"
                        : "border-slate-200 focus:border-teal-500"
                    }`}>
                    {STATUS_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </td>
                <td className="text-center px-2 py-2">
                  {!readOnly && !planLocked && !orthoLine ? (
                    <button onClick={() => remove(idx)} className="p-1 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600"
                      data-testid={`item-remove-${idx}`} aria-label="Remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Footer slot — the "add these to the plan?" toggle attaches here so
          it reads as an action on the rows above, not a floating card. */}
      {footer ? <div className="border-t border-slate-100">{footer}</div> : null}
    </div>
  );
}

function SubtotalSummary({ items, currency, isPlanBound, hasPlan }) {
  // For plan visits, the subtotal here is informational ("performed
  // today") — NOT what the patient owes. The plan's billing model
  // drives Total due in PaymentStep. We split done items into plan vs
  // walk-in buckets and show the walk-in net inline; the plan portion
  // appears in PaymentStep's Bill section, anchored to the actual
  // billing model.
  const planBound = typeof isPlanBound === "function" ? isPlanBound : () => false;
  const doneItems    = items.filter((it) => it.outcome === "done");
  const walkInItems  = doneItems.filter((it) => !planBound(it));

  const walkInSubtotal = walkInItems.reduce(
    (s, it) => s + Number(it.qty || 1) * Number(it.performed_price_centavos || 0), 0);
  const walkInDiscount = walkInItems.reduce(
    (s, it) => s + Number(it.item_discount_centavos || 0), 0);
  const walkInNet = Math.max(0, walkInSubtotal - walkInDiscount);

  if (hasPlan) {
    // The confusing "N plan procedures · walk-in subtotal · plan portion
    // shown at payment" line is dropped — the real bill breakdown (plan
    // portion + walk-in) lives on the payment step, which is clearer.
    return <div data-testid="checkout-subtotal" data-tutorial-id="checkout-subtotal" />;
  }

  // Walk-in only — legacy display.
  const subtotal = walkInSubtotal;
  const discount = walkInDiscount;
  const net = walkInNet;
  return (
    <div className="text-sm" data-testid="checkout-subtotal" data-tutorial-id="checkout-subtotal">
      <span className="text-slate-500">{walkInItems.length} item{walkInItems.length === 1 ? "" : "s"} · </span>
      <span className="text-slate-700">Subtotal {fmtPeso(subtotal, currency)}</span>
      {discount > 0 ? <span className="text-slate-500"> · Discount {fmtPeso(discount, currency)}</span> : null}
      <span className="ml-2 text-slate-900 font-semibold">Net {fmtPeso(net, currency)}</span>
    </div>
  );
}

export function FlexibleCollectionDecisionCard({
  remainingCentavos = 0,
  decision = null,
  amountCentavos = null,
  onAmountChange,
  onNoCollection,
  currency = "PHP",
  dentureItemBilling = false,
}) {
  const amount = decision === "amount" ? Math.max(0, Number(amountCentavos || 0)) : 0;
  const remainingAfter = Math.max(0, Number(remainingCentavos || 0) - amount);
  return (
    <section
      className="rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-4"
      data-testid="flexible-collection-decision"
    >
      <p className="text-sm font-semibold text-slate-900">Collect any amount</p>
      <p className="mt-0.5 text-xs text-slate-600">
        {dentureItemBilling ? "Denture billing · 50% target met" : "Flexible plan"} · {fmtPeso(remainingCentavos, currency)} remaining
      </p>
      {!dentureItemBilling ? (
        <p className="mt-1 text-xs text-violet-700">
          Enter the amount agreed by the clinic and patient for this visit.
        </p>
      ) : null}
      {dentureItemBilling ? (
        <p className="mt-1 text-xs text-violet-700">
          Payment is optional until a denture is installed. Any amount collected stays assigned to a specific denture.
        </p>
      ) : null}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <PesoInput
          centavos={decision === "amount" ? amountCentavos : null}
          onChange={onAmountChange}
          blankWhenNull
          placeholder="Enter amount"
          testid="flexible-collection-amount"
          inputClassName="h-10 w-40 px-3 text-base"
        />
        <button
          type="button"
          onClick={() => onAmountChange(Math.max(0, Number(remainingCentavos || 0)))}
          data-testid="flexible-collect-remaining"
          className="h-10 rounded-lg border border-violet-300 bg-white px-3 text-xs font-semibold text-violet-700 transition-colors hover:border-violet-400 hover:bg-violet-100"
        >
          Collect remaining {fmtPeso(remainingCentavos, currency)}
        </button>
        <button
          type="button"
          onClick={onNoCollection}
          data-testid="flexible-no-collection"
          className={`h-10 rounded-lg border px-3 text-xs font-semibold ${
            decision === "none"
              ? "border-slate-700 bg-slate-800 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          }`}
        >
          No payment today
        </button>
      </div>
      {decision === "amount" && amount > 0 ? (
        <p className="mt-2 text-xs font-medium text-violet-900" data-testid="flexible-balance-after">
          Balance after payment: {fmtPeso(remainingAfter, currency)}
        </p>
      ) : decision === "none" ? (
        <p className="mt-2 text-xs font-medium text-slate-700" data-testid="flexible-no-payment-summary">
          No payment today · {fmtPeso(remainingCentavos, currency)} remains
        </p>
      ) : (
        <p className="mt-2 text-xs text-amber-800">Enter an amount or choose No payment today.</p>
      )}
    </section>
  );
}

export function PaymentStep({
  hydration, items, subtotalCents, itemDiscountTotalCents,
  seniorPwdDiscountCents,
  seniorPwdPct = DEFAULT_SENIOR_PWD_DISCOUNT_PCT / 100,
  seniorPwdPctLabel = formatPercentLabel(DEFAULT_SENIOR_PWD_DISCOUNT_PCT),
  hmoCoveredCents, paymentState, setPaymentState, netCents,
  submitting, submitError, onClearSubmitError, activeQr, onOpenQrModal, onBack, onSubmit,
  onPatientUpdated,
  foldShareCents = 0,
  // Plan-aware props — passed only when the visit is bound to a Tx Plan.
  // planBillingState drives the compact Step 2 Tx Plan Status card and
  // final-visit shortfall warning.
  planId = null,
  planBillingState = null,
  planIsBundle = false,
  planCode = null,
  extraPlanBillings = [],
  onUserEditedAmount = null,
  // Plan vs walk-in split for the Bill section.
  planDoneItems = [],
  walkInDoneItems = [],
  planSuggestedCents = 0,
  planAllocationTargetCents = null,
  simpleFlexiblePlanPayment = false,
  walkInSubtotalCents = 0,
  walkInItemDiscountCents = 0,
  walkInSeniorPwdCents = 0,
  walkInNetCents = 0,
  orthoCollectionItem = null,
  orthoCollectionDecision = "collect_remaining",
  orthoCollectionExtraCents = 0,
  currentVisitDueCents = 0,
  totalDueCents = 0,
  selectedPriorArCents = 0,
  priorArRows = [],
  priorArSelections = {},
  priorArLoading = false,
  priorArError = null,
  onPriorArSelectionChange = null,
  planRemainingCents = 0,
  paymentCapCents = 0,
  settlementPreview = null,
  settlementPreviewLoading = false,
  settlementPreviewError = null,
  onSettlementPreviewRequest = null,
  hmoTutorialDemo = false,
}) {
  const currency = hydration.currency;
  const tendered = Number(paymentState.amount_tendered_cents || 0);
  const hasPlanBucket = planId != null;
  const priorArSelectedCents = Number(selectedPriorArCents || 0);
  const tenderedForCurrentVisit = Math.max(0, tendered - priorArSelectedCents);
  const currentDueCents = Number(currentVisitDueCents || 0);
  const effectivePaymentCapCents = Math.max(totalDueCents, Number(paymentCapCents || totalDueCents));

  const overpayCents = Math.max(0, tendered - effectivePaymentCapCents);
  const advancePlanCents = hasPlanBucket
    ? Math.max(0, Math.min(tendered, effectivePaymentCapCents) - totalDueCents)
    : 0;
  const underpayCents = Math.max(0, totalDueCents - tendered);
  const estimatedPlanPaymentCents = hasPlanBucket
    ? Math.min(Number(planRemainingCents || 0), Math.max(0, tenderedForCurrentVisit - walkInNetCents))
    : 0;
  const estimatedPlanRemainingAfterPaymentCents = hasPlanBucket
    ? Math.max(0, Number(planRemainingCents || 0) - estimatedPlanPaymentCents)
    : 0;
  const paymentCurrentDentureItemIds = useMemo(
    () => currentDentureItemIds(planDoneItems),
    [planDoneItems],
  );
  const defaultDentureAllocation = useMemo(() => buildDenturePaymentAllocation({
    billingState: planBillingState,
    amountCentavos: planAllocationTargetCents,
    currentItemIds: paymentCurrentDentureItemIds,
  }), [paymentCurrentDentureItemIds, planAllocationTargetCents, planBillingState]);
  const dentureAllocation = useMemo(() => buildDenturePaymentAllocation({
    billingState: planBillingState,
    amountCentavos: planAllocationTargetCents,
    currentItemIds: paymentCurrentDentureItemIds,
    manualAmounts: paymentState.denture_item_allocation_overrides_enabled
      ? paymentState.denture_item_allocation_overrides
      : null,
  }), [
    paymentState.denture_item_allocation_overrides,
    paymentState.denture_item_allocation_overrides_enabled,
    planAllocationTargetCents,
    planBillingState,
    paymentCurrentDentureItemIds,
  ]);
  const payableDentureItems = dentureAllocation.items.filter(
    (item) => !isDentureItemPaidInFull(item),
  );
  const planCodeValue = planCode || planBillingState?.plan_code || hydration?.plan?.plan_code || null;
  const planCodeLabel = planCodeValue || "ongoing treatment";
  const isCash = paymentState.method === "cash";
  const needsRef = paymentState.method !== "cash" && tendered > 0;
  const refOK = !needsRef || paymentState.reference_number.trim().length > 0;
  const isDigital = ["gcash","maya","bank_transfer"].includes(paymentState.method);
  const isTutorialMockQr = activeQr?.source === "tutorial_mock";
  const seniorPwdEligibleForPayment = (hydration.patient.is_senior_citizen || hydration.patient.is_pwd) && !paymentState.decline_senior_pwd;
  const seniorPwdPercent = seniorPwdPct * 100;
  const seniorPwdRemovalRequiresApproval =
    (hydration.patient.is_senior_citizen || hydration.patient.is_pwd)
    && paymentState.decline_senior_pwd;
  const seniorPwdRemovalApproved = !seniorPwdRemovalRequiresApproval
    || (
      paymentState.senior_pwd_discount_removed_by?.trim()
      && paymentState.senior_pwd_discount_removed_reason?.trim()
    );
  const canSubmitBase = canSubmitCheckoutPayment({
    submitting,
    totalDueCents,
    tenderedCents: tendered,
    hasPlanBucket,
    referenceOk: refOK,
    seniorPwdRemovalApproved,
  });
  const priorArCollectionCovered = priorArSelectedCents <= 0 || tendered >= priorArSelectedCents;
  function set(k, v) {
    onClearSubmitError?.();
    setPaymentState((prev) => ({ ...prev, [k]: v }));
  }

  // Mark the receptionist's intent as user-edited so the parent's
  // auto-fill effect stops mirroring totalDue. Plain delegate; the
  // parent owns the actual ref.
  function markUserEdit() { onUserEditedAmount?.(); }

  function quickFill(amountCents) {
    markUserEdit();
    set("amount_tendered_cents", amountCents);
    void onSettlementPreviewRequest?.(amountCents);
  }

  const allocationRows = useMemo(() => {
    const netFor = (it) => {
      const lineGross = Number(it.qty || 1) * Number(it.performed_price_centavos || 0);
      const seniorPwdLine = seniorPwdEligibleForPayment
        ? calculatePercentDiscountCentavos(lineGross, seniorPwdPercent)
        : 0;
      return Math.max(
        0,
        lineGross
          - Number(it.item_discount_centavos || 0)
          - seniorPwdLine,
      );
    };
    const rows = [];
    walkInDoneItems.forEach((it, idx) => {
      rows.push({
        key: `walkin-${it._localId || it.tn_id || it.procedure_id || idx}`,
        item_index: Math.max(0, items.indexOf(it)),
        kind: "single",
        label: it.procedure_name || it.custom_name || "Procedure",
        detail: it.tooth_number ? `#${it.tooth_number}` : "Single-visit",
        due_centavos: netFor(it),
      });
    });

    const planNetTotal = planDoneItems.reduce((sum, it) => sum + netFor(it), 0);
    const effectivePlanAllocationTarget = planAllocationTargetCents == null
      ? Number(planSuggestedCents || 0)
      : Number(planAllocationTargetCents || 0);
    let planRemainder = effectivePlanAllocationTarget;
    planDoneItems.forEach((it, idx) => {
      const isLast = idx === planDoneItems.length - 1;
      const share = planNetTotal > 0
        ? (isLast ? planRemainder : Math.min(planRemainder, Math.round(effectivePlanAllocationTarget * (netFor(it) / planNetTotal))))
        : (isLast ? planRemainder : 0);
      planRemainder -= share;
      rows.push({
        key: `plan-${it._localId || it.tn_id || it.procedure_visit_id || idx}`,
        item_index: Math.max(0, items.indexOf(it)),
        kind: "plan",
        label: it.procedure_name || it.custom_name || "Tx plan procedure",
        detail: "Tx plan",
        due_centavos: Math.max(0, share),
      });
    });

    const priorityRows = [
      ...rows.filter((r) => r.kind === "single").sort((a, b) => a.due_centavos - b.due_centavos),
      ...rows.filter((r) => r.kind !== "single"),
    ];

    let remaining = tenderedForCurrentVisit;
    return priorityRows
      .filter((r) => r.due_centavos > 0)
      .map((r) => {
        const defaultAmount = Math.min(r.due_centavos, Math.max(0, remaining));
        remaining -= defaultAmount;
        const override = (paymentState.procedure_allocation_overrides || [])
          .find((ov) => Number(ov.item_index) === Number(r.item_index));
        const amount = paymentState.allocation_overrides_enabled
          ? Number(override?.amount_centavos ?? defaultAmount)
          : defaultAmount;
        return { ...r, amount_centavos: Math.max(0, amount), default_amount_centavos: defaultAmount };
      });
  }, [
    items,
    paymentState.allocation_overrides_enabled,
    paymentState.procedure_allocation_overrides,
    planDoneItems,
    planAllocationTargetCents,
    planSuggestedCents,
    seniorPwdPercent,
    seniorPwdEligibleForPayment,
    tenderedForCurrentVisit,
    walkInDoneItems,
  ]);

  const allocationTotalCents = allocationRows.reduce((sum, row) => sum + Number(row.amount_centavos || 0), 0);
  const allocationCreditCents = calculateCurrentVisitAllocationCreditCentavos({
    tenderedCents: tendered,
    priorArSelectedCents,
    allocationTotalCents,
  });
  const managerApprovalRequired = paymentState.allocation_overrides_enabled
    && allocationRows.some((row) => row.kind === "single" && row.due_centavos > 0 && Number(row.amount_centavos || 0) === 0);
  const managerApprovalOK = !managerApprovalRequired
    || (paymentState.manager_approval_by?.trim() && paymentState.manager_approval_reason?.trim());
  const dentureAllocationOK = !planBillingState?.denture_item_billing
    || Number(planAllocationTargetCents || 0) <= 0
    || dentureAllocation.valid;
  const canSubmit = canSubmitBase && managerApprovalOK && priorArCollectionCovered && dentureAllocationOK;

  function setAllocationOverride(row, amountCents) {
    setPaymentState((prev) => {
      const nextOverrides = (prev.procedure_allocation_overrides || [])
        .filter((ov) => Number(ov.item_index) !== Number(row.item_index));
      nextOverrides.push({ item_index: row.item_index, amount_centavos: Math.max(0, Number(amountCents || 0)) });
      return { ...prev, procedure_allocation_overrides: nextOverrides };
    });
  }

  function startManualDentureSplit() {
    onClearSubmitError?.();
    setPaymentState((prev) => ({
      ...prev,
      denture_item_allocation_overrides_enabled: true,
      denture_item_allocation_overrides: defaultDentureManualAmounts(defaultDentureAllocation),
    }));
  }

  function stopManualDentureSplit() {
    onClearSubmitError?.();
    setPaymentState((prev) => ({
      ...prev,
      denture_item_allocation_overrides_enabled: false,
      denture_item_allocation_overrides: {},
    }));
  }

  function setDentureAllocationOverride(itemId, amountCents) {
    onClearSubmitError?.();
    const item = dentureAllocation.items.find(
      (candidate) => candidate.treatment_plan_item_id === itemId,
    );
    const remainingBalance = Math.max(0, Number(item?.balance_centavos || 0));
    const cappedAmount = Math.min(
      remainingBalance,
      Math.max(0, Number(amountCents || 0)),
    );
    setPaymentState((prev) => ({
      ...prev,
      denture_item_allocation_overrides: {
        ...(prev.denture_item_allocation_overrides || {}),
        [itemId]: cappedAmount,
      },
    }));
  }

  // Plan-aware shortfall hint at the top of payment step. The
  // BillingStateCard already shows visit math + suggested amount;
  // here we just call out the explicit "this is the last visit and
  // patient is short by ₱X" case so the receptionist sees it before
  // they hit Continue. No hard block — proceed-anyway lands in the
  // post-commit AR dialog.
  const isFinalShortfall = !!planBillingState?.is_final_visit_pending
    && Number(planBillingState?.remaining_centavos ?? 0) > 0
    && tendered < Number(planBillingState?.remaining_centavos ?? 0);
  const paymentMethodLabel = {
    cash: "Cash",
    gcash: "GCash",
    maya: "Maya",
    bank_transfer: "Bank transfer",
    card: "Card",
  }[paymentState.method] || "Payment";
  const denturePaymentReview = planBillingState?.denture_item_billing
    && dentureAllocation.items.length > 0 ? (
      <div className="border-t border-slate-100" data-testid="denture-payment-allocation">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Applied to treatment</p>
            <p className="mt-0.5 text-xs text-slate-500">This payment will go to the work shown below.</p>
          </div>
          {payableDentureItems.length > 1 ? (
            paymentState.denture_item_allocation_overrides_enabled ? (
              <button
                type="button"
                onClick={stopManualDentureSplit}
                data-testid="denture-allocation-use-default"
                className="text-xs font-semibold text-teal-700 underline underline-offset-2"
              >
                Use automatic split
              </button>
            ) : (
              <button
                type="button"
                onClick={startManualDentureSplit}
                data-testid="denture-allocation-split-manually"
                className="text-xs font-semibold text-slate-600 underline underline-offset-2 hover:text-slate-900"
              >
                Change split
              </button>
            )
          ) : null}
        </div>

        <div className="divide-y divide-slate-100">
          {dentureAllocation.items.map((item) => {
            const itemId = item.treatment_plan_item_id;
            const paidInFull = isDentureItemPaidInFull(item);
            const target = dentureAllocation.targets.find(
              (candidate) => candidate.treatment_plan_item_id === itemId,
            );
            const applied = Number(target?.amount_centavos || 0);
            const remainingAfterPayment = Math.max(0, Number(item.balance_centavos || 0) - applied);
            return (
              <div
                key={itemId}
                className={`grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center ${paidInFull ? "bg-slate-50/80" : ""}`}
                data-testid={`denture-allocation-row-${itemId}`}
              >
                <div className="min-w-0">
                  <div className={paidInFull ? "line-through decoration-slate-300" : ""}>
                    <p className={`text-sm font-semibold ${paidInFull ? "text-slate-400" : "text-slate-900"}`}>
                      {dentureItemLabel(item)}
                    </p>
                    <p className={`mt-0.5 text-xs ${paidInFull ? "text-slate-400" : "text-slate-500"}`}>
                      Treatment price {fmtPeso(item.fee_centavos, currency)}
                    </p>
                  </div>
                  {paidInFull ? (
                    <p className="mt-1 text-[11px] font-semibold text-emerald-700">
                      Paid in full · no more payment can be added
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-slate-600">
                      Remaining after payment: <span className="font-semibold tabular-nums text-slate-900">{fmtPeso(remainingAfterPayment, currency)}</span>
                    </p>
                  )}
                </div>

                {paidInFull ? (
                  <span
                    className="shrink-0 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800"
                    data-testid={`denture-allocation-paid-${itemId}`}
                  >
                    Paid in full
                  </span>
                ) : paymentState.denture_item_allocation_overrides_enabled ? (
                  <PesoInput
                    centavos={Number(paymentState.denture_item_allocation_overrides?.[itemId] || 0)}
                    onChange={(value) => setDentureAllocationOverride(itemId, value)}
                    testid={`denture-allocation-amount-${itemId}`}
                    inputClassName="w-36 h-10 px-3 text-base"
                  />
                ) : (
                  <div className="text-left sm:text-right">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Applied today</p>
                    <p className="text-base font-bold tabular-nums text-slate-950">{fmtPeso(applied, currency)}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!dentureAllocationOK ? (
          <div className="border-t border-rose-100 bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-700" data-testid="denture-allocation-error">
            {dentureAllocation.error}
          </div>
        ) : null}
      </div>
    ) : null;
  return (
    <>
      <PaymentOverviewGrid
        hydration={hydration}
        planId={planId}
        planBillingState={planBillingState}
        planIsBundle={planIsBundle}
        planCode={planCode}
        planSuggestedCents={planSuggestedCents}
        foldShareCents={foldShareCents}
        orthoCollectionExtraCents={orthoCollectionExtraCents}
        extraPlanBillings={extraPlanBillings}
        walkInDoneItems={walkInDoneItems}
        planDoneItems={planDoneItems}
        walkInSubtotalCents={walkInSubtotalCents}
        walkInItemDiscountCents={walkInItemDiscountCents}
        walkInSeniorPwdCents={walkInSeniorPwdCents}
        walkInNetCents={walkInNetCents}
        procedureCount={items.filter((it) => (it.outcome || "done") === "done").length}
        hmoCoveredCents={hmoCoveredCents}
        totalDueCents={currentDueCents}
        paymentState={paymentState}
        setPaymentState={setPaymentState}
        set={set}
        onPatientUpdated={onPatientUpdated}
        currency={currency}
        seniorPwdPct={seniorPwdPct}
        seniorPwdPctLabel={seniorPwdPctLabel}
        hmoTutorialDemo={hmoTutorialDemo}
      />

      <PriorArCollectionPanel
        rows={priorArRows}
        selected={priorArSelections}
        loading={priorArLoading}
        error={priorArError}
        currency={currency}
        selectedTotalCents={priorArSelectedCents}
        onChange={(next) => onPriorArSelectionChange?.(next)}
      />

      {isFinalShortfall ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2 text-[12px] text-amber-900"
             data-testid="payment-final-visit-shortfall">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.25} />
          <div className="flex-1 min-w-0">
            <p className="font-semibold">
              Final visit — patient still owes {fmtPeso(Number(planBillingState.remaining_centavos) - tendered, currency)}.
            </p>
            <p className="mt-0.5">
              Collect the full balance to close the plan cleanly. If the patient is short,
              we'll route the gap to AR after checkout — you'll be asked for a reason.
            </p>
          </div>
        </div>
      ) : null}

      {orthoCollectionItem ? (
        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3" data-testid="payment-additional-adjustments">
          {orthoCollectionExtraCents > 0 ? (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-baseline justify-between gap-2"
            data-testid="payment-ortho-remaining-balance"
          >
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-amber-800">
                Remaining Ortho Program Balance
              </p>
              <p className="text-[11px] text-amber-700/90">
                Added from the final-visit Ortho checkout decision.
              </p>
            </div>
            <p className="text-base font-semibold tabular-nums text-amber-950">
              {fmtPeso(orthoCollectionExtraCents, currency)}
            </p>
          </div>
        ) : orthoCollectionDecision !== "collect_remaining" ? (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-baseline justify-between gap-2"
            data-testid="payment-ortho-remaining-left-open"
          >
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-amber-800">
                Final Visit AR
              </p>
              <p className="text-[11px] text-amber-700/90">
                Remaining balance stays open after the final Ortho visit.
              </p>
            </div>
            <p className="text-sm font-semibold tabular-nums text-amber-950">
              {fmtPeso(Number(orthoCollectionItem.remaining_after_visit_centavos || 0), currency)}
            </p>
          </div>
        ) : null}

          <div className="border-t border-slate-200 pt-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-900">Total due</span>
            <span className="text-lg font-bold text-slate-900 tabular-nums" data-testid="payment-total-due">
              {fmtPeso(totalDueCents, currency)}
            </span>
          </div>
        </div>
      ) : null}

      {/* Payment method picker */}
      <div className="bg-white rounded-2xl shadow-sm p-5 space-y-4" data-tutorial-id="payment-method-section">
        <p className="text-sm font-semibold text-slate-800">Payment method</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <MethodButton method="cash"          icon={Banknote}    label="Cash"          state={paymentState} setState={setPaymentState} />
          <MethodButton method="gcash"         icon={Smartphone}  label="GCash"         state={paymentState} setState={setPaymentState} tutorialId="payment-method-gcash" />
          <MethodButton method="maya"          icon={Smartphone}  label="Maya"          state={paymentState} setState={setPaymentState} />
          <MethodButton method="bank_transfer" icon={Building2}   label="Bank transfer" state={paymentState} setState={setPaymentState} />
          <MethodButton method="card"          icon={CreditCard}  label="Card"          state={paymentState} setState={setPaymentState} disabled comingSoon />
        </div>

        {/* QR card for digital methods */}
        {isDigital ? (
          <button type="button" onClick={onOpenQrModal}
            data-testid="payment-show-qr"
            data-tutorial-id="payment-show-qr"
            className="w-full bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl p-3 flex items-center gap-3 text-left transition">
            {activeQr?.image_url ? (
              <img src={activeQr.image_url} alt="QR"
                className="w-14 h-14 object-contain rounded border border-slate-200" />
            ) : (
              <div className="w-14 h-14 flex items-center justify-center rounded border border-slate-200 bg-white">
                <QrCode className="w-6 h-6 text-slate-300" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">
                {isTutorialMockQr ? "Demo GCash QR" : activeQr ? "Tap to enlarge / send link" : "No QR uploaded"}
              </p>
              <p className="text-xs text-slate-500">
                {isTutorialMockQr
                  ? "Practice only. This QR is not linked to a real GCash account."
                  : activeQr
                  ? `${activeQr.label || "Clinic QR"} · Show it to the patient or send via email.`
                  : "Upload a QR in Settings → QR Codes."}
              </p>
            </div>
            <Send className="w-4 h-4 text-slate-400 shrink-0" />
          </button>
        ) : null}

        {/* Card "Coming soon" notice */}
        {paymentState.method === "card" ? (
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-600">
            Card payments need a payment processor. We'll wire HitPay (or your existing one) when you're ready.
          </div>
        ) : null}

        {/* Receipt photo (if proof received from modal) */}
        {paymentState.proof_url ? (
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 flex items-center gap-2 text-sm text-emerald-900" data-tutorial-id="payment-received-ui">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span className="flex-1">Patient proof attached</span>
            <a href={paymentState.proof_url} target="_blank" rel="noopener"
              className="text-xs font-semibold text-emerald-800 underline">View</a>
          </div>
        ) : null}

        {/* Reference number for digital */}
        {needsRef ? (
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="ref">Reference / transaction #</label>
            <input id="ref" type="text"
              value={paymentState.reference_number}
              onChange={(e) => set("reference_number", e.target.value)}
              placeholder="Required for digital payments"
              data-testid="reference-number"
              data-tutorial-id="payment-reference-number"
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-teal-500 outline-none" />
          </div>
        ) : null}

        {/* Amount */}
        <div data-testid="payment-amount-panel">
          <label className="text-sm font-semibold text-slate-800">
            Amount received
          </label>
          <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <PesoInput centavos={paymentState.amount_tendered_cents}
                onChange={(v) => {
                  markUserEdit();
                  set("amount_tendered_cents", v);
                  void onSettlementPreviewRequest?.(v);
                }}
                testid="amount-tendered"
                tutorialId="amount-tendered"
                inputClassName="w-40 h-11 px-3 text-xl" />
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => quickFill(totalDueCents)}
                  data-testid="amount-quick-full"
                  data-tutorial-id="amount-quick-full"
                  className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                  Exact amount
                </button>
                <button type="button" onClick={() => quickFill(Math.round(totalDueCents / 2))}
                  data-testid="amount-quick-half"
                  data-tutorial-id="amount-quick-half"
                  className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                  Half
                </button>
                <button type="button" onClick={() => quickFill(0)}
                  data-testid="amount-quick-zero"
                  data-tutorial-id="amount-quick-zero"
                  className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                  No payment
                </button>
              </div>
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-500">Nothing is posted until the final checkout confirmation.</p>
          {tendered === 0 && totalDueCents > 0 ? (
            <div
              className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="payment-zero-to-ar-warning"
            >
              <p className="font-semibold">No payment today.</p>
              <p className="mt-0.5">
                {hasPlanBucket
                  ? "The unpaid amount stays on the treatment plan balance. If this checkout closes the plan, the remaining balance posts to patient AR."
                  : `${fmtPeso(totalDueCents, currency)} will be posted to patient AR when checkout is completed.`}
              </p>
            </div>
          ) : null}
          {/* Status hints */}
          {tendered > 0 && totalDueCents > 0 ? (
            <div className="mt-2 text-xs space-y-1">
              {priorArSelectedCents > 0 ? (
                priorArCollectionCovered ? (
                  <p className="text-teal-700">
                    Prior AR collection — {fmtPeso(priorArSelectedCents, currency)} will be applied before today’s visit allocation.
                  </p>
                ) : (
                  <p className="text-rose-700" data-testid="prior-ar-full-coverage-warning">
                    Tendered amount must cover the selected prior AR collection. Need {fmtPeso(priorArSelectedCents, currency)} before this checkout can continue.
                  </p>
                )
              ) : null}
              {overpayCents > 0 && isCash ? (
                <div className="flex items-center gap-2 text-slate-700">
                  <span>Overpayment: <span className="font-semibold">{fmtPeso(overpayCents, currency)}</span></span>
                  <label className="inline-flex items-center gap-1.5">
                    <input type="checkbox"
                      checked={paymentState.keep_overpay_as_credit}
                      onChange={(e) => set("keep_overpay_as_credit", e.target.checked)}
                      data-testid="keep-overpay-as-credit"
                      className="w-3 h-3 rounded border-slate-300 text-teal-600" />
                    Keep as patient credit
                  </label>
                  {!paymentState.keep_overpay_as_credit
                    ? <span className="text-slate-500">→ change due ₱{(overpayCents / 100).toLocaleString()}</span>
                    : null}
                </div>
              ) : null}
              {overpayCents > 0 && !isCash ? (
                <p className="text-slate-700">Overpayment <span className="font-semibold">{fmtPeso(overpayCents, currency)}</span> — keeping as patient credit.</p>
              ) : null}
            </div>
          ) : null}
        </div>

      </div>

      <CheckoutSettlementPreviewCard
        preview={settlementPreview}
        loading={settlementPreviewLoading}
        error={settlementPreviewError}
        currency={currency}
        openTreatmentCode={extraPlanBillings.length === 0 ? planCodeValue : null}
        hideRoutes={!!planBillingState?.denture_item_billing}
      >
        {denturePaymentReview}
      </CheckoutSettlementPreviewCard>

      <FamilyPayerPanel
        patientId={hydration.patient.id}
        paymentState={paymentState}
        setPaymentState={setPaymentState}
      />

      {/* Submit */}
      {submitError ? (
        <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{submitError}</div>
      ) : null}
      {/* Payment application is opt-in now (the old always-on "Payment covers"
          block was noise). Credit is surfaced as a slim note; the manager-
          approval gate still appears the moment override mode is on. */}
      {planBillingState?.denture_item_billing ? null : paymentState.allocation_overrides_enabled ? (
        <PaymentAllocationSummary
          rows={allocationRows}
          currency={currency}
          fmtPeso={fmtPeso}
          paymentState={paymentState}
          setPaymentState={setPaymentState}
          setPaymentField={set}
          setAllocationOverride={setAllocationOverride}
          allocationCreditCents={allocationCreditCents}
          managerApprovalRequired={managerApprovalRequired}
          hasPlanBucket={hasPlanBucket}
          renderAmountInput={(row) => (
            <PesoInput
              centavos={row.amount_centavos}
              onChange={(v) => setAllocationOverride(row, v)}
              testid={`allocation-amount-${row.item_index}`}
            />
          )}
        />
      ) : simpleFlexiblePlanPayment ? (
        <div
          className="rounded-2xl border border-teal-100 bg-teal-50/60 px-5 py-3 text-sm"
          data-testid="payment-plan-application-summary"
        >
          {tendered <= 0 ? (
            <span className="text-slate-700">
              No payment today · <span className="font-semibold tabular-nums">{fmtPeso(planRemainingCents, currency)}</span> remains
            </span>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium text-teal-900">
                {fmtPeso(estimatedPlanPaymentCents, currency)} will be applied to {planCodeLabel}
              </span>
              <span className="text-slate-600">
                Balance after payment: <span className="font-semibold tabular-nums text-slate-900">{fmtPeso(estimatedPlanRemainingAfterPaymentCents, currency)}</span>
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-slate-100 bg-white px-5 py-3 shadow-sm text-sm">
          {allocationCreditCents > 0 ? (
            <span className="text-teal-700" data-testid="payment-credit-note">
              Patient credit <span className="font-semibold tabular-nums">{fmtPeso(allocationCreditCents, currency)}</span> — kept on the patient's account.
            </span>
          ) : (
            <span className="text-slate-500">Payment applies to today's bill first.</span>
          )}
          <button
            type="button"
            onClick={() => set("allocation_overrides_enabled", true)}
            data-testid="payment-change-application"
            className="shrink-0 text-slate-600 underline underline-offset-2 hover:text-slate-900"
          >
            Change what this payment covers
          </button>
        </div>
      )}
      <div className="flex items-center gap-3 sticky bottom-0 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm px-5 py-3 border border-slate-100">
        <button type="button" onClick={onBack} disabled={submitting}
          data-testid="payment-back"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" />Back to Today
        </button>
        <span className={`ml-auto text-sm font-semibold ${
          tendered <= 0
            ? "text-slate-500"
            : overpayCents > 0 || advancePlanCents > 0 || tendered >= totalDueCents
              ? "text-teal-700"
              : "text-amber-700"
        }`}>
          {tendered > 0 ? <>{fmtPeso(tendered, currency)} · {paymentMethodLabel} · </> : null}
          {tendered <= 0
            ? totalDueCents <= 0
              ? "No payment required"
              : hasPlanBucket
                ? "No payment · balance stays open"
                : <>No payment · {fmtPeso(totalDueCents, currency)} to AR</>
            : overpayCents > 0
            ? <>{isCash && !paymentState.keep_overpay_as_credit ? `Change ₱${(overpayCents / 100).toLocaleString()}` : `Credit ${fmtPeso(overpayCents, currency)}`}</>
            : advancePlanCents > 0
            ? <>{fmtPeso(advancePlanCents, currency)} applied to plan</>
            : tendered < totalDueCents
            ? <>Partial · {fmtPeso(underpayCents, currency)} short</>
            : "Paid in full"}
        </span>
        <button type="button" onClick={onSubmit} disabled={!canSubmit}
          data-testid="payment-submit"
          data-tutorial-id="payment-submit"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </>
  );
}

export function CheckoutSettlementPreviewCard({
  preview,
  loading = false,
  error = null,
  currency = "PHP",
  openTreatmentCode = null,
  hideRoutes = false,
  children = null,
}) {
  const routes = Array.isArray(preview?.routes) ? preview.routes : [];
  const patientPayment = Number(preview?.patient_payment_centavos || 0);
  const patientAr = Number(preview?.patient_ar_centavos || 0);
  const hmoAr = Number(preview?.hmo_ar_centavos || 0);
  const deferred = Number(preview?.deferred_centavos || 0);
  const extra = Number(preview?.credit_or_change_centavos || 0);
  const hasException = patientAr > 0 || hmoAr > 0 || deferred > 0 || extra > 0;

  return (
    <section
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      data-testid="checkout-settlement-preview"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <div>
          <p className="text-sm font-semibold text-slate-950">Payment summary</p>
          <p className="mt-1 text-xs text-slate-500">
            What will happen when this checkout is completed.
          </p>
        </div>
        {loading ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />Checking...
          </span>
        ) : null}
      </div>

      {error ? (
        <div className="border-t border-rose-200 bg-rose-50 px-5 py-3 text-sm text-rose-800">
          {error}
        </div>
      ) : null}

      {!loading && !error && preview ? (
        <div className="border-t border-slate-100">
          <div className="flex items-center justify-between gap-3 bg-slate-50/70 px-5 py-4">
            <span className="text-sm text-slate-600">Amount received</span>
            <span className="text-xl font-bold tabular-nums text-slate-950" data-testid="settlement-amount-received">
              {fmtPeso(patientPayment, currency)}
            </span>
          </div>

          {!hideRoutes && routes.length > 0 ? (
            <div className="divide-y divide-slate-100" data-testid="settlement-route-list">
              {routes.map((route, index) => {
                const paid = Number(route.payment_centavos || 0);
                const routePatientAr = Number(route.patient_ar_centavos || 0);
                const routeHmoAr = Number(route.hmo_ar_centavos || 0);
                const routeDeferred = Number(route.deferred_centavos || 0);
                const isCompletedWork = route.kind === "completed_work";
                const isMultiVisitPayment = route.kind === "open_treatment";
                const summaryLabel = isCompletedWork
                  ? "Work completed today"
                  : isMultiVisitPayment
                  ? openTreatmentCode || route.label || "Ongoing treatment"
                  : route.label || "Today's bill";
                return (
                  <div
                    key={`${route.kind || "route"}-${index}`}
                    className="grid gap-2 px-5 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{summaryLabel}</p>
                      {!isCompletedWork && !isMultiVisitPayment ? (
                        <p className="mt-0.5 text-xs text-slate-500">
                          Due today {fmtPeso(Number(route.due_centavos || 0), currency)}
                        </p>
                      ) : null}
                    </div>
                    <div className="text-left sm:text-right">
                      {paid > 0 ? (
                        <p className="text-sm font-semibold tabular-nums text-slate-950">{fmtPeso(paid, currency)}</p>
                      ) : (
                        <p className="text-sm font-semibold text-slate-500">No payment today</p>
                      )}
                      {routePatientAr > 0 ? <p className="text-xs text-amber-700">{fmtPeso(routePatientAr, currency)} will become patient balance</p> : null}
                      {routeHmoAr > 0 ? <p className="text-xs text-sky-700">{fmtPeso(routeHmoAr, currency)} will go to HMO</p> : null}
                      {routeDeferred > 0 ? <p className="text-xs text-slate-500">{fmtPeso(routeDeferred, currency)} remains due later</p> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {children}
        </div>
      ) : !loading && !error ? (
        <p className="border-t border-slate-100 px-5 py-4 text-sm text-slate-500">
          Enter the amount received to see the payment summary.
        </p>
      ) : null}

      {patientAr > 0 ? (
        <div className="border-t border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-900" data-testid="settlement-patient-ar">
          <span className="font-semibold">Patient balance after checkout:</span> {fmtPeso(patientAr, currency)}
        </div>
      ) : deferred > 0 ? (
        <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 text-sm text-slate-700" data-testid="settlement-deferred">
          <span className="font-semibold">Remaining for a later visit:</span> {fmtPeso(deferred, currency)}
        </div>
      ) : extra > 0 ? (
        <div className="border-t border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-900" data-testid="settlement-extra-payment">
          <span className="font-semibold">Extra payment:</span> {fmtPeso(extra, currency)}. Choose cash change or patient credit above.
        </div>
      ) : null}

      {hmoAr > 0 ? (
        <div className="border-t border-sky-200 bg-sky-50 px-5 py-3 text-sm text-sky-900" data-testid="settlement-hmo-ar">
          <span className="font-semibold">HMO balance after checkout:</span> {fmtPeso(hmoAr, currency)}
        </div>
      ) : null}

      {!loading && !error && preview && !hasException && patientPayment <= 0 ? (
        <div className="border-t border-slate-100 px-5 py-3 text-sm text-slate-600">No payment will be posted today.</div>
      ) : null}
    </section>
  );
}

export function PriorArCollectionPanel({
  rows = [],
  selected = {},
  loading = false,
  error = null,
  currency = "PHP",
  selectedTotalCents = 0,
  onChange,
}) {
  const normalized = normalizeCheckoutPriorArRows(rows);
  const [expandedId, setExpandedId] = useState(null);

  if (!loading && !error && normalized.length === 0) return null;

  function setCollect(row, collect) {
    onChange?.({
      ...selected,
      [row.ar_receivable_id]: !!collect,
    });
  }

  function setCollectAmount(row, amountCents) {
    const outstanding = Number(row.outstanding_centavos || 0);
    const cleanAmount = Math.min(outstanding, Math.max(0, Math.round(Number(amountCents || 0))));
    onChange?.({
      ...selected,
      [row.ar_receivable_id]: cleanAmount > 0 ? { amount_centavos: cleanAmount } : false,
    });
  }

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid="checkout-prior-ar-panel"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-950">Outstanding AR</p>
          <p className="mt-1 text-xs text-slate-500">
            Collect prior patient balances during this checkout or leave them open.
          </p>
        </div>
        {selectedTotalCents > 0 ? (
          <p className="text-lg font-bold tabular-nums text-slate-950" data-testid="checkout-prior-ar-selected-total">
            {fmtPeso(selectedTotalCents, currency)}
          </p>
        ) : null}
      </div>

      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />Loading prior AR...
        </div>
      ) : null}

      {error ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {error}
        </div>
      ) : null}

      {normalized.length > 0 ? (
        <div className="mt-4 space-y-2">
          {normalized.map((row) => {
            const isSelected = !!selected[row.ar_receivable_id];
            const isExpanded = expandedId === row.ar_receivable_id;
            const selectedAmount = priorArCollectionAmountCentavos(row, selected[row.ar_receivable_id]);
            const remainingAfterCollection = Math.max(0, Number(row.outstanding_centavos || 0) - selectedAmount);
            const sourceLabel = checkoutArSourceDisplay(row);
            return (
              <div
                key={row.ar_receivable_id}
                className="rounded-xl border border-slate-200 px-3 py-3"
                data-testid={`checkout-prior-ar-row-${row.ar_receivable_id}`}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : row.ar_receivable_id)}
                      className="text-left text-xs font-semibold uppercase tracking-wider text-teal-700 hover:text-teal-900"
                      data-testid={`checkout-prior-ar-origin-${row.ar_receivable_id}`}
                    >
                      {row.ar_number}
                    </button>
                    <p className="mt-1 truncate text-sm font-semibold text-slate-900">
                      {sourceLabel}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {Number(row.age_days ?? row.days_outstanding ?? 0)} days outstanding
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 sm:justify-end">
                    <p className="text-base font-bold tabular-nums text-slate-950">
                      {fmtPeso(row.outstanding_centavos, currency)}
                    </p>
                    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                      <button
                        type="button"
                        onClick={() => setCollect(row, true)}
                        className={`rounded-md px-2.5 py-1.5 text-xs font-semibold ${isSelected ? "bg-teal-600 text-white shadow-sm" : "text-slate-600 hover:bg-white"}`}
                        data-testid={`checkout-prior-ar-yes-${row.ar_receivable_id}`}
                      >
                        Collect now
                      </button>
                      <button
                        type="button"
                        onClick={() => setCollect(row, false)}
                        className={`rounded-md px-2.5 py-1.5 text-xs font-semibold ${!isSelected ? "bg-white text-slate-800 shadow-sm" : "text-slate-600 hover:bg-white"}`}
                        data-testid={`checkout-prior-ar-no-${row.ar_receivable_id}`}
                      >
                        Keep open
                      </button>
                    </div>
                  </div>
                </div>

                {isSelected ? (
                  <div className="mt-3 flex flex-col gap-2 rounded-lg border border-teal-100 bg-teal-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs font-semibold text-teal-950">Collect amount</p>
                      <p className="mt-0.5 text-xs text-teal-800">
                        {remainingAfterCollection > 0
                          ? `${fmtPeso(remainingAfterCollection, currency)} stays open`
                          : "This AR closes after payment"}
                      </p>
                    </div>
                    <PesoInput
                      centavos={selectedAmount}
                      onChange={(value) => setCollectAmount(row, value)}
                      testid={`checkout-prior-ar-amount-${row.ar_receivable_id}`}
                    />
                  </div>
                ) : null}

                {isExpanded ? (
                  <div
                    className="mt-3 grid gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600 sm:grid-cols-3"
                    data-testid={`checkout-prior-ar-origin-panel-${row.ar_receivable_id}`}
                  >
                    <PriorArOriginDetail label="Source" value={sourceLabel} />
                    <PriorArOriginDetail label="Service date" value={row.origin_service_date || row.service_date || row.aging_start_date || "Not recorded"} />
                    <PriorArOriginDetail label="Status" value={row.status || "open"} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {selectedTotalCents > 0 ? (
        <div className="mt-4 rounded-xl border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-900">
          Selected AR collection is applied before today’s visit allocation. Any unpaid AR balance stays open.
        </div>
      ) : null}
    </section>
  );
}

function PriorArOriginDetail({ label, value }) {
  return (
    <div>
      <p className="font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-0.5 font-medium text-slate-700">{value || "Not recorded"}</p>
    </div>
  );
}

function checkoutArMetadata(row) {
  const metadata = row?.metadata;
  if (!metadata) return {};
  if (typeof metadata === "object" && !Array.isArray(metadata)) return metadata;
  if (typeof metadata !== "string") return {};
  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function checkoutArProcedureLabel(row) {
  const metadata = checkoutArMetadata(row);
  return [
    row?.origin_procedure_name,
    row?.origin_procedure_label,
    row?.procedure_name,
    row?.procedure_label,
    row?.source_procedure_name,
    metadata.origin_procedure_name,
    metadata.origin_procedure_label,
    metadata.procedure_name,
    metadata.procedure_label,
    metadata.custom_name,
  ].find((value) => typeof value === "string" && value.trim())?.trim() || "";
}

function checkoutArSourceDisplay(row) {
  const source = checkoutArSourceLabel(row?.source);
  const procedure = checkoutArProcedureLabel(row);
  if (!procedure) return source;
  if (procedure.toLowerCase() === source.toLowerCase()) return source;
  return `${procedure} - ${source}`;
}

function checkoutArSourceLabel(source) {
  const key = String(source || "").toLowerCase();
  if (key === "standalone_underpayment") return "Standalone underpayment";
  if (key === "final_visit_underpayment") return "Final visit underpayment";
  if (key === "patient_copay") return "Patient copay";
  if (key === "abandoned_plan") return "Abandoned plan";
  if (key === "plan_acceptance") return "Tx plan balance";
  return key ? key.replace(/_/g, " ") : "Patient AR";
}

export function PaymentOverviewGrid({
  hydration,
  planId,
  planBillingState,
  planIsBundle = false,
  planCode = null,
  planSuggestedCents,
  foldShareCents = 0,
  orthoCollectionExtraCents = 0,
  extraPlanBillings = [],
  procedureCount,
  walkInDoneItems,
  planDoneItems = [],
  walkInSubtotalCents,
  walkInItemDiscountCents,
  walkInSeniorPwdCents,
  walkInNetCents,
  hmoCoveredCents = 0,
  totalDueCents,
  paymentState,
  setPaymentState,
  set,
  onPatientUpdated,
  currency,
  seniorPwdPct = DEFAULT_SENIOR_PWD_DISCOUNT_PCT / 100,
  seniorPwdPctLabel,
  hmoTutorialDemo = false,
}) {
  const hasPlan = planId != null;
  const patient = hydration?.patient || {};
  const flexibleAmountChosen = requiresFlexibleCollectionDecision(planBillingState);
  const patientCode = patient.patient_code || patient.code || "";
  const seniorPwdLabel = patient.is_senior_citizen ? "Senior Citizen" : patient.is_pwd ? "PWD" : "";
  const seniorPwdIdLabel = patient.is_senior_citizen ? "SC ID" : "PWD ID";
  const seniorPwdId = patient.is_senior_citizen ? patient.senior_citizen_id : patient.pwd_id;
  const seniorPwdPercent = Number(seniorPwdPct || 0) * 100;
  const planDueToday = Number(planSuggestedCents || 0);
  const presetLabel = billingPresetLabel(planBillingState?.preset, { compact: true });
  // Only a genuine plan code (not a fallback) is worth a chip on the plan line.
  const realPlanCode = planCode || planBillingState?.plan_code || hydration?.plan?.plan_code || null;
  const planProgress = buildCheckoutPlanProgressSummary({
    planBillingState,
    planDueTodayCents: planDueToday,
  });
  const planPaymentCopy = buildPlanPaymentCopy({
    planBillingState,
    planDueTodayCents: planDueToday,
    currency,
    planIsBundle,
  });

  // A bundle is open-ended, so numbered-visit language ("Visit 1 of 2",
  // "0 of 2 visits completed") is meaningless and reads as an error when
  // ₱0 is due. Replace the whole footer with one plain sentence that says
  // what the front desk should actually do this visit.
  const bundleFinishing = !!planBillingState?.is_final_visit_pending;
  const bundleFooter = !planIsBundle ? null
    : planDueToday > 0
      ? (bundleFinishing
          ? {
              line: `${fmtPeso(planDueToday, currency)} to collect — finishing today`,
              sub: "Collect this to close the treatment. Anything unpaid moves to AR.",
            }
          : {
              line: `${fmtPeso(planDueToday, currency)} to collect this visit`,
              sub: "This is the deposit. The rest is due when you finish the treatment.",
            })
      : planBillingState?.preset === "custom" && Number(planBillingState?.remaining_centavos || 0) > 0
        ? {
            line: "No payment today",
            sub: `${fmtPeso(Number(planBillingState.remaining_centavos), currency)} remains on the Flexible plan.`,
          }
        : {
            line: "Nothing to collect this visit",
            sub: "They're paid up for now. The balance is due when you tap “Finish this treatment.”",
          };

  const chargeableRows = Array.isArray(walkInDoneItems) ? walkInDoneItems : [];
  const planProcedureRows = Array.isArray(planDoneItems) ? planDoneItems : [];
  const procedureRows = hasPlan ? [...planProcedureRows, ...chargeableRows] : chargeableRows;
  const otherDiscountCents = Number(paymentState.visit_discount_cents || 0);
  const showManualDiscountReason = otherDiscountCents > 0;
  const [planOpen, setPlanOpen] = useState(false);
  const [addonOpen, setAddonOpen] = useState(false);
  const [adjustmentsOpen, setAdjustmentsOpen] = useState(() => !!hmoTutorialDemo);
  const [openExtraPlans, setOpenExtraPlans] = useState(() => new Set());
  const toggleExtraPlan = (id) => setOpenExtraPlans((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // Breakdown of "Collect today" so every peso shows its reason + destination.
  // Add-on residual = total − plan − ortho, split into two intentional kinds:
  //   • folded add-ons ride on the PLAN's schedule → roll into the plan line
  //   • standalone add-ons are billed 100% today → their own itemized line
  // The lines still reconcile to the headline regardless of discount math.
  const orthoExtraCents = Number(orthoCollectionExtraCents || 0);
  // Every OTHER active plan on this visit is its own line (Phase 2). Its due
  // is subtracted from the add-on residual so a second plan is never mislabeled
  // as an add-on. The money engine already posts each plan its own charge.
  const extraPlans = (Array.isArray(extraPlanBillings) ? extraPlanBillings : [])
    .filter((b) => b?.plan_id && Number(b?.suggested_next_centavos ?? 0) > 0);
  const extraPlanSumCents = extraPlans.reduce(
    (s, b) => s + Math.max(0, Number(b?.suggested_next_centavos ?? 0)), 0);
  // A plan-targeted "Other discount" comes off the plan, not the add-ons.
  // Add it back into the add-on residual (so add-ons show their true amount)
  // and take it off the plan line, so the breakdown matches the "comes off the
  // plan" note and the engine (which stamps the discount on the plan).
  const discountTargetPlanId = paymentState.visit_discount_target_plan_id || null;
  const planTargetedDiscountCents = discountTargetPlanId
    ? Math.min(Number(paymentState.visit_discount_cents || 0), planDueToday)
    : 0;
  const addOnDueCents = Math.max(0, Number(totalDueCents || 0) - planDueToday - orthoExtraCents - extraPlanSumCents + planTargetedDiscountCents);
  const foldedAddOnCents = Math.min(Number(foldShareCents || 0), addOnDueCents);
  const standaloneAddOnCents = Math.max(0, addOnDueCents - foldedAddOnCents);
  // The plan line shows the plan installment PLUS any add-ons folded onto it,
  // minus any discount aimed at this plan.
  const planLineDueCents = Math.max(0, planDueToday + foldedAddOnCents - planTargetedDiscountCents);
  // ── Discount target pick list + the selected target's current balance ──
  // (discountTargetPlanId / planTargetedDiscountCents are computed above, next
  // to the add-on residual they adjust.)
  const planDiscountTargets = [];
  if (hasPlan && planId) {
    const planBalCents = Math.max(0, Number(
      planBillingState?.final_total_centavos
      ?? planBillingState?.plan_total_items_centavos
      ?? planBillingState?.total_centavos ?? 0)
      - Number(planBillingState?.paid_centavos ?? 0));
    planDiscountTargets.push({
      id: planId,
      label: `${planIsBundle ? "Ongoing treatment" : "Treatment plan"}${realPlanCode ? ` · ${realPlanCode}` : ""}`,
      balanceCents: planBalCents,
    });
  }
  extraPlans.forEach((b) => {
    const code = b.plan_code || null;
    planDiscountTargets.push({
      id: b.plan_id,
      label: `${/^MV/i.test(code || "") ? "Ongoing treatment" : "Treatment plan"}${code ? ` · ${code}` : ""}`,
      balanceCents: Math.max(0, Number(b.remaining_centavos ?? b.final_total_centavos ?? b.total_centavos ?? 0)),
    });
  });
  const selectedDiscountTarget = planDiscountTargets.find((t) => t.id === discountTargetPlanId) || null;
  const shownProcedureCount = Number.isFinite(Number(procedureCount))
    ? Number(procedureCount)
    : procedureRows.length;
  const procedureLabel = `${shownProcedureCount} procedure${shownProcedureCount === 1 ? "" : "s"}`;
  const showTotalsBreakdown =
    walkInItemDiscountCents > 0
    || walkInSeniorPwdCents > 0
    || hmoCoveredCents > 0
    || otherDiscountCents > 0;
  const addOnDiscountLines = [
    walkInItemDiscountCents > 0
      ? { key: "item", label: "Item discounts", cents: walkInItemDiscountCents }
      : null,
    walkInSeniorPwdCents > 0
      ? { key: "senior-pwd", label: "SC/PWD discount", cents: walkInSeniorPwdCents }
      : null,
    (otherDiscountCents > 0 && !discountTargetPlanId)
      ? {
          key: "other",
          label: "Other discount",
          cents: otherDiscountCents,
          reason: paymentState.visit_discount_reason?.trim() || "Reason not entered",
        }
      : null,
  ].filter(Boolean);
  const addOnDiscountTotalCents = addOnDiscountLines.reduce((sum, line) => sum + Number(line.cents || 0), 0);
  const addOnGrossCents = walkInSubtotalCents > 0
    ? Number(walkInSubtotalCents || 0)
    : standaloneAddOnCents + addOnDiscountTotalCents;
  const showAddOnGrossNet = addOnDiscountTotalCents > 0 && addOnGrossCents > standaloneAddOnCents;

  return (
    <section
      className="space-y-3"
      data-testid="payment-overview-grid"
    >
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="payment-todays-bill" data-tutorial-id="payment-todays-bill">
        <div>
          <p className="text-xs font-semibold text-slate-500">
            {flexibleAmountChosen ? "Collect any amount" : "Collect today"}
          </p>
          <p className="mt-1 text-4xl font-bold tabular-nums text-slate-950" data-testid="payment-collect-today">
            {fmtPeso(totalDueCents, currency)}
          </p>
          <p className="mt-2 border-t border-slate-200 pt-3 text-xs text-slate-500">
            This visit only · {procedureLabel}
          </p>
        </div>

        {/* Itemized bill — every charge shows its reason and where it goes.
            Add-on procedures is the residual, so the lines always sum to
            "Collect today". The plan line expands INLINE to its model +
            balances (attached to the line, not a separate card). */}
        <div className="mt-4 overflow-hidden rounded-xl border border-slate-200" data-testid="payment-bill-breakdown">
          {hasPlan && planDueToday > 0 ? (
            <div className="border-b border-slate-100 last:border-b-0">
              <button
                type="button"
                onClick={() => setPlanOpen((o) => !o)}
                data-testid="payment-plan-breakdown-line"
                data-tutorial-id="payment-plan-breakdown-line"
                aria-expanded={planOpen}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-teal-500" />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                      {planIsBundle ? "Ongoing treatment" : "Treatment plan"}
                      {realPlanCode ? <span className="font-mono text-[11px] font-normal text-slate-400">{realPlanCode}</span> : null}
                    </span>
                    <span className="block text-xs text-slate-500">{planPaymentCopy.todayBillLineDetail}</span>
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  <span className="text-sm font-semibold tabular-nums text-slate-900">{fmtPeso(planLineDueCents, currency)}</span>
                  <span className="text-slate-400">{planOpen ? "▴" : "▾"}</span>
                </span>
              </button>
              {planOpen ? (
                <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3" data-testid="payment-tx-plan-status" data-tutorial-id="payment-tx-plan-status">
                  <dl className="space-y-1.5 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">Payment model</dt>
                      <dd className="font-medium text-slate-900" data-testid="payment-plan-model">{presetLabel}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">Plan total</dt>
                      <dd className="font-medium tabular-nums text-slate-900" data-testid="payment-plan-total">{fmtPeso(planProgress.planTotalCents, currency)}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">Collected so far</dt>
                      <dd className="font-medium tabular-nums text-slate-900" data-testid="payment-plan-collected">{fmtPeso(planProgress.collectedSoFarCents, currency)}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">Current balance <span className="text-slate-400">· before this payment</span></dt>
                      <dd className="font-medium tabular-nums text-slate-900" data-testid="payment-plan-current-balance">{fmtPeso(planProgress.currentBalanceCents, currency)}</dd>
                    </div>
                    {foldedAddOnCents > 0 ? (
                      <>
                        <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-1.5">
                          <dt className="text-slate-500">Plan installment this visit</dt>
                          <dd className="tabular-nums text-slate-900">{fmtPeso(planDueToday, currency)}</dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-slate-500">Added chairside procedures on this plan</dt>
                          <dd className="tabular-nums text-slate-900">{fmtPeso(foldedAddOnCents, currency)}</dd>
                        </div>
                        <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-1.5">
                          <dt className="font-semibold text-teal-800">Due this visit</dt>
                          <dd className="font-semibold tabular-nums text-teal-900" data-testid="payment-plan-due-this-visit">{fmtPeso(planLineDueCents, currency)}</dd>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-1.5">
                        <dt className="font-semibold text-teal-800">Due this visit</dt>
                        <dd className="font-semibold tabular-nums text-teal-900" data-testid="payment-plan-due-this-visit">{fmtPeso(planLineDueCents, currency)}</dd>
                      </div>
                    )}
                  </dl>
                  <p className="mt-2 text-xs text-slate-500" data-testid="payment-plan-portion">
                    {planIsBundle ? bundleFooter.sub : planPaymentCopy.statusVisitCopy}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}

          {extraPlans.map((b) => {
            const code = b.plan_code || null;
            const isBundle = /^MV/i.test(code || "");
            const due = Math.max(0, Number(b.suggested_next_centavos ?? 0));
            const open = openExtraPlans.has(b.plan_id);
            const total = Number(b.final_total_centavos ?? b.total_centavos ?? 0);
            const collected = Number(b.paid_centavos ?? 0);
            const balance = Number(b.remaining_centavos ?? 0);
            const model = billingPresetLabel(b.preset, { compact: true });
            return (
              <div key={b.plan_id} className="border-b border-slate-100 last:border-b-0">
                <button
                  type="button"
                  onClick={() => toggleExtraPlan(b.plan_id)}
                  aria-expanded={open}
                  data-testid={`payment-extra-plan-line-${b.plan_id}`}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
                >
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-violet-400" />
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                        {isBundle ? "Ongoing treatment" : "Treatment plan"}
                        {code ? <span className="font-mono text-[11px] font-normal text-slate-400">{code}</span> : null}
                      </span>
                      <span className="block text-xs text-slate-500">Also being paid this visit</span>
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <span className="text-sm font-semibold tabular-nums text-slate-900">{fmtPeso(due, currency)}</span>
                    <span className="text-slate-400">{open ? "▴" : "▾"}</span>
                  </span>
                </button>
                {open ? (
                  <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3">
                    <dl className="space-y-1.5 text-sm">
                      <div className="flex items-center justify-between gap-3"><dt className="text-slate-500">Payment model</dt><dd className="font-medium text-slate-900">{model}</dd></div>
                      <div className="flex items-center justify-between gap-3"><dt className="text-slate-500">Plan total</dt><dd className="font-medium tabular-nums text-slate-900">{fmtPeso(total, currency)}</dd></div>
                      <div className="flex items-center justify-between gap-3"><dt className="text-slate-500">Collected so far</dt><dd className="font-medium tabular-nums text-slate-900">{fmtPeso(collected, currency)}</dd></div>
                      <div className="flex items-center justify-between gap-3"><dt className="text-slate-500">Current balance <span className="text-slate-400">· before this payment</span></dt><dd className="font-medium tabular-nums text-slate-900">{fmtPeso(balance, currency)}</dd></div>
                      <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-1.5"><dt className="font-semibold text-teal-800">Due this visit</dt><dd className="font-semibold tabular-nums text-teal-900">{fmtPeso(due, currency)}</dd></div>
                    </dl>
                  </div>
                ) : null}
              </div>
            );
          })}

          {orthoExtraCents > 0 ? (
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0">
              <span className="flex min-w-0 items-center gap-2.5">
                <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-900">Ortho</span>
                  <span className="block text-xs text-slate-500">Collect this visit · from the Ortho decision</span>
                </span>
              </span>
              <span className="text-sm font-semibold tabular-nums text-slate-900">{fmtPeso(orthoExtraCents, currency)}</span>
            </div>
          ) : null}

          {standaloneAddOnCents > 0 ? (
            <div className="border-b border-slate-100 last:border-b-0" data-testid="payment-addon-breakdown-line">
              <button
                type="button"
                onClick={() => setAddonOpen((o) => !o)}
                aria-expanded={addonOpen}
                data-testid="payment-addon-toggle"
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-slate-400" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-900">Billed in full today</span>
                    <span className="block text-xs text-slate-500">
                      Scheduled + walk-in · not on a plan
                      {chargeableRows.length ? ` · ${chargeableRows.length} item${chargeableRows.length === 1 ? "" : "s"}` : ""}
                    </span>
                    {addOnDiscountTotalCents > 0 ? (
                      <span className="mt-1 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                        Discount applied · −{fmtPeso(addOnDiscountTotalCents, currency)}
                      </span>
                    ) : null}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  <span className="flex flex-col items-end leading-tight">
                    {showAddOnGrossNet ? (
                      <span className="text-[11px] font-medium text-slate-500">
                        Subtotal before discounts{" "}
                        <span className="tabular-nums text-slate-400 line-through">
                          {fmtPeso(addOnGrossCents, currency)}
                        </span>
                      </span>
                    ) : null}
                    {showAddOnGrossNet ? (
                      <span className="mt-0.5 text-[11px] font-medium text-slate-500">Amount to collect</span>
                    ) : null}
                    <span className="text-sm font-semibold tabular-nums text-slate-900">
                      {fmtPeso(standaloneAddOnCents, currency)}
                    </span>
                  </span>
                  <span className="text-slate-400">{addonOpen ? "▴" : "▾"}</span>
                </span>
              </button>
              {/* Collapsed peek only — when expanded, the discount detail lives
                  in the net summary at the bottom, so don't repeat it here. */}
              {!addonOpen && addOnDiscountLines.length > 0 ? (
                <div
                  className="border-t border-emerald-100 bg-emerald-50/70 px-4 py-2.5"
                  data-testid="payment-addon-discount-summary"
                >
                  <div className="mb-1.5 flex items-center justify-between gap-3 text-xs font-semibold text-emerald-900">
                    <span>Discount applied to billed-in-full work</span>
                    <span className="shrink-0 tabular-nums">−{fmtPeso(addOnDiscountTotalCents, currency)}</span>
                  </div>
                  <div className="space-y-1">
                    {addOnDiscountLines.map((line) => (
                      <div key={line.key} className="flex items-baseline justify-between gap-3 text-xs">
                        <span className="min-w-0 text-slate-600">
                          <span className="font-medium text-slate-700">{line.label}</span>
                          {line.reason ? <span className="text-slate-500"> · {line.reason}</span> : null}
                        </span>
                        <span className="shrink-0 tabular-nums text-emerald-700">−{fmtPeso(line.cents, currency)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {addonOpen ? (
                <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3" data-testid="payment-addon-items">
                  {chargeableRows.length > 0 ? (
                    <ul className="space-y-1.5 text-sm">
                      {chargeableRows.map((it, idx) => {
                        const lineGross = Number(it.qty || 1) * Number(it.performed_price_centavos || 0);
                        const lineManualDiscount = Math.max(0, Number(it.item_discount_centavos || 0));
                        const lineSeniorPwdDiscount = walkInSeniorPwdCents > 0
                          ? calculatePercentDiscountCentavos(lineGross, seniorPwdPercent)
                          : 0;
                        const lineDiscount = Math.min(lineGross, lineManualDiscount + lineSeniorPwdDiscount);
                        const lineNet = Math.max(0, lineGross - lineDiscount);
                        return (
                          <li
                            key={it._localId || it.tn_id || it.procedure_id || idx}
                            className="space-y-1"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <span className="min-w-0 truncate text-slate-700">
                                {it.procedure_name || it.custom_name || "Procedure"}
                                {it.tooth_number ? <span className="text-slate-400"> #{it.tooth_number}</span> : null}
                              </span>
                              <span className="shrink-0 tabular-nums text-slate-900">
                                {fmtPeso(lineGross, currency)}
                              </span>
                            </div>
                            {lineDiscount > 0 ? (
                              <div className="space-y-0.5 rounded-lg bg-white px-2 py-1.5 text-xs">
                                <div className="flex items-center justify-between gap-3 text-slate-500">
                                  <span>Gross</span>
                                  <span className="tabular-nums">{fmtPeso(lineGross, currency)}</span>
                                </div>
                                {lineManualDiscount > 0 ? (
                                  <div className="flex items-center justify-between gap-3 text-emerald-700">
                                    <span>Line discount</span>
                                    <span className="tabular-nums">−{fmtPeso(lineManualDiscount, currency)}</span>
                                  </div>
                                ) : null}
                                {lineSeniorPwdDiscount > 0 ? (
                                  <div className="flex items-center justify-between gap-3 text-emerald-700">
                                    <span>SC/PWD</span>
                                    <span className="tabular-nums">−{fmtPeso(lineSeniorPwdDiscount, currency)}</span>
                                  </div>
                                ) : null}
                                <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-1 font-medium text-slate-800">
                                  <span>Net</span>
                                  <span className="tabular-nums">{fmtPeso(lineNet, currency)}</span>
                                </div>
                              </div>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500">Chairside procedures added this visit.</p>
                  )}
                  {addOnDiscountLines.length > 0 ? (
                    <dl
                      className="mt-3 space-y-1.5 rounded-lg border border-emerald-100 bg-white px-3 py-2 text-sm"
                      data-testid="payment-addon-net-summary"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <dt className="text-slate-500">Subtotal before discounts</dt>
                        <dd className="tabular-nums text-slate-900">{fmtPeso(addOnGrossCents, currency)}</dd>
                      </div>
                      {addOnDiscountLines.map((line) => (
                        <div key={`expanded-${line.key}`} className="flex items-baseline justify-between gap-3">
                          <dt className="min-w-0 font-medium text-emerald-800">
                            {line.label}
                            {line.reason ? <span className="text-slate-500"> · {line.reason}</span> : null}
                          </dt>
                          <dd className="shrink-0 tabular-nums text-emerald-700">−{fmtPeso(line.cents, currency)}</dd>
                        </div>
                      ))}
                      <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-1.5">
                        <dt className="font-semibold text-slate-900">Amount to collect</dt>
                        <dd className="font-semibold tabular-nums text-slate-950">{fmtPeso(standaloneAddOnCents, currency)}</dd>
                      </div>
                    </dl>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-50/60"
          data-testid="payment-adjustments"
        >
          <button
            type="button"
            onClick={() => setAdjustmentsOpen((open) => !open)}
            aria-expanded={adjustmentsOpen}
            data-testid="payment-adjustments-toggle"
            className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left hover:bg-slate-100/70"
          >
            <span>
              <span className="block text-sm font-semibold text-slate-900">Discounts and coverage</span>
              <span className="mt-0.5 block text-xs text-slate-500">
                {showTotalsBreakdown ? "An adjustment is applied" : "Senior/PWD, HMO, or other discount"}
              </span>
            </span>
            <span className="text-xs font-semibold text-slate-500">{adjustmentsOpen ? "Hide" : "Add or change"}</span>
          </button>
          {adjustmentsOpen ? (
          <div className="space-y-3 border-t border-slate-200 px-3 py-3">
            {showTotalsBreakdown ? (
              <div className="space-y-2 rounded-lg bg-white px-3 py-2">
                <DiscountLine label="Subtotal" cents={walkInSubtotalCents} currency={currency} />
                {walkInItemDiscountCents > 0 ? (
                  <DiscountLine label="Item-level discounts" cents={-walkInItemDiscountCents} currency={currency} dim />
                ) : null}
              </div>
            ) : null}
            {seniorPwdLabel ? (
              <div
                className="rounded-xl border border-teal-200 bg-teal-50/70 px-3 py-2 text-xs text-teal-900"
                data-testid="payment-senior-pwd-inline"
              >
                <span className="font-semibold">{seniorPwdLabel}</span>
                {" "}mandatory {seniorPwdPctLabel} discount is auto-applied below.
                {" "}{seniorPwdIdLabel}: <span className="font-mono">{seniorPwdId || "(not on file)"}</span>.
              </div>
            ) : null}
            <SeniorPwdRow
              patient={patient}
              set={set}
              decline={paymentState.decline_senior_pwd}
              discountCents={walkInSeniorPwdCents}
              currency={currency}
              onPatientUpdated={onPatientUpdated}
              scopeLabel="today’s bill"
              discountPctLabel={seniorPwdPctLabel}
            />
            <HmoCoveragePanel
              patientId={patient?.id}
              paymentState={paymentState}
              setPaymentState={setPaymentState}
              currency={currency}
              billBaseCents={totalDueCents + hmoCoveredCents}
              tutorialDemo={hmoTutorialDemo}
            />
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-sm text-slate-700 shrink-0">Other discount</label>
              <PesoInput
                centavos={paymentState.visit_discount_cents}
                onChange={(v) => set("visit_discount_cents", v)}
                testid="visit-discount"
              />
              {/* Where does it land? Add-ons (default) or a specific plan. Only
                  shown when the patient has a plan — otherwise there's nothing
                  to choose and it goes to the add-ons as before. */}
              {planDiscountTargets.length > 0 ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-xs text-slate-500 shrink-0">applies to</span>
                  <select
                    value={discountTargetPlanId || ""}
                    onChange={(e) => set("visit_discount_target_plan_id", e.target.value || null)}
                    data-testid="visit-discount-target"
                    className="text-sm rounded border border-slate-200 bg-white px-2 py-1 focus:border-teal-500 outline-none"
                  >
                    <option value="">Billed in full today</option>
                    {planDiscountTargets.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </span>
              ) : null}
              {showManualDiscountReason ? (
                <input
                  type="text"
                  placeholder="Reason (optional)"
                  value={paymentState.visit_discount_reason}
                  onChange={(e) => set("visit_discount_reason", e.target.value)}
                  data-testid="visit-discount-reason"
                  className="flex-1 min-w-32 px-2 py-1 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none"
                />
              ) : null}
            </div>
            {otherDiscountCents > 0 ? (
              selectedDiscountTarget ? (
                <p
                  className="-mt-1 text-xs font-medium text-teal-700"
                  data-testid="payment-other-discount-target"
                >
                  Comes off {selectedDiscountTarget.label}: {fmtPeso(selectedDiscountTarget.balanceCents, currency)}
                  {" "}− {fmtPeso(otherDiscountCents, currency)}
                  {" "}→ {fmtPeso(Math.max(0, selectedDiscountTarget.balanceCents - otherDiscountCents), currency)}. The plan balance drops by this amount.
                </p>
              ) : (
                <p
                  className="-mt-1 text-xs text-slate-500"
                  data-testid="payment-other-discount-target"
                >
                  Applies to billed-in-full work: {fmtPeso(addOnGrossCents, currency)}
                  {" "}− {fmtPeso(otherDiscountCents, currency)}
                  {" "}→ {fmtPeso(Math.max(0, addOnGrossCents - otherDiscountCents), currency)} before other automatic adjustments.
                </p>
              )
            ) : null}

            {showTotalsBreakdown ? (
              <div
                className="flex items-center justify-between border-t border-slate-200 pt-3"
                data-testid="payment-adjusted-total"
              >
                <span className="text-sm font-semibold text-slate-950">Total due today</span>
                <span className="text-lg font-bold tabular-nums text-slate-950">
                  {fmtPeso(totalDueCents, currency)}
                </span>
              </div>
            ) : null}
          </div>
          ) : null}
        </div>
      </div>

    </section>
  );
}

function billingPresetLabel(preset, opts = {}) {
  const compact = !!opts.compact;
  if (preset === "50_50") return compact ? "50/50" : "50 / 50 plan";
  if (preset === "per_phase") return compact ? "Even split" : "Even split plan";
  if (preset === "custom") return "Flexible";
  if (preset === "full") return compact ? "Full upfront" : "Full upfront";
  return "Tx Plan billing";
}

function buildPlanPaymentCopy({ planBillingState, planDueTodayCents, currency, planIsBundle = false }) {
  const dueToday = Number(planDueTodayCents || 0);
  const hasDueToday = dueToday > 0;
  // A bundle is branded "Ongoing treatment", not "Tx Plan".
  const noun = planIsBundle ? "Ongoing treatment" : "Tx Plan";
  const inNoun = planIsBundle ? "Ongoing treatment" : "In Tx Plan,";
  const preset = planBillingState?.preset || null;
  const modelLabel = billingPresetLabel(preset, { compact: true });
  const completedVisits = Number(planBillingState?.completed_visits || 0);
  const totalVisits = Number(planBillingState?.total_visits || 0);
  const visitNumber = totalVisits > 0 ? Math.min(totalVisits, completedVisits + 1) : null;
  const visitPrefix = visitNumber ? `Visit ${visitNumber}: ` : "";
  const isFinalVisit = !!planBillingState?.is_final_visit_pending
    || (totalVisits > 0 && visitNumber === totalVisits);
  const dentureItems = Array.isArray(planBillingState?.denture_items)
    ? planBillingState.denture_items
    : [];
  const isStartingDentureDeposit = planIsBundle
    && preset === "50_50"
    && dentureItems.some((item) => item?.due_stage === "deposit_due");

  const dueDetailByPreset = {
    "50_50": isStartingDentureDeposit
      ? "First 50% deposit is due on this checkout."
      : isFinalVisit
      ? "Final 50/50 payment is due on this checkout."
      : "Scheduled 50/50 payment is due on this checkout.",
    per_phase: "Even-split plan installment is due on this checkout.",
    custom: "Clinic chose this Flexible amount for this visit.",
    full: "Full-upfront plan amount is due on this checkout.",
  };
  const noDueDetailByPreset = {
    "50_50": "50/50 has no middle-visit installment due today.",
    per_phase: "Even-split plan has no scheduled installment due today.",
    custom: "Flexible plan has no fixed amount for this visit.",
    full: "Full-upfront plan is handled outside this visit's payment.",
  };

  if (hasDueToday) {
    const dueText = fmtPeso(dueToday, currency);
    return {
      completedBucketLabel: isStartingDentureDeposit
        ? `${inNoun} deposit due today`
        : isFinalVisit
        ? `${inNoun} final payment due today`
        : `${inNoun} payment due today`,
      completedBucketDetail: `${modelLabel}: ${dueText} is due today`,
      statusVisitCopy: `${visitPrefix}${dueText} due today`,
      todayBillLineLabel: `${noun} payment due today`,
      todayBillLineDetail: dueDetailByPreset[preset] || "Plan billing model schedules a payment for this checkout.",
    };
  }

  return {
    completedBucketLabel: `${inNoun} no installment due today`,
    completedBucketDetail: noDueDetailByPreset[preset] || "No plan installment is scheduled for this checkout.",
    statusVisitCopy: `${visitPrefix}no installment due today`,
    todayBillLineLabel: `${noun} payment today`,
    todayBillLineDetail: noDueDetailByPreset[preset] || "No plan installment is scheduled for this checkout.",
  };
}

function MethodButton({ method, icon: Icon, label, state, setState, disabled, comingSoon, tutorialId }) {
  const active = state.method === method;
  return (
    <button type="button"
      onClick={() => { if (!disabled) setState((prev) => ({ ...prev, method })); }}
      disabled={disabled}
      data-testid={`method-${method}`}
      data-tutorial-id={tutorialId}
      title={comingSoon ? "Coming soon" : undefined}
      className={`relative flex flex-col items-center gap-1 p-3 rounded-lg border transition ${
        disabled
          ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed opacity-70"
          : active
            ? "bg-teal-50 border-teal-300 text-teal-700"
            : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
      }`}>
      <Icon className="w-5 h-5" />
      <span className="text-xs font-medium">{label}</span>
      {comingSoon ? (
        <span className="absolute -top-1.5 -right-1.5 text-[9px] uppercase tracking-wider font-semibold text-slate-500 bg-white border border-slate-200 rounded px-1 py-0.5">
          Soon
        </span>
      ) : null}
    </button>
  );
}

function DiscountLine({ label, cents, currency, dim }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${dim ? "text-slate-500" : "text-slate-700"}`}>{label}</span>
      <span className={`text-sm tabular-nums ${dim ? "text-slate-500" : "text-slate-700"}`}>
        {cents < 0 ? `−${fmtPeso(-cents, currency)}` : fmtPeso(cents, currency)}
      </span>
    </div>
  );
}

function DoneScreen({ result, hydration, navigate, items, paymentState, breakdown, planBillingState }) {
  const r = useMemo(() => result || {}, [result]);
  const s = useMemo(() => r.summary || {}, [r]);
  const noTreatmentReasonCode = r.no_treatment_reason_code || null;
  const noPaymentCheckout = isNoPaymentCheckoutResult(r);
  const handlePrint = () => window.print();
  const [receiptSignatureDoc, setReceiptSignatureDoc] = useState(null);
  const [receiptSignOpen, setReceiptSignOpen] = useState(false);
  const [receiptSignatureBusy, setReceiptSignatureBusy] = useState(false);
  const receiptSignature = signatureForPdf(receiptSignatureDoc);
  const receiptSigned = !!receiptSignature;
  const canSignReceipt = !!r.receipt_number && !!r.visit_id && !receiptSigned;

  // When the visit is plan-bound AND the plan has at least one
  // amendment, surface the three-line breakdown so the on-screen
  // close-out matches what the printed receipt shows. If amendments
  // are zero, the original/final lines collapse and we fall back to
  // the per-visit summary alone.
  const showPlanBreakdown =
    planBillingState
    && Number(planBillingState.amendments_centavos || 0) !== 0;

  useEffect(() => {
    let cancelled = false;
    if (!r.visit_id) {
      setReceiptSignatureDoc(null);
      return () => { cancelled = true; };
    }
    getSignedDocumentStatus({
      documentType: DOCUMENT_TYPES.checkoutReceipt,
      sourceId: r.visit_id,
    }).then((doc) => {
      if (!cancelled) setReceiptSignatureDoc(doc || null);
    }).catch((err) => {
      if (!cancelled) {
        console.warn("[checkout] receipt signature status failed", err);
        setReceiptSignatureDoc(null);
      }
    });
    return () => { cancelled = true; };
  }, [r.visit_id]);

  const handleReceiptSignatureSubmit = useCallback(async (signature) => {
    if (!r.visit_id) return;
    setReceiptSignatureBusy(true);
    try {
      const snapshot = await createDocumentSnapshot({
        documentType: DOCUMENT_TYPES.checkoutReceipt,
        sourceTable: "visits",
        sourceId: r.visit_id,
        patientId: hydration?.patient?.id || null,
        documentNumber: r.receipt_number || null,
        documentTitle: "Checkout Receipt",
        documentVersion: "checkout_receipt_print_v1",
        signatureAnchor: { page: "last", slot: "receipt_acknowledgment" },
        documentPayload: {
          result: r,
          summary: s,
          patient: hydration?.patient || null,
          clinic: hydration?.clinic || null,
          appointment: hydration?.appointment || null,
          items: (items || []).filter((it) => (it.outcome || "done") === "done"),
          payment: paymentState || null,
          breakdown: breakdown || null,
          plan_billing_state: planBillingState || null,
        },
      });
      const signed = await signDocumentSnapshot({
        documentId: snapshot?.id,
        signature,
      });
      setReceiptSignatureDoc(signed || null);
      setReceiptSignOpen(false);
      toast.success("Receipt signed.");
    } catch (err) {
      toast.error(err?.message || "Could not sign receipt.");
      console.warn("[checkout] receipt signature failed", err);
    } finally {
      setReceiptSignatureBusy(false);
    }
  }, [breakdown, hydration, items, paymentState, planBillingState, r, s]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-slate-900">
          {noPaymentCheckout ? "Visit closed — no payment" : "Checkout complete"}
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          {r.receipt_number
            ? <>Receipt <span className="font-mono text-slate-700">{r.receipt_number}</span></>
            : noPaymentCheckout
              ? <>{noTreatmentReasonCode
                ? "No receipt issued. The treatment remains open for human follow-up."
                : "No receipt issued. No items or payment were recorded for this visit."}</>
              : <>Visit closed.</>}
        </p>
      </div>

      {showPlanBreakdown ? (
        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-2" data-testid="done-plan-breakdown">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
            Treatment plan
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Original estimate</span>
            <span className="text-sm text-slate-800 tabular-nums">
              {fmtPeso(planBillingState.original_estimate_centavos || 0, hydration.currency)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Amendments</span>
            <span className={`text-sm tabular-nums ${
              Number(planBillingState.amendments_centavos) < 0 ? "text-rose-700" : "text-slate-800"
            }`}>
              {Number(planBillingState.amendments_centavos) < 0 ? "−" : "+"}
              {fmtPeso(Math.abs(planBillingState.amendments_centavos || 0), hydration.currency)}
            </span>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 pt-2 mt-2">
            <span className="text-sm font-semibold text-slate-800">Final total</span>
            <span className="text-sm font-bold text-slate-900 tabular-nums">
              {fmtPeso(planBillingState.final_total_centavos || 0, hydration.currency)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Paid (plan, all visits)</span>
            <span className="text-sm text-slate-800 tabular-nums">
              {fmtPeso(planBillingState.paid_centavos || 0, hydration.currency)}
            </span>
          </div>
          {Number(planBillingState.remaining_centavos || 0) > 0 ? (
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Plan balance</span>
              <span className="text-sm text-amber-700 font-semibold tabular-nums">
                {fmtPeso(planBillingState.remaining_centavos, hydration.currency)}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="bg-white rounded-2xl shadow-sm p-5 space-y-2">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
          {showPlanBreakdown ? "This visit" : "Summary"}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Patient</span>
          <span className="text-sm text-slate-800 font-medium">{hydration.patient.full_name}</span>
        </div>
        {s.subtotal_centavos != null ? (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Subtotal</span>
            <span className="text-sm text-slate-800 tabular-nums">{fmtPeso(s.subtotal_centavos, hydration.currency)}</span>
          </div>
        ) : null}
        {s.total_discount_centavos > 0 ? (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Discount</span>
            <span className="text-sm text-slate-800 tabular-nums">−{fmtPeso(s.total_discount_centavos, hydration.currency)}</span>
          </div>
        ) : null}
        {s.net_charged_centavos != null ? (
          <div className="flex items-center justify-between border-t border-slate-100 pt-2 mt-2">
            <span className="text-sm font-semibold text-slate-800">Net charged</span>
            <span className="text-sm font-bold text-slate-900 tabular-nums">{fmtPeso(s.net_charged_centavos, hydration.currency)}</span>
          </div>
        ) : null}
        {s.total_paid_centavos != null ? (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Paid</span>
            <span className="text-sm text-slate-800 tabular-nums">{fmtPeso(s.total_paid_centavos, hydration.currency)}</span>
          </div>
        ) : null}
        {s.credit_applied_centavos > 0 ? (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Credit applied</span>
            <span className="text-sm text-slate-800 tabular-nums">{fmtPeso(s.credit_applied_centavos, hydration.currency)}</span>
          </div>
        ) : null}
        {s.refund_issued_centavos > 0 ? (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Refund issued</span>
            <span className="text-sm text-slate-800 tabular-nums">{fmtPeso(s.refund_issued_centavos, hydration.currency)}</span>
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <button type="button" onClick={() => navigate("/calendar")}
          data-testid="done-back-calendar"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg">
          <ArrowLeft className="w-4 h-4" />Back to Calendar
        </button>
        {canSignReceipt ? (
          <button type="button" onClick={() => setReceiptSignOpen(true)}
            disabled={receiptSignatureBusy}
            data-testid="done-sign-receipt"
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-teal-800 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg disabled:opacity-50">
            {receiptSignatureBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
            Sign receipt
          </button>
        ) : receiptSigned ? (
          <span
            data-testid="done-receipt-signed"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg"
          >
            <CheckCircle2 className="w-4 h-4" />
            Receipt signed
          </span>
        ) : <div className="ml-auto" />}
        {r.receipt_number ? (
          <button type="button" onClick={handlePrint}
            data-testid="done-print"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg">
            <PrinterIcon className="w-4 h-4" />Print receipt
          </button>
        ) : null}
      </div>

      {/* Hidden printable receipt (renders via portal at body root). */}
      {r.receipt_number ? (
        <ReceiptPrint
          result={result}
          hydration={hydration}
          items={items}
          paymentState={paymentState}
          breakdown={breakdown}
          planBillingState={planBillingState}
          signature={receiptSignature}
        />
      ) : null}

      <SignatureDialog
        open={receiptSignOpen}
        onOpenChange={(next) => {
          if (!next && !receiptSignatureBusy) setReceiptSignOpen(false);
        }}
        context={{
          title: "Sign checkout receipt",
          documentNumber: r.receipt_number,
          patientName: hydration?.patient?.full_name,
        }}
        defaultSignerName={hydration?.patient?.full_name || ""}
        busy={receiptSignatureBusy}
        onSubmit={handleReceiptSignatureSubmit}
      />
    </div>
  );
}

// =====================================================================
// FutureNeedsFollowUp — Step 3 core.
// Lists every Open future need on the patient (carried in from chair-
// side painting + items the doctor flagged "Future need" today) and
// prompts the front desk to decide one of: Book / Recall / Decline.
//
// Recall: queue UI ships later — for now the button records intent in
// a toast and leaves the need Open. Once the recall queue lands, every
// Open need will surface there automatically, so nothing is lost.
// =====================================================================
// Smart duration heuristic for bundled surfaces.
//   * First surface = full procedure duration (e.g. 30 min for a composite)
//   * Each additional surface on the SAME tooth + SAME procedure = 50% of
//     full duration (the prep / anesthesia / setup is already done — only
//     the placement/finish time repeats).
// Falls back to 30 min if the procedure has no suggested_duration_minutes.
// Result is rounded up to the nearest 5 minutes for clean schedule slots.
function estimateBundleMinutes(bundle, procDurationMin) {
  const base = procDurationMin && procDurationMin > 0 ? procDurationMin : 30;
  const surfaces = Math.max(1, bundle.needs.length);
  const raw = base + (surfaces - 1) * (base * 0.5);
  return Math.ceil(raw / 5) * 5;
}

function buildFutureNeedBundlesFromNeeds(needs, procMap = {}) {
  const byTooth = new Map();
  for (const n of (needs || [])) {
    const toothKey = n.tooth_number || "General";
    if (!byTooth.has(toothKey)) byTooth.set(toothKey, new Map());
    const byProc = byTooth.get(toothKey);
    const procKey = n.procedure_id;
    if (!byProc.has(procKey)) byProc.set(procKey, []);
    byProc.get(procKey).push(n);
  }
  const out = [];
  for (const [toothKey, byProc] of byTooth) {
    for (const [, items] of byProc) {
      const surfaceOrder = ["M", "O", "D", "B", "L", "I"];
      const sorted = [...items].sort((a, b) => {
        const ai = surfaceOrder.indexOf((a.surface || "").toUpperCase());
        const bi = surfaceOrder.indexOf((b.surface || "").toUpperCase());
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
      const bundle = {
        key: `${toothKey}__${sorted[0].procedure_id}`,
        procedure_id: sorted[0].procedure_id,
        procedure_name: sorted[0].procedure_name,
        color_hex: sorted[0].color_hex,
        tooth_number: sorted[0].tooth_number,
        surfaces: sorted.map((n) => n.surface).filter(Boolean),
        subtotalCents: sorted.reduce((s, n) => s + Number(n.default_fee_centavos || 0), 0),
        needs: sorted,
        doctorRequestedDays: pickDoctorRequestedDays(sorted),
        tutorial_id: sorted[0].tutorial_id || null,
        book_tutorial_id: sorted[0].book_tutorial_id || null,
        handled_tutorial_id: sorted[0].handled_tutorial_id || null,
      };
      bundle.followUpKind = isPostOpFollowUpBundle(bundle) ? "post_op_follow_up" : "future_need";
      const procRecord = procMap[bundle.procedure_id];
      bundle.estimatedMinutes = estimateBundleMinutes(bundle, procRecord?.duration);
      bundle.category = procRecord?.category || null;
      out.push(bundle);
    }
  }
  return out.sort((a, b) => {
    const at = a.tooth_number || "General";
    const bt = b.tooth_number || "General";
    if (at === "General") return 1;
    if (bt === "General") return -1;
    return Number(at) - Number(bt) || String(at).localeCompare(String(bt));
  });
}

function fmtMinutes(m) {
  if (!m || m < 1) return "—";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h} hr` : `${h} hr ${r} min`;
}

// Relative-time tag for the "flagged today / open since X" provenance
// label on each future-need row. Helps the front desk eyeball which
// items came out of *this* visit (low-friction follow-up — patient is
// still in chair-side mindset) vs old standing needs the patient has
// been ducking (higher friction; needs softer framing).
function fmtNeedAge(createdAtIso) {
  if (!createdAtIso) return null;
  const created = new Date(createdAtIso);
  if (Number.isNaN(created.getTime())) return null;
  const ageMs   = Date.now() - created.getTime();
  const ageDays = Math.floor(ageMs / (24 * 60 * 60 * 1000));
  if (ageDays <= 0) return { label: "flagged today",         tone: "fresh" };
  if (ageDays === 1) return { label: "flagged yesterday",    tone: "fresh" };
  if (ageDays <= 7)  return { label: `${ageDays} days old`,  tone: "warm" };
  if (ageDays <= 30) return { label: `${ageDays} days old`,  tone: "stale" };
  return {
    label: `open since ${created.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`,
    tone: "stale",
  };
}

function pickDoctorRequestedDays(needs) {
  const values = Array.from(new Set(
    (needs ?? [])
      .map((n) => Number(n?.follow_up_after_days))
      .filter((n) => Number.isFinite(n) && n > 0),
  ));
  if (values.length === 0) return null;
  return Math.min(...values);
}

// =====================================================================
// PostOpSuggestionsSection — Step 3 bookable post-op follow-up cards.
//
// Per the chief-dentist clinical review: the patient is at the desk
// post-procedure, this is the highest-conversion booking moment.
// Treating post-op as a passive "Auto-create at checkout" advisory
// loses that moment. So each suggestion renders identically to the
// Next Plan Visit card: green Book CTA + outlined Unsure.
//
// Decision flow:
//   * Book   → opens BookFutureNeedDialog with appointmentStatus=Pencil.
//              Pencil reserves chair time. At commit, the trigger
//              creates the treatment_need; post-commit we link the
//              new need to the Pencil via schedule_treatment_need
//              and the Pencil promotes to Scheduled.
//   * Unsure → stages a defer-to-recall disposition. Auto-need is
//              still created at commit (status=Open) for Recall
//              Center pickup. No Pencil.
//
// Complete checkout is gated until every card has Book or Unsure.
// =====================================================================
function PostOpSuggestionsSection({
  items, scheduled, postOpByProcedure, followupWaivers, patientName,
  patientId, currency, defaultDoctorId, defaultChairId,
  priorVisitDate,
  decisions, setDecisions, onResolutionChange,
  planVisitBookings, setPlanVisitBookings,
  demoMode = false,
}) {
  // One suggestion per Done item whose procedure has post_op_followup
  // enabled and that the doctor didn't wave off at chairside. Planned
  // rows key off procedure_visit_id; impromptu checkout rows key off
  // their local item id so they still flow into the atomic commit.
  const suggestions = useMemo(() => {
    return buildPostOpSuggestions({ items, scheduled, postOpByProcedure, followupWaivers });
  }, [items, scheduled, postOpByProcedure, followupWaivers]);

  // Publish resolution stats to the parent's completion gate.
  useEffect(() => {
    const total = suggestions.length;
    const resolved = suggestions.filter((s) => decisions?.[s.key]?.kind).length;
    onResolutionChange?.({ ready: true, total, unresolved: total - resolved });
  }, [suggestions, decisions, onResolutionChange]);

  // Unbooked plan visit info — the "Book together" CTA appears when
  // BOTH a post-op suggestion AND an unbooked plan visit exist for
  // this patient. Pulled directly via PostgREST (the same data lives
  // inside PlanVisitFollowUp but lifting it through props would force
  // a render coupling we don't need yet). Refreshed when patient or
  // existing bookings change.
  const [unbookedPlanSlot, setUnbookedPlanSlot] = useState(null);
  useEffect(() => {
    if (demoMode) { setUnbookedPlanSlot(null); return undefined; }
    if (!patientId) { setUnbookedPlanSlot(null); return; }
    let cancelled = false;
    (async () => {
      // Three flat queries (no nested PostgREST select) because the
      // codebase does not declare FK constraints between these tables;
      // the schema cache can't follow embedded selects.
      const { data: plans, error: e1 } = await supabase
        .from("treatment_plans")
        .select("id, plan_code, status, created_at, phase_meta")
        .eq("patient_id", patientId)
        .in("status", ["Needs Booking", "Scheduled", "In Progress", "Accepted", "Partially Accepted"])
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (e1 || !Array.isArray(plans) || plans.length === 0) { setUnbookedPlanSlot(null); return; }

      const planIdList = plans.map((p) => p.id);
      const { data: itemsRaw, error: e2 } = await supabase
        .from("treatment_plan_items")
        .select("id, treatment_plan_id, status, procedure_id, name_snapshot, fee_centavos")
        .in("treatment_plan_id", planIdList);
      if (cancelled) return;
      if (e2) { setUnbookedPlanSlot(null); return; }
      const itemsByPlan = new Map();
      const itemIdList = [];
      for (const it of (itemsRaw || [])) {
        if (!itemsByPlan.has(it.treatment_plan_id)) itemsByPlan.set(it.treatment_plan_id, []);
        itemsByPlan.get(it.treatment_plan_id).push(it);
        itemIdList.push(it.id);
      }
      if (itemIdList.length === 0) { setUnbookedPlanSlot(null); return; }

      const { data: visitsRaw, error: e3 } = await supabase
        .from("procedure_visits")
        .select("id, treatment_plan_item_id, sequence, template_label, appointment_id, completed_at, outcome, phase_number, lab_gap_days")
        .in("treatment_plan_item_id", itemIdList);
      if (cancelled) return;
      if (e3) { setUnbookedPlanSlot(null); return; }

      const appointmentIds = Array.from(new Set(
        (visitsRaw || []).map((v) => v.appointment_id).filter(Boolean),
      ));
      let appointmentStartById = new Map();
      if (appointmentIds.length > 0) {
        const { data: apptsRaw } = await supabase
          .from("appointments")
          .select("id, scheduled_start")
          .in("id", appointmentIds);
        if (cancelled) return;
        appointmentStartById = new Map(
          (apptsRaw || []).map((appt) => [appt.id, appt.scheduled_start]),
        );
      }

      const visitsByItem = new Map();
      for (const v of (visitsRaw || [])) {
        if (!visitsByItem.has(v.treatment_plan_item_id)) visitsByItem.set(v.treatment_plan_item_id, []);
        visitsByItem.get(v.treatment_plan_item_id).push({
          ...v,
          appointment_starts_at: appointmentStartById.get(v.appointment_id) || null,
        });
      }

      // Pick the first plan with a truly unbooked visit (no appointment,
      // not completed, no outcome), parent item still bookable, and not
      // already pencilled in this checkout session.
      const alreadyPencilled = new Set(
        Array.from(planVisitBookings?.keys?.() || [])
      );
      for (const plan of plans) {
        if (alreadyPencilled.has(plan.id)) continue;
        const items = itemsByPlan.get(plan.id) || [];
        const visits = [];
        const visitsLedger = [];
        for (const item of items) {
          if (item.status === "Declined" || item.status === "Cancelled") continue;
          for (const v of (visitsByItem.get(item.id) || [])) {
            const decorated = {
              id:                    v.id,
              item_id:               item.id,
              procedure_name:        item.name_snapshot,
              fee_centavos:          item.fee_centavos,
              sequence:              v.sequence,
              template_label:        v.template_label,
              phase_number:          v.phase_number,
              lab_gap_days:          v.lab_gap_days,
              appointment_id:        v.appointment_id,
              appointment_starts_at: v.appointment_starts_at,
              completed_at:          v.completed_at,
              outcome:               v.outcome,
            };
            visitsLedger.push(decorated);
            if (v.appointment_id || v.completed_at || v.outcome) continue;
            visits.push(decorated);
          }
        }
        if (visits.length > 0) {
          visits.sort((a, b) => {
            const ap = a.phase_number ?? 1, bp = b.phase_number ?? 1;
            if (ap !== bp) return ap - bp;
            return (a.sequence ?? 1) - (b.sequence ?? 1);
          });
          setUnbookedPlanSlot({
            plan_id:   plan.id,
            plan_code: plan.plan_code,
            plan,
            visits,
            visitsLedger,
          });
          return;
        }
      }
      setUnbookedPlanSlot(null);
    })();
    return () => { cancelled = true; };
  }, [demoMode, patientId, planVisitBookings]);

  // Open / staged-decision modal state
  const [bookingFor, setBookingFor] = useState(null);  // single suggestion
  const [bookingTogether, setBookingTogether] = useState(null); // { postOpSuggestion, planSlot }
  const [unsureFor, setUnsureFor]   = useState(null);
  const [unsureReason, setUnsureReason] = useState("");

  if (suggestions.length === 0) return null;

  const fmtDate = (d) => {
    try {
      return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    } catch { return ""; }
  };

  // Synthetic bundle for BookFutureNeedDialog. The dialog renders
  // around `bundle.procedure_name + tooth + surfaces` and uses
  // `bundle.estimatedMinutes` for the slot-block size.
  const bookBundle = bookingFor ? {
    procedure_name:   bookingFor.followup,
    tooth_number:     null,
    surfaces:         [],
    needs:            [{
      source_procedure_visit_id: bookingFor.source_procedure_visit_id,
      procedure_name:            bookingFor.followup,
      follow_up_after_days:      bookingFor.days,
      notes:                     `Auto-suggested follow-up after ${bookingFor.sourceName}.`,
    }],
    doctorRequestedDays: bookingFor.days,
    estimatedMinutes: bookingFor.followup_duration_min,
    category:         bookingFor.followup_category,
    isAll:            false,
  } : null;

  const stageBook = (suggestion, apptId, label) => {
    setDecisions?.((prev) => ({
      ...prev,
      [suggestion.key]: {
        kind:               "book",
        appointment_id:     apptId,
        appointment_label:  label,
        followup_procedure_id:    suggestion.followup_procedure_id,
        source_procedure_visit_id: suggestion.source_procedure_visit_id || undefined,
        source_item_key:           suggestion.source_item_key || undefined,
        source_procedure_name:     suggestion.sourceName,
        source_tooth_number:       suggestion.sourceTooth || undefined,
        follow_up_after_days:      suggestion.days,
      },
    }));
  };

  const stageUnsure = (suggestion, reason) => {
    setDecisions?.((prev) => ({
      ...prev,
      [suggestion.key]: {
        kind:                       "unsure",
        reason,
        source_procedure_visit_id:  suggestion.source_procedure_visit_id || undefined,
        source_item_key:            suggestion.source_item_key || undefined,
        source_procedure_name:      suggestion.sourceName,
        source_tooth_number:        suggestion.sourceTooth || undefined,
        followup_procedure_id:      suggestion.followup_procedure_id,
        follow_up_after_days:       suggestion.days,
      },
    }));
  };

  const clearDecision = (key) => {
    setDecisions?.((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // "Book together" — when the patient has both a post-op suggestion
  // AND an unbooked Tx Plan visit, surface a single CTA to book both
  // on the same day. Chief-dentist rule: one trip is better than two
  // when timing allows.
  const showBookTogether = suggestions.length > 0 && unbookedPlanSlot && unbookedPlanSlot.visits.length > 0;
  const combinedBundle = showBookTogether && bookingTogether ? (() => {
    // First post-op + first unbooked plan visit. Duration is the sum
    // so the slot grid reserves enough chair time.
    const postOp = bookingTogether.postOpSuggestion;
    const planVisits = bookingTogether.planSlot.visits;
    const totalMinutes = (Number(postOp.followup_duration_min) || 30) + planVisits.length * 30;
    return {
      procedure_name:   `${postOp.followup} + ${planVisits[0].procedure_name}`,
      tooth_number:     null,
      surfaces:         [],
      needs:            [{
        source_procedure_visit_id: postOp.source_procedure_visit_id,
        procedure_name:            postOp.followup,
        follow_up_after_days:      postOp.days,
        notes:                     `Auto-suggested follow-up after ${postOp.sourceName}.`,
      }],
      doctorRequestedDays: postOp.days,
      estimatedMinutes: totalMinutes,
      category:         postOp.followup_category,
      isAll:            false,
    };
  })() : null;

  return (
    <>
      <section className="bg-white rounded-2xl shadow-sm" data-testid="postop-suggestions-section">
        <div className="px-5 py-3 border-b border-slate-100 flex items-baseline justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Post-op follow-ups</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              The patient is here. Book the post-op now or mark timing as Unsure.
            </p>
          </div>
          <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
            {suggestions.length} {suggestions.length === 1 ? "new" : "new"}
          </span>
        </div>
        {showBookTogether ? (
          <div className="px-5 py-3 border-b border-slate-100 bg-teal-50/40 flex items-center gap-3 flex-wrap"
               data-testid="postop-book-together-row">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-teal-900">
                Book together with {unbookedPlanSlot.plan_code || "Tx Plan"} next visit?
              </p>
              <p className="text-[11px] text-teal-800/80 mt-0.5">
                Patient comes in once for both the post-op check and {
                  unbookedPlanSlot.visits.length === 1
                    ? unbookedPlanSlot.visits[0].procedure_name
                    : `${unbookedPlanSlot.visits.length} sub-visits`
                }.
              </p>
            </div>
            <button type="button"
              onClick={() => setBookingTogether({
                postOpSuggestion: suggestions[0],
                planSlot:         unbookedPlanSlot,
              })}
              data-testid="postop-book-together"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg shadow-sm">
              <CalendarPlus className="w-3.5 h-3.5" />Book together
            </button>
          </div>
        ) : null}
        <ul className="divide-y divide-slate-100">
          {suggestions.map((s) => {
            const decision = decisions?.[s.key];
            const testKey = safeTestId(s.key);
            return (
              <li key={s.key}
                  data-testid={`postop-suggestion-${testKey}`}
                  className="px-5 py-3 flex items-center gap-3 flex-wrap">
                <span className="w-2 h-2 rounded-full shrink-0 bg-emerald-500" />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${decision ? "text-slate-500" : "text-slate-800"}`}>
                    {s.followup}
                    <span className="text-slate-400 font-normal"> · after {s.sourceName}</span>
                  </p>
                  <p className="text-[11px] text-slate-500 tabular-nums">
                    ~{s.days}d · {fmtDate(s.dueDate)}
                  </p>
                </div>
                {decision?.kind === "book" ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                      <Check className="w-3 h-3" />
                      {decision.appointment_label || "Pencilled"}
                    </span>
                    <button type="button" onClick={() => clearDecision(s.key)}
                      data-testid={`postop-undo-book-${testKey}`}
                      title="Undo — release the pencil"
                      className="text-[11px] text-slate-500 hover:text-slate-700">
                      Undo
                    </button>
                  </div>
                ) : decision?.kind === "unsure" ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      Unsure: {decision.reason || "deferred"}
                    </span>
                    <button type="button" onClick={() => clearDecision(s.key)}
                      data-testid={`postop-undo-unsure-${testKey}`}
                      className="text-[11px] text-slate-500 hover:text-slate-700">
                      Undo
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button type="button" onClick={() => setBookingFor(s)}
                      data-testid={`postop-book-${testKey}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg shadow-sm">
                      <CalendarPlus className="w-3.5 h-3.5" />Book post-op
                    </button>
                    <button type="button" onClick={() => { setUnsureReason(""); setUnsureFor(s); }}
                      data-testid={`postop-unsure-${testKey}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-300">
                      Unsure
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {/* Book modal — Pencil-status, mirrors PlanVisitFollowUp's flow */}
      {bookingFor ? (
        <BookFutureNeedDialog
          open={true}
          onOpenChange={(v) => { if (!v) setBookingFor(null); }}
          bundle={bookBundle}
          patientId={patientId}
          patientName={patientName}
          defaultDoctorId={defaultDoctorId}
          defaultChairId={defaultChairId}
          appointmentStatus="Pencil"
          hideHintForVisitOne={true}
          priorVisitDate={priorVisitDate}
          doctorRequestedDays={followUpRequestedDays(bookBundle)}
          headingOverride={bookBundle ? followUpBookingHeading(bookBundle) : null}
          onBooked={async (apptId) => {
            // Pencil exists now; fetch the slot label and stage it.
            const { data: appt } = await supabase
              .from("appointments")
              .select("scheduled_start, scheduled_end")
              .eq("id", apptId)
              .maybeSingle();
            let label = "Pencilled";
            try {
              if (appt?.scheduled_start) {
                const start = new Date(appt.scheduled_start);
                label = start.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
                  " " + start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
              }
            } catch { /* keep default */ }
            stageBook(bookingFor, apptId, label);
            setBookingFor(null);
          }}
        />
      ) : null}

      {/* Book together modal — one Pencil that covers BOTH the post-op
          check AND the unbooked plan visits. On success we stage TWO
          things against the same appointment_id: the post-op decision
          and the planVisitBookings entry. Commit-time wiring (existing
          plan-visit promotion + post-op need link) treats them as the
          same trip. */}
      {bookingTogether && combinedBundle ? (
        (() => {
          const phaseCadence = resolvePhaseCadenceForVisits({
            plan: bookingTogether.planSlot.plan,
            visits: bookingTogether.planSlot.visitsLedger || [],
            selectedVisits: bookingTogether.planSlot.visits || [],
          });
          const priority = resolvePostOpPlanBookingPriority({
            postOpSuggestion: bookingTogether.postOpSuggestion,
            phaseCadence,
            priorVisitDate,
          });
          return (
        <BookFutureNeedDialog
          open={true}
          onOpenChange={(v) => { if (!v) setBookingTogether(null); }}
          bundle={combinedBundle}
          patientId={patientId}
          patientName={patientName}
          defaultDoctorId={defaultDoctorId}
          defaultChairId={defaultChairId}
          appointmentStatus="Pencil"
          hideHintForVisitOne={true}
          priorVisitDate={priority.priorVisitDate}
          doctorRequestedDays={priority.doctorRequestedDays}
          headingOverride="Book post-op + next plan visit"
          subtitleDurationContext={`${bookingTogether.planSlot.plan_code || "Tx Plan"} · ${combinedBundle.procedure_name}`}
          appointmentSummary={(
            <PostOpPlanPrioritySummary
              postOpSuggestion={bookingTogether.postOpSuggestion}
              planSlot={bookingTogether.planSlot}
              priority={priority}
            />
          )}
          onBooked={async (apptId) => {
            const { data: appt } = await supabase
              .from("appointments")
              .select("scheduled_start, scheduled_end")
              .eq("id", apptId)
              .maybeSingle();
            let label = "Pencilled";
            try {
              if (appt?.scheduled_start) {
                const start = new Date(appt.scheduled_start);
                label = start.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
                  " " + start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
              }
            } catch { /* keep default */ }

            const slot = bookingTogether.planSlot;
            const postOp = bookingTogether.postOpSuggestion;

            // Stage the post-op decision pointing at the same appointment.
            stageBook(postOp, apptId, label);

            // Stage the plan visits onto that same appointment. Mirrors
            // PlanVisitFollowUp's setBookings write so commit-time
            // promotion + procedure_visit linking treats this as the
            // canonical "next visit booked" entry for that plan.
            setPlanVisitBookings?.((prev) => {
              const next = new Map(prev);
              next.set(slot.plan_id, {
                appointment_id:    apptId,
                appointment_label: label,
                visit_ids:    slot.visits.map((v) => v.id),
                visit_labels: slot.visits.map(
                  (v) => `${v.procedure_name}${v.template_label ? ` · ${v.template_label}` : ""}`
                ),
              });
              return next;
            });

            setBookingTogether(null);
          }}
        />
          );
        })()
      ) : null}

      {/* Unsure modal */}
      {unsureFor ? (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             data-testid="postop-unsure-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
            <p className="text-sm font-semibold text-slate-900">Defer the post-op check?</p>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              {unsureFor.followup} after {unsureFor.sourceName}. Picks up in Recall Center after checkout.
            </p>
            <input type="text" value={unsureReason} autoFocus
              onChange={(e) => setUnsureReason(e.target.value)}
              placeholder="Why defer? (e.g. patient needs to check schedule)"
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-amber-500" />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button type="button" onClick={() => { setUnsureFor(null); setUnsureReason(""); }}
                className="px-3 py-1.5 text-xs border border-slate-200 rounded text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" disabled={!unsureReason.trim()}
                onClick={() => {
                  stageUnsure(unsureFor, unsureReason.trim());
                  setUnsureFor(null);
                  setUnsureReason("");
                }}
                className="px-3 py-1.5 text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-40">
                Mark Unsure
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function PostOpPlanPrioritySummary({ postOpSuggestion, planSlot, priority }) {
  const postOpDays = Number(priority?.doctorRequestedDays || postOpSuggestion?.days || 0) || null;
  const txDays = Number(priority?.txPlanOffsetDays || 0) || null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-700" />
        <div className="space-y-1.5 min-w-0">
          <p className="font-semibold">Post-op date is the clinical anchor for this booking.</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <div className="rounded-md bg-white/70 border border-amber-100 px-2 py-1.5">
              <p className="text-[10px] uppercase tracking-wide font-bold text-amber-800">Required</p>
              <p className="font-semibold text-slate-900">
                {postOpSuggestion?.followup || "Post-op check"}
                {postOpDays ? ` · within ${postOpDays} day${postOpDays === 1 ? "" : "s"}` : ""}
              </p>
            </div>
            <div className="rounded-md bg-white/70 border border-amber-100 px-2 py-1.5">
              <p className="text-[10px] uppercase tracking-wide font-bold text-amber-800">Preferred</p>
              <p className="font-semibold text-slate-900">
                {planSlot?.plan_code || "Tx Plan"} next visit
                {txDays ? ` · usually ~${txDays} day${txDays === 1 ? "" : "s"}` : ""}
              </p>
            </div>
          </div>
          {priority?.conflict ? (
            <p className="text-[11px] text-amber-900">
              Tx Plan timing is secondary here. Book the post-op window first; keep the plan work on this visit only if clinically appropriate.
            </p>
          ) : (
            <p className="text-[11px] text-amber-900">
              These can share one appointment if the doctor is comfortable doing the plan work during the post-op visit.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function FutureNeedsFollowUp({
  patientId, patientName, currency, navigate,
  demoMode = false,
  demoNeeds = null,
  demoProcMap = null,
  dispositions, setDispositions, onTotalsChange,
  defaultDoctorId, defaultChairId, priorVisitDate,
  planVisitBookings,
  onPlanVisitFutureNeedsChange,
  orthoNextVisit,
  orthoNextVisitDecision,
  onOrthoBundled,
}) {
  const [needs, setNeeds]   = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError]   = useState(null);

  // Procedure → { duration, category } lookup, fetched once. Empty
  // until ready; bundle math falls back to a 30-min default while
  // loading. The category drives the smart-default urgency window
  // for new bookings (e.g. Restorative → 1-2 weeks out).
  const [procMap, setProcMap] = useState({});

  // Selection is now an ARRAY of needs — same tooth + same procedure
  // collapses into one bundled row, so a single Book/Decline action
  // dispositions every surface at once.
  const [bookBundle, setBookBundle]       = useState(null);
  const [declineBundle, setDeclineBundle] = useState(null);

  const reload = useCallback(async () => {
    if (demoMode) {
      setNeeds(Array.isArray(demoNeeds) ? demoNeeds : []);
      setLoaded(true);
      setError(null);
      return;
    }
    if (!patientId) { setLoaded(true); return; }
    const { data, error: e } = await supabase.rpc("list_open_treatment_needs", { p_patient_id: patientId });
    if (e) { setError(e.message ?? "Could not load future needs."); setLoaded(true); return; }
    setNeeds(Array.isArray(data) ? data : []);
    setLoaded(true);
  }, [demoMode, demoNeeds, patientId]);

  useEffect(() => { reload(); }, [reload]);

  // One-shot fetch of clinic procedures so we can compute duration
  // estimates AND category-driven smart booking defaults client-side.
  // Doesn't gate the panel render.
  useEffect(() => {
    if (demoMode) {
      setProcMap(demoProcMap || {});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("list_clinic_procedures");
      if (cancelled) return;
      const m = {};
      for (const p of (data ?? [])) {
        m[p.id] = {
          duration: Number(p.suggested_duration_minutes || 0),
          category: p.category || null,
          name:     p.name || null,
        };
      }
      setProcMap(m);
    })();
    return () => { cancelled = true; };
  }, [demoMode, demoProcMap]);

  // Smart grouping — group by tooth, then bundle same-procedure rows
  // within a tooth into a single row with a comma-list of surfaces.
  // Composite Restoration · O + Composite Restoration · L on tooth #14
  // becomes one row "Composite Restoration · O, L" with one set of
  // action buttons. Same patient job, same booking decision.
  const groupedToothBundles = useMemo(() => {
    const byTooth = new Map();
    for (const n of needs) {
      const toothKey = n.tooth_number || "General";
      if (!byTooth.has(toothKey)) byTooth.set(toothKey, new Map());
      const byProc = byTooth.get(toothKey);
      const procKey = n.procedure_id;
      if (!byProc.has(procKey)) byProc.set(procKey, []);
      byProc.get(procKey).push(n);
    }
    const out = [];
    for (const [toothKey, byProc] of byTooth) {
      const bundles = [];
      for (const [, items] of byProc) {
        // Stable surface order: M / O / D / B / L / I (oral convention)
        const SURFACE_ORDER = ["M", "O", "D", "B", "L", "I"];
        const sorted = [...items].sort((a, b) => {
          const ai = SURFACE_ORDER.indexOf((a.surface || "").toUpperCase());
          const bi = SURFACE_ORDER.indexOf((b.surface || "").toUpperCase());
          if (ai === -1 && bi === -1) return 0;
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });
        const bundle = {
          key: `${toothKey}__${sorted[0].procedure_id}`,
          procedure_id:   sorted[0].procedure_id,
          procedure_name: sorted[0].procedure_name,
          color_hex:      sorted[0].color_hex,
          tooth_number:   sorted[0].tooth_number,
          surfaces:       sorted.map((n) => n.surface).filter(Boolean),
          subtotalCents:  sorted.reduce((s, n) => s + Number(n.default_fee_centavos || 0), 0),
          needs:          sorted,
          doctorRequestedDays: pickDoctorRequestedDays(sorted),
          tutorial_id: sorted[0].tutorial_id || null,
          book_tutorial_id: sorted[0].book_tutorial_id || null,
          handled_tutorial_id: sorted[0].handled_tutorial_id || null,
        };
        bundle.followUpKind = isPostOpFollowUpBundle(bundle) ? "post_op_follow_up" : "future_need";
        const procRecord = procMap[bundle.procedure_id];
        bundle.estimatedMinutes = estimateBundleMinutes(
          bundle, procRecord?.duration);
        bundle.category = procRecord?.category || null;
        // Bundle's "age" = the OLDEST need in it. Provenance tag uses
        // this so a freshly-flagged surface isn't hidden behind an
        // older one in the same bundle.
        let oldest = null;
        for (const n of sorted) {
          if (n.created_at && (!oldest || new Date(n.created_at) < new Date(oldest))) {
            oldest = n.created_at;
          }
        }
        bundle.oldestCreatedAt = oldest;
        bundles.push(bundle);
      }
      out.push([toothKey, bundles]);
    }
    return out.sort(([a], [b]) => {
      if (a === "General") return 1;
      if (b === "General") return -1;
      return Number(a) - Number(b) || String(a).localeCompare(String(b));
    });
  }, [needs, procMap]);

  const totalCents = useMemo(
    () => needs.reduce((s, n) => s + Number(n.default_fee_centavos || 0), 0),
    [needs],
  );

  // Total scheduled time across bundles. Smart-adjusted means each
  // bundle individually applies the "first surface full, +50% per
  // additional surface" rule. Different bundles still get the full
  // first-surface time even if the procedure is the same on a
  // different tooth — different tooth means setup is partially
  // re-done, so we don't bundle across teeth at v1.
  const totalMinutes = useMemo(
    () => groupedToothBundles.reduce(
      (s, [, bundles]) => s + bundles.reduce((bs, b) => bs + (b.estimatedMinutes || 0), 0),
      0,
    ),
    [groupedToothBundles],
  );

  const orthoBundleNeed = useMemo(
    () => buildBundleableOrthoNeed(orthoNextVisit, orthoNextVisitDecision),
    [orthoNextVisit, orthoNextVisitDecision],
  );

  const displayTotalCents = totalCents + Number(orthoBundleNeed?.subtotalCents || 0);
  const displayTotalMinutes = totalMinutes + Number(orthoBundleNeed?.estimatedMinutes || 0);

  // Synthetic "all" bundle — every Open need flattened into one
  // bundle. Used by the Book all / Decline all buttons. surfaces[]
  // stays empty so the dialog doesn't try to display a comma-list
  // for a bundle that spans multiple teeth + procedures.
  // Category is the most-urgent category in the set (drives smart
  // booking defaults — see categoryDefaultDays in dialog).
  const allBundle = useMemo(() => {
    const cats = new Set();
    for (const [, bundles] of groupedToothBundles) {
      for (const b of bundles) {
        if (b.category) cats.add(b.category);
      }
    }
    const out = {
      key: "__all__",
      procedure_id:   null,
      procedure_name: needs.length === 1 ? needs[0].procedure_name : `${needs.length} ${followUpItemNounPlural({ needs }, needs.length)}`,
      color_hex:      null,
      tooth_number:   null,
      surfaces:       [],
      subtotalCents:  totalCents,
      needs:          needs,
      estimatedMinutes: totalMinutes,
      isAll:          true,
      categories:     [...cats],
      doctorRequestedDays: pickDoctorRequestedDays(needs),
    };
    out.followUpKind = isPostOpFollowUpBundle(out) ? "post_op_follow_up" : "future_need";
    return out;
  }, [needs, totalCents, totalMinutes, groupedToothBundles]);

  const bookAllBundle = useMemo(
    () => withOrthoBundle(allBundle, orthoBundleNeed),
    [allBundle, orthoBundleNeed],
  );

  const planVisitFutureNeedOptions = useMemo(() => {
    const out = [];
    for (const [, bundles] of groupedToothBundles) {
      for (const b of bundles) {
        if (!dispositions?.has(b.key)) out.push(b);
      }
    }
    return out;
  }, [groupedToothBundles, dispositions]);

  useEffect(() => {
    onPlanVisitFutureNeedsChange?.(loaded ? planVisitFutureNeedOptions : []);
  }, [loaded, planVisitFutureNeedOptions, onPlanVisitFutureNeedsChange]);

  // Already-staged Tx Plan visit appointments can also carry
  // non-plan future needs. The data stays separate: the plan visit
  // remains linked through procedure_visits, while these dispositions
  // schedule treatment_needs into the same appointment at checkout
  // commit. Most checkouts have only one active plan booking, but the
  // array shape preserves the multi-plan path for later UI polish.
  const planBookingChoices = useMemo(() => {
    if (!planVisitBookings?.entries) return [];
    return Array.from(planVisitBookings.entries())
      .map(([planId, booking]) => ({
        planId,
        appointment_id: booking?.appointment_id || null,
        appointment_label: booking?.appointment_label || null,
        visit_count: Array.isArray(booking?.visit_ids) ? booking.visit_ids.length : 0,
      }))
      .filter((b) => b.appointment_id);
  }, [planVisitBookings]);

  const primaryPlanBooking = planBookingChoices[0] || null;
  const planBookingByPlanId = useMemo(
    () => new Map(planBookingChoices.map((b) => [b.planId, b])),
    [planBookingChoices],
  );
  const planBookingAppointmentIds = useMemo(
    () => new Set(planBookingChoices.map((b) => b.appointment_id).filter(Boolean)),
    [planBookingChoices],
  );

  // If the receptionist moves the Tx Plan appointment after linking a
  // future need to it, keep that staging pointed at the new appointment.
  // If they release the plan booking entirely, clear only those linked
  // future-need decisions. Never cancel the plan Pencil from here; the
  // PlanVisitFollowUp card owns that appointment lifecycle.
  useEffect(() => {
    setDispositions((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [key, d] of prev) {
        if (!d?.plan_visit_booking) continue;
        const replacement = d.plan_id ? planBookingByPlanId.get(d.plan_id) : null;
        if (replacement?.appointment_id) {
          if (
            d.appointment_id !== replacement.appointment_id
            || d.appointment_label !== (replacement.appointment_label || "plan visit appointment")
          ) {
            next.set(key, {
              ...d,
              appointment_id: replacement.appointment_id,
              appointment_label: replacement.appointment_label || "plan visit appointment",
            });
            changed = true;
          }
        } else if (!planBookingAppointmentIds.has(d.appointment_id)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [planBookingByPlanId, planBookingAppointmentIds, setDispositions]);

  // Stage a disposition (book / recall / decline) into parent state.
  // Each click here is a CLIENT-ONLY operation — nothing hits the DB
  // until the front desk clicks "Complete checkout" at the bottom of
  // Step 3. That sends one atomic commit_checkout_with_followup RPC
  // that fires payment + every staged disposition in one transaction.
  // "All" actions fan out into per-bundle entries so undoing one
  // (e.g. one tooth out of a "Book all" set) leaves the others.
  // EXCEPTION: a 'book' disposition came with a Pencil appointment
  // row created by BookFutureNeedDialog. If the receptionist replaces
  // their book (different slot, or different kind), the OLD Pencil
  // is released via cancel_pencil_appointment — otherwise the calendar
  // would show ghost reservations.
  const stageDisposition = useCallback((targetBundle, disposition) => {
    setDispositions((prev) => {
      const next = new Map(prev);
      const releasePrevPencil = (key) => {
        const old = prev.get(key);
        if (
          old?.kind === "book"
          && old.appointment_id
          && disposition.appointment_id !== old.appointment_id
          && !old.plan_visit_booking
          && !old.ortho_installation_booking
          && !planBookingAppointmentIds.has(old.appointment_id)
        ) {
          supabase
            .rpc("cancel_pencil_appointment", { p_appointment_id: old.appointment_id })
            .catch(() => { /* best-effort; the row is harmless if it lingers and gets overridden */ });
        }
      };
      if (targetBundle?.isAll) {
        for (const [, bundles] of groupedToothBundles) {
          for (const b of bundles) {
            releasePrevPencil(b.key);
            next.set(b.key, {
              ...disposition,
              tutorial_id: b.handled_tutorial_id || undefined,
              needIds: b.needs.map((n) => n.id),
            });
          }
        }
      } else {
        releasePrevPencil(targetBundle.key);
        next.set(targetBundle.key, {
          ...disposition,
          tutorial_id: targetBundle.handled_tutorial_id || undefined,
          needIds: targetBundle.needs.map((n) => n.id),
        });
      }
      return next;
    });
  }, [groupedToothBundles, setDispositions, planBookingAppointmentIds]);

  const addToPlanVisitAppointment = useCallback((targetBundle, booking = primaryPlanBooking) => {
    if (!targetBundle || !booking?.appointment_id) return;
    stageDisposition(targetBundle, {
      kind: "book",
      appointment_id: booking.appointment_id,
      appointment_label: booking.appointment_label || "plan visit appointment",
      plan_visit_booking: true,
      plan_id: booking.planId,
    });
  }, [primaryPlanBooking, stageDisposition]);

  const clearDisposition = useCallback((bundleKey) => {
    setDispositions((prev) => {
      const next = new Map(prev);
      const old = prev.get(bundleKey);
      if (
        old?.kind === "book"
        && old.appointment_id
        && !old.plan_visit_booking
        && !old.ortho_installation_booking
        && !planBookingAppointmentIds.has(old.appointment_id)
      ) {
        supabase
          .rpc("cancel_pencil_appointment", { p_appointment_id: old.appointment_id })
          .catch(() => { /* best-effort */ });
      }
      next.delete(bundleKey);
      return next;
    });
  }, [setDispositions, planBookingAppointmentIds]);

  function onRecall(bundle, when) {
    const labelMap = {
      tomorrow: "Tomorrow",
      standard: "In 14 days",
      custom:   when?.label || "Custom date",
    };
    stageDisposition(bundle, {
      kind: "recall",
      due_at:    when?.date ? when.date.toISOString().slice(0, 10) : null,
      due_label: labelMap[when?.kind] || "Recall",
    });
  }

  // Report bundle/needs totals up to the parent so it can gate the
  // sticky "Complete checkout" button.
  useEffect(() => {
    const totalBundles = groupedToothBundles.reduce(
      (s, [, bundles]) => s + bundles.length, 0,
    ) + (orthoBundleNeed ? 1 : 0);
    onTotalsChange?.({ totalBundles, totalNeeds: needs.length + (orthoBundleNeed ? 1 : 0), ready: loaded });
  }, [groupedToothBundles, needs.length, loaded, onTotalsChange, orthoBundleNeed]);

  // Doctor's "come back when" intent across all needs. Computed ABOVE
  // the early returns so the hook ordering stays stable across renders.
  // Three shapes:
  //   * uniform: every doctor-flagged need shares the same window.
  //              "Doctor: come back in 2 weeks."
  //   * mixed:   different windows on different needs. Banner surfaces
  //              the breakdown by window so the operator can decide
  //              whether to book in one slot or split.
  //   * null:    no doctor set anything — banner hidden, dialog falls
  //              back to the category default.
  const doctorWindowSummary = useMemo(() => {
    const withWindow = needs.filter((n) => n.follow_up_after_days != null);
    if (withWindow.length === 0) return null;
    const unique = Array.from(new Set(withWindow.map((n) => n.follow_up_after_days)));
    if (unique.length === 1) {
      return {
        kind: "uniform",
        days: unique[0],
        coversAll: withWindow.length === needs.length,
        flaggedCount: withWindow.length,
        totalCount: needs.length,
      };
    }
    const byWindow = new Map();
    for (const n of withWindow) {
      const k = n.follow_up_after_days;
      if (!byWindow.has(k)) byWindow.set(k, []);
      byWindow.get(k).push(n);
    }
    const entries = Array.from(byWindow.entries()).sort((a, b) => a[0] - b[0]);
    return { kind: "mixed", entries };
  }, [needs]);

  if (!loaded) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-5 text-sm text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading future needs…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-2xl px-4 py-3 text-sm text-rose-700">
        <AlertTriangle className="w-4 h-4 inline mr-1.5" />{error}
      </div>
    );
  }

  // The dedicated Ortho card above owns the only outstanding decision in
  // this shape. Do not render a second generic panel that either says there
  // is no follow-up or shows an empty "0 of 1" list.
  if (needs.length === 0 && orthoBundleNeed) return null;

  if (needs.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-6 text-center" data-testid="followup-empty">
        <div className="w-10 h-10 mx-auto rounded-full bg-slate-100 flex items-center justify-center">
          <CalendarPlus className="w-5 h-5 text-slate-400" />
        </div>
        <p className="mt-2 text-sm font-semibold text-slate-800">No other future needs to handle.</p>
        <p className="text-xs text-slate-500 mt-0.5">This follow-up step is ready.</p>
      </div>
    );
  }

  const totalBundles = groupedToothBundles.reduce(
    (s, [, bundles]) => s + bundles.length, 0,
  ) + (orthoBundleNeed ? 1 : 0);
  const hasMultipleBundles = totalBundles > 1;
  const handledCount = dispositions?.size ?? 0;
  const allHandled   = totalBundles > 0 && handledCount >= totalBundles;

  // Decision tally — for the empty-state celebration after every
  // bundle has been handled. Reads dispositions Map.
  const dispositionTally = (() => {
    let book = 0, recall = 0, decline = 0;
    for (const d of (dispositions?.values() ?? [])) {
      if (d.kind === "book")    book++;
      else if (d.kind === "recall")  recall++;
      else if (d.kind === "decline") decline++;
    }
    return { book, recall, decline };
  })();

  return (
    <>
      {/* === Unified panel — status, hero CTA, and per-tooth list all
          in one card. Sections separated by light dividers, not card
          boundaries, so the page reads as one continuous container
          instead of three competing visual blocks. === */}
      <div
        className="bg-white rounded-2xl shadow-sm overflow-visible"
        data-testid="followup-future-needs"
        data-tutorial-id={demoMode ? "checkout-future-needs-step3" : undefined}
      >
        {/* --- Doctor's "come back when" hand-off banner --- */}
        {doctorWindowSummary ? (
          <div
            className="px-5 py-3 border-b border-amber-200 bg-amber-50/60 flex items-start gap-3"
            data-testid="followup-doctor-window-banner"
          >
            <Stethoscope className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1 text-sm">
              {doctorWindowSummary.kind === "uniform" ? (
                <p className="text-amber-900">
                  <strong className="font-semibold">Doctor is asking for this patient to be back within </strong>
                  <strong className="font-semibold">{describeFollowUpDays(doctorWindowSummary.days)}</strong>
                  {doctorWindowSummary.coversAll
                    ? " for these items."
                    : ` (for ${doctorWindowSummary.flaggedCount} of ${doctorWindowSummary.totalCount} items — the rest have no preference set).`}
                </p>
              ) : (
                <>
                  <p className="text-amber-900 font-semibold">
                    Doctor flagged a mix of windows:
                  </p>
                  <ul className="mt-1 text-xs text-amber-900/90 space-y-0.5">
                    {doctorWindowSummary.entries.map(([days, items]) => (
                      <li key={days}>
                        &bull; <strong className="font-semibold">{describeFollowUpDays(days)}</strong>
                        {" — "}
                        {items.map((n) => `${n.procedure_name}${n.tooth_number ? ` #${n.tooth_number}` : ""}`).join(", ")}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-amber-900/70 mt-1.5">
                    The Book button defaults to the most urgent. You can still pick a single slot for everything if the patient prefers.
                  </p>
                </>
              )}
            </div>
          </div>
        ) : null}

        {/* --- Status section (always visible) --- */}
        <div className="px-5 py-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-slate-900">
                {allHandled
                  ? <><Check className="w-4 h-4 inline text-emerald-600 mr-1" />Patient's all set!</>
                  : <>{handledCount} of {totalBundles} needs handled</>}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {allHandled
                  ? "Click Complete checkout below to wrap up."
                  : "Book, recall, or decline each one. Nothing is saved until Complete checkout."}
              </p>
            </div>
            <div className="ml-auto text-right shrink-0">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Estimated total</p>
              <p className="text-base font-bold text-slate-900 tabular-nums">{fmtPeso(displayTotalCents, currency)}</p>
              {displayTotalMinutes > 0 ? (
                <p className="text-xs text-slate-500 tabular-nums">~{fmtMinutes(displayTotalMinutes)} chair time</p>
              ) : null}
            </div>
          </div>

          {totalBundles > 0 ? (
            <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden" data-testid="followup-progress-bar">
              <div className={`h-full transition-all duration-500 ${allHandled ? "bg-emerald-500" : "bg-teal-500"}`}
                   style={{ width: `${(handledCount / Math.max(1, totalBundles)) * 100}%` }} />
            </div>
          ) : null}

          {allHandled ? (
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="followup-decision-tally">
              <p className="text-xs text-slate-600">Decisions:</p>
              {dispositionTally.book > 0 ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  <CalendarPlus className="w-3 h-3" />{dispositionTally.book} booked
                </span>
              ) : null}
              {dispositionTally.recall > 0 ? (
                <span className="inline-flex items-center gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  <Inbox className="w-3 h-3" />{dispositionTally.recall} recalled
                </span>
              ) : null}
              {dispositionTally.decline > 0 ? (
                <span className="inline-flex items-center gap-1 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
                  <Ban className="w-3 h-3" />{dispositionTally.decline} declined
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* --- Quick batch section (only when 2+ pending bundles) --- */}
        {hasMultipleBundles && !allHandled ? (
          <div className="px-5 py-4 border-t border-slate-100" data-testid="followup-quick-batch">
            <button type="button" onClick={() => setBookBundle(bookAllBundle)}
              data-testid="followup-book-all"
              className="w-full inline-flex items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-xl shadow-sm">
              <span className="inline-flex items-center gap-2">
                <CalendarPlus className="w-4 h-4" />
                Book all {totalBundles - handledCount} {followUpItemNounPlural(bookAllBundle, totalBundles - handledCount)} on one appointment
              </span>
              <ArrowRight className="w-4 h-4 opacity-90" />
            </button>
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
              <span>Or for the whole batch:</span>
              {primaryPlanBooking ? (
                <button type="button"
                  onClick={() => addToPlanVisitAppointment(allBundle)}
                  title={primaryPlanBooking.appointment_label
                    ? `Use ${primaryPlanBooking.appointment_label}`
                    : "Use the staged plan visit appointment"}
                  data-testid="followup-add-all-to-plan-visit"
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-teal-800 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded">
                  <CalendarPlus className="w-3 h-3" />Add all to plan visit appointment
                </button>
              ) : null}
              <RecallMenu bundle={allBundle} onRecall={(when) => onRecall(allBundle, when)} labelOverride="Recall all" />
              <button type="button" onClick={() => setDeclineBundle(allBundle)}
                data-testid="followup-decline-all"
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-rose-700 hover:text-rose-900 hover:bg-rose-50 rounded">
                <Ban className="w-3 h-3" />Decline all
              </button>
            </div>
          </div>
        ) : null}

        {/* --- Per-tooth list — sections separated by horizontal dividers,
            no nested card boundaries. --- */}
        {groupedToothBundles.map(([toothKey, bundles], toothIdx) => {
          const toothMinutes = bundles.reduce((s, b) => s + (b.estimatedMinutes || 0), 0);
          const allBundleHandled = bundles.every((b) => dispositions?.has(b.key));
          return (
            <div key={toothKey}
              className={`px-5 py-3.5 border-t border-slate-100 transition ${allBundleHandled ? "opacity-70" : ""}`}
              data-testid={`followup-tooth-${toothKey}`}>
              <div className="flex items-baseline gap-2 mb-2">
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                  {toothKey === "General" ? "General" : <>🦷 Tooth #{toothKey}</>}
                </p>
                {toothMinutes > 0 ? (
                  <p className="ml-auto text-[11px] text-slate-400 tabular-nums">~{fmtMinutes(toothMinutes)}</p>
                ) : null}
              </div>
              <ul className="space-y-2">
                {bundles.map((b) => {
                  const disposition = dispositions?.get(b.key);
                  const age = fmtNeedAge(b.oldestCreatedAt);
                  return (
                  <li key={b.key} className="flex items-center gap-3 flex-wrap"
                    data-testid={`followup-bundle-${b.key}`}>
                    <span className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: b.color_hex || "#94a3b8" }} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${disposition ? "text-slate-500 line-through decoration-1" : "text-slate-800"}`}>
                        {b.procedure_name}
                        {b.surfaces.length > 0 ? (
                          <span className="text-slate-400 font-normal"> · {b.surfaces.join(", ")}</span>
                        ) : null}
                      </p>
                      <p className="text-[11px] text-slate-500 tabular-nums flex items-center gap-2 flex-wrap">
                        <span>{fmtPeso(b.subtotalCents, currency)}</span>
                        {b.estimatedMinutes > 0 ? <span className="text-slate-400">~{fmtMinutes(b.estimatedMinutes)}</span> : null}
                        {age ? (
                          // Two-tone provenance pill: amber for anything
                          // ≤30 days old (today, yesterday, N days), slate
                          // for stale (>30 days). No emojis — text alone
                          // is clear, calmer, more professional.
                          <span className={
                            age.tone === "stale"
                              ? "text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded"
                              : "text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded"
                          }>
                            {age.label}
                          </span>
                        ) : null}
                      </p>
                    </div>
                    {disposition ? (
                      <DispositionBadge disposition={disposition}
                        onUndo={() => clearDisposition(b.key)}
                        bundleKey={b.key}
                        tutorialId={disposition.tutorial_id} />
                    ) : (
                      <div className="flex items-center gap-1 shrink-0">
                        {/* Hierarchy: Book = primary filled, Recall =
                            outline, Decline = text-only ghost. Most front
                            desks click Book ~80% of the time; matching the
                            visual weight to the click frequency cuts
                            decision fatigue. */}
                        <button type="button" onClick={() => setBookBundle(b)}
                          data-testid={`followup-book-${b.key}`}
                          data-tutorial-id={b.book_tutorial_id || undefined}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg shadow-sm">
                          <CalendarPlus className="w-3.5 h-3.5" />Book
                        </button>
                        {primaryPlanBooking ? (
                          <button type="button"
                            onClick={() => addToPlanVisitAppointment(b)}
                            title={primaryPlanBooking.appointment_label
                              ? `Use ${primaryPlanBooking.appointment_label}`
                              : "Use the staged plan visit appointment"}
                            data-testid={`followup-add-to-plan-visit-${b.key}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-teal-800 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg">
                            <CalendarPlus className="w-3.5 h-3.5" />Add to plan visit
                          </button>
                        ) : null}
                        <RecallMenu bundle={b} onRecall={(when) => onRecall(b, when)} />
                        <button type="button" onClick={() => setDeclineBundle(b)}
                          data-testid={`followup-decline-${b.key}`}
                          className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-slate-500 hover:text-rose-700 hover:bg-rose-50 rounded">
                          <Ban className="w-3.5 h-3.5" />Decline
                        </button>
                      </div>
                    )}
                  </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      <BookFutureNeedDialog
        open={!!bookBundle}
        onOpenChange={(o) => { if (!o) setBookBundle(null); }}
        bundle={bookBundle}
        patientId={patientId}
        patientName={patientName}
        navigate={navigate}
        defaultDoctorId={defaultDoctorId}
        defaultChairId={defaultChairId}
        priorVisitDate={priorVisitDate || null}
        doctorRequestedDays={followUpRequestedDays(bookBundle)}
        appointmentInputExtra={bookBundle?.appointmentInputExtra || null}
        initialNotes={bookBundle?.initialNotes || ""}
        headingOverride={bookBundle ? followUpBookingHeading(bookBundle) : null}
        demoMode={demoMode}
        demoInitialDate={demoMode ? "2026-05-19" : undefined}
        demoReferenceData={demoMode ? CHECKOUT_TODAY_TUTORIAL_BOOKING_DATA : undefined}
        demoDayAppointments={demoMode ? [] : undefined}
        demoUpcomingAppointments={demoMode ? [] : undefined}
        onBooked={(appointment_id, appointment_label) => {
          stageDisposition(bookBundle, {
            kind: "book",
            appointment_id,
            appointment_label: appointment_label || null,
          });
          if (bookBundle?.orthoNeed) {
            onOrthoBundled?.(appointment_id, appointment_label || null);
          }
          setBookBundle(null);
        }}
      />

      <DeclineFutureNeedDialog
        open={!!declineBundle}
        onOpenChange={(o) => { if (!o) setDeclineBundle(null); }}
        bundle={declineBundle}
        patientName={patientName}
        onDeclined={(reason) => {
          stageDisposition(declineBundle, { kind: "decline", reason });
          setDeclineBundle(null);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------
// RecallMenu — three timing options, popover-style.
//   • Standard (14 days)  — DEFAULT, clicking "Recall" directly picks this
//   • Tomorrow            — for "I'll think about it" patients
//   • Pick a date…        — opens a date input
//
// 14d default is the sweet spot between "patient's still warm" and
// "they've had time to process the bill" — see proposal docs.
// Persistence (recall_due_at column + queue surface) ships in PR B;
// this v1 surfaces the chosen window in a toast so the UX is locked in.
// ---------------------------------------------------------------------
// ---------------------------------------------------------------------
// DispositionBadge — replaces the [Book][Recall][Decline] cluster
// once a bundle has been staged. Shows the chosen disposition with a
// short summary, plus an Undo button that wipes the staging entry
// (which sets the bundle back to "pending" and re-shows the action
// buttons). Until "Complete checkout" fires, all of this is just
// client state — fully reversible with zero DB cost.
// ---------------------------------------------------------------------
function DispositionBadge({ disposition, onUndo, bundleKey, tutorialId }) {
  const styles = {
    book:    { bg: "bg-emerald-50",  text: "text-emerald-800",  border: "border-emerald-200",  icon: <CalendarPlus className="w-3.5 h-3.5" />, label: "Booked" },
    recall:  { bg: "bg-amber-50",    text: "text-amber-800",    border: "border-amber-200",    icon: <Inbox className="w-3.5 h-3.5" />,        label: "Recall" },
    decline: { bg: "bg-rose-50",     text: "text-rose-800",     border: "border-rose-200",     icon: <Ban className="w-3.5 h-3.5" />,          label: "Declined" },
  }[disposition.kind] || { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200", icon: null, label: "Done" };

  let detail = null;
  if (disposition.kind === "book") {
    detail = disposition.plan_visit_booking
      ? `With plan visit · ${disposition.appointment_label || "Linked appointment"}`
      : disposition.ortho_installation_booking
      ? `With Ortho installation · ${disposition.appointment_label || "Linked appointment"}`
      : (disposition.appointment_label || "Linked to appointment");
  }
  if (disposition.kind === "recall")  detail = disposition.due_label;
  if (disposition.kind === "decline") detail = `"${(disposition.reason || "").slice(0, 32)}${(disposition.reason || "").length > 32 ? "…" : ""}"`;

  return (
    <div
      className="flex items-center gap-1.5 shrink-0"
      data-testid={`followup-handled-${bundleKey}`}
      data-tutorial-id={tutorialId || undefined}
    >
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border ${styles.bg} ${styles.text} ${styles.border}`}>
        {styles.icon}
        <span>{styles.label}</span>
        {detail ? <span className="font-normal opacity-80 max-w-[180px] truncate">· {detail}</span> : null}
      </span>
      <button type="button" onClick={onUndo}
        data-testid={`followup-undo-${bundleKey}`}
        className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-slate-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg">
        Undo
      </button>
    </div>
  );
}

function RecallMenu({ bundle, onRecall, labelOverride }) {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customDate, setCustomDate] = useState("");
  const label = labelOverride || "Recall";

  const tomorrow = useMemo(() => {
    return clinicDateAt(addClinicDays(clinicTodayDateString(), 1), 9);
  }, []);
  const standard = useMemo(() => {
    return clinicDateAt(addClinicDays(clinicTodayDateString(), 14), 9);
  }, []);
  const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: CLINIC_TIME_ZONE });

  function pickStandard() { onRecall({ kind: "standard", date: standard }); setOpen(false); }
  function pickTomorrow() { onRecall({ kind: "tomorrow", date: tomorrow }); setOpen(false); }
  function pickCustom() {
    if (!customDate) return;
    const d = clinicDateAt(customDate, 9);
    onRecall({ kind: "custom", date: d, label: fmt(d) });
    setOpen(false); setShowCustom(false); setCustomDate("");
  }

  return (
    <div className="relative inline-block">
      <button type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`followup-recall-${bundle.key}`}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg">
        <Inbox className="w-3.5 h-3.5" />{label}
      </button>
      {open ? (
        <>
          {/* click-away backdrop */}
          <div className="fixed inset-0 z-30" onClick={() => { setOpen(false); setShowCustom(false); }} />
          <div className="absolute right-0 mt-1 z-40 w-64 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden"
               data-testid={`recall-menu-${bundle.key}`}>
            <p className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              When should we recall?
            </p>
            <button type="button" onClick={pickStandard}
              data-testid={`recall-pick-standard-${bundle.key}`}
              className="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-baseline justify-between gap-2">
              <span className="text-sm font-semibold text-slate-800">Standard</span>
              <span className="text-[11px] text-slate-500">14 days · {fmt(standard)}</span>
            </button>
            <button type="button" onClick={pickTomorrow}
              data-testid={`recall-pick-tomorrow-${bundle.key}`}
              className="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-baseline justify-between gap-2 border-t border-slate-100">
              <span className="text-sm text-slate-700">Tomorrow</span>
              <span className="text-[11px] text-slate-500">{fmt(tomorrow)}</span>
            </button>
            {showCustom ? (
              <div className="px-3 py-2 border-t border-slate-100 flex items-center gap-2">
                <input type="date" value={customDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setCustomDate(e.target.value)}
                  data-testid={`recall-custom-date-${bundle.key}`}
                  className="flex-1 px-2 py-1 text-sm rounded border border-slate-200 focus:border-amber-500 outline-none" />
                <button type="button" onClick={pickCustom} disabled={!customDate}
                  data-testid={`recall-custom-confirm-${bundle.key}`}
                  className="px-2.5 py-1 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded disabled:opacity-50">
                  Set
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setShowCustom(true)}
                data-testid={`recall-pick-custom-${bundle.key}`}
                className="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-baseline justify-between gap-2 border-t border-slate-100">
                <span className="text-sm text-slate-700">Pick a date…</span>
                <span className="text-[11px] text-slate-400">→</span>
              </button>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function SeniorPwdRow({
  patient,
  set,
  decline,
  discountCents,
  currency,
  onPatientUpdated,
  scopeLabel = "today’s bill",
  discountPctLabel = formatPercentLabel(DEFAULT_SENIOR_PWD_DISCOUNT_PCT),
}) {
  const eligible = patient.is_senior_citizen || patient.is_pwd;
  const [open, setOpen] = useState(eligible);
  const [adhocSenior, setAdhocSenior] = useState(false);
  const [adhocPwd, setAdhocPwd] = useState(false);
  const [adhocId, setAdhocId] = useState("");
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [managerName, setManagerName] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const discountLabel = patient.is_senior_citizen ? "Senior Citizen" : "PWD";

  function restoreMandatoryDiscount() {
    set("decline_senior_pwd", false);
    set("senior_pwd_discount_removed_by", "");
    set("senior_pwd_discount_removed_reason", "");
    set("senior_pwd_discount_removed_at", "");
    setOverrideOpen(false);
    toast.success(`${discountLabel} discount restored.`);
  }

  function confirmMandatoryDiscountRemoval() {
    const approvedBy = managerName.trim();
    const reason = overrideReason.trim();
    if (!approvedBy || !reason) {
      toast.error("Manager confirmation and reason are required.");
      return;
    }
    set("decline_senior_pwd", true);
    set("senior_pwd_discount_removed_by", approvedBy);
    set("senior_pwd_discount_removed_reason", reason);
    set("senior_pwd_discount_removed_at", new Date().toISOString());
    setOverrideOpen(false);
    toast.warning(`${discountLabel} discount removed with manager confirmation.`);
  }

  // For ad-hoc tagging at checkout we'd want to call set_patient_eligibility,
  // but receptionists may also just want to apply for this visit. v1: keep
  // it simple — show a toggle to apply, surface a hint about the patient
  // profile for permanent tagging.
  if (eligible) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="apply-sc"
              checked={!decline}
              readOnly
              data-testid="apply-senior-pwd"
              className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-teal-600 disabled:opacity-70"
              aria-describedby="senior-pwd-lock-note"
            />
            <div>
              <label htmlFor="apply-sc" className="text-sm text-slate-700">
                {decline ? "Removed" : "Applied"} {discountLabel} {discountPctLabel} discount on {scopeLabel}
              </label>
              <p id="senior-pwd-lock-note" className="text-[10px] text-slate-500">
                Mandatory discount is locked for staff. Removal requires manager confirmation and is logged at checkout.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-sm text-slate-700 tabular-nums">
              {!decline && discountCents > 0 ? `−${fmtPeso(discountCents, currency)}` : "—"}
            </span>
            {decline ? (
              <button
                type="button"
                onClick={restoreMandatoryDiscount}
                className="rounded border border-teal-200 px-2 py-1 text-[11px] font-semibold text-teal-700 hover:bg-teal-50"
                data-testid="senior-pwd-restore"
              >
                Restore
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setOverrideOpen((v) => !v)}
                className="rounded border border-amber-200 px-2 py-1 text-[11px] font-semibold text-amber-700 hover:bg-amber-50"
                data-testid="senior-pwd-request-removal"
              >
                Manager remove
              </button>
            )}
          </div>
        </div>

        {overrideOpen ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="senior-pwd-removal-confirmation">
            <p className="text-xs font-semibold text-amber-900">Manager confirmation required to remove mandatory discount.</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                type="text"
                value={managerName}
                onChange={(e) => setManagerName(e.target.value)}
                placeholder="Manager name / PIN"
                data-testid="senior-pwd-manager"
                className="w-full rounded border border-amber-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-amber-500"
              />
              <input
                type="text"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Reason"
                data-testid="senior-pwd-removal-reason"
                className="w-full rounded border border-amber-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-amber-500"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOverrideOpen(false)} className="px-2.5 py-1.5 text-xs font-semibold text-slate-600">
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmMandatoryDiscountRemoval}
                className="rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                data-testid="senior-pwd-confirm-removal"
              >
                Confirm removal
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
  // Patient isn't tagged on profile — let staff apply ad-hoc.
  return (
    <div className="space-y-2">
      <button type="button" onClick={() => setOpen((o) => !o)}
        data-testid="senior-pwd-toggle"
        className="flex w-full items-center gap-1.5 text-left text-xs font-semibold text-slate-600 hover:text-slate-900">
        {open ? "−" : "+"} Apply Senior / PWD discount
      </button>
      {open ? (
        <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex items-center gap-3 text-xs">
            <label className="inline-flex items-center gap-1.5">
              <input type="radio" name="adhoc-sp" checked={adhocSenior}
                onChange={() => { setAdhocSenior(true); setAdhocPwd(false); }}
                className="w-3 h-3" />
              Senior Citizen
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input type="radio" name="adhoc-sp" checked={adhocPwd}
                onChange={() => { setAdhocSenior(false); setAdhocPwd(true); }}
                className="w-3 h-3" />
              PWD
            </label>
          </div>
          <input type="text" placeholder="ID number (required)"
            value={adhocId} onChange={(e) => setAdhocId(e.target.value)}
            className="w-full px-2 py-1 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none" />
          <button type="button"
            disabled={!adhocId.trim() || (!adhocSenior && !adhocPwd)}
            onClick={async () => {
              const { error } = await supabase.rpc("set_patient_eligibility", {
                p_patient_id: patient.id,
                p_input: {
                  is_senior_citizen: adhocSenior,
                  is_pwd:            adhocPwd,
                  senior_citizen_id: adhocSenior ? adhocId.trim() : null,
                  pwd_id:            adhocPwd    ? adhocId.trim() : null,
                },
              });
              if (error) { toast.error(error.message ?? "Could not save."); return; }
              // Update local hydration in place — staying on Step 2.
              onPatientUpdated?.({
                is_senior_citizen: adhocSenior,
                is_pwd:            adhocPwd,
                senior_citizen_id: adhocSenior ? adhocId.trim() : null,
                pwd_id:            adhocPwd    ? adhocId.trim() : null,
              });
              set("decline_senior_pwd", false);
              setOpen(false);
              toast.success(`${discountPctLabel} discount applied — also tagged on the patient profile.`);
            }}
            className="w-full px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:opacity-50">
            Tag patient & apply {discountPctLabel} discount
          </button>
          <p className="text-[10px] text-slate-400">Tagging saves to the patient profile so future visits auto-apply.</p>
        </div>
      ) : null}
    </div>
  );
}

function HmoCoveragePanel({ patientId, paymentState, setPaymentState, currency, billBaseCents = 0, tutorialDemo = false }) {
  const [open, setOpen]       = useState(!!paymentState.hmo_payer_id);
  const [payers, setPayers]   = useState([]);
  const [loadingPayers, setLoadingPayers] = useState(false);
  const [profileWarning, setProfileWarning] = useState("");
  const coveredCents = Number(paymentState.hmo_covered_cents || 0);
  const patientPaysCents = Math.max(0, Number(billBaseCents || 0) - coveredCents);
  const payerOptions = tutorialDemo
    ? [
        HMO_TUTORIAL_DEMO_PAYER,
        ...payers.filter((payer) => payer.id !== HMO_TUTORIAL_DEMO_PAYER.id),
      ]
    : payers;

  function set(k, v) { setPaymentState((prev) => ({ ...prev, [k]: v })); }

  useEffect(() => {
    if (tutorialDemo || !patientId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("list_patient_hmo_profiles", { p_patient_id: patientId });
      if (cancelled || error) return;
      const primary = (data ?? []).find((profile) => profile.is_primary && !profile.archived);
      if (!primary) return;
      const expired = isPastDate(primary.expiry_date);
      setOpen(true);
      setProfileWarning(expired ? "This HMO card may be expired. Verify with patient." : "");
      setPaymentState((prev) => {
        if (prev.hmo_payer_id || prev.hmo_member_id || prev.patient_hmo_profile_id) return prev;
        return {
          ...prev,
          hmo_payer_id: primary.payer_id || "",
          hmo_member_id: primary.member_id || "",
          patient_hmo_profile_id: primary.id || "",
        };
      });
    })();
    return () => { cancelled = true; };
  }, [patientId, setPaymentState, tutorialDemo]);

  useEffect(() => {
    if (!open || payers.length > 0) return;
    if (tutorialDemo) {
      setPayers([HMO_TUTORIAL_DEMO_PAYER]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingPayers(true);
      const { data } = await supabase.rpc("list_insurance_payers", { p_include_archived: false });
      setLoadingPayers(false);
      if (!cancelled) setPayers(data ?? []);
    })();
    return () => { cancelled = true; };
  }, [open, payers.length, tutorialDemo]);

  useEffect(() => {
    if (!tutorialDemo || !open) return;
    setPaymentState((prev) => {
      const next = {
        ...prev,
        hmo_payer_id: prev.hmo_payer_id || HMO_TUTORIAL_DEMO_PAYER.id,
        hmo_loa_number: prev.hmo_loa_number || HMO_TUTORIAL_DEMO_LOA_NUMBER,
        hmo_member_id: prev.hmo_member_id || HMO_TUTORIAL_DEMO_MEMBER_ID,
        hmo_covered_cents: Number(prev.hmo_covered_cents || 0) > 0
          ? prev.hmo_covered_cents
          : HMO_TUTORIAL_DEMO_COVERED_CENTAVOS,
        patient_hmo_profile_id: "",
      };
      return (
        next.hmo_payer_id === prev.hmo_payer_id
        && next.hmo_loa_number === prev.hmo_loa_number
        && next.hmo_member_id === prev.hmo_member_id
        && next.hmo_covered_cents === prev.hmo_covered_cents
        && next.patient_hmo_profile_id === prev.patient_hmo_profile_id
      ) ? prev : next;
    });
  }, [open, setPaymentState, tutorialDemo]);

  function clearHmo() {
    set("hmo_payer_id", "");
    set("hmo_loa_number", "");
    set("hmo_member_id", "");
    set("hmo_covered_cents", 0);
    set("patient_hmo_profile_id", "");
    setProfileWarning("");
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        data-testid="hmo-toggle"
        data-tutorial-id="hmo-toggle"
        className="flex w-full items-center gap-1.5 text-left text-xs font-semibold text-slate-600 hover:text-slate-900">
        + Add HMO coverage
      </button>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="hmo-panel" data-tutorial-id="hmo-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-700">Insurance / HMO coverage</p>
        <button type="button"
          onClick={() => { clearHmo(); setOpen(false); }}
          className="text-xs text-slate-500 hover:text-slate-700">Remove</button>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="hmo-payer">Payer</label>
        <select id="hmo-payer" value={paymentState.hmo_payer_id}
          onChange={(e) => setPaymentState((prev) => ({ ...prev, hmo_payer_id: e.target.value, patient_hmo_profile_id: "" }))}
          data-testid="hmo-payer"
          data-tutorial-id="hmo-payer"
          className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none">
          <option value="">{loadingPayers ? "Loading…" : "— select payer —"}</option>
          {payerOptions.map((p) => <option key={p.id} value={p.id}>{p.name}{p.short_name ? ` (${p.short_name})` : ""}</option>)}
        </select>
        {tutorialDemo ? (
          <p className="text-[10px] text-slate-400 mt-1">Demo payer: Maxicare. In live checkout, select the HMO company that issued the LOA.</p>
        ) : payerOptions.length === 0 && !loadingPayers ? (
          <p className="text-[10px] text-amber-700 mt-1">No payers configured. Add them in Settings → Insurance Payers.</p>
        ) : null}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="hmo-loa">LOA #</label>
          <input id="hmo-loa" type="text" value={paymentState.hmo_loa_number}
            onChange={(e) => set("hmo_loa_number", e.target.value)}
            placeholder="Approval number from HMO"
            data-testid="hmo-loa"
            data-tutorial-id="hmo-loa"
            className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none" />
          <p className="mt-1 text-[10px] text-slate-400">Use the LOA or approval reference when submitting and following up on the claim.</p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="hmo-member">Member ID</label>
          <input id="hmo-member" type="text" value={paymentState.hmo_member_id}
            onChange={(e) => setPaymentState((prev) => ({ ...prev, hmo_member_id: e.target.value, patient_hmo_profile_id: "" }))}
            placeholder="ID from patient HMO card"
            data-testid="hmo-member"
            data-tutorial-id="hmo-member"
            className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none" />
          <p className="mt-1 text-[10px] text-slate-400">This ties the claim to the covered member, not just the clinic visit.</p>
        </div>
      </div>
      {profileWarning ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] font-semibold text-amber-800">
          {profileWarning}
        </div>
      ) : null}
      <div>
        <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Covered amount</label>
        <div className="mt-1">
          <PesoInput centavos={paymentState.hmo_covered_cents}
            onChange={(v) => set("hmo_covered_cents", v)}
            testid="hmo-covered"
            tutorialId="hmo-covered" />
        </div>
        <p className="text-[10px] text-slate-400 mt-1">Patient pays the difference. The HMO claim is opened automatically when checkout completes.</p>
        <p className="text-[10px] text-slate-400 mt-0.5">Type only the amount the LOA covers. If Maxicare covers ₱1,800, enter 1800 and collect the remaining patient co-pay.</p>
      </div>
      {coveredCents > 0 ? (
        <div className="space-y-1 border-t border-slate-100 pt-2" data-tutorial-id="hmo-summary">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-700">HMO covers</span>
            <span className="text-sm font-semibold text-slate-900 tabular-nums">{fmtPeso(coveredCents, currency)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-700">Patient pays</span>
            <span className="text-sm font-semibold text-slate-900 tabular-nums">{fmtPeso(patientPaysCents, currency)}</span>
          </div>
          <div className="rounded-md bg-teal-50 px-2 py-1 text-[11px] font-semibold text-teal-800">
            HMO covers {fmtPeso(coveredCents, currency)} / Patient pays {fmtPeso(patientPaysCents, currency)}.
          </div>
        </div>
      ) : null}
    </div>
  );
}

function validateHmoCoverageForCheckout({ paymentState, billBaseCents, patientCopayCents }) {
  const payerId = paymentState.hmo_payer_id?.trim?.() || "";
  const loaNumber = paymentState.hmo_loa_number?.trim?.() || "";
  const memberId = paymentState.hmo_member_id?.trim?.() || "";
  const coveredCents = Number(paymentState.hmo_covered_cents || 0);
  const hasAnyHmo = !!payerId || !!loaNumber || !!memberId || coveredCents > 0;
  if (!hasAnyHmo) return null;
  if (!payerId) return "Select an HMO provider.";
  if (!loaNumber) return "Enter the HMO LOA number.";
  if (!memberId) return "Enter the HMO member ID.";
  if (coveredCents <= 0) return "Enter an HMO covered amount greater than zero.";
  if (coveredCents > Number(billBaseCents || 0)) {
    return "HMO covered amount cannot exceed the total bill.";
  }
  if (Number(patientCopayCents || 0) > 0
      && Number(paymentState.amount_tendered_cents || 0) < Number(patientCopayCents || 0)) {
    return "Collect the patient co-pay before completing HMO checkout.";
  }
  return null;
}

function isPastDate(value) {
  if (!value) return false;
  return value < new Date().toISOString().slice(0, 10);
}

function FamilyPayerPanel({ patientId, paymentState, setPaymentState }) {
  const [open, setOpen] = useState(!!paymentState.paid_by_patient_id);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  function set(k, v) { setPaymentState((prev) => ({ ...prev, [k]: v })); }

  useEffect(() => {
    if (!open || paymentState.paid_by_patient_id) return;
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      const { data } = await supabase.rpc("search_patients_for_payer", {
        p_query:   search,
        p_exclude: patientId,
      });
      if (cancelled) return;
      setBusy(false);
      setResults(data ?? []);
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, search, patientId, paymentState.paid_by_patient_id]);

  if (!open) {
    return (
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3">
        <button type="button" onClick={() => setOpen(true)}
          data-testid="family-payer-toggle"
          className="text-xs font-semibold text-slate-600 hover:text-slate-900 flex items-center gap-1.5">
          + Paid by someone else?
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 space-y-2" data-testid="family-payer-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-700">Paid by someone else</p>
        <button type="button"
          onClick={() => {
            set("paid_by_patient_id", "");
            set("paid_by_full_name", "");
            setOpen(false);
          }}
          className="text-xs text-slate-500 hover:text-slate-700">Remove</button>
      </div>
      {paymentState.paid_by_patient_id ? (
        <div className="bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-teal-700" />
          <span className="text-sm text-teal-900 flex-1">{paymentState.paid_by_full_name || "Selected payer"}</span>
          <button type="button"
            onClick={() => { set("paid_by_patient_id", ""); set("paid_by_full_name", ""); }}
            className="text-xs text-teal-700 hover:underline">Change</button>
        </div>
      ) : (
        <>
          <input type="search" placeholder="Search by name, patient #, or phone…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="family-payer-search"
            className="w-full px-3 py-1.5 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none" />
          <ul className="max-h-48 overflow-y-auto divide-y divide-slate-100 border border-slate-100 rounded-lg">
            {busy ? (
              <li className="px-3 py-2 text-xs text-slate-400 text-center">
                <Loader2 className="w-3 h-3 animate-spin inline mr-1" />Searching…
              </li>
            ) : results.length === 0 ? (
              <li className="px-3 py-2 text-xs text-slate-400 text-center">No matches.</li>
            ) : results.map((p) => (
              <li key={p.id}>
                <button type="button"
                  onClick={() => {
                    set("paid_by_patient_id", p.id);
                    set("paid_by_full_name", p.full_name);
                  }}
                  data-testid={`family-payer-pick-${p.id}`}
                  className="w-full text-left px-3 py-1.5 hover:bg-slate-50">
                  <p className="text-sm text-slate-800">{p.full_name}</p>
                  <p className="text-[11px] text-slate-500">
                    {p.patient_number}{p.phone ? ` · ${p.phone}` : ""}
                  </p>
                </button>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-slate-400">
            Visit + treatment record stay under the original patient. This just records who actually paid.
          </p>
        </>
      )}
    </div>
  );
}

function Step({ label, active, muted }) {
  const cls = active ? "text-teal-700 bg-teal-50 border-teal-200"
            : muted  ? "text-slate-400 bg-slate-50 border-slate-200"
            : "text-slate-700 bg-white border-slate-200";
  return (
    <span className={`inline-flex items-center text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full border ${cls}`}>
      {label}
    </span>
  );
}
function StepDivider() {
  return <span className="h-px bg-slate-200 flex-1 max-w-12" />;
}

// Local-state peso input. We keep the raw string while the user types
// (so 7.0 doesn't snap to 7.00 mid-keystroke), commit on blur.
function formatPesoInputValue(value, blankWhenNull) {
  return blankWhenNull && value == null ? "" : centavosToPesoInput(value);
}

function PesoInput({ centavos, onChange, disabled, testid, tutorialId, inputClassName = "", blankWhenNull = false, placeholder = "" }) {
  const initial = formatPesoInputValue(centavos, blankWhenNull);
  const [draft, setDraft] = useState(initial);
  const lastCentavosRef = useRef(centavos);
  // Resync when the row's centavos change externally (e.g. row swap, hydrate).
  useEffect(() => {
    if (centavos !== lastCentavosRef.current) {
      lastCentavosRef.current = centavos;
      setDraft(formatPesoInputValue(centavos, blankWhenNull));
    }
  }, [centavos, blankWhenNull]);

  function onChangeRaw(e) {
    // Allow only digits + at most one decimal point. Reject everything else.
    let v = e.target.value.replace(/[^\d.]/g, "");
    const firstDot = v.indexOf(".");
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
    }
    setDraft(v);
  }
  function commit() {
    if (blankWhenNull && !String(draft || "").trim()) {
      lastCentavosRef.current = null;
      onChange(null);
      setDraft("");
      return;
    }
    const cents = parsePesoToCentavos(draft);
    lastCentavosRef.current = cents;
    onChange(cents);
    setDraft(centavosToPesoInput(cents));
  }

  return (
    <div className="inline-flex items-center gap-1">
      <span className="text-xs text-slate-400">₱</span>
      <input type="text" inputMode="decimal" value={draft}
        disabled={disabled}
        placeholder={placeholder}
        onChange={onChangeRaw}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
        data-testid={testid}
        data-tutorial-id={tutorialId}
        className={`${inputClassName || "w-24 px-1 py-1 text-sm"} text-right rounded border border-slate-200 focus:border-teal-500 outline-none disabled:bg-slate-50 disabled:text-slate-400`} />
    </div>
  );
}

function fmtPeso(centavos, currency = "PHP") {
  if (centavos == null) return "Depends on case";
  const n = (centavos / 100).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
  return currency === "PHP" ? `₱${n}` : `${n} ${currency}`;
}

function labelize(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function coerceJsonArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function activeOrthoNextVisit(value) {
  if (!value || typeof value !== "object") return null;
  if (!["needs_booking", "already_booked"].includes(value.status)) return null;
  if (!value.case_id || !value.current_ortho_visit_id) return null;
  return value;
}

function orthoNextVisitDecisionKey(value) {
  const request = activeOrthoNextVisit(value);
  if (!request) return null;
  return [
    request.case_id,
    request.current_ortho_visit_id,
    request.next_visit_type || "adjustment",
    request.interval_weeks || "",
  ].join(":");
}

function formatShortDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function orthoDateWindowText(request) {
  const start = formatShortDate(request?.window_start_date);
  const end = formatShortDate(request?.window_end_date);
  if (start && end && start !== end) return `${start}-${end}`;
  return start || end || "";
}

function orthoNextVisitSubtitle(request) {
  const pieces = [];
  if (request?.procedure_name) pieces.push(request.procedure_name);
  if (request?.next_visit_number) pieces.push(`Adjustment ${request.next_visit_number}`);
  if (request?.interval_weeks) pieces.push(`${request.interval_weeks}w interval`);
  if (request?.next_visit_type === "debonding") pieces.push("ready for debonding");
  const window = orthoDateWindowText(request);
  if (window) pieces.push(`target ${window}`);
  return pieces.join(" · ");
}

function orthoNextVisitBookingNote(request) {
  const interval = Number(request?.interval_weeks || 0);
  const visitNumber = Number(request?.next_visit_number || 0);
  const isDebonding = request?.next_visit_type === "debonding";
  const parts = [isDebonding ? "Ortho debonding" : "Next Ortho adjustment"];
  if (visitNumber) parts.push(`Adjustment ${visitNumber}`);
  if (interval) parts.push(`${interval}w interval`);
  const window = orthoDateWindowText(request);
  if (window) parts.push(`target ${window}`);
  return parts.join(" · ");
}

const ORTHO_NO_ADJUSTMENT_REASONS = [
  { value: "bracket_repair", label: "Bracket repair / rebond" },
  { value: "wire_repair", label: "Wire repair / replacement" },
  { value: "elastic_issue", label: "Elastic issue" },
  { value: "other", label: "Other non-adjustment issue" },
];

// ---------------------------------------------------------------------
// ScheduledNeedsPanel — Step 1's mirror of the chairside Today's Plan
// card. Shows every treatment_need scheduled to this appointment with
// its current state from the visit's draft:
//
//   No action → no `tn_id` in items, no entry in chairsideDispositions.
//                Receptionist sees "the doctor never decided" and can
//                act on the row directly (this covers the "doctor
//                dictated to me" flow + the "doctor forgot" case).
//   Done      → tn_id in items with outcome='done'. Bills at checkout.
//   Not today → chairsideDispositions[tn_id].kind === 'not_today'.
//                At commit, the new `unschedule` follow-up kind flips
//                the need's status back to Open in the patient's pool.
//   Declined  → chairsideDispositions[tn_id].kind === 'declined'. At
//                commit, decline_treatment_need fires with the reason.
//
// Receptionist-side flip controls let them set / change any state per
// row from this surface. All mutations stage in items / dispositions
// — nothing hits the DB until the visit's atomic commit fires.
// ---------------------------------------------------------------------
export function ScheduledNeedsPanel({
  scheduled, items, setItems,
  chairsideDispositions, setChairsideDispositions,
  chartReconciled = new Map(),
  currency, readOnly = false,
  planIsBundle = false,
  postOpByProcedure = {},
  followupWaivers   = {},
  setFollowupWaivers,
  appointmentId = null,
  onNoAdjustmentSaved,
  // When true, this panel drops its own card chrome so it can sit as one
  // region inside the shared plan+billing card (see the Today step render).
  embedded = false,
  // Split today's booked work across the checkout's buckets:
  //   only="nonplan" → standalone work (Scheduled bucket)
  //   only="plan"    → plan-bound work (Plan / Ongoing bucket)
  //   null           → everything (legacy single-list behaviour)
  only = null,
  title = "Today's work",
  subtitle = "From booking · doctor's chairside marks",
}) {
  const list = (Array.isArray(scheduled) ? scheduled.filter((s) => scheduledWorkKey(s)) : [])
    .filter((s) => only === "plan"    ? !!s.treatment_plan_id
                 : only === "nonplan" ? !s.treatment_plan_id
                 : true);
  const [declining, setDeclining] = useState(null); // { tn_id, name }
  const [reason, setReason] = useState("");
  const [deferring, setDeferring] = useState(null); // scheduled row awaiting a reason
  // Open the waive-reason modal for one scheduled row. { row, name }
  const [waiving, setWaiving] = useState(null);
  const [waiveReason, setWaiveReason] = useState("");
  const [noAdjusting, setNoAdjusting] = useState(null);
  const [noAdjustmentReason, setNoAdjustmentReason] = useState("bracket_repair");
  const [noAdjustmentSubmitting, setNoAdjustmentSubmitting] = useState(false);

  // Pre-canned waive reasons (the doctor picks; "Other" lets them type).
  const WAIVE_REASONS = [
    "Simple case, no sutures",
    "Patient declined return",
    "No clinical indication",
    "Other",
  ];

  if (list.length === 0) return null;

  const stateOf = (s) => {
    const key = scheduledWorkKey(s);
    const clinicalOnlyOrtho = s?.source === "ortho_checkout"
      && (s?.clinical_only === true || s?.payable_status === "clinical_only");
    const doctorSaveRequired = requiresSavedOrthoClinicalRecord(s);
    const inItems = (!doctorSaveRequired
      && items.some((it) => sameScheduledWork(it, s) && (it.outcome || "done") === "done"))
      || (clinicalOnlyOrtho && (s?.outcome || "done") === "done");
    if (inItems) return "done";
    if (key && chartReconciled?.has?.(key)) return "done";
    const d = chairsideDispositions?.[key] || (s.tn_id ? chairsideDispositions?.[`tn:${s.tn_id}`] : null);
    if (d?.kind === "not_today") return "not_today";
    if (d?.kind === "declined")  return "declined";
    if (d?.kind === "logged")    return "logged";
    return "pending";
  };

  const counts = list.reduce((acc, s) => {
    const st = stateOf(s);
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  function clearDisposition(s) {
    const key = scheduledWorkKey(s);
    setChairsideDispositions((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function markDone(s) {
    if (readOnly) return;
    // Bare tooth-based booking: "Done" checks it off WITHOUT a charge — the
    // real (surface-priced) work is recorded separately. A "logged" disposition
    // has no procedure_visit_id, so the commit ignores it (no bill, no decline).
    if (isBareToothBooking(s)) {
      const key = scheduledWorkKey(s);
      setItems((prev) => prev.filter((it) => !sameScheduledWork(it, s)));
      setChairsideDispositions((prev) => ({ ...prev, [key]: { kind: "logged" } }));
      return;
    }
    setItems((prev) => {
      if (prev.some((it) => sameScheduledWork(it, s))) return prev;
      return [...prev, seedItemFromSchedule(s)];
    });
    clearDisposition(s);
  }

  function undoDone(s) {
    if (readOnly) return;
    setItems((prev) => prev.filter((it) => !sameScheduledWork(it, s)));
  }

  function setNotToday(s) {
    if (readOnly) return;
    setDeferring(s);
  }

  function confirmNotToday(reasonCode) {
    if (!deferring || readOnly) return;
    const s = deferring;
    const key = scheduledWorkKey(s);
    setItems((prev) => prev.filter((it) => !sameScheduledWork(it, s)));
    setChairsideDispositions((prev) => ({
      ...prev,
      [key]: { kind: "not_today", reason_code: reasonCode },
    }));
    setDeferring(null);
  }

  function confirmDecline() {
    if (!declining || readOnly) return;
    const trimmed = (reason || "").trim();
    if (!trimmed) return;
    setItems((prev) => prev.filter((it) => !sameScheduledWork(it, declining)));
    setChairsideDispositions((prev) => ({
      ...prev,
      [scheduledWorkKey(declining)]: { kind: "declined", reason: trimmed },
    }));
    setDeclining(null);
    setReason("");
  }

  async function confirmNoAdjustment() {
    if (!noAdjusting || readOnly || noAdjustmentSubmitting) return;
    const caseId = noAdjusting.ortho_case_id;
    const currentAppointmentId = appointmentId || noAdjusting.appointment_id || noAdjusting.scheduled_appointment_id;
    if (!caseId || !currentAppointmentId || !noAdjustmentReason) {
      toast.error("This Ortho row is missing its case or appointment. Refresh checkout and try again.");
      return;
    }

    setNoAdjustmentSubmitting(true);
    const ohStatusCandidate = noAdjusting.oh_status || "good";
    const ohStatus = ["good", "fair", "poor"].includes(ohStatusCandidate) ? ohStatusCandidate : "good";
    const intervalCandidate = Number(noAdjusting.next_visit_interval_weeks || 4);
    const nextVisitIntervalWeeks = [4, 5, 6, 8].includes(intervalCandidate) ? intervalCandidate : 4;
    const idempotencyKey = buildOrthoVisitIdempotencyKey({
      caseId,
      appointmentId: currentAppointmentId,
      visitType: "no_adjustment",
    });
    const { error } = await supabase.rpc("record_ortho_no_adjustment_visit", {
      p_case_id: caseId,
      p_input: {
        idempotency_key: idempotencyKey,
        appointment_id: currentAppointmentId,
        non_adjustment_reason: noAdjustmentReason,
        oh_status: ohStatus,
        next_visit_interval_weeks: nextVisitIntervalWeeks,
      },
    });
    setNoAdjustmentSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not record no-adjustment visit.");
      return;
    }
    setNoAdjusting(null);
    setNoAdjustmentReason("bracket_repair");
    toast.success("No adjustment recorded for checkout.");
    await onNoAdjustmentSaved?.();
  }

  const fmtFee = (c) => {
    if (c == null) return "Depends on case";
    const sym = currency === "PHP" ? "₱" : currency === "USD" ? "$" : currency ? `${currency} ` : "";
    return sym + Math.round((Number(c) || 0) / 100).toLocaleString("en-US");
  };

  return (
    <section className={embedded ? "h-full" : "bg-white rounded-2xl shadow-sm ring-1 ring-teal-100"} data-testid="checkout-scheduled-panel" data-tutorial-id="checkout-scheduled-panel">
      <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <span className="text-[11px] text-slate-500">
            {(counts.done || 0)}/{list.length} done
            {counts.pending ? <span className="text-amber-700"> · {counts.pending} to review</span> : null}
            {counts.not_today ? <span className="text-sky-700"> · {counts.not_today} not today</span> : null}
            {counts.declined ? <span className="text-rose-700"> · {counts.declined} declined</span> : null}
          </span>
        </div>
        <span className="text-[11px] text-slate-400">{subtitle}</span>
      </header>
      <ul className="divide-y divide-slate-100">
        {list.map((s) => {
          const key = scheduledWorkKey(s);
          const state = stateOf(s);
          const clinicalOnlyOrtho = s?.source === "ortho_checkout"
            && (s?.clinical_only === true || s?.payable_status === "clinical_only");
          const doctorSaveRequired = requiresSavedOrthoClinicalRecord(s);
          const tooth = s.tooth_number ? `#${s.tooth_number}${s.surface ? ` ${s.surface}` : ""}` : "General";
          const rowBg =
            state === "done"      ? "bg-emerald-50/40"
            : state === "logged"    ? "bg-slate-50"
            : state === "not_today" ? "bg-sky-50/40"
            : state === "declined"  ? "bg-rose-50/40"
            : state === "pending"   ? "bg-amber-50/40"
            : "";
          const bareTooth = isBareToothBooking(s);
          const reconciled = chartReconciled?.get?.(key) || null;
          // Done work is NOT struck through — it's completed, billable work
          // the front desk needs to read clearly, not discarded. Only
          // Declined (genuinely cancelled) keeps the crossed-out treatment,
          // so both lists show "done" the same positive way.
          const strike =
            state === "declined" ? "line-through decoration-rose-300"
            : "";
          // Post-op follow-up surfacing per the chief-dentist clinical
          // rule. If this procedure has post_op_followup_enabled, show
          // a chip after the row turns Done. Doctor can wave it off
          // with one tap + reason (followupWaivers state). Receptionist
          // sees the doctor's decision but cannot waive (they didn't
          // do the work; clinical judgment isn't theirs to make).
          const postOp = s.procedure_id ? postOpByProcedure[s.procedure_id] : null;
          const waiverKey = key;
          const waiver = followupWaivers?.[waiverKey] || null;
          const showPostOpChip = !!postOp && state === "done";
          return (
            <li
              key={key}
              className={`px-4 py-2.5 ${rowBg}`}
              data-testid={`checkout-scheduled-item-${safeTestId(key)}`}
            >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className={`text-sm truncate ${state === "pending" ? "text-slate-900" : state === "done" ? "text-slate-700" : "text-slate-500"}`}>
                  <span className={`font-medium ${strike}`}>
                    {s.display_label || s.procedure_name || "Procedure"}
                  </span>
                  <span className="text-slate-400"> · {tooth}</span>
                  {/* An informal MV grows one visit at a time. Its current
                      visit_count is the number of rows recorded/scaffolded so
                      far, not a promised clinical total, so "Visit N of M"
                      is false. Formal Treatment Plans still show their fixed
                      itinerary and named stage. */}
                  {s.template_label && s.visit_count > 1 && !s.is_informal ? (
                    <span className="text-slate-400">
                      {" · Visit "}{s.visit_sequence ?? "?"} of {s.visit_count}
                      {" · "}{s.template_label}
                    </span>
                  ) : null}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  {/* Bare tooth-based bookings carry no real price yet — the
                      chart / recorded work prices them — so hide the flat guess. */}
                  {!bareTooth ? (
                    <span className="text-[11px] text-slate-400">{fmtFee(s.default_fee_centavos)}</span>
                  ) : null}
                  {/* On-plan tag ties this row to the Treatment plan billing
                      card. Standalone scheduled work (e.g. a booked exam not on
                      any plan) has no tag and isn't part of the plan total. */}
                  {s.treatment_plan_id ? (
                    <span className="text-[10px] font-semibold text-teal-800 bg-teal-100 rounded px-1.5 py-0.5 whitespace-nowrap"
                      title={planIsBundle
                        ? "Part of this patient's ongoing treatment"
                        : "On the treatment plan — billed on the plan schedule"}>
                      {planIsBundle ? "Ongoing" : "On plan"}
                    </span>
                  ) : null}
                </div>
                {isPendingClinicalSaveOrthoRow(s) ? (
                  <p className="text-[11px] font-medium text-amber-700 mt-0.5">
                    {doctorSaveRequired
                      ? "Doctor must save the Debonding record before payment."
                      : "Front desk can mark Done, No adjustment, Not today, or Decline."}
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {state === "pending" ? (
                  <span className="text-[10px] uppercase font-bold tracking-wider text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 mr-1 whitespace-nowrap">
                    {isPendingClinicalSaveOrthoRow(s)
                      ? (doctorSaveRequired ? "Waiting for doctor" : "Front desk confirm")
                      : "Review with doctor"}
                  </span>
                ) : null}
                {state === "done" && reconciled ? (
                  <span
                    data-testid={`checkout-scheduled-reconciled-${safeTestId(key)}`}
                    title="Auto-checked from the chart — recorded on the tooth chart this visit"
                    className="text-[11px] font-semibold bg-emerald-100 text-emerald-800 rounded px-2.5 py-1 inline-flex items-center gap-1 whitespace-nowrap">
                    ✓ Done · from chart{reconciled.tooth ? ` #${reconciled.tooth}` : ""}
                  </span>
                ) : state === "done" ? (
                  <button type="button" onClick={() => undoDone(s)} disabled={readOnly || clinicalOnlyOrtho}
                    data-testid={`checkout-scheduled-undo-${safeTestId(key)}`}
                    className="text-[11px] font-semibold bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-40">
                    {clinicalOnlyOrtho ? "✓ No adjustment" : "✓ Done"}
                  </button>
                ) : state === "logged" ? (
                  <button type="button" onClick={() => clearDisposition(s)} disabled={readOnly}
                    data-testid={`checkout-scheduled-clear-logged-${safeTestId(key)}`}
                    title="Logged with no charge — record the real work to bill it"
                    className="text-[11px] font-semibold bg-slate-200 hover:bg-slate-300 text-slate-700 rounded px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-40">
                    ✓ Logged
                  </button>
                ) : state === "not_today" ? (
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => setDeferring(s)} disabled={readOnly}
                      data-testid={`checkout-scheduled-edit-not-today-${safeTestId(key)}`}
                      className="text-[11px] font-semibold bg-sky-100 hover:bg-sky-200 text-sky-800 rounded px-2.5 py-1 disabled:opacity-40">
                      Not today · {notTodayReasonLabel(chairsideDispositions?.[key]?.reason_code, { short: true })}
                    </button>
                    <button type="button" onClick={() => clearDisposition(s)} disabled={readOnly}
                      data-testid={`checkout-scheduled-clear-not-today-${safeTestId(key)}`}
                      className="text-[11px] text-slate-500 hover:text-slate-800 disabled:opacity-40">
                      Undo
                    </button>
                  </div>
                ) : state === "declined" ? (
                  <button type="button" onClick={() => clearDisposition(s)} disabled={readOnly}
                    data-testid={`checkout-scheduled-clear-declined-${safeTestId(key)}`}
                    title={chairsideDispositions[key]?.reason ? `Declined: ${chairsideDispositions[key].reason}` : ""}
                    className="text-[11px] font-semibold bg-rose-100 hover:bg-rose-200 text-rose-800 rounded px-2.5 py-1 disabled:opacity-40">
                    Declined
                  </button>
                ) : (
                  <>
                    {isPendingClinicalSaveOrthoRow(s) && !doctorSaveRequired ? (
                      <button
                        type="button"
                        onClick={() => {
                          setNoAdjusting({ ...s, name: s.procedure_name || "this Ortho visit" });
                          setNoAdjustmentReason("bracket_repair");
                        }}
                        disabled={readOnly || noAdjustmentSubmitting}
                        data-testid={`checkout-scheduled-no-adjustment-${safeTestId(key)}`}
                        className="text-[11px] font-semibold border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 rounded px-2.5 py-1 disabled:opacity-40"
                      >
                        No adjustment
                      </button>
                    ) : null}
                    {!doctorSaveRequired ? (
                      <>
                        <button type="button" onClick={() => markDone(s)}
                          disabled={readOnly}
                          data-testid={`checkout-scheduled-done-${safeTestId(key)}`}
                          className="text-[11px] font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded px-2.5 py-1 disabled:opacity-40">
                          ✓ Done
                        </button>
                        <button type="button" onClick={() => setNotToday(s)} disabled={readOnly}
                          data-testid={`checkout-scheduled-not-today-${safeTestId(key)}`}
                          className="text-[11px] text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded px-2 py-1 disabled:opacity-40">
                          Not today
                        </button>
                        <button type="button"
                          onClick={() => { setDeclining({ ...s, name: s.procedure_name || "this need" }); setReason(""); }}
                          disabled={readOnly}
                          data-testid={`checkout-scheduled-decline-${safeTestId(key)}`}
                          className="text-[11px] text-rose-600 hover:text-rose-800 hover:bg-rose-50 rounded px-2 py-1 disabled:opacity-40">
                          Decline
                        </button>
                      </>
                    ) : null}
                  </>
                )}
              </div>
            </div>
              {showPostOpChip ? (
                <div className="mt-1.5 flex items-center gap-2 text-[11px]"
                     data-testid={`checkout-scheduled-postop-${safeTestId(key)}`}>
                  {waiver ? (
                    <>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600 line-through">
                        ↪ {postOp.followup_procedure_name} in {postOp.after_days}d
                      </span>
                      <span className="text-slate-500 italic">
                        Waived: {waiver.reason}
                      </span>
                      <button type="button" onClick={() => setFollowupWaivers?.((prev) => {
                          const next = { ...prev };
                          delete next[waiverKey];
                          return next;
                        })} disabled={readOnly}
                        title="Restore — re-enable post-op follow-up"
                        className="ml-auto text-[11px] text-slate-500 hover:text-slate-700 disabled:opacity-40">
                        Undo
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold">
                        ↪ {postOp.followup_procedure_name} in {postOp.after_days}d ✓
                      </span>
                      <button type="button" onClick={() => {
                          setWaiveReason(WAIVE_REASONS[0]);
                          setWaiving({ row: s, key: waiverKey, name: s.procedure_name || "this procedure" });
                        }} disabled={readOnly}
                        title="Wave off — doctor judges no follow-up needed (requires a reason)"
                        className="text-[11px] text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline disabled:opacity-40">
                        Wave off
                      </button>
                    </>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      <NotTodayReasonDialog
        open={!!deferring}
        procedureName={deferring?.procedure_name || deferring?.display_label || "this treatment"}
        initialReasonCode={deferring ? chairsideDispositions?.[scheduledWorkKey(deferring)]?.reason_code : null}
        onCancel={() => setDeferring(null)}
        onConfirm={confirmNotToday}
        testIdPrefix="checkout-not-today"
      />

      {declining ? (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
            <p className="text-sm font-semibold text-slate-900">Decline {declining.name}?</p>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              The need closes at checkout commit and won't surface for rebooking.
            </p>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
              placeholder="Reason (required)…"
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-rose-500" />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button type="button" onClick={() => { setDeclining(null); setReason(""); }}
                className="px-3 py-1.5 text-xs border border-slate-200 rounded text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" onClick={confirmDecline} disabled={!reason.trim()}
                className="px-3 py-1.5 text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded disabled:opacity-40">
                Decline
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {noAdjusting ? (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" data-testid="checkout-scheduled-no-adjustment-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
            <p className="text-sm font-semibold text-slate-900">No adjustment today?</p>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              Record the reason for this Ortho visit without saving the routine adjustment or adding its charge.
            </p>
            <label className="block text-xs font-semibold text-slate-700" htmlFor="checkout-no-adjustment-reason">
              Reason
            </label>
            <select
              id="checkout-no-adjustment-reason"
              aria-label="No adjustment reason"
              value={noAdjustmentReason}
              onChange={(event) => setNoAdjustmentReason(event.target.value)}
              disabled={noAdjustmentSubmitting}
              className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 focus:outline-none focus:border-teal-500 disabled:opacity-60"
            >
              {ORTHO_NO_ADJUSTMENT_REASONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setNoAdjusting(null)}
                disabled={noAdjustmentSubmitting}
                className="px-3 py-1.5 text-xs border border-slate-200 rounded text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmNoAdjustment}
                disabled={noAdjustmentSubmitting || !noAdjustmentReason}
                data-testid="checkout-scheduled-no-adjustment-confirm"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded disabled:opacity-40"
              >
                {noAdjustmentSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Record no adjustment
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Wave-off reason picker for the post-op follow-up chip.
          Audit trail: the reason is required (a doctor's clinical
          judgment to skip a default follow-up is a chartable decision,
          not a silent skip). */}
      {waiving ? (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             data-testid="checkout-scheduled-postop-waive-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
            <p className="text-sm font-semibold text-slate-900">Skip the post-op follow-up?</p>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              Defaults are conservative — most {waiving.name.toLowerCase()} cases benefit from a follow-up.
              Pick a reason; the chart will keep the record.
            </p>
            <div className="flex flex-col gap-1.5">
              {WAIVE_REASONS.map((r) => (
                <label key={r} className="inline-flex items-center gap-2 text-xs text-slate-700">
                  <input type="radio" name="waive-reason" value={r}
                    checked={waiveReason === r}
                    onChange={() => setWaiveReason(r)} />
                  {r}
                </label>
              ))}
              {waiveReason === "Other" ? (
                <input type="text" autoFocus
                  placeholder="Type the reason…"
                  onChange={(e) => setWaiveReason(e.target.value)}
                  className="mt-1 px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-teal-500" />
              ) : null}
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button type="button" onClick={() => { setWaiving(null); setWaiveReason(""); }}
                className="px-3 py-1.5 text-xs border border-slate-200 rounded text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" disabled={!waiveReason.trim() || waiveReason === "Other"}
                onClick={() => {
                  setFollowupWaivers?.((prev) => ({
                    ...prev,
                    [waiving.key]: { waived: true, reason: waiveReason.trim() },
                  }));
                  setWaiving(null);
                  setWaiveReason("");
                }}
                className="px-3 py-1.5 text-xs font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded disabled:opacity-40">
                Skip follow-up
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function seedItemFromSchedule(s) {
  return seedCheckoutScheduledItem(s);
}

function scheduledWorkKey(row) {
  return checkoutWorkKey(row);
}

function findOrthoCollectionTarget(doneItems) {
  const rows = Array.isArray(doneItems) ? doneItems : [];
  return rows.find((it) => {
    if (it?.source !== "ortho_checkout") return false;
    if (it?.payable_status !== "payable") return false;
    const contractRemaining = Number(it.contract_remaining_centavos || 0);
    const currentVisit = Number(it.current_visit_charge_centavos ?? it.performed_price_centavos ?? 0);
    if (contractRemaining <= currentVisit) return false;
    if (it.collection_options_required === true) return true;
    if (it.is_final_ortho_visit === true) return true;
    const code = String(it.procedure_code || "").toUpperCase();
    const label = `${it.procedure_name || ""} ${it.display_label || ""}`.toLowerCase();
    return code === "ALGN-END" || label.includes("completion") || label.includes("debond");
  }) || null;
}

function normalizeOrthoCollectionChoice(item, choice) {
  if (!item) {
    return { decision: "collect_remaining", amount_centavos: 0, underpayment_confirmed: false };
  }
  const key = scheduledWorkKey(item);
  const contractRemaining = Number(item.contract_remaining_centavos || 0);
  const currentVisit = Number(item.current_visit_charge_centavos ?? item.performed_price_centavos ?? 0);
  if (!choice || choice.key !== key) {
    return {
      decision: "collect_remaining",
      amount_centavos: contractRemaining,
      underpayment_confirmed: false,
    };
  }
  const decision = choice.decision === "collect_visit_only" ? "collect_visit_only" : "collect_remaining";
  const rawAmount = decision === "collect_visit_only" ? currentVisit : contractRemaining;
  return {
    ...choice,
    decision,
    amount_centavos: Math.min(Math.max(rawAmount, 0), contractRemaining),
    underpayment_confirmed: !!choice.underpayment_confirmed,
  };
}

function getOrthoCollectionDueCents(item, state) {
  if (!item) return 0;
  const contractRemaining = Number(item.contract_remaining_centavos || 0);
  const currentVisit = Number(item.current_visit_charge_centavos ?? item.performed_price_centavos ?? 0);
  if (state?.decision === "collect_visit_only") {
    return Math.min(currentVisit, contractRemaining);
  }
  return contractRemaining;
}

function getOrthoCollectionShortfallCents(item, state) {
  if (!item) return 0;
  const contractRemaining = Number(item.contract_remaining_centavos || 0);
  return Math.max(0, contractRemaining - getOrthoCollectionDueCents(item, state));
}

function getOrthoCollectionExtraCents(item, state) {
  if (!item) return 0;
  const contractRemaining = Number(item.contract_remaining_centavos || 0);
  const currentVisit = Number(item.current_visit_charge_centavos ?? item.performed_price_centavos ?? 0);
  const due = getOrthoCollectionDueCents(item, state);
  return Math.max(0, Math.min(due, contractRemaining) - currentVisit);
}

function sameScheduledWork(a, b) {
  return sameCheckoutWork(a, b);
}

function safeTestId(value) {
  return String(value || "unknown").replace(/[^a-zA-Z0-9_-]/g, "-");
}

// =====================================================================
// ExitConfirmDialog — fires when the receptionist tries to leave a
// checkout that has unsaved staged work. Three exits:
//   * Stay and finish      → primary, default focus, just closes the dialog.
//   * Save and come back   → secondary, draft is already auto-saved; just
//                            navigates away. Pencil bookings stay reserved.
//   * Discard checkout     → calls discard_checkout_draft_v2, which releases
//                            any Pencil-status appointments staged from
//                            checkout while preserving doctor-staged
//                            procedures and chairside dispositions.
//
// Summary list shows the clerk exactly what's at stake so the choice is
// informed, not a generic "leave?" prompt.
// =====================================================================
// Map a route path to a friendly label for the dialog header. Falls back to
// the path itself if unknown. Keeps in sync with the Sidebar NAV labels.
const NAV_LABELS = {
  "/calendar":      "Calendar",
  "/dashboard":     "Dashboard",
  "/patients":      "Patients",
  "/doctor":        "Charting",
  "/recall-center": "Recall",
  "/tx-plans":      "Treatment plans",
  "/ortho-center":  "Ortho",
  "/checkout":      "Checkout queue",
  "/insurance-claims": "HMO Claims",
  "/reports":       "Reports",
  "/settings":      "Settings",
  "/admin-panel":   "Admin Panel",
};
function destinationLabel(path) {
  if (!path) return "Calendar";
  return NAV_LABELS[path] || path;
}

function ExitConfirmDialog({
  items, chairsideDispositions, dispositions, paymentState,
  pencilBookingCount, discarding, destination,
  onStay, onSaveAndLeave, onDiscardAndLeave,
}) {
  const summary = [];
  const doneCount = items.filter((it) => (it.outcome || "done") === "done").length;
  const otherItemsCount = items.length - doneCount;
  if (doneCount > 0)
    summary.push(`${doneCount} procedure${doneCount === 1 ? "" : "s"} recorded as Done`);
  if (otherItemsCount > 0)
    summary.push(`${otherItemsCount} other Today item${otherItemsCount === 1 ? "" : "s"} pending`);
  const chairsideCount = Object.keys(chairsideDispositions || {}).length;
  if (chairsideCount > 0)
    summary.push(`${chairsideCount} chairside disposition${chairsideCount === 1 ? "" : "s"} (Not today / Decline)`);
  let bookCount = 0, otherDispCount = 0;
  for (const d of dispositions.values()) {
    if (d.kind === "book") bookCount++;
    else otherDispCount++;
  }
  if (bookCount > 0)
    summary.push(`${bookCount} future appointment${bookCount === 1 ? "" : "s"} pencilled in`);
  if (otherDispCount > 0)
    summary.push(`${otherDispCount} other follow-up decision${otherDispCount === 1 ? "" : "s"}`);
  if (paymentState.amount_tendered_cents > 0) summary.push("Payment amount entered");
  if (paymentState.visit_discount_cents > 0)  summary.push("Visit discount entered");
  if (paymentState.hmo_covered_cents > 0)     summary.push("HMO coverage entered");

  return (
    <div role="dialog" aria-modal="true"
      data-testid="checkout-exit-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !discarding) onStay(); }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col">
        <div className="px-6 py-5 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            Hold up &mdash; checkout isn&rsquo;t finished
          </h2>
          <p className="text-xs text-slate-500 mt-1.5">
            You&rsquo;re about to leave this checkout to go to <strong className="text-slate-700">{destinationLabel(destination)}</strong>. Pick what to do with your in-progress work.
          </p>
        </div>
        <div className="px-6 py-4 space-y-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">What&rsquo;s currently staged</p>
            <ul className="text-sm text-slate-700 space-y-1">
              {summary.length === 0 ? (
                <li className="text-slate-400">Nothing tracked yet — safe to leave.</li>
              ) : summary.map((line, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-slate-400">&bull;</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          {pencilBookingCount > 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900">
              <strong>Heads up:</strong> the {pencilBookingCount === 1 ? "future slot you pencilled in stays" : `${pencilBookingCount} future slots you pencilled in stay`} reserved on the calendar if you leave. Choose <em>Discard checkout changes</em> to release {pencilBookingCount === 1 ? "it" : "them"} while keeping the doctor&rsquo;s staged work.
            </div>
          ) : null}
        </div>
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-2 flex-wrap">
          <button type="button" onClick={onStay} disabled={discarding}
            data-testid="checkout-exit-stay"
            autoFocus
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
            Stay and finish
          </button>
          <button type="button" onClick={onSaveAndLeave} disabled={discarding}
            data-testid="checkout-exit-save"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
            Save draft &amp; go to {destinationLabel(destination)}
          </button>
          <button type="button" onClick={onDiscardAndLeave} disabled={discarding}
            data-testid="checkout-exit-discard"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 rounded-lg disabled:opacity-50">
            {discarding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Discard checkout changes
          </button>
        </div>
      </div>
    </div>
  );
}
